// DispatchDeck - BI Module
// Customer-facing Business Intelligence dashboard
// Founder-only for v1: fleet utilization, financials, maintenance health, operations
console.log('[BI] Module loaded — v1.15.1068 (date-range selector)');

import {
  db,
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
  functions,
  httpsCallable
} from '../firebase-config.js?v=1868';
// TASK-4866 — firebase-config.js does not re-export runTransaction. Imported
// straight from the CDN, exactly as hr.js:41 already does for serverTimestamp,
// and from the IDENTICAL 10.8.0 URL firebase-config.js itself imports, so the
// browser serves ONE module instance. (Pitfall #1 is about cross-importing OUR
// modules with a `?v=` buster — a CDN SDK URL is not that.)
//
// Load-bearing: `linesOfCredit` is a WHOLE-ARRAY field on the tenant root, and
// as of TASK-4866 it has a SECOND writer (hr.js, via
// window.BIModule.upsertCreditCardByName). Two read-modify-write updateDoc()
// calls racing on one array silently drop whichever landed first. Every writer
// in this file now goes through _writeLinesOfCredit's transaction.
import { runTransaction } from 'https://www.gstatic.com/firebasejs/10.8.0/firebase-firestore.js';
// TASK-9872 — side-effect module: attaches window.RevenueCanon (which revenue rows count).
import '../shared/revenue-entry-canon.js?v=9942001';
// TASK-9878 S2 — side-effect module: attaches window.JobCost (the labor-cost
// rate rule + the Customer / Job / Project drill-down attribution, shared with
// the Claude connector's get_job_cost). Imported ONLY here.
import '../shared/job-cost.js?v=10171004'; // TASK-10171 item 4 maintCost
// TASK-9701 S1 — side-effect module: attaches window.PnlStatement (the one P&L statement).
// Loaded with a dynamic import so it stays out of the cold-load bundle (perf budget
// appColdLoadGzip); it starts loading when bi.js evaluates and the P&L tab waits on it.
const _pnlStatementReady = __ddChunkWrap(import('../shared/pnl-statement.js?v=10342131')) // TASK-9701 fu loan month-starts inside the period · TASK-10172-A capital categories + pnlUnitInPeriod over TASK-10171 shop-once (integ/2129)
  .then(() => !!window.PnlStatement, (err) => { console.error('[BI] pnl-statement load', err); return false; });
// TASK-9701 S2 — the printable statement + the P&L CSV rows (lazy, views/biPnlPrint.js).
let _pnlPrintLoad = null;
function _loadPnlPrint() {
  if (!_pnlPrintLoad) _pnlPrintLoad = __ddChunkWrap(import('./biPnlPrint.js?v=10302129')).catch((e) => { _pnlPrintLoad = null; throw e; });
  return _pnlPrintLoad;
}

// ============================================
// COST-STATUS PREDICATES (TASK-564) — SOURCE-SCOPED
// ============================================
// Driver/staff-submitted expenses (costEntries with source:'driver' from
// driverExpenseCallable, or source:'staff' from createMemberExpense — TASK-527)
// carry a review lifecycle: status pending|pending_ocr → approved|rejected|clarification_requested.
// Maintenance/inventory/admin costEntries have NO `source` field (they write
// `sourceType`/`source:'admin'` and their own statuses), so these gates are a
// strict no-op for them — they MUST stay unchanged. Hence the source-scope FIRST:
// a status-only filter would wrongly drop maintenance/inventory entries whose
// own 'rejected'/'pending_review' statuses mean something different.
//
// Defined identically in bi.js AND reconciliation.js (NOT cross-imported): ES
// modules with mismatched ?v= cache-busters create duplicate instances
// (CLAUDE.md pitfall #1). Keep the two copies in sync if either changes.

// Is this a *recognized* cost for financial aggregates (P&L, truck rollups, ROI)?
// Excludes driver/staff submissions that are rejected or not-yet-approved.
// ---------------------------------------------------------------------------
// TASK-4529 — fleet ref resolver. A jobDay/ticket ref may be a Firestore doc
// ID, a unit number, a nickname, or a legacy name — and on a live tenant one
// truck's DOC ID can equal another truck's UNIT number (TITS: doc id "15" =
// Dove, while Blue Jay's unit = "15"). A single-pass
// `find(t => t.id === ref || t.unit === ref)` returns whichever happens to
// sort first, so ID matches must be exhausted across the WHOLE list before
// any unit/nickname/name match is considered.
// KEEP IN SYNC — identical copies live in dispatch.js / jobboard.js /
// clock.js / billing.js / bi.js / jsa.js (cross-imports forbidden: Pitfall #1
// — ?v= cache busters make duplicate module instances). Twin registered in
// docs/audits/twin-audit-2026-08-19.md. A seventh SERVER copy lives in
// functions/src/services/ticketGateway.js. Byte-equality of all copies is
// asserted by tests/task4529-fleet-ref-resolver.unit.cjs.
function _resolveFleetRef(ref, list) {
  if (ref === null || ref === undefined || ref === '') return null;
  const s = String(ref);
  const arr = list || [];
  const byId = arr.find(x => x && x.id !== null && x.id !== undefined && String(x.id) === s);
  if (byId) return byId;
  const eq = (v) => v !== null && v !== undefined && v !== '' && String(v) === s;
  return arr.find(x => x && (eq(x.unit) || eq(x.nickname) || eq(x.name) || eq(x.partNumber))) || null;
}

// TASK-9917 — a company-card charge marked personal (markExpensePersonal):
// payer 'owner' = an owner draw, 'employee' = owed back to the company. Never a
// business cost — every cost reader that goes through isRecognizedCost drops
// it; the equity / balance-sheet readers below pick it up on purpose.
// Mirrors functions/src/services/personalCharge.js personalChargeOf.
function personalChargeOf(c) {
  const p = c && c.personalCharge;
  return p && typeof p === 'object' && (p.payer === 'owner' || p.payer === 'employee') ? p : null;
}
// An owner draw: the Owner Draws category, or a charge marked personal for the owner.
function isOwnerDrawCost(c) {
  const p = personalChargeOf(c);
  return p ? p.payer === 'owner' : !!c && c.category === 'draws';
}
// What an employee still owes on a personal charge (dollars; 0 otherwise).
function personalOwedOutstanding(c) {
  const p = personalChargeOf(c);
  if (!p || p.payer !== 'employee') return 0;
  const repaid = Number(p.repaidAmount);
  const left = Math.round(((Number(c.amount) || 0) - (Number.isFinite(repaid) ? repaid : 0)) * 100) / 100;
  return left > 0 ? left : 0;
}

// TASK-9917 slice 2 — repayments against employee personal charges, in the
// GL CSV / IIF / bank-feed exports. The booking logic lives in the LAZY
// views/personalRepay.js (perf budget — it only runs when an export is
// clicked); bi.js hands it personalChargeOf + toDate (pitfall #1: no
// cross-import between ?v= modules).
function _loadPersonalRepay() {
  return __ddChunkWrap(import('./personalRepay.js?v=9917701'));
}
const _personalRepayDeps = () => ({ personalChargeOf, toDate });

// TASK-9917 (COD r3) — the category an EXPORT books a cost under: a personal
// charge is not its receipt category. Owner -> Owner Draws; employee -> Due
// from Employees (a receivable, not an expense). Exports (IIF / GL / expense
// report) list every cost entry, so they relabel instead of dropping the cash-out.
function exportCategoryOf(c) {
  const p = personalChargeOf(c);
  if (p) return p.payer === 'owner' ? 'draws' : 'employee_receivable';
  return (c && c.category) || 'other';
}

function isRecognizedCost(c) {
  // TASK-9917 — a company-card charge marked personal is not a business cost.
  // Inline (not personalChargeOf) so this stays self-contained: tests and the
  // qboExpenseStaging mirror lift it alone.
  if (c && c.personalCharge && (c.personalCharge.payer === 'owner' || c.personalCharge.payer === 'employee')) return false;
  // TASK-9947 — a refund recorded since refund approvals (it carries refundTo)
  // lowers cost only once a second person approved it AND the expense it
  // reverses counts (a pending / rejected driver expense: neither counts).
  // Older credits were approved when written and keep counting.
  if (c && c.entryType === 'credit' && Object.prototype.hasOwnProperty.call(c, 'refundTo')) {
    return c.status === 'approved' && c.originalApproved !== false;
  }
  if (c && (c.source === 'driver' || c.source === 'staff')) {
    if (c.status === 'rejected' || c.status === 'pending' ||
        c.status === 'clarification_requested' || c.status === 'pending_ocr') {
      return false;
    }
  }
  return true;
}

// TASK-9493 — split receipts. One costEntry at the FULL amount may carry
// `allocations: [{costAllocationId, jobId, vehicleId, amount}]`. Every reader
// that attributes cost to a VEHICLE, JOB, CUSTOMER, PROJECT or ALLOCATION reads
// through these two instead of the raw entries, so a split counts once per line
// and REPLACES the parent's (null) attribution. An unsplit entry expands to
// `[c]` — the SAME object — so every existing sum is byte-identical. Readers
// that only total by category / period / vendor keep the raw list on purpose:
// the lines sum to the parent in cents, and a count of entries must not grow.
// window.CostSplit is the generated mirror of functions/src/shared/
// costAllocationSplit.js (app.js side-effect import); if it is ever missing the
// fallback reads every entry as unsplit — totals stay right, a split entry
// shows as unassigned rather than double-counting.
function _costViews(c) {
  return (window.CostSplit ? window.CostSplit.expandCostEntry(c) : [c]).map(_withAllocationVehicle);
}
// TASK-9592 — a truck/trailer Cost Allocation names the vehicle. Entries written
// before the server derived vehicleId from it (or by any writer that missed it)
// carry the allocation but a blank vehicleId, and were invisible to per-truck
// cost. Fall back to the allocation's refId — ONLY when the view has no vehicle
// at all (vehicleId / truckId / trailerId / assetId), never overriding one. Returns a clone when it
// fills, the same object otherwise; the raw entry is never mutated (edit flows
// read BiState.costEntries).
function _withAllocationVehicle(c) {
  if (!c || c.vehicleId || c.truckId || c.trailerId || c.assetId || !c.costAllocationId) return c;
  const a = BiState.costAllocById ? BiState.costAllocById.get(c.costAllocationId) : null;
  if (!a || (a.type !== 'truck' && a.type !== 'trailer')) return c;
  const ref = a.refId == null ? '' : String(a.refId).trim();
  if (!ref) return c;
  return { ...c, vehicleId: ref, vehicleType: c.vehicleType || a.type, _vehicleFromAllocation: true };
}
// Memoized per ARRAY: BiState.costEntries is replaced wholesale on every
// snapshot (never mutated in place), and the per-truck loops call this once per
// vehicle — without the cache that is trucks x entries fresh allocations.
const _costViewsCache = new WeakMap();
function _costViewsOf(list) {
  // TASK-9592 — every view also gets the allocation-vehicle fallback. The cache
  // keys on the allocation map too: it is replaced wholesale on each snapshot.
  const allocs = BiState.costAllocById || null;
  if (!window.CostSplit) return (list || []).map(_withAllocationVehicle);
  if (!Array.isArray(list)) return window.CostSplit.expandCostEntries(list || []).map(_withAllocationVehicle);
  const hit = _costViewsCache.get(list);
  if (hit && hit.len === list.length && hit.allocs === allocs) return hit.out;
  const out = window.CostSplit.expandCostEntries(list).map(_withAllocationVehicle);
  _costViewsCache.set(list, { len: list.length, allocs, out });
  return out;
}

// Is this pretrip (DVIR) a pass? (TASK-593) — the DVIR flow writes pretrips with
// `status:'pass'|'defects'` and NO `result` field (canonical write pretrip.js:3113);
// some older/patched docs carry `result:'pass'|'Pass'` instead. Test BOTH fields.
// Testing only `result` made the Overview pass rate read 0% and the Recent DVIR
// Defects panel list every inspection. Use this helper at EVERY pass/fail read site
// so the predicates can't drift again. "Defect" = !_isPretripPass(p).
function _isPretripPass(p) {
  return p.status === 'pass' || p.result === 'pass' || p.result === 'Pass';
}

// ============================================
// STATE
// ============================================
const BiState = {
  tenantId: null,
  revenueBasis: 'invoiced', // TASK-9242 — Overview revenue card only: 'invoiced' | 'performed'. Persisted per tenant.
  unsubscribers: [],
  currentTab: 'overview',
  period: 'month',
  periodStart: null,
  periodEnd: null,
  customStart: null,   // ISO 'YYYY-MM-DD' from the custom-range date input
  customEnd: null,     // ISO 'YYYY-MM-DD' from the custom-range date input

  // Raw Firestore data
  tickets: [],
  ticketLinesCache: {},
  jobs: [],
  jobDays: [],
  trucks: [],
  // TASK-10172-A — deactivated / sold units, kept so a past period's P&L still
  // has them (PnlStatement.pnlUnitInPeriod decides which period). Never in the
  // active lists.
  endedUnits: { trucks: [], trailers: [], assets: [] },
  trailers: [],
  assets: [],
  maintenance: [],
  pretrips: [],
  clockEntries: [],
  people: [],
  customers: [],
  timeOff: [],
  metersLog: [],
  revenueEntries: [],
  revenueEntriesRaw: [], // TASK-9872 — every row as stored; revenueEntries = the canonical set
  revenueDropped: [],    // TASK-9872 — rows left out of revenueEntries, with the reason
  // TASK-842-C — server-only rental-slice ledger (billing/admin read-gated).
  // null = not loaded / unreadable for this persona → every revenue surface
  // stays on the legacy per-entry path. Array = engine-aware period aggregation.
  rentalSlices: null,
  costEntries: [],
  // TASK-9592 — costAllocations by id (null = not loaded / unreadable). Feeds
  // the per-truck fallback in _withAllocationVehicle.
  costAllocById: null,
  apInvoices: [],   // AP invoices for aging report
  // TASK-9969 S2 — QuickBooks transfer summary for the Financial Health card.
  // null = no QuickBooks connection (card unchanged). See ensureQbBooksSummary.
  qbBooks: null,

  // Financing data (from tenant doc)
  linesOfCredit: [],
  investorNotes: [],
  balanceSheetConfig: {},

  // Lazy-load flags
  dataLoaded: { core: false, tickets: false, jobs: false, maintenance: false, pretrips: false, clock: false, timeOff: false, revenue: false, rentalSlices: false }, // TASK-9512: revenue + rentalSlices = settled (snapshot OR error)

  // Maintenance reserve config
  biConfig: { reserves: { vac: 18, semi: 12, _default: 12 } },

  // Visual card period toggle: 'ytd' | 'mtd' | 'wtd'
  visualCardPeriod: { people: 'ytd', vehicles: 'ytd', trailers: 'ytd', assets: 'ytd', customers: 'ytd' }
};

// ============================================
// PERIOD HELPERS
// ============================================
function computePeriodDates(period) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  let start;

  // Custom range: parse stored ISO date strings in LOCAL time (not UTC — `new Date('YYYY-MM-DD')`
  // would drift across the day boundary). Falls back to current month if either bound is missing/invalid.
  if (period === 'custom') {
    const s = BiState.customStart ? new Date(BiState.customStart + 'T00:00:00') : null;
    const e = BiState.customEnd ? new Date(BiState.customEnd + 'T23:59:59') : null;
    if (s && e && !isNaN(s) && !isNaN(e) && s <= e) {
      BiState.periodStart = s;
      BiState.periodEnd = e;
      return;
    }
    // Invalid/incomplete custom range — keep whatever window was last valid; don't null out.
    if (BiState.periodStart && BiState.periodEnd) return;
    // No prior window at all: fall through to month default below.
  }

  switch (period) {
    case 'week': {
      const day = now.getDay();
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
      break;
    }
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'quarter': {
      const qMonth = Math.floor(now.getMonth() / 3) * 3;
      start = new Date(now.getFullYear(), qMonth, 1);
      break;
    }
    case 'ytd':
      start = new Date(now.getFullYear(), 0, 1);
      break;
    default:
      start = new Date(now.getFullYear(), now.getMonth(), 1);
  }

  BiState.periodStart = start;
  BiState.periodEnd = today;
}

function toDate(val) {
  if (!val) return null;
  if (val.toDate) return val.toDate();
  if (val instanceof Date) return val;
  if (typeof val === 'string') return new Date(val);
  return null;
}

// TASK-9979 — BI's overtime weeks are PAYROLL's workweeks: the same helper
// clock.js and payRates.js use (window.TenantLocalTime.weekKey — company zone,
// settings.workWeekStartDay, TITS = Monday) and the same threshold source as
// clock.js getOTThreshold. The old key was the UTC date of "Sunday at the
// clock-in's time of day", so a Central clock-in after ~7 PM landed in the
// next week's bucket and OT over 40 was undercounted.
function _biOtWeekKey(d) {
  const s = (window.AppState && window.AppState.settings) || {};
  return window.TenantLocalTime.weekKey(d, s.workWeekStartDay ?? 1);
}
function _biOtThreshold() {
  const t = Number(window.AppState?.settings?.overtimeThreshold ?? 40);
  return Number.isFinite(t) ? t : 40;
}

function isInPeriod(dateVal) {
  const d = toDate(dateVal);
  if (!d) return false;
  return d >= BiState.periodStart && d <= BiState.periodEnd;
}

function getPeriodDays() {
  const ms = BiState.periodEnd - BiState.periodStart;
  return Math.max(1, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

// ============================================
// TREND HELPERS (previous-period comparison)
// ============================================
function getPreviousPeriodDates() {
  const duration = BiState.periodEnd - BiState.periodStart;
  return {
    start: new Date(BiState.periodStart.getTime() - duration),
    end: new Date(BiState.periodStart.getTime() - 1)
  };
}

function isInPreviousPeriod(dateVal) {
  const d = toDate(dateVal);
  if (!d) return false;
  const prev = getPreviousPeriodDates();
  return d >= prev.start && d <= prev.end;
}

// ============================================
// TASK-9242 — OVERVIEW REVENUE BASIS (invoiced vs work performed)
// ============================================
// The Overview revenue card runs on the INVOICE clock (revenueEntries.invoicedAt)
// while Total Jobs beside it runs on the WORK clock (jobDays.date). The toggle lets
// the owner read the card on either clock. Overview only — Financial, P&L, Financing,
// Reconciliation and per-vehicle/customer revenue stay accrual-on-invoice.
function _biRevenueBasisKey(tenantId) { return 'bi.revenueBasis.' + tenantId; }

function loadRevenueBasis(tenantId) {
  try {
    const v = window.localStorage.getItem(_biRevenueBasisKey(tenantId));
    return v === 'performed' ? 'performed' : 'invoiced';
  } catch (e) { return 'invoiced'; }
}

function saveRevenueBasis(tenantId, basis) {
  try { window.localStorage.setItem(_biRevenueBasisKey(tenantId), basis); } catch (e) { /* private window / blocked storage — per-viewer convenience only */ }
}

// The day the work happened, as a LOCAL-NOON Date.
// 🔴 `t.workDate` is a bare 'YYYY-MM-DD' string. toDate() would parse it as UTC midnight
// (Sep 5 19:00 CDT for '2026-09-06') and shift every ticket one day early in any
// UTC-negative timezone — silently, at both window edges. Parse the parts into local noon.
// 🔴 BI keeps RAW ticket docs: on a ticket with no workDate, `t.date` is a Firestore
// Timestamp. Branch on type — never call .split on an unchecked value.
function _biWorkDate(t) {
  const wd = t && t.workDate;
  if (typeof wd === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(wd);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  } else if (wd) {
    const d = toDate(wd);
    if (d && !isNaN(d.getTime())) return d;
  }
  const fb = t && t.date;
  if (fb) {
    const d = toDate(fb);
    if (d && !isNaN(d.getTime())) return d;
  }
  return null;
}

// revenueEntry-shaped rows for tickets whose WORK day is in range. Drafts count (the work
// happened) and are flagged _unbilled so the card can name them. Mirrors the invoiced
// sibling on writtenOff (does not filter it). Amount = stored grandTotal — see TASK-9242
// for why that can differ ~0.5% from the invoiced basis; disclosed in the UI, not fixed here.
function performedRevenueEntries(inRange) {
  const out = [];
  (BiState.tickets || []).forEach(t => {
    if (!t || t.status === 'deleted') return;
    const wd = _biWorkDate(t);
    if (!wd || !inRange(wd)) return;
    out.push({
      amount: Number(t.grandTotal) || 0,
      _periodDate: wd,
      _performed: true,
      _unbilled: !_biTicketInvoiced(t),
      ticketId: t.id || null,
      ticketNo: t.ticketNumber || t.ticketNo || null,
      customerName: t.customerName || null,
      customerId: t.customerId || null,
      jobName: t.jobName || t.jobTitle || null, // mirrors recordRevenueEntry (billing.js) — legacy drill labels
      jobId: t.jobId || null,
      invoicedAt: t.invoicedAt || null
    });
  });
  return out;
}

function setRevenueBasis(basis) {
  BiState.revenueBasis = basis === 'performed' ? 'performed' : 'invoiced';
  saveRevenueBasis(BiState.tenantId, BiState.revenueBasis);
  renderOverviewTab();
}

// ============================================
// TASK-842-C — RENTAL PERIOD-AWARE REVENUE SELECTION (dual-path)
// ============================================
// The rental-period engine (TASK-842-A) bills ONE rental job as SEVERAL tickets
// over its life (one per billing slice). Each invoiced ticket writes one
// revenueEntry stamped with the INVOICE date, so pure per-entry aggregation
// books a multi-week rental slice entirely on the day it was invoiced
// (mis-bucketed periods), and any surface re-deriving rental amounts per ticket
// could count a re-billed period twice. The server-only `rentalSlices` ledger
// (firestore.rules ~3011; deterministic doc id per (job, deployTx, sliceStart))
// is the source of truth for WHAT period each rental dollar covers — so rental
// revenue is aggregated by PERIOD, once per slice, not per ticket line.
//
// Selector for the accrual (invoicedAt-basis) revenue surfaces:
//   • Entry whose ticket has NO slice records — legacy rental tickets,
//     all non-rental tickets, or slices unreadable/absent — behaves EXACTLY
//     like today: included iff inRange(invoicedAt), amount untouched.
//   • Entry whose ticket HAS slice records (engine rental ticket):
//     – each slice contributes its own amount, attributed to the period it
//       covers (sliceStartMs), deduped by slice doc id across ALL entries so a
//       period can never count twice;
//     – the non-slice residual (hand-added lines on the rental ticket) stays on
//       the invoice date, with the 'Rentals' bucket of its revenueBreakdown
//       reduced by the slice total (engine slice lines default to 'Rentals').
//   Conservation: residual + slice amounts == entry amount, unless TASK-511
//   line exclusions invoiced less than the slice ledger (residual clamps at 0).
// Deliberately NOT wired: cash-basis (paidAt) surfaces (cash = money movement,
// per-entry is correct), AR aging (per-invoice by design), the bank-feed export
// (transaction-shaped rows must match real deposits), and all-time balance-sheet
// sums (conservation makes them identical).
function rentalAwareRevenueEntries(inRange) {
  const all = BiState.revenueEntries || [];
  const slices = BiState.rentalSlices;
  if (!Array.isArray(slices) || slices.length === 0) {
    // Legacy path — also the graceful degrade when this persona can't read
    // rentalSlices (billing/admin gate) or the tenant has no engine rentals.
    return all.filter(r => inRange(r.invoicedAt));
  }
  const byTicket = Object.create(null);
  for (const s of slices) {
    if (!s || !s.ticketId) continue; // also skips the `_lease_` concurrency docs (no ticketId)
    (byTicket[s.ticketId] = byTicket[s.ticketId] || []).push(s);
  }
  const out = [];
  const seenTickets = new Set();
  const seenSlices = new Set();
  for (const r of all) {
    const tSlices = r.ticketId ? byTicket[r.ticketId] : undefined;
    if (!tSlices) {
      if (inRange(r.invoicedAt)) out.push(r); // non-rental + legacy rental math untouched
      continue;
    }
    // Engine rental ticket — slices are the source of truth for the rental portion.
    // COD catch (Code Reviewer, 2026-07-17): the residual MUST sit behind the
    // per-ticket dedup too — pre-TASK-950 duplicate revenue entries for the same
    // ticket would otherwise each emit their own residual row (duplicate
    // residual = the exact double-count this selector exists to prevent).
    // First entry wins for BOTH residual and slices.
    if (seenTickets.has(r.ticketId)) continue;
    seenTickets.add(r.ticketId);
    let sliceSum = 0;
    for (const s of tSlices) sliceSum += (Number(s.amount) || 0);
    const residual = Math.max(0, (Number(r.amount) || 0) - sliceSum);
    if (residual > 0.005 && inRange(r.invoicedAt)) {
      const breakdown = _residualRevenueBreakdown(r.revenueBreakdown, sliceSum);
      const res = { ...r, amount: Math.round(residual * 100) / 100, _rentalResidual: true };
      if (breakdown) {
        res.revenueBreakdown = breakdown;
        res.revenueCategories = [...new Set(breakdown.map(b => b.category).filter(c => c && c !== 'Uncategorized'))];
      } else if (res.revenueBreakdown) {
        delete res.revenueBreakdown;
        delete res.revenueCategories;
      }
      out.push(res);
    }
    // (per-ticket dedup happens above, before the residual — see COD note)
    for (const s of tSlices) {
      const sid = s.id || (r.ticketId + ':' + s.sliceStartMs);
      if (seenSlices.has(sid)) continue;
      seenSlices.add(sid);
      const amt = Math.round((Number(s.amount) || 0) * 100) / 100;
      if (!amt) continue; // $0 (e.g. rateUnresolved) slices add nothing
      const startMs = Number(s.sliceStartMs);
      // Attribute to the slice period; a slice without a usable start falls
      // back to invoice-date attribution (conservation over precision).
      const periodDate = Number.isFinite(startMs) && startMs > 0 ? new Date(startMs) : null;
      if (!inRange(periodDate || r.invoicedAt)) continue;
      const sliceEntry = {
        ...r,
        amount: amt,
        invoicedAt: periodDate || r.invoicedAt, // toDate() handles Date instances
        workDate: periodDate ? isoDateLocal(periodDate) : (r.workDate || null),
        revenueBreakdown: [{ category: 'Rentals', amount: amt }],
        revenueCategories: ['Rentals'],
        _rentalSlice: true,
        _sliceId: sid
      };
      // Tax stays on the invoice-dated record only — never duplicated across
      // synthetic slice rows (no BI surface reads tax off revenueEntries today).
      delete sliceEntry.subtotal; delete sliceEntry.taxAmount;
      delete sliceEntry.taxRate; delete sliceEntry.taxEnabled;
      out.push(sliceEntry);
    }
  }
  return out;
}

// Reduce the 'Rentals' bucket of an entry's revenueBreakdown by the slice total
// (the slice-covered portion is re-attributed to its periods by the caller).
function _residualRevenueBreakdown(breakdown, sliceSum) {
  if (!Array.isArray(breakdown) || breakdown.length === 0) return null;
  let remaining = sliceSum;
  const out = [];
  for (const b of breakdown) {
    if (!b) continue;
    const amt = Number(b.amount) || 0;
    if (b.category === 'Rentals' && remaining > 0) {
      const take = Math.min(remaining, amt);
      remaining -= take;
      const left = amt - take;
      if (left > 0.005) out.push({ category: 'Rentals', amount: Math.round(left * 100) / 100 });
    } else {
      out.push(b);
    }
  }
  return out.length ? out : null;
}

function trendBadge(current, previous, invertColors) {
  // TASK-1266: a metric with no data for the period is null (not 0) — there is no trend to draw.
  if (previous == null || current == null) return '';
  if (previous === 0 && current === 0) return '';
  if (previous === 0) return `<span style="font-size:11px;color:#10B981;font-weight:600;">● New</span>`;
  const pctChange = ((current - previous) / previous * 100);
  const magnitude = Math.abs(pctChange).toFixed(0);
  // TASK-1266: whatever ROUNDS to "0%" is no change — render a neutral arrow, never a red/grey ↓ 0%.
  // Keyed off the rendered magnitude (not `< 1`) so 0.5–0.99% changes, which also display "0%",
  // don't keep an arrow that contradicts the number next to it.
  const isFlat = magnitude === '0';
  const isUp = pctChange > 0;
  const isGood = invertColors ? !isUp : isUp;
  const color = Math.abs(pctChange) < 1 ? 'var(--text-secondary)' : isGood ? '#10B981' : '#EF4444';
  const arrow = isFlat ? '→' : isUp ? '↑' : '↓';
  return `<span style="font-size:11px;color:${color};font-weight:600;">${arrow} ${magnitude}%</span>`;
}

// TASK-862 — delegates to shared window.Fmt.currency (sharedFormat.js); byte-identical.
// TASK-9287 — and escapes the result. Every caller in this file drops it straight
// into innerHTML (statCard, horizontalBar's value, renderHBars, tables), and the
// input is often a SUM of document fields like `m.costs?.totalCost`, which nothing
// type-checks (driverMaintCreateOk does not look at `costs`). A string there turns
// `0 + x` into concatenation, and Fmt.currency's `.toLocaleString()` returns a
// string unchanged — so a planted `<img onerror>` came out the other side as
// markup. For any real number the output ("$1,234", "-$5") has none of &<>" and is
// byte-identical; only a non-numeric string changes, and it now renders as text.
function formatCurrency(val) {
  return escBI(window.Fmt.currency(val));
}

function formatPct(val) {
  return (val || 0).toFixed(0) + '%';
}

// ============================================
// INIT
// ============================================
export function initBI(container, tenantId) {
  console.log('[BI] Initializing for tenant:', tenantId);

  if (!tenantId) {
    container.innerHTML = `
      <div class="empty-state" style="padding:40px;text-align:center;">
        <span class="material-icons" style="font-size:64px;color:var(--text-secondary);">error_outline</span>
        <div class="empty-state-text">Unable to load BI dashboard. Please try again.</div>
      </div>
    `;
    return () => {};
  }

  BiState.tenantId = tenantId;
  BiState.revenueBasis = loadRevenueBasis(tenantId); // TASK-9242
  BiState.currentTab = 'overview';
  BiState.period = 'month';
  BiState.customStart = null;   // reset on (re)init / tenant switch — no stale prefill
  BiState.customEnd = null;
  BiState.ticketLinesCache = {};
  BiState.rentalSlices = null; // TASK-842-C — no stale cross-tenant slices before first snapshot
  BiState.costAllocById = null; // TASK-9592 — no stale cross-tenant allocations either
  BiState.dataLoaded = { core: false, tickets: false, jobs: false, maintenance: false, pretrips: false, clock: false, timeOff: false, people: false, revenue: false, rentalSlices: false }; // TASK-9512 · people: TASK-9503
  BiState.revenueError = false; // TASK-9512
  BiState.ticketsError = false; // TASK-9242 — Work Done runs on tickets, so a failed tickets read must say Unavailable
  BiState.qbBooks = null; // TASK-9969 S2 — no stale cross-tenant QuickBooks transfer summary
  ensureQbBooksSummary();

  computePeriodDates('month');
  container.innerHTML = getBIHTML();

  // Scroll fade hint: hide right-edge gradient when tabs are fully scrolled
  const tabsEl = container.querySelector('.crm-tabs');
  const tabsWrapper = container.querySelector('.bi-tabs-wrapper');
  if (tabsEl && tabsWrapper) {
    const updateFade = () => {
      const atEnd = tabsEl.scrollLeft + tabsEl.clientWidth >= tabsEl.scrollWidth - 2;
      tabsWrapper.classList.toggle('at-end', atEnd);
    };
    tabsEl.addEventListener('scroll', updateFade, { passive: true });
    // Initial check after layout
    requestAnimationFrame(updateFade);
  }

  // TASK-4867 — initial-tab hand-off from the Billing strip's Reconcile pill.
  // Consumed HERE, synchronously, immediately after getBIHTML() has painted the
  // tab bar and the (empty) tab panes — so switchTab always finds its DOM and
  // there is no setTimeout race with the first data render (the bug in the
  // intra-module pattern at :1361, which lands on Overview when the first
  // render is slow). The tab's own content fills in normally: every snapshot
  // listener below calls renderActiveTab(), which honors BiState.currentTab.
  //
  // Whitelisted against the tab ids in getBIHTML(): an unknown tab would set
  // BiState.currentTab to something _renderActiveTabNow() has no branch for and
  // leave the user staring at an empty pane with every tab de-activated.
  // Expired at 30s so a navigation the gate bounced (trial wall, cancelled PIN
  // challenge) can't sit there and hijack an unrelated BI visit later — same
  // shape as the Billing hand-offs at billing.js:3236/3250.
  if (window._billingBiDeepLink) {
    const bdl = window._billingBiDeepLink;
    delete window._billingBiDeepLink;
    const KNOWN_TABS = ['overview', 'visual', 'financial', 'maintenance', 'profitability', 'financing', 'reconciliation', 'books', 'reports'];
    if (bdl && KNOWN_TABS.includes(bdl.tab) && Date.now() - (bdl.at || 0) < 30000) {
      switchTab(bdl.tab);
    }
  }

  loadBiConfig();
  subscribeToCore();
  subscribeToTickets();
  subscribeToRevenueEntries();
  subscribeToRentalSlices();
  subscribeToCostEntries();
  subscribeToCostAllocations(); // TASK-9592
  subscribeToAPInvoices();
  subscribeToTenantFinancing();
  subscribeToJobs();
  subscribeToMaintenance();
  subscribeToPretrips();
  subscribeToClockEntries();
  // NOTE (TASK-424 #5b/C1): subscribeToTimeOff() intentionally removed. The
  // timeOff collection was streamed unbounded (full collection, every view
  // entry) but BiState.timeOff has ZERO consumers anywhere in this module —
  // it was dead weight. Removing the listener is a pure perf win with NO
  // possible change to any displayed BI number (nothing read it). If a future
  // tab needs time-off data, re-add a *windowed* subscription here.

  return () => {
    BiState.unsubscribers.forEach(u => u());
    BiState.unsubscribers = [];
    BiState.ticketLinesCache = {};
  };
}

async function loadBiConfig() {
  try {
    const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
    const settingsDoc = await getDoc(doc(db, 'tenants', tenant, 'settings', 'general'));
    if (settingsDoc.exists()) {
      const data = settingsDoc.data();
      if (data.biConfig) {
        BiState.biConfig = { ...BiState.biConfig, ...data.biConfig };
      }
    }
  } catch (e) {
    console.warn('[BI] Could not load biConfig:', e);
  }
}

// ============================================
// HTML TEMPLATE
// ============================================
function getBIHTML() {
  return `
    <div class="bi-view">
      <div style="text-align:center;margin-bottom:var(--spacing-md);">
        <h1 style="margin:0;font-size:24px;font-weight:700;background:linear-gradient(135deg,var(--brand-navy),var(--brand-blue));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;">Business Intelligence</h1>
        <p style="margin:4px 0 0;color:var(--text-secondary);font-size:14px;">Fleet performance, financials & operations</p>
      </div>

      <!-- Period Selector -->
      <div class="bi-period-controls">
        <div class="bi-period-selector">
          <button class="bi-period-pill" data-period="week" onclick="window.BIModule.changePeriod('week')">This Week</button>
          <button class="bi-period-pill active" data-period="month" onclick="window.BIModule.changePeriod('month')">This Month</button>
          <button class="bi-period-pill" data-period="quarter" onclick="window.BIModule.changePeriod('quarter')">This Quarter</button>
          <button class="bi-period-pill" data-period="ytd" onclick="window.BIModule.changePeriod('ytd')">YTD</button>
          <button class="bi-period-pill" data-period="custom" onclick="window.BIModule.changePeriod('custom')">Custom</button>
        </div>
        <!-- Custom range inputs (hidden unless Custom is active) -->
        <div class="bi-custom-range" id="bi-custom-range" style="display:none;">
          <input type="date" class="form-control bi-date-input" id="bi-custom-start" aria-label="Start date">
          <span class="bi-range-sep">to</span>
          <input type="date" class="form-control bi-date-input" id="bi-custom-end" aria-label="End date">
          <button class="btn btn-primary btn-sm" onclick="window.BIModule.applyCustomRange()">Apply</button>
        </div>
        <!-- Active range label — always shows the concrete window being viewed -->
        <div class="bi-range-label" id="bi-range-label">${getPeriodLabel()}</div>
      </div>

      <!-- Tabs -->
      <div class="bi-tabs-wrapper">
      <div class="crm-tabs">
        <button class="crm-tab active" data-tab="overview" onclick="window.BIModule.switchTab('overview')">
          <span class="material-icons">dashboard</span> Overview
        </button>
        <button class="crm-tab" data-tab="visual" onclick="window.BIModule.switchTab('visual')">
          <span class="material-icons">grid_view</span> Visual
        </button>
        <button class="crm-tab" data-tab="financial" onclick="window.BIModule.switchTab('financial')">
          <span class="material-icons">attach_money</span> Financial
        </button>
        <button class="crm-tab" data-tab="maintenance" onclick="window.BIModule.switchTab('maintenance')">
          <span class="material-icons">build</span> Maintenance
        </button>
        <button class="crm-tab" data-tab="profitability" onclick="window.BIModule.switchTab('profitability')">
          <span class="material-icons">trending_up</span> Profitability
        </button>
        <button class="crm-tab" data-tab="financing" onclick="window.BIModule.switchTab('financing')">
          <span class="material-icons">account_balance</span> Financing
        </button>
        <button class="crm-tab" data-tab="reconciliation" onclick="window.BIModule.switchTab('reconciliation')">
          <span class="material-icons">balance</span> Reconciliation
        </button>
        <button class="crm-tab" data-tab="books" onclick="window.BIModule.switchTab('books')">
          <span class="material-icons">fact_check</span> QuickBooks check
        </button>
        <button class="crm-tab" data-tab="reports" onclick="window.BIModule.switchTab('reports')">
          <span class="material-icons">summarize</span> Reports
        </button>
      </div>
      </div>

      <!-- Tab Content -->
      <div id="bi-tab-overview" class="crm-tab-content active"><div class="loading-state"><div class="loading-spinner"></div><p>Loading...</p></div></div>
      <div id="bi-tab-visual" class="crm-tab-content"></div>
      <div id="bi-tab-financial" class="crm-tab-content"></div>
      <div id="bi-tab-maintenance" class="crm-tab-content"></div>
      <div id="bi-tab-profitability" class="crm-tab-content"></div>
      <div id="bi-tab-financing" class="crm-tab-content"></div>
      <div id="bi-tab-reconciliation" class="crm-tab-content"></div>
      <div id="bi-tab-books" class="crm-tab-content"></div>
      <div id="bi-tab-reports" class="crm-tab-content"></div>
    </div>
  `;
}

// ============================================
// TAB SWITCHING & PERIOD
// ============================================
function switchTab(tab) {
  BiState.currentTab = tab;
  document.querySelectorAll('.crm-tab').forEach(btn => btn.classList.toggle('active', btn.dataset.tab === tab));
  document.querySelectorAll('.crm-tab-content').forEach(el => el.classList.toggle('active', el.id === `bi-tab-${tab}`));
  _renderActiveTabNow(); // user tab switch — instant paint
}

function changePeriod(period) {
  BiState.period = period;
  document.querySelectorAll('.bi-period-pill[data-period]').forEach(p => p.classList.toggle('active', p.dataset.period === period));

  const customRow = document.getElementById('bi-custom-range');

  if (period === 'custom') {
    // Reveal the date inputs but DON'T recompute the window yet — the user
    // picks dates then clicks Apply. The dashboard keeps showing the prior
    // window until then, so nothing flickers or empties out.
    if (customRow) {
      customRow.style.display = '';
      // Seed the inputs with the currently-viewed window for a sensible starting point.
      const startInput = document.getElementById('bi-custom-start');
      const endInput = document.getElementById('bi-custom-end');
      if (startInput && !startInput.value) startInput.value = BiState.customStart || isoDateLocal(BiState.periodStart);
      if (endInput && !endInput.value) endInput.value = BiState.customEnd || isoDateLocal(BiState.periodEnd);
    }
    updateRangeLabel();
    return;
  }

  if (customRow) customRow.style.display = 'none';
  BiState.ticketLinesCache = {};
  computePeriodDates(period);
  updateRangeLabel();
  _renderActiveTabNow(); // user period change — instant paint
}

// Format a Date as a local 'YYYY-MM-DD' string for <input type="date"> (avoids UTC drift).
function isoDateLocal(d) {
  if (!d || isNaN(d)) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function updateRangeLabel() {
  const lbl = document.getElementById('bi-range-label');
  if (lbl) lbl.textContent = getPeriodLabel();
}

function applyCustomRange() {
  const startVal = document.getElementById('bi-custom-start')?.value;
  const endVal = document.getElementById('bi-custom-end')?.value;
  if (!startVal || !endVal) {
    window.showToast?.('Pick both a start and end date', 'error');
    return;
  }
  const s = new Date(startVal + 'T00:00:00');
  const e = new Date(endVal + 'T23:59:59');
  if (isNaN(s) || isNaN(e)) {
    window.showToast?.('Invalid date', 'error');
    return;
  }
  if (s > e) {
    window.showToast?.('Start date must be on or before the end date', 'error');
    return;
  }
  BiState.customStart = startVal;
  BiState.customEnd = endVal;
  BiState.period = 'custom';
  BiState.ticketLinesCache = {};
  computePeriodDates('custom');
  updateRangeLabel();
  _renderActiveTabNow(); // user applied custom range — instant paint
}

// ============================================
// RENDER COALESCING (TASK-424 #4 / C4 — BI follow-up)
// ============================================
// BI's ~12 Firestore stream listeners each call renderActiveTab() on every
// snapshot, so a burst of snapshots in one tick used to fire ~12 full tab
// re-renders → jank. renderActiveTab() is now the COALESCED entry (N requests
// in the same frame → ONE render via a rAF dirty flag). _renderActiveTabNow()
// is the synchronous render — called directly from user-action paths
// (tab switch, period change, custom range, save-settings) that expect the
// tab painted in the same tick. Mirrors the dispatch-board coalescing
// (dispatch.js scheduleRender). Trailing render always fires (flag cleared
// inside the rAF callback right before render; no debounce that can starve);
// renderActiveTab is module-internal (crm.js has its OWN renderActiveTab).
let _biRenderQueued = false;
function scheduleBiRender() {
  if (_biRenderQueued) return;
  _biRenderQueued = true;
  const raf = window.requestAnimationFrame || ((cb) => setTimeout(cb, 16));
  raf(() => { _biRenderQueued = false; _renderActiveTabNow(); });
}

// Coalesced entry — default for the ~12 snapshot-listener call sites.
function renderActiveTab() {
  scheduleBiRender();
}

// Synchronous render (original renderActiveTab body) — call directly from
// user actions that expect instant paint.
function _renderActiveTabNow() {
  const tab = BiState.currentTab;
  if (tab === 'overview') { ensureQbBooksSummary(); renderOverviewTab(); } // TASK-9969 S2 — no-op while fresh
  else if (tab === 'visual') renderVisualTab();
  else if (tab === 'financial') renderFinancialTab();
  else if (tab === 'maintenance') renderMaintenanceTab();
  else if (tab === 'profitability') renderProfitabilityTab();
  else if (tab === 'financing') renderFinancingTab();
  else if (tab === 'reconciliation') renderReconciliationTab();
  else if (tab === 'books') renderBooksTab();
  else if (tab === 'reports') renderReportsTab();
}

// ============================================
// FIRESTORE SUBSCRIPTIONS
// ============================================
// TASK-1481 — comp resolver: peopleComp doc first (window.PersonComp, loaded
// once via clock.js's side-effect import — Pitfall #1, never import it here),
// legacy person-doc fields as fallback. Every BI labor-cost / pay-badge read
// resolves through this; a raw p.hrHourlyRate read silently prices labor at $0
// after the TASK-1481 strip step.
function _biComp(person) {
  return (window.PersonComp && person) ? window.PersonComp.effective(person) : person;
}

// 🔴 DENIED ≠ ABSENT (TASK-1481) — true when the viewer was DENIED the
// peopleComp collection AND the person doc carries no legacy comp fields
// (post-strip). Labor-cost surfaces must render "n/a — needs HR access" for
// these, never a silent $0.
function _biCompUnknown(person) {
  return !!(window.PersonComp && person && window.PersonComp.compUnknown(person));
}

// ============================================================================
// LABOR COST RATE — the one resolver for every "hours × rate" site in BI
// ============================================================================
// TASK-9878 S2 — the resolver (costRate → hrHourlyRate → salary ÷ productive
// hours on job-clocked hours only, tenant burden from biConfig, compDenied
// counted) now lives in functions/src/shared/jobCost.js jcLaborRateCoreFactory,
// moved there VERBATIM with its full rationale, so the Claude connector
// (get_job_cost) costs labor with the SAME code. window.JobCost is the
// generated mirror (public/src/shared/job-cost.js, side-effect import at the top
// of this file). JOB-COST surfaces use buildJobCostRates + jobCostForEntry;
// WAGE surfaces (accrued wages, payroll JE, bank feed, 1099, contractor
// payments) use buildPersonRateMap — read the header there before changing
// which a surface uses.
const _BiLaborRate = window.JobCost.labor;

// Thin binding: comp-resolved rate map for the live BiState.people array.
// COD Code Reviewer (2026-08-23): the people visual-card pay BADGE read raw
// p.hrHourlyRate / p.costRate while the COST directly beneath it already went
// through the comp-aware resolver — so after TASK-1481 stripped comp off the
// broadcast doc the badge could show nothing, or a stale legacy rate, next to a
// correct cost. Same field, two answers, on one card.
//
// Both now resolve through _biComp (peopleComp first). The costRate arm is gone
// from the badge entirely: it is a dead field with no writer anywhere, so a
// badge sourced from it can only ever be legacy seed data.
function _biBadgeSalary(person) {
  const c = _biComp(person) || {};
  const t = String(c.hrEmploymentType || person.hrEmploymentType || '');
  const v = Number(c.hrAnnualSalary);
  return (t === 'salary' && Number.isFinite(v) && v > 0) ? v : 0;
}
function _biBadgeRate(person) {
  const v = Number((_biComp(person) || {}).hrHourlyRate);
  return (Number.isFinite(v) && v > 0) ? v : 0;
}

// JOB-COST rates for the live BiState.people array: burdened, plus the
// salary map for job-clocked hours. Reads the tenant knobs off BiState.biConfig
// (tenants/{id}/settings/general.biConfig — laborBurdenPct, salaryProductiveHours).
function _biJobCostRates(people) {
  return _BiLaborRate.buildJobCostRates(people || [], _biComp, BiState.biConfig, _biCompUnknown);
}

// WAGE rates: raw, unburdened, hourly-only. Accrued wages / payroll JE /
// bank feed / 1099 / contractor payments only. NOT for job costing.
function _biRateMap(people) {
  return _BiLaborRate.buildPersonRateMap(people || [], _biComp);
}

function subscribeToCore() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;

  // TASK-1481 — comp accessor: mirror the people subscription; re-render when
  // the comp snapshot lands so labor costs resolve comp-first.
  window.PersonComp?.subscribe?.(tenant, {
    key: 'bi',
    onUpdate: () => { try { renderActiveTab(); } catch (e) { /* view may be mid-teardown */ } },
  });

  // TASK-10174 — BiState.vehicleLoans = the server-only vehicleLoans home (owner /
  // bi / billing read; any other login, and demo, is refused and keeps an empty
  // map). Each unit's `loan` is a getter over it (_biUnits), so every reader
  // below (Financing, balance sheet, P&L, break-even, depreciation CSV,
  // forecast) keeps reading e.loan.
  BiState.vehicleLoans = new Map();
  BiState.unsubscribers.push(onSnapshot(collection(db, 'tenants', tenant, 'vehicleLoans'), snap => {
    BiState.vehicleLoans = new Map(snap.docs.map(d => [d.id, d.data()]));
    renderActiveTab();
  }, () => {}));

  // Trucks
  const unsubTrucks = onSnapshot(collection(db, 'tenants', tenant, 'trucks'), snap => {
    const allTrucks = _biUnits(snap, 'truck'); // TASK-10174 loan getter; TASK-10172-A keeps the ended ones
    BiState.trucks = allTrucks.filter(t => t.active !== false);
    BiState.endedUnits.trucks = allTrucks.filter(t => t.active === false);
    renderActiveTab();
  }, err => { console.error('[BI] Trucks error:', err); BiState.trucks = []; BiState.endedUnits.trucks = []; });
  BiState.unsubscribers.push(unsubTrucks);

  // Trailers
  const unsubTrailers = onSnapshot(collection(db, 'tenants', tenant, 'trailers'), snap => {
    const allTrailers = _biUnits(snap, 'trailer'); // TASK-10174 loan getter; TASK-10172-A keeps the ended ones
    BiState.trailers = allTrailers.filter(t => t.active !== false);
    BiState.endedUnits.trailers = allTrailers.filter(t => t.active === false);
    renderActiveTab();
  }, err => { console.error('[BI] Trailers error:', err); BiState.trailers = []; BiState.endedUnits.trailers = []; });
  BiState.unsubscribers.push(unsubTrailers);

  // People
  const unsubPeople = onSnapshot(collection(db, 'tenants', tenant, 'people'), snap => {
    BiState.people = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => !p.noLongerEmployed);
    // TASK-9503 — the card "Held by / Unclaimed" line needs to know the people
    // list is REAL: an empty list before the first snapshot, or after a denied
    // read, must not paint every card Unclaimed (COD Code Reviewer, 2026-10-02).
    BiState.dataLoaded.people = true;
    renderActiveTab();
  }, err => { console.error('[BI] People error:', err); BiState.people = []; BiState.dataLoaded.people = false; });
  BiState.unsubscribers.push(unsubPeople);

  // Customers
  const unsubCust = onSnapshot(collection(db, 'tenants', tenant, 'customers'), snap => {
    BiState.customers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    renderActiveTab();
  }, err => { console.error('[BI] Customers error:', err); BiState.customers = []; });
  BiState.unsubscribers.push(unsubCust);

  // Assets
  const unsubAssets = onSnapshot(collection(db, 'tenants', tenant, 'assets'), snap => {
    const allAssets = _biUnits(snap, 'asset'); // TASK-10174 loan getter; TASK-10172-A keeps the ended ones
    BiState.assets = allAssets.filter(a => a.active !== false);
    BiState.endedUnits.assets = allAssets.filter(a => a.active === false);
    renderActiveTab();
  }, err => { console.error('[BI] Assets error:', err); BiState.assets = []; BiState.endedUnits.assets = []; });
  BiState.unsubscribers.push(unsubAssets);

  // Meters log
  const unsubMeters = onSnapshot(collection(db, 'tenants', tenant, 'metersLog'), snap => {
    BiState.metersLog = snap.docs.map(d => ({ id: d.id, ...d.data() }));
  }, err => { console.error('[BI] MetersLog error:', err); BiState.metersLog = []; });
  BiState.unsubscribers.push(unsubMeters);

  BiState.dataLoaded.core = true;
}

function subscribeToTickets() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const q = query(collection(db, 'tenants', tenant, 'tickets'), orderBy('date', 'desc'));
  const unsub = onSnapshot(q, snap => {
    BiState.tickets = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    BiState.ticketsError = false;
    BiState.dataLoaded.tickets = true;
    _biApplyRevenueCanon(); // TASK-9872 — re-judge revenue rows against the live tickets
    renderActiveTab();
  }, err => {
    console.error('[BI] Tickets error:', err);
    // TASK-9512 — settled (with what we have) so the Overview never spins forever.
    BiState.ticketsError = true; // TASK-9242
    BiState.dataLoaded.tickets = true;
    _biApplyRevenueCanon(); // TASK-9872 — judge rows without ticket state rather than against a stale list
    renderActiveTab();
  });
  BiState.unsubscribers.push(unsub);
}

// TASK-9872 — BiState.revenueEntries is the CANONICAL set: one row per ticket
// per live invoice (window.RevenueCanon, generated from
// functions/src/shared/revenueEntryCanon.js — the connector's get_revenue runs
// the same rule server-side). Raw rows stay in BiState.revenueEntriesRaw; what
// was left out (and why) in BiState.revenueDropped. Tickets are passed only once
// their snapshot has landed without error — before that, rows are judged
// without ticket state (voided rows out, one row per ticket), never blanked.
function _biApplyRevenueCanon() {
  const raw = BiState.revenueEntriesRaw || [];
  const canon = window.RevenueCanon;
  if (!canon || typeof canon.canonicalRevenueEntries !== 'function') {
    BiState.revenueEntries = raw;
    BiState.revenueDropped = [];
    return;
  }
  const haveTickets = BiState.dataLoaded && BiState.dataLoaded.tickets && !BiState.ticketsError && Array.isArray(BiState.tickets);
  const byId = haveTickets ? new Map(BiState.tickets.map(t => [t.id, t])) : null;
  const out = canon.canonicalRevenueEntries(raw, byId);
  BiState.revenueEntries = out.rows;
  BiState.revenueDropped = out.dropped;
}

function subscribeToRevenueEntries() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const q = query(collection(db, 'tenants', tenant, 'revenueEntries'), orderBy('invoicedAt', 'desc'));
  const unsub = onSnapshot(q, snap => {
    BiState.revenueEntriesRaw = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    _biApplyRevenueCanon(); // TASK-9872 — duplicates / superseded / orphan rows never reach a BI sum
    BiState.dataLoaded.revenue = true; // TASK-9512 — first snapshot landed (an empty tenant is still "loaded")
    renderActiveTab();
  }, err => {
    console.error('[BI] RevenueEntries error:', err);
    BiState.revenueEntries = [];
    BiState.revenueEntriesRaw = [];
    BiState.revenueDropped = [];
    BiState.revenueError = true;
    BiState.dataLoaded.revenue = true; // TASK-9512 — settled; the card says "unavailable", never spins
    renderActiveTab();
  });
  BiState.unsubscribers.push(unsub);
}

// TASK-842-C — rental-slice ledger (source of truth for rental billing periods).
// Read-only here, same collection billing.js openReviewTicket reads for the
// span UI. firestore.rules ~3011: read = isTenantAdmin || hasModule('billing'),
// writes Admin-SDK-only. If this persona can see BI but can't read slices, the
// error callback leaves rentalSlices = null and every revenue surface degrades
// gracefully to the legacy per-entry path (never errors, never blanks a tab).
function subscribeToRentalSlices() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const unsub = onSnapshot(collection(db, 'tenants', tenant, 'rentalSlices'), snap => {
    BiState.rentalSlices = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    BiState.dataLoaded.rentalSlices = true; // TASK-9512 — null alone can't tell "not loaded" from "error"
    renderActiveTab();
  }, err => {
    console.warn('[BI] rentalSlices unavailable — using legacy per-entry rental aggregation:', err?.code || err);
    BiState.rentalSlices = null;
    BiState.dataLoaded.rentalSlices = true; // TASK-9512 — settled on the legacy path
    renderActiveTab();
  });
  BiState.unsubscribers.push(unsub);
}

function subscribeToCostEntries() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const q = query(collection(db, 'tenants', tenant, 'costEntries'), orderBy('date', 'desc'));
  const unsub = onSnapshot(q, snap => {
    BiState.costEntries = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    renderActiveTab();
  }, err => { console.error('[BI] CostEntries error:', err); BiState.costEntries = []; });
  BiState.unsubscribers.push(unsub);
}

// TASK-9592 — the tenant's Cost Allocation list, so per-truck cost can fall
// back to a truck/trailer allocation's refId when an entry has no vehicleId.
// Rules: costAllocations read = canReadTenant. On error the map stays null and
// every reader behaves exactly as before (vehicleId only).
function subscribeToCostAllocations() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const unsub = onSnapshot(collection(db, 'tenants', tenant, 'costAllocations'), snap => {
    const m = new Map();
    snap.docs.forEach(d => { const x = d.data() || {}; m.set(d.id, { type: x.type || null, refId: x.refId == null ? null : x.refId }); });
    BiState.costAllocById = m;
    renderActiveTab();
  }, err => { console.warn('[BI] costAllocations unavailable — per-truck cost uses vehicleId only:', err?.code || err); BiState.costAllocById = null; });
  BiState.unsubscribers.push(unsub);
}

function subscribeToAPInvoices() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const q = query(collection(db, 'tenants', tenant, 'apInvoices'), orderBy('invoiceDate', 'desc'));
  const unsub = onSnapshot(q, snap => {
    // TASK-9624 — a Draft bill (not yet saved in AP) is out of BI.
    BiState.apInvoices = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.draft !== true);
    renderActiveTab();
  }, err => { console.error('[BI] APInvoices error:', err); BiState.apInvoices = []; });
  BiState.unsubscribers.push(unsub);
}

function subscribeToTenantFinancing() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const unsub = onSnapshot(doc(db, 'tenants', tenant), snap => {
    const data = snap.data() || {};
    BiState.linesOfCredit = (data.linesOfCredit || []).map(loc => ({ ...loc, subtype: loc.subtype || 'bank_loc' }));
    BiState.investorNotes = data.investorNotes || [];
    BiState.balanceSheetCash = data.balanceSheetCash || 0;
    BiState.balanceSheetConfig = data.balanceSheetConfig || {};
    if (BiState.currentTab === 'financing') renderActiveTab();
  }, err => { console.error('[BI] Tenant financing error:', err); });
  BiState.unsubscribers.push(unsub);

  // TASK-1620 -- bankAccounts moved OFF the broadcast tenant root (any
  // driver-PIN session could raw-SDK-read cash balances via canReadTenant)
  // into the admin/'bi'-module-gated `financeSecure/bankAccounts` sibling
  // doc. Separate subscription so the rest of the Financing tab (lines of
  // credit, investor notes, balance-sheet config — unchanged, out of scope
  // for this task) keeps working exactly as before.
  const unsubBank = onSnapshot(doc(db, 'tenants', tenant, 'financeSecure', 'bankAccounts'), snap => {
    const data = snap.data() || {};
    BiState.bankAccounts = data.accounts || [];
    if (BiState.currentTab === 'financing') renderActiveTab();
  }, err => { console.error('[BI] bankAccounts error:', err); });
  BiState.unsubscribers.push(unsubBank);
}

function subscribeToJobs() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const unsubJobs = onSnapshot(collection(db, 'tenants', tenant, 'jobs'), snap => {
    BiState.jobs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    BiState.dataLoaded.jobs = true;
  }, err => { console.error('[BI] Jobs error:', err); });
  BiState.unsubscribers.push(unsubJobs);

  const unsubJD = onSnapshot(collection(db, 'tenants', tenant, 'jobDays'), snap => {
    BiState.jobDays = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    renderActiveTab();
  }, err => { console.error('[BI] JobDays error:', err); });
  BiState.unsubscribers.push(unsubJD);
}

function subscribeToMaintenance() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const unsub = onSnapshot(collection(db, 'tenants', tenant, 'maintenance'), snap => {
    BiState.maintenance = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    BiState.dataLoaded.maintenance = true;
    renderActiveTab();
  }, err => { console.error('[BI] Maintenance error:', err); });
  BiState.unsubscribers.push(unsub);
}

function subscribeToPretrips() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const unsub = onSnapshot(collection(db, 'tenants', tenant, 'pretrips'), snap => {
    BiState.pretrips = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    BiState.dataLoaded.pretrips = true;
  }, err => { console.error('[BI] Pretrips error:', err); });
  BiState.unsubscribers.push(unsub);
}

function subscribeToClockEntries() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const unsub = onSnapshot(collection(db, 'tenants', tenant, 'clockEntries'), snap => {
    BiState.clockEntries = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    BiState.dataLoaded.clock = true;
  }, err => { console.error('[BI] ClockEntries error:', err); });
  BiState.unsubscribers.push(unsub);
}

// (subscribeToTimeOff removed — TASK-424 #5b/C1: dead unbounded stream, no consumer. See initBI note.)

// TASK-9490 — THE per-vehicle revenue rule. Used by the truck/trailer
// analytics modals (getVehiclePeriodData — the LIVE surface) and renderFleetTab
// (period + "lifetime" ROI; NOTE renderFleetTab has no caller today — no Fleet
// tab is wired into getBIHTML/_renderActiveTabNow), and ported line for
// line to functions/src/services/revenueAggregation.js for the connector's
// get_revenue_by_vehicle — tests/task9490-revenue-by-vehicle.unit.cjs lifts
// these two functions out of THIS source and runs both sides on one fixture.
// Change both, or neither.
//   - A ticket counts unless it is a draft or DELETED (before 9490 deleted
//     tickets counted: ~$6.3K of TITS Sept 2026 per-truck revenue).
//   - Only EQUIPMENT lines, keyed on the line's assetId (the vehicle doc id).
//     The old `|| line.fleetNumber === truck.unit` arm counted a line on TWO
//     trucks whenever its fleetNumber named another unit (TITS lifetime:
//     $38,955.50 counted twice — Frank lines under Falcon, Hawk under Frank).
function _biTicketCountsForVehicleRevenue(t) {
  return !!t && t.status !== 'draft' && t.status !== 'deleted';
}
// -> { [assetId]: { amount, tickets, byCustomer: { [customerName]: amount } } }
function _biVehicleRevenueByAsset(tickets, linesByTicket, inRange) {
  const out = Object.create(null);
  for (const t of tickets || []) {
    if (!_biTicketCountsForVehicleRevenue(t) || !inRange(t.date)) continue;
    const lines = (linesByTicket && linesByTicket[t.id]) || [];
    const seen = new Set();
    for (const line of lines) {
      if (!line || line.type !== 'EQUIPMENT' || !line.assetId || typeof line.assetId !== 'string') continue;
      const amt = Number(line.lineAmount) || 0;
      const a = out[line.assetId] || (out[line.assetId] = { amount: 0, tickets: 0, byCustomer: Object.create(null) });
      a.amount += amt;
      if (!seen.has(line.assetId)) { seen.add(line.assetId); a.tickets += 1; }
      const cName = t.customerName || 'Unknown';
      a.byCustomer[cName] = (a.byCustomer[cName] || 0) + amt;
    }
  }
  return out;
}

// Batch-load ticket lines for fleet revenue attribution
async function loadTicketLinesForPeriod() {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const periodTickets = BiState.tickets.filter(t => isInPeriod(t.date) && _biTicketCountsForVehicleRevenue(t));

  const uncached = periodTickets.filter(t => !BiState.ticketLinesCache[t.id]);
  if (uncached.length === 0) return;

  const batchSize = 20;
  for (let i = 0; i < uncached.length; i += batchSize) {
    const batch = uncached.slice(i, i + batchSize);
    const promises = batch.map(async (ticket) => {
      try {
        const snap = await getDocs(collection(db, 'tenants', tenant, 'tickets', ticket.id, 'ticketLines'));
        BiState.ticketLinesCache[ticket.id] = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (e) {
        BiState.ticketLinesCache[ticket.id] = [];
      }
    });
    await Promise.all(promises);
  }
}

// ============================================
// OVERVIEW TAB
// ============================================
// TASK-9512 — the Overview's money numbers (revenue card + Financial Health)
// are only honest once revenueEntries, rentalSlices and tickets have each
// SETTLED (first snapshot or an error). Keyed on settled flags, never on
// `revenueEntries.length === 0` (an empty tenant would spin forever) or on
// `rentalSlices === null` (that is both "not loaded" and "error").
function biOverviewMoneyReady(state) {
  const dl = (state && state.dataLoaded) || {};
  return dl.revenue === true && dl.rentalSlices === true && dl.tickets === true;
}

// TASK-4860 — Fleet Utilization, two numbers side by side (Chris, 2026-09-29).
//   "Vehicles used"  = active vehicles that ran at least one day in the period
//                      ÷ active vehicles. A vehicle that ran 1 of 30 days counts
//                      the same as one that ran 30 of 30 — it answers "is any of
//                      the fleet sitting idle all period", nothing more.
//   "Vehicle-days"   = Σ distinct days each active vehicle was on a jobDay
//                      ÷ (active vehicles × calendar days in the period).
// Rules both share:
//   - jobDay.trucks refs (id, unit, nickname or name — _resolveFleetRef) RESOLVE to an active vehicle. A ref to
//     an inactive/unknown unit counts toward neither number, so "used" can never
//     exceed 100% and one vehicle referenced by id on one day and by unit on
//     another is still one vehicle.
//   - A vehicle on 2+ jobs the same day is ONE vehicle-day. Multiple runs a day
//     are normal; they are not double-counted and not flagged.
//   - A day with no jobs still counts in the denominator (it is a day the fleet
//     was available and idle).
//   - Days are LOCAL calendar days (jobDay.date is anchored to noon local), and
//     period length is a calendar-day count, so a DST month is 30/31, not +1.
function _biLocalDayKey(val) {
  if (typeof val === 'string') {
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(val);
    if (m) return m[1];
  }
  const d = toDate(val);
  if (!d || isNaN(d.getTime())) return null;
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function _biCalendarDays(start, end) {
  if (!start || !end) return 1;
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.max(1, Math.round((e - s) / 86400000) + 1);
}

function _biPrevCalendarWindow(start, end) {
  const n = _biCalendarDays(start, end);
  const pe = new Date(start.getFullYear(), start.getMonth(), start.getDate() - 1, 23, 59, 59);
  const ps = new Date(start.getFullYear(), start.getMonth(), start.getDate() - n, 0, 0, 0);
  return { start: ps, end: pe, contains: v => { const d = toDate(v); return !!d && d >= ps && d <= pe; } };
}

function biFleetUtilization(jobDays, trucks, periodStart, periodEnd, inPeriod) {
  // Refs resolve through the shared TASK-4529 resolver (ids exhausted before
  // unit/nickname/name; string-normalized), against the ACTIVE list only — same
  // as the other BI per-truck readers. Memoized: refs repeat across hundreds of jobDays.
  const refCache = new Map();
  const resolveId = (ref) => {
    const key = ref && typeof ref === 'object' ? ref.id : ref;
    if (key === null || key === undefined || key === '') return null;
    const k = String(key);
    if (!refCache.has(k)) { const t = _resolveFleetRef(k, trucks || []); refCache.set(k, t ? t.id : null); }
    return refCache.get(k);
  };
  const daysByTruck = new Map();
  (jobDays || []).forEach(jd => {
    if (!jd) return;
    // Normalize to the local day FIRST, then test that day's local noon against the
    // window: toDate() reads a bare 'YYYY-MM-DD' as UTC midnight (the prior evening
    // in Central), which would drop day 1 and pull in day N+1 (COD Code Reviewer).
    const day = _biLocalDayKey(jd.date);
    if (!day) return;
    const p = day.split('-');
    if (!inPeriod(new Date(+p[0], +p[1] - 1, +p[2], 12, 0, 0))) return;
    (jd.trucks || []).forEach(ref => {
      const id = resolveId(ref);
      if (!id) return;
      if (!daysByTruck.has(id)) daysByTruck.set(id, new Set());
      daysByTruck.get(id).add(day);
    });
  });
  const activeTrucks = (trucks || []).length;
  const periodDays = _biCalendarDays(periodStart, periodEnd);
  let vehicleDays = 0;
  const perTruck = (trucks || []).map(t => {
    const days = Math.min(periodDays, (daysByTruck.get(t.id) || new Set()).size);
    vehicleDays += days;
    return { truck: t, days, pct: days / periodDays * 100 };
  });
  const availableDays = activeTrucks * periodDays;
  return {
    activeTrucks,
    vehiclesUsed: daysByTruck.size,
    usedPct: activeTrucks > 0 ? (daysByTruck.size / activeTrucks * 100) : 0,
    vehicleDays,
    availableDays,
    periodDays,
    dayPct: availableDays > 0 ? (vehicleDays / availableDays * 100) : 0,
    perTruck,
  };
}

function renderOverviewTab() {
  const el = document.getElementById('bi-tab-overview');
  if (!el) return;
  const moneyReady = biOverviewMoneyReady(BiState);

  const performedBasis = BiState.revenueBasis === 'performed'; // TASK-9242
  const periodRevenue = performedBasis ? performedRevenueEntries(isInPeriod) : rentalAwareRevenueEntries(isInPeriod); // TASK-842-C / TASK-9242
  const totalRevenue = periodRevenue.reduce((sum, r) => sum + (r.amount || 0), 0);
  const unbilledPerformed = performedBasis ? periodRevenue.filter(r => r._unbilled).reduce((sum, r) => sum + (r.amount || 0), 0) : 0;

  const periodJobDays = BiState.jobDays.filter(jd => isInPeriod(jd.date));
  const uniqueJobIds = new Set(periodJobDays.map(jd => jd.jobId));

  // TASK-4860 — both fleet numbers from one pass over the same jobDays/trucks.
  const fleetUtil = biFleetUtilization(BiState.jobDays, BiState.trucks, BiState.periodStart, BiState.periodEnd, isInPeriod);

  const periodPretrips = BiState.pretrips.filter(p => isInPeriod(p.createdAt));
  const passCount = periodPretrips.filter(p => _isPretripPass(p)).length;
  // TASK-1266: no inspections is NOT a perfect safety record — null (rendered "N/A"), never a fake 100%.
  // Mirrors the per-driver scorecard, which already uses null for the same situation.
  const dvirPct = periodPretrips.length > 0 ? (passCount / periodPretrips.length * 100) : null;

  // Previous period comparisons for trend arrows
  // TASK-9242 — the previous period MUST use the same basis or the trend badge compares two clocks.
  const prevRevenue = performedBasis ? performedRevenueEntries(isInPreviousPeriod) : rentalAwareRevenueEntries(isInPreviousPeriod); // TASK-842-C
  const prevTotalRevenue = prevRevenue.reduce((sum, r) => sum + (r.amount || 0), 0);
  const prevJobDays = BiState.jobDays.filter(jd => isInPreviousPeriod(jd.date));
  const prevUniqueJobIds = new Set(prevJobDays.map(jd => jd.jobId));
  // Fleet compares against the same number of CALENDAR days just before the period
  // (getPreviousPeriodDates shifts by elapsed ms, which lands at 23:00 across DST and
  // would count a day the noon filter can never fill — COD Code Reviewer round 2).
  const prevFleetWin = _biPrevCalendarWindow(BiState.periodStart, BiState.periodEnd);
  const prevFleetUtil = biFleetUtilization(BiState.jobDays, BiState.trucks, prevFleetWin.start, prevFleetWin.end, prevFleetWin.contains);
  const prevPretrips = BiState.pretrips.filter(p => isInPreviousPeriod(p.createdAt));
  const prevPassCount = prevPretrips.filter(p => _isPretripPass(p)).length;
  const prevDvirPct = prevPretrips.length > 0 ? (prevPassCount / prevPretrips.length * 100) : null; // TASK-1266

  // Store overview data in drillCtx for drill-downs
  drillCtx = {
    _overviewMode: true,
    revenueBasis: performedBasis ? 'performed' : 'invoiced',
    periodRevenue, totalRevenue, uniqueJobIds, periodJobDays,
    fleetUtil,
    periodPretrips, passCount, dvirPct
  };

  // TASK-4859 FIX C — days from ticket date to INVOICE date, scoped to the selected
  // period. This is NOT DSO. DSO is invoice -> cash, and DD cannot compute it: nothing
  // records a payment date (0 of 993 TITS tickets have ever carried paid:true — the
  // field is only ever written by the Stripe Connect webhook, functions/src/index.js).
  // It also used to average EVERY invoiced ticket ever (694 of them, 8 days) under a
  // header reading "Sep 1 - Sep 9". The sample count travels with the average so an
  // empty period can say "nothing invoiced" instead of a fake "0 days".
  let dtiSum = 0, dtiCount = 0;
  BiState.tickets.forEach(t => {
    if (!t.invoicedAt || !t.date || !isInPeriod(t.invoicedAt)) return;
    const inv = toDate(t.invoicedAt);
    const created = toDate(t.date);
    if (inv && created) {
      const days = (inv - created) / (1000 * 60 * 60 * 24);
      if (days >= 0 && days < 365) { dtiSum += days; dtiCount++; }
    }
  });
  const daysToInvoice = { avgDays: dtiCount > 0 ? Math.round(dtiSum / dtiCount) : 0, sampleCount: dtiCount };

  // TASK-1266: the null decision has to happen HERE, before formatPct() — it coerces null to '0%'.
  const dvirDisplay = dvirPct === null ? 'N/A' : formatPct(dvirPct);
  const dvirTrend = dvirPct === null ? '' : trendBadge(dvirPct, prevDvirPct);

  // TASK-4859 FIX D — the revenue card filters revenueEntries on invoicedAt while Total
  // Jobs filters jobDays on jd.date. Same header, two clocks: TITS Sep 1-9 was $49,388
  // invoiced against $34,862 of work performed, and the trend badge compares against a
  // window that held a month-end invoice batch, so it reads as a revenue collapse that
  // is really an invoicing artifact. Label the card for the clock it runs on. The
  // recognition basis is deliberately NOT changed — accrual-on-invoice is correct and the
  // P&L depends on it; on a tenant with rentalSlices the amount is sliced by slice date,
  // which is why the label does not promise a single invoice date.
  // TASK-9512 — before the money data settles, say "Loading…" instead of "$0"
  // (which sat next to "Financials look good" for 5–10 s); after an error, say so.
  // The basis decides which read has to have succeeded: invoiced = revenueEntries, performed = tickets.
  const revUnavailable = performedBasis ? !!BiState.ticketsError : !!BiState.revenueError;
  const revenueValue = !moneyReady
    ? '<span style="color:var(--text-secondary);font-size:0.95rem;font-weight:600;">Loading…</span>'
    : (revUnavailable ? '<span style="color:var(--text-secondary);font-size:0.95rem;font-weight:600;">Unavailable</span>' : formatCurrency(totalRevenue));
  const revenueTrend = moneyReady && !revUnavailable ? trendBadge(totalRevenue, prevTotalRevenue) : '';
  // TASK-9242 — basis pills live ON the card (this toggle governs one card, not the shared period header).
  const pill = (key, label) => `<button type="button" class="bi-period-pill${(performedBasis ? 'performed' : 'invoiced') === key ? ' active' : ''}" onclick="event.stopPropagation();window.BIModule.setRevenueBasis('${key}')" style="font-size:12px;padding:8px 12px;min-height:36px;">${label}</button>`;
  const revenueExtra = `<div style="display:flex;gap:8px;margin-top:8px;">${pill('invoiced', 'Invoiced')}${pill('performed', 'Work Done')}</div>`
    + (performedBasis && moneyReady && unbilledPerformed > 0
      ? `<div style="margin-top:4px;font-size:11px;color:var(--text-secondary);">includes ${formatCurrency(unbilledPerformed)} not yet invoiced</div>`
      : '');
  const healthHtml = moneyReady
    ? renderFinancialHealth(daysToInvoice, totalRevenue, performedBasis ? 'performed' : 'invoiced', revUnavailable)
    : `<div class="bi-financial-health" style="padding:16px;color:var(--text-secondary);font-size:0.9rem;display:flex;align-items:center;gap:8px;" role="status" aria-live="polite">
         <span class="material-icons" style="font-size:18px;">hourglass_empty</span> Loading financials…
       </div>`;
  el.innerHTML = `
    <div class="crm-stats-grid" style="margin-bottom:8px;">
      ${statCard('attach_money', performedBasis ? 'Revenue (work performed)' : 'Revenue (invoiced)', revenueValue, 'green', moneyReady ? 'ov-revenue' : null, revenueTrend, revenueExtra)}
      ${statCard('work', 'Total Jobs', uniqueJobIds.size, 'blue', 'ov-jobs', trendBadge(uniqueJobIds.size, prevUniqueJobIds.size))}
      ${statCard('local_shipping', 'Fleet Utilization', renderFleetUtilValue(fleetUtil, prevFleetUtil), 'amber', 'ov-fleet', '')}
      ${statCard('verified', 'DVIR Pass Rate', dvirDisplay, 'purple', 'ov-dvir', dvirTrend)}
    </div>
    <div id="bi-drill-panel" data-active-key=""></div>

    ${healthHtml}
  `;
}

// TASK-4860 — the two fleet numbers, side by side inside the one card, each with
// its own plain-English label and "N of M" so neither reads as the other.
function renderFleetUtilValue(fu, prev) {
  const col = (pct, prevPct, label, detail) => `
    <div style="flex:1;min-width:0;">
      <div>${formatPct(pct)}</div>
      <div style="font-size:11px;font-weight:600;color:var(--text-primary);line-height:1.3;">${label}</div>
      <div style="font-size:11px;font-weight:400;color:var(--text-secondary);line-height:1.3;">${detail}</div>
      ${prev ? `<div style="margin-top:2px;">${trendBadge(pct, prevPct)}</div>` : ''}
    </div>`;
  // .crm-stat-value is nowrap + overflow:hidden; the labels must wrap on a phone-width card, not clip.
  return `<div style="display:flex;gap:12px;align-items:flex-start;width:100%;white-space:normal;">`
    + col(fu.usedPct, prev && prev.usedPct, 'Vehicles used', `${fu.vehiclesUsed} of ${fu.activeTrucks} ran at least 1 day`)
    + col(fu.dayPct, prev && prev.dayPct, 'Vehicle-days worked', `${fu.vehicleDays} of ${fu.availableDays} possible days`)
    + `</div>`;
}

function statCard(icon, label, value, color, drillKey, trend, extraHtml) {
  const colors = { green: '#10B981', blue: '#3B82F6', amber: '#F59E0B', purple: '#8B5CF6', red: '#EF4444', teal: '#06B6D4' };
  const bgColors = { green: '#ECFDF5', blue: '#EFF6FF', amber: '#FFFBEB', purple: '#F5F3FF', red: '#FEF2F2', teal: '#F0FDFA' };
  const clickAttr = drillKey ? `onclick="window.BIModule.drillDown('${escJsAttr(drillKey)}')" style="cursor:pointer;"` : '';
  return `
    <div class="crm-stat-card bi-stat-vertical ${drillKey ? 'bi-stat-clickable' : ''}" ${clickAttr}>
      <div class="crm-stat-icon" style="background:${bgColors[color]};color:${colors[color]};width:36px;height:36px;">
        <span class="material-icons" style="font-size:18px;">${icon}</span>
      </div>
      <div class="crm-stat-value" style="font-size:1.25rem;">${value}</div>
      <div class="crm-stat-label">${label}</div>
      ${trend ? `<div style="margin-top:2px;">${trend}</div>` : ''}
      ${extraHtml || ''}
    </div>
  `;
}

function alertItem(type, icon, title, subtitle, clickAction) {
  const borderColors = { critical: '#EF4444', warning: '#F59E0B', info: '#3B82F6', success: '#10B981' };
  const clickAttr = clickAction ? `onclick="${clickAction}" style="border-left-color:${borderColors[type]};cursor:pointer;"` : `style="border-left-color:${borderColors[type]};"`;
  return `
    <div class="bi-alert-item" ${clickAttr}>
      <span class="material-icons" style="color:${borderColors[type]};font-size:20px;">${icon}</span>
      <div style="flex:1;">
        <div style="font-weight:600;font-size:13px;color:var(--text-primary);">${title}</div>
        <div style="font-size:12px;color:var(--text-secondary);">${subtitle}</div>
      </div>
      ${clickAction ? '<span class="material-icons" style="font-size:16px;color:var(--text-secondary);">chevron_right</span>' : ''}
    </div>
  `;
}

// TASK-4859 — buckets that are NOT a cost of revenue. Same three the P&L declares
// (BELOW_THE_LINE_CATEGORIES in computePLData) and the equity calc excludes. An
// owner draw is a distribution, not a cost of goods; counting one here would print
// a margin collapse that never happened. TITS has 0 such entries, so this changes
// no number on this tenant today — it is the guard, not the fix.
const OVERVIEW_NON_COST_CATEGORIES = ['other_income', 'draws', 'contributions'];

// TASK-4859 — the Overview panel was the last surface in this file still running
// the naive predicates. The Financial tab and the Profitability P&L, both in this
// same file, already outgrew every one of them. Three of its four items said
// something other than what they measured:
//   "Outstanding AR"  -> unbilled drafts, on a `!t.paid` clause that is inert
//   "Average DSO"     -> days-to-invoice, all-time, under a period header
//   "Gross Margin"    -> 100%, because every cost in the window was gated out
// The rule for this function: name the thing you actually counted, and when the
// inputs cannot support a number, say so instead of printing one.
// ---------------------------------------------------------------------------
// TASK-9969 S2 — QuickBooks Books on the Financial Health card.
// Only a company with a QuickBooks connection gets anything here; for everyone
// else BiState.qbBooks stays null and the card is unchanged.
//   BiState.qbBooks = { tenantId, phase: 'loading' | 'ok' | 'error', summary, at }
//   summary = qboBooksStatus().summary — the CALLER's view (owner vs team, the
//   server filtered owner-only lines before summing); null = never written.
// ensureQbBooksSummary() reads it (once per tenant, again after 5 minutes);
// qbBooksHealthItem() is pure: summary -> at most ONE passive row.
const QB_BOOKS_FRESH_MS = 5 * 60 * 1000;
let _qbBooksSeq = 0;

// 'yes' | 'no' | 'unknown' (tenantData not loaded yet). COD r1: unknown is not
// "no connection" — it holds the all-clear until the tenant doc arrives.
function _biQbConnection() {
  const td = window.AppState && window.AppState.tenantData;
  if (!td || typeof td !== 'object') return 'unknown';
  // COD r2: a LIVE "connected" from Billing (getQBOConnectionTruth) beats a
  // falsy cached tenant field — Billing stopped trusting that cache (TASK-9589).
  const live = window.BillingModule && typeof window.BillingModule.getQBOConnectionTruth === 'function' ? window.BillingModule.getQBOConnectionTruth() : null;
  if (live === true) return 'yes';
  return (td.qboConnected === true || (typeof td.qboRealmId === 'string' && td.qboRealmId)) ? 'yes' : 'no';
}

function ensureQbBooksSummary() {
  const tid = BiState.tenantId;
  const conn = tid ? _biQbConnection() : 'no';
  if (conn === 'no') { BiState.qbBooks = null; return; }
  if (conn === 'unknown') { BiState.qbBooks = { tenantId: tid, phase: 'no_tenant_yet', summary: null, at: 0 }; return; }
  const cur = BiState.qbBooks;
  // COD r2: a dead QuickBooks login (reauth_required) keeps qboConnected, and
  // the stored summary can still read ok — say so on the card.
  const td = window.AppState.tenantData;
  const live = window._qboLiveStatus || null;
  const reauth = td.qboStatus === 'reauth_required' || !!(live && live.status === 'reauth_required');
  if (cur && cur.tenantId === tid) cur.reauth = reauth;
  if (cur && cur.tenantId === tid && (cur.phase === 'loading' || Date.now() - (cur.at || 0) < QB_BOOKS_FRESH_MS)) return;
  const seq = ++_qbBooksSeq;
  BiState.qbBooks = { tenantId: tid, phase: 'loading', summary: null, at: Date.now(), reauth };
  httpsCallable(functions, 'qboBooksStatus')({ tenantId: tid }).then((res) => {
    if (seq !== _qbBooksSeq || BiState.tenantId !== tid) return;
    const s = (res && res.data && res.data.summary) || null;
    BiState.qbBooks = { tenantId: tid, phase: 'ok', summary: s, at: Date.now(), reauth: !!(BiState.qbBooks && BiState.qbBooks.reauth) };
    // Counts only — never a memo, vendor or account name in a log.
    console.log('[BI] QuickBooks transfer summary', { status: s ? s.status : 'none', waiting: s ? (Number(s.waitingOnApprover) || 0) : 0, gap: !!(s && s.syncGap && s.syncGap.flag) });
  }).catch((e) => {
    if (seq !== _qbBooksSeq || BiState.tenantId !== tid) return;
    BiState.qbBooks = { tenantId: tid, phase: 'error', summary: null, at: Date.now() };
    console.warn('[BI] QuickBooks transfer summary unavailable:', (e && e.code) || 'error');
  }).finally(() => {
    if (seq === _qbBooksSeq && BiState.tenantId === tid && BiState.currentTab === 'overview') renderActiveTab();
  });
}

/**
 * qb -> { row, holdRow }, each null or alertItem() args.
 *   row      ONE passive row, dollars first, when the transfer needs a person:
 *            not run, did not add up, waiting on the approver, a sync gap, or
 *            lines QuickBooks changed after they came in.
 *   holdRow  replaces "Financials look good" while the read is in flight or
 *            failed — the same rule as revUnavailable (1996 COD): an unknown
 *            is never a clean bill of health.
 * Every string that reaches the HTML goes through escBI / formatCurrency.
 */
function qbBooksHealthItem(qb) {
  const none = { row: null, holdRow: null };
  if (!qb) return none;
  const link = 'window.BIModule.openQbBooks()';
  if (qb.phase !== 'ok' && qb.phase !== 'error') { // loading · stale · no_tenant_yet
    return { row: null, holdRow: ['info', 'hourglass_empty', 'Checking QuickBooks…', 'Financial health is not settled until the QuickBooks transfer loads', link] };
  }
  if (qb.phase === 'error') {
    return { row: null, holdRow: ['warning', 'error_outline', 'QuickBooks transfer could not be checked', 'So financial health can’t be confirmed. Open QuickBooks Books, or refresh to try again', link] };
  }
  const s = qb.summary;
  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const plural = (k, word) => `${k} ${word}${k === 1 ? '' : 's'}`;
  const parts = []; // [title, detail]
  let tone = 'warning';
  if (qb.reauth) parts.push(['QuickBooks needs to be reconnected', 'DispatchDeck cannot see new QuickBooks changes until it is']);
  if (!s || s.status === 'did_not_run') {
    parts.push(['QuickBooks transfer: Not run', 'No QuickBooks month has been checked yet']);
  } else {
    if (s.status === 'did_not_reconcile') {
      tone = 'critical';
      parts.push(['QuickBooks transfer counts did not add up', 'Do not rely on them']);
    }
    const waiting = n(s.waitingOnApprover);
    if (waiting > 0) parts.push([`${formatCurrency(n(s.waitingDollars))} waiting for approval`, `${plural(waiting, 'QuickBooks line')} wait on the approver`]);
    const g = s.syncGap || {};
    if (g.flag) {
      const months = Array.isArray(g.months) ? g.months.map(String) : [];
      const days = Number.isFinite(g.days) ? g.days : null;
      const title = months.length ? `QuickBooks changes not seen for ${escBI(months.join(', '))}`
        : days == null ? 'QuickBooks changes not seen yet'
          : `QuickBooks changes not seen for ${plural(days, 'day')}`;
      // The day count is already the title; months / never-checked need the server's words.
      parts.push([title, months.length || days == null ? escBI(g.message || '') : '']);
    }
    const changed = n(s.changedInQuickBooksAfterApproval);
    if (changed > 0) parts.push([`${formatCurrency(n(s.changedDollars))} changed in QuickBooks after it came in`, `${plural(changed, 'line')} need a look`]);
    if (!parts.length && s.status !== 'ok') parts.push(['QuickBooks transfer needs a look', '']);
  }
  if (!parts.length) return none;
  const detail = [parts[0][1], ...parts.slice(1).map((p) => p[0]), 'Open QuickBooks Books'].filter(Boolean).join(' · ');
  return { row: [tone, 'account_balance', parts[0][0], detail, link], holdRow: null };
}

// After a QuickBooks import (qboBooks.js): the next Overview render re-reads.
// COD r1: an import that lands while a read is in flight must not leave that
// pre-import answer cached — drop it (seq) and read again on the next render.
function refreshQbBooks() {
  if (!BiState.qbBooks) return;
  _qbBooksSeq += 1;
  BiState.qbBooks = { ...BiState.qbBooks, phase: 'stale', summary: null, at: 0 };
}

// The Books step lives in AR / AP → QuickBooks setup (qboSetup.js, owned by
// billing.js). Same hand-off as qboBooks.js openMapSetup (pitfall #1: no import).
function openQbBooks() {
  const go = () => window.BillingModule && typeof window.BillingModule.openQBOSetup === 'function'
    ? (window.BillingModule.openQBOSetup(null, 'books'), true) : false;
  if (go()) return;
  const hint = 'Open AR / AP → QuickBooks → Books.'; // TASK-10123 — the header button opens setup
  if (typeof window.navigateToView !== 'function') { window.showToast?.(hint, 'info'); return; }
  window.navigateToView('billing');
  let tries = 0;
  const t = setInterval(() => {
    tries += 1;
    if (go()) clearInterval(t);
    else if (tries > 40) { clearInterval(t); window.showToast?.(hint, 'info'); }
  }, 150);
}

function renderFinancialHealth(daysToInvoice, totalRevenue, basis, revUnavailable) {
  const items = [];
  // TASK-9242 — copy must name the basis the total was computed on.
  const revNoun = basis === 'performed' ? 'work performed' : 'invoiced revenue';

  // ---- Unbilled work (was "Outstanding AR") ----------------------------------
  // Draft and ready tickets have never been sent to anyone. Calling them a
  // receivable overstates money-you-are-owed with money-you-have-not-billed.
  // The old `!t.paid` clause did nothing: 0 of 993 TITS tickets have ever carried
  // paid:true. `_biTicketInvoiced` is the canonical test (billing.js:1554-1556) and
  // is what keeps the new label true on a tenant that DOES invoice from a draft.
  //
  // 🔴 Do NOT swap in the Financial tab's predicate. It would put ~$1.48M of
  // invoiced-unpaid on the founder's home screen with zero payment data behind it
  // — a collections illusion. True AR stays on the Financial tab, where
  // `arNoPaymentsRecorded` already carries the caveat.
  const unbilledTickets = BiState.tickets.filter(t =>
    (t.status === 'draft' || t.status === 'ready') && !_biTicketInvoiced(t)
  );
  const unbilledTotal = unbilledTickets.reduce((s, t) => s + (t.grandTotal || 0), 0);
  const draftCount = unbilledTickets.filter(t => t.status === 'draft').length;
  const readyCount = unbilledTickets.length - draftCount;
  // Billing has no single "unbilled" tab — draft and ready are separate filters,
  // and 'all' does not call ensureFullTicketHistory() (billing.js:7338), so it can
  // land on a short window. Deep-link to whichever bucket holds more tickets and
  // print the split in the subtitle, so the count is EXPLAINED before the tap
  // rather than contradicted after it. The old link always said 'ready': the owner
  // tapped a 20-ticket card and landed on a list of one.
  const unbilledLink = `window._biDeepLink={view:'tickets',filter:'${readyCount > draftCount ? 'ready' : 'draft'}'};window.location.hash='#/billing'`;
  const unbilledSplit = `${draftCount} draft + ${readyCount} ready`;
  if (unbilledTotal > 0) {
    items.push(alertItem('warning', 'receipt_long',
      `Unbilled work: ${formatCurrency(unbilledTotal)}`,
      `${unbilledTickets.length} ticket${unbilledTickets.length === 1 ? '' : 's'} not yet invoiced (${unbilledSplit})`,
      unbilledLink
    ));
  }

  // ---- Unbilled 30+ days (was "Aging AR") ------------------------------------
  // Same list, so it has to carry the same label. As "Aging AR" it would sooner or
  // later announce that DRAFTS are overdue. Renders nothing on TITS today (the
  // oldest unbilled ticket is 2026-08-28) — latent, not live, and fixed anyway.
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  const agingTickets = unbilledTickets.filter(t => {
    const d = toDate(t.date);
    return d && d < thirtyDaysAgo;
  });
  const agingTotal = agingTickets.reduce((s, t) => s + (t.grandTotal || 0), 0);
  if (agingTotal > 0) {
    const agingDrafts = agingTickets.filter(t => t.status === 'draft').length;
    items.push(alertItem('critical', 'warning',
      `Unbilled 30+ days: ${formatCurrency(agingTotal)}`,
      `${agingTickets.length} ticket${agingTickets.length === 1 ? '' : 's'} still not invoiced (${agingDrafts} draft + ${agingTickets.length - agingDrafts} ready)`,
      unbilledLink
    ));
  }

  // ---- Avg days to invoice (was "Average DSO") -------------------------------
  // Computed period-scoped in renderOverviewTab. An empty period renders NOTHING
  // rather than "0 days" — zero samples is not a zero-day turnaround.
  const dti = daysToInvoice || { avgDays: 0, sampleCount: 0 };
  if (dti.sampleCount > 0) {
    const d = dti.avgDays;
    const dtiType = d > 45 ? 'warning' : d > 30 ? 'info' : 'success';
    items.push(alertItem(dtiType, 'schedule',
      `Avg days to invoice: ${d} day${d === 1 ? '' : 's'}`,
      `${dti.sampleCount} ticket${dti.sampleCount === 1 ? '' : 's'} invoiced this period · ticket date → invoice date (not DSO — no payment dates are recorded)`,
      "window._biDeepLink={view:'tickets',filter:'invoiced'};window.location.hash='#/billing'"
    ));
  }

  // ---- Margin, or an honest reason there isn't one ---------------------------
  // Measured on TITS Sep 1-9: recognized costs $0 against $49,388 of revenue, so
  // this card printed "Gross Margin: 100%". Three compounding causes, all real:
  //   1. isRecognizedCost() drops pending/rejected driver+staff expenses. That gate
  //      is CORRECT for a P&L; printing its output as a headline margin with no
  //      footnote is not. 21 entries / $10,411 vanished from that window silently.
  //   2. Labor and maintenance are excluded entirely here and INCLUDED by the P&L,
  //      so one dashboard rendered two different margins. 845.7 clocked hours in
  //      that window are priced at $0 on this card.
  //   3. costEntries is not a full cost ledger on this tenant — no payroll, no
  //      insurance, no truck notes. A margin computed off it cannot be meaningful.
  // The precedent is the P&L's cashBasisGap: measure what the basis leaves out and
  // hand it to the render layer, so the statement can say "$0 means nothing
  // recorded, not nothing spent."
  const periodCostsAll = BiState.costEntries.filter(c => isInPeriod(c.date));
  const periodCosts = periodCostsAll.filter(c =>
    isRecognizedCost(c) && !OVERVIEW_NON_COST_CATEGORIES.includes(c.category || 'other')
  );
  const totalCost = periodCosts.reduce((s, c) => s + (c.amount || 0), 0);
  // periodCosts has ALREADY dropped the pending rows, so the disclosure cannot be
  // derived from it. Second, deliberately un-gated pass over the same window.
  const pendingCosts = periodCostsAll.filter(c => !isRecognizedCost(c) && !personalChargeOf(c)); // TASK-9917 — personal is decided, not unapproved
  const pendingTotal = pendingCosts.reduce((s, c) => s + (c.amount || 0), 0);
  const pendingNote = pendingCosts.length > 0
    ? ` · ${pendingCosts.length} unapproved expense${pendingCosts.length === 1 ? '' : 's'} (${formatCurrency(pendingTotal)}) not counted`
    : '';
  const scopeNote = ' · labor and maintenance are not included — see Profitability for the full P&L';
  const profitLink = "window.BIModule.switchTab('profitability')";

  if (revUnavailable) {
    // no margin from a revenue figure that failed to load
  } else if (totalRevenue > 0 && totalCost === 0) {
    // No margin percentage at all. 100% is not a rounding problem, it means the
    // costs never reached the calculation.
    items.push(alertItem('info', 'info',
      'Costs not recorded for this period',
      `No expenses recorded against ${formatCurrency(totalRevenue)} of ${revNoun}, so no margin is shown${pendingNote}${scopeNote}`,
      profitLink
    ));
  } else if (totalRevenue > 0 && totalCost > 0) {
    const margin = (totalRevenue - totalCost) / totalRevenue * 100;
    const marginType = margin < 10 ? 'critical' : margin < 25 ? 'warning' : 'success';
    items.push(alertItem(marginType, 'trending_up',
      `Margin (recorded costs only): ${margin.toFixed(0)}%`,
      `${basis === 'performed' ? 'Work performed' : 'Revenue'} ${formatCurrency(totalRevenue)} − recorded costs ${formatCurrency(totalCost)} = ${formatCurrency(totalRevenue - totalCost)}${pendingNote}${scopeNote}`,
      profitLink
    ));
  } else if (totalCost > 0) {
    // Costs but nothing invoiced. The old code divided by a zero-guard to 0 and
    // printed "Gross Margin: 0%" in critical red, which reads as a business in
    // freefall rather than a period that has not been billed yet.
    items.push(alertItem('info', 'info',
      basis === 'performed' ? 'No work performed this period' : 'No revenue invoiced this period',
      `${formatCurrency(totalCost)} of costs recorded with ${basis === 'performed' ? 'no work performed' : 'nothing invoiced'}, so no margin is shown${pendingNote}`,
      profitLink
    ));
  }

  // TASK-9969 S2 — one passive QuickBooks transfer row (QuickBooks-connected
  // companies only; BiState.qbBooks is null for everyone else).
  const qbHealth = BiState.qbBooks ? qbBooksHealthItem(BiState.qbBooks) : null;
  if (qbHealth && qbHealth.row) items.push(alertItem(...qbHealth.row));

  if (items.length === 0 && revUnavailable) {
    // 1996 COD: a failed revenue read must never read as a clean bill of health.
    items.push(alertItem('warning', 'error_outline', 'Revenue unavailable', 'Revenue could not be loaded, so financial health can’t be checked. Refresh to try again.'));
  } else if (items.length === 0 && qbHealth && qbHealth.holdRow) {
    // Same rule for the QuickBooks read: in flight or failed is not "looks good".
    items.push(alertItem(...qbHealth.holdRow));
  } else if (items.length === 0) {
    items.push(alertItem('success', 'check_circle', 'Financials look good', 'No outstanding issues detected'));
  }

  return `<div class="crm-section-card" style="margin-top:16px;">
    <div class="crm-section-title" style="margin-bottom:12px;">
      <span class="material-icons" style="color:var(--text-secondary);font-size:20px;">monitoring</span>
      Financial Health
    </div>
    ${items.join('')}
  </div>`;
}

function getUnitLabel(task) {
  if (task.truckId) {
    const t = BiState.trucks.find(tr => tr.id === task.truckId);
    return t ? (window.getUnitDisplay ? window.getUnitDisplay(t) : `#${t.unit}`) : '';
  }
  if (task.trailerId) {
    const t = BiState.trailers.find(tr => tr.id === task.trailerId);
    return t ? (window.getUnitDisplay ? window.getUnitDisplay(t) : `#${t.unit}`) : '';
  }
  return '';
}

// ============================================
// FLEET TAB
// ============================================
async function renderFleetTab() {
  const el = document.getElementById('bi-tab-fleet');
  if (!el) return;
  el.innerHTML = '<div class="loading-state"><div class="loading-spinner"></div><p>Loading fleet data...</p></div>';

  await loadTicketLinesForPeriod();
  // TASK-10171 — the shop mirror rule is in the lazy statement module.
  if (!(await _pnlStatementReady)) { el.textContent = 'The P&L could not be loaded — refresh and try again.'; return; }

  const periodDays = getPeriodDays();
  const periodJobDays = BiState.jobDays.filter(jd => isInPeriod(jd.date));
  const periodMaint = BiState.maintenance.filter(m => isInPeriod(m.createdAt));
  // TASK-9490: one pass, the shared rule (deleted excluded, assetId only).
  const periodRevByAsset = _biVehicleRevenueByAsset(BiState.tickets, BiState.ticketLinesCache, isInPeriod);
  const lifetimeRevByAsset = _biVehicleRevenueByAsset(BiState.tickets, BiState.ticketLinesCache, () => true);

  // Build per-vehicle data
  // TASK-10171 — a work order's shop line is in its total AND mirrored into the
  // cost ledger; both are summed below, so the mirror rows are left out.
  const fleetCostViews = _costViewsOf(window.PnlStatement.withoutWoShopMirrors(BiState.costEntries, BiState.maintenance));
  const vehicleData = BiState.trucks.map(truck => {
    const display = window.getUnitDisplay ? window.getUnitDisplay(truck) : `#${truck.unit}`;
    const typeSlug = (truck.type || '').toLowerCase();

    // Utilization: days with at least one jobDay assignment
    // Truck refs are on jobDay.trucks (per-day assignments), NOT job.trucks
    const assignedDays = new Set();
    periodJobDays.forEach(jd => {
      const truckRefs = jd.trucks || [];
      if (truckRefs.some(ref => ref === truck.id || ref === truck.unit || ref === truck.name)) {
        const dayStr = typeof jd.date === 'string' ? jd.date : toDate(jd.date)?.toISOString?.()?.slice(0, 10);
        if (dayStr) assignedDays.add(dayStr);
      }
    });
    const utilPct = Math.min(100, (assignedDays.size / periodDays) * 100);

    // Revenue from ticket lines (TASK-9490 shared rule)
    const revenue = periodRevByAsset[truck.id]?.amount || 0;

    // Maintenance spend
    let maintSpend = 0;
    periodMaint.forEach(m => {
      if (m.truckId === truck.id) maintSpend += window.PnlStatement.woCountedCost(m); // TASK-10171 (item 4: in-house labor is info)
    });

    // Direct costs from costEntries (fuel, tolls, etc.) — TASK-10171: without
    // the work orders' shop mirror rows (their dollars are in maintSpend).
    let directCosts = 0;
    fleetCostViews.filter(c => isRecognizedCost(c) && isInPeriod(c.date) && c.vehicleId === truck.id).forEach(c => { // TASK-9493 split-aware
      directCosts += c.amount || 0;
    });
    const totalCosts = maintSpend + directCosts;

    // Cost per mile from metersLog
    const truckMeters = BiState.metersLog
      .filter(m => (m.assetId === truck.id || m.truckId === truck.id) && isInPeriod(m.recordedAt || m.date))
      .map(m => ({ odo: m.odometer || m.mileage || 0, date: toDate(m.recordedAt || m.date) }))
      .filter(m => m.odo > 0);
    let periodMiles = 0;
    if (truckMeters.length >= 2) {
      const sorted = truckMeters.sort((a, b) => a.date - b.date);
      periodMiles = sorted[sorted.length - 1].odo - sorted[0].odo;
    }
    const costPerMile = periodMiles > 0 ? totalCosts / periodMiles : null;

    // Revenue per mile
    const revenuePerMile = periodMiles > 0 ? revenue / periodMiles : null;

    // Fuel economy (MPG)
    let fuelGallons = 0;
    _costViewsOf(BiState.costEntries).filter(c => isRecognizedCost(c) && isInPeriod(c.date) && c.vehicleId === truck.id && c.category === 'fuel').forEach(c => { // TASK-9493 — split gallons prorated
      fuelGallons += c.fuelGallons || 0;
    });
    const mpg = (periodMiles > 0 && fuelGallons > 0) ? periodMiles / fuelGallons : null;

    // Equipment ROI (lifetime: all revenue vs all costs, not period-filtered)
    const lifetimeRevenue = lifetimeRevByAsset[truck.id]?.amount || 0;
    let lifetimeCosts = 0;
    BiState.maintenance.forEach(m => { if (m.truckId === truck.id) lifetimeCosts += window.PnlStatement.woCountedCost(m); });
    fleetCostViews.forEach(c => { if (isRecognizedCost(c) && c.vehicleId === truck.id) lifetimeCosts += c.amount || 0; }); // TASK-9493 / TASK-10171
    const lifetimeROI = lifetimeCosts > 0 ? lifetimeRevenue / lifetimeCosts : null;

    // Revenue per truck per day
    const rptd = assignedDays.size > 0 ? revenue / assignedDays.size : null;

    // Reserve calculation
    const reservePct = BiState.biConfig.reserves[typeSlug] ?? BiState.biConfig.reserves._default ?? 12;
    const reserveTarget = revenue * (reservePct / 100);
    const reserveRatio = reserveTarget > 0 ? (maintSpend / reserveTarget) : 0;

    return { truck, display, typeSlug, assignedDays: assignedDays.size, utilPct, revenue, maintSpend, directCosts, totalCosts, costPerMile, revenuePerMile, periodMiles, mpg, lifetimeROI, rptd, reservePct, reserveTarget, reserveRatio };
  });

  // Sort by utilization desc
  vehicleData.sort((a, b) => b.utilPct - a.utilPct);

  el.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
      <div style="font-size:13px;color:var(--text-secondary);">${vehicleData.length} active vehicles &middot; ${periodDays}-day period</div>
      <button class="btn btn-sm btn-secondary" onclick="window.BIModule.showReserveSettings()">
        <span class="material-icons" style="font-size:16px;">settings</span> Reserve %
      </button>
    </div>
    <div class="bi-vehicle-grid">
      ${vehicleData.map(v => vehicleCard(v)).join('')}
    </div>
    ${vehicleData.length === 0 ? '<div class="empty-state" style="padding:40px;text-align:center;"><span class="material-icons" style="font-size:48px;color:var(--text-secondary);">local_shipping</span><p>No active vehicles found</p></div>' : ''}
  `;
}

function vehicleCard(v) {
  const utilColor = v.utilPct >= 70 ? '#10B981' : v.utilPct >= 40 ? '#F59E0B' : '#EF4444';
  const reserveColor = v.reserveRatio <= 0.95 ? '#10B981' : v.reserveRatio <= 1.05 ? '#F59E0B' : '#EF4444';
  const typeBadge = v.typeSlug ? `<span class="bi-type-badge">${escBI(window.getTypeLabel ? window.getTypeLabel(v.typeSlug, 'truckTypes') : v.typeSlug)}</span>` : ''; // TASK-9884 — (slug, kind); was reversed and read "TruckTypes"

  return `
    <div class="bi-vehicle-card" style="border-left-color:${utilColor};">
      <div style="display:flex;justify-content:space-between;align-items:start;margin-bottom:8px;">
        <div>
          <div style="font-weight:600;font-size:14px;color:var(--text-primary);">${escBI(v.display)}</div>
          ${typeBadge}
        </div>
        <div style="text-align:right;font-size:20px;font-weight:700;color:${utilColor};">${formatPct(v.utilPct)}</div>
      </div>
      <div style="margin-bottom:10px;">
        <div style="font-size:11px;color:var(--text-secondary);margin-bottom:3px;">Utilization (${v.assignedDays}d assigned)</div>
        <div class="bi-bar-track"><div class="bi-bar-fill" style="width:${Math.min(100, v.utilPct)}%;background:${utilColor};"></div></div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;font-size:12px;margin-bottom:6px;">
        <div><div style="color:var(--text-secondary);">Revenue</div><div style="font-weight:600;color:var(--text-primary);">${formatCurrency(v.revenue)}</div></div>
        <div><div style="color:var(--text-secondary);">Total Cost</div><div style="font-weight:600;color:var(--text-primary);">${formatCurrency(v.totalCosts)}</div></div>
        <div><div style="color:var(--text-secondary);">Reserve ${v.reservePct}%</div><div style="font-weight:600;color:${reserveColor};">${v.reserveTarget > 0 ? formatCurrency(v.reserveTarget - v.maintSpend) : '—'}</div></div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;font-size:12px;margin-bottom:6px;">
        <div><div style="color:var(--text-secondary);">Cost/Mi</div><div style="font-weight:600;color:var(--text-primary);">${v.costPerMile !== null ? '$' + v.costPerMile.toFixed(2) : '—'}</div></div>
        <div><div style="color:var(--text-secondary);">Rev/Mi</div><div style="font-weight:600;color:var(--text-primary);">${v.revenuePerMile !== null ? '$' + v.revenuePerMile.toFixed(2) : '—'}</div></div>
        <div><div style="color:var(--text-secondary);">Rev/Day</div><div style="font-weight:600;color:var(--text-primary);">${v.rptd !== null ? formatCurrency(v.rptd) : '—'}</div></div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;font-size:12px;">
        <div><div style="color:var(--text-secondary);">Miles</div><div style="font-weight:600;color:var(--text-primary);">${v.periodMiles > 0 ? v.periodMiles.toLocaleString() : '—'}</div></div>
        <div><div style="color:var(--text-secondary);">MPG</div><div style="font-weight:600;color:var(--text-primary);">${v.mpg !== null ? v.mpg.toFixed(1) : '—'}</div></div>
        <div><div style="color:var(--text-secondary);">ROI</div><div style="font-weight:600;color:${v.lifetimeROI !== null && v.lifetimeROI >= 1 ? '#10B981' : v.lifetimeROI !== null ? '#EF4444' : 'var(--text-primary)'};">${v.lifetimeROI !== null ? v.lifetimeROI.toFixed(2) + 'x' : '—'}</div></div>
      </div>
      ${v.reserveTarget > 0 ? `
      <div style="margin-top:8px;">
        <div style="font-size:11px;color:var(--text-secondary);margin-bottom:3px;">Maint spend vs ${v.reservePct}% reserve</div>
        <div class="bi-bar-track"><div class="bi-bar-fill" style="width:${Math.min(100, v.reserveRatio * 100)}%;background:${reserveColor};"></div></div>
      </div>` : ''}
    </div>
  `;
}

// ============================================
// FINANCIAL TAB
// ============================================
function renderFinancialTab() {
  const el = document.getElementById('bi-tab-financial');
  if (!el) return;

  // Invoiced revenue from revenueEntries (TASK-842-C: rental slices by billed period)
  const periodRevenue = rentalAwareRevenueEntries(isInPeriod);
  const invoicedTotal = periodRevenue.reduce((s, r) => s + (r.amount || 0), 0);

  // Previous period for trends
  const prevPeriodRevenue = rentalAwareRevenueEntries(isInPreviousPeriod);
  const prevInvoicedTotal = prevPeriodRevenue.reduce((s, r) => s + (r.amount || 0), 0);

  // Revenue by customer (from revenueEntries)
  const revByCustomer = {};
  const revByCustomerId = {};
  periodRevenue.forEach(r => {
    const name = r.customerName || 'Unknown';
    const custId = r.customerId || name;
    revByCustomer[name] = (revByCustomer[name] || 0) + (r.amount || 0);
    revByCustomerId[custId] = (revByCustomerId[custId] || 0) + (r.amount || 0);
  });
  const custEntries = Object.entries(revByCustomer).sort((a, b) => b[1] - a[1]);
  const maxCustRev = custEntries.length > 0 ? custEntries[0][1] : 1;

  // Revenue by month (from revenueEntries, for quarter/YTD)
  const revByMonth = {};
  periodRevenue.forEach(r => {
    const d = toDate(r.invoicedAt);
    if (d) {
      const key = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      revByMonth[key] = (revByMonth[key] || 0) + (r.amount || 0);
    }
  });
  const monthEntries = Object.entries(revByMonth);
  const maxMonthRev = monthEntries.length > 0 ? Math.max(...monthEntries.map(e => e[1])) : 1;

  // Ticket status breakdown (pipeline)
  const allPeriodTickets = BiState.tickets.filter(t => isInPeriod(t.date));
  const draftTickets = allPeriodTickets.filter(t => t.status === 'draft');
  const readyTickets = allPeriodTickets.filter(t => t.status === 'ready');
  const archivedTickets = allPeriodTickets.filter(t => t.status === 'archived' || t.status === 'locked');
  const draftTotal = draftTickets.reduce((s, t) => s + (t.grandTotal || 0), 0);
  const readyTotal = readyTickets.reduce((s, t) => s + (t.grandTotal || 0), 0);
  const archivedTotal = archivedTickets.reduce((s, t) => s + (t.grandTotal || 0), 0);

  // Previous period ticket totals for trends
  const prevPeriodTickets = BiState.tickets.filter(t => isInPreviousPeriod(t.date));
  const prevDraftTotal = prevPeriodTickets.filter(t => t.status === 'draft').reduce((s, t) => s + (t.grandTotal || 0), 0);
  const prevReadyTotal = prevPeriodTickets.filter(t => t.status === 'ready').reduce((s, t) => s + (t.grandTotal || 0), 0);

  // Aging receivables (outstanding tickets)
  const now = new Date();
  const outstanding = BiState.tickets.filter(t =>
    t.status === 'draft' || t.status === 'ready' ||
    (_biTicketInvoiced(t) && !t.paid && !t.writtenOff && t.status !== 'deleted')
  );
  const agingBuckets = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
  const agingDetails = []; // for AR detail section
  outstanding.forEach(t => {
    const ageDate = (_biTicketInvoiced(t) && t.invoicedAt) ? toDate(t.invoicedAt) : toDate(t.date);
    if (!ageDate) return;
    const age = Math.floor((now - ageDate) / (1000 * 60 * 60 * 24));
    const amt = t.grandTotal || 0;
    const bucket = age <= 30 ? '0-30' : age <= 60 ? '31-60' : age <= 90 ? '61-90' : '90+';
    agingBuckets[bucket] += amt;
    // TASK-9344 — ticketNumber / invoiceNumber / status / doc id are unfenced on a
    // driver-PIN ticket CREATE, and the AR detail rows print them: escBI there.
    agingDetails.push({ customer: t.customerName || 'Unknown', ticketNum: t.ticketNumber || t.id?.slice(0,8) || '', invoiceNum: t.invoiceNumber || '', date: ageDate, age, amount: amt, bucket, status: t.status });
  });
  const agingTotal = Object.values(agingBuckets).reduce((s, v) => s + v, 0);

  // 🔴 SAY WHY THE AGING LOOKS LIKE THAT. Fixing the status predicate above means
  // 602 genuinely-invoiced TITS tickets enter AR for the first time — and because
  // NOTHING in the product ever writes `paid` on a ticket (measured live: 0 of
  // 881), every one of them ages straight into 90+. That reads as a collections
  // catastrophe when it is actually a missing-data problem, so the number must
  // arrive with its own caveat. Same discipline as the cash-basis banner: we do
  // not hide the dollars, and we do not let them be misread.
  const _arEverPaid = (BiState.tickets || []).some(t => t.paid === true);
  const _arInvoicedUnpaid = outstanding.filter(t => _biTicketInvoiced(t) && !t.paid && !t.writtenOff);
  const arNoPaymentsRecorded = (!_arEverPaid && _arInvoicedUnpaid.length > 0)
    ? { count: _arInvoicedUnpaid.length, amount: _arInvoicedUnpaid.reduce((t2, t3) => t2 + (t3.grandTotal || 0), 0) }
    : null;

  // Group AR by customer for detail view
  const arByCustomer = {};
  agingDetails.forEach(d => {
    if (!arByCustomer[d.customer]) arByCustomer[d.customer] = { total: 0, buckets: { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 }, tickets: [] };
    arByCustomer[d.customer].total += d.amount;
    arByCustomer[d.customer].buckets[d.bucket] += d.amount;
    arByCustomer[d.customer].tickets.push(d);
  });
  const arCustomerEntries = Object.entries(arByCustomer).sort((a, b) => b[1].total - a[1].total);

  // Customer concentration risk
  const topCustPct = invoicedTotal > 0 && custEntries.length > 0 ? (custEntries[0][1] / invoicedTotal * 100) : 0;
  const concentrationRisk = topCustPct > 50 ? 'critical' : topCustPct > 40 ? 'warning' : null;

  // AP Aging (payables)
  const apOutstanding = (BiState.apInvoices || []).filter(i => i.paymentStatus !== 'paid');
  const apAgingBuckets = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
  const apAgingDetails = [];
  apOutstanding.forEach(inv => {
    const dueDate = inv.dueDate ? toDate(inv.dueDate) : toDate(inv.invoiceDate);
    if (!dueDate) return;
    const age = Math.floor((now - dueDate) / (1000 * 60 * 60 * 24));
    const amt = (inv.grandTotal || 0) - (inv.paidAmount || 0);
    if (amt <= 0) return;
    const bucket = age <= 0 ? '0-30' : age <= 30 ? '0-30' : age <= 60 ? '31-60' : age <= 90 ? '61-90' : '90+';
    apAgingBuckets[bucket] += amt;
    apAgingDetails.push({ vendor: inv.vendorName || 'Unknown', invoiceNum: inv.vendorInvoiceNumber || '', date: dueDate, age: Math.max(0, age), amount: amt, bucket });
  });
  const apAgingTotal = Object.values(apAgingBuckets).reduce((s, v) => s + v, 0);

  // Group AP by vendor
  const apByVendor = {};
  apAgingDetails.forEach(d => {
    if (!apByVendor[d.vendor]) apByVendor[d.vendor] = { total: 0, buckets: { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 }, invoices: [] };
    apByVendor[d.vendor].total += d.amount;
    apByVendor[d.vendor].buckets[d.bucket] += d.amount;
    apByVendor[d.vendor].invoices.push(d);
  });
  const apVendorEntries = Object.entries(apByVendor).sort((a, b) => b[1].total - a[1].total);

  el.innerHTML = `
    <!-- Export action bar -->
    <div style="display:flex;justify-content:flex-end;gap:8px;margin:0 0 12px;flex-wrap:wrap;">
      <div style="position:relative;display:inline-block;" id="bi-financial-export-dropdown">
        <button class="btn btn-secondary btn-sm" onclick="document.getElementById('bi-financial-export-menu').classList.toggle('hidden')" style="border-radius:8px;">
          <span class="material-icons" style="font-size:18px;vertical-align:middle;">download</span> Export
        </button>
        <div id="bi-financial-export-menu" class="hidden" style="position:absolute;right:0;top:100%;margin-top:4px;background:#fff;border:1px solid var(--border-light);border-radius:8px;box-shadow:var(--shadow-lg);z-index:100;min-width:220px;">
          <button class="bi-export-item" onclick="window.BIModule.exportPLStatement()">
            <span class="material-icons">description</span> P&L Statement
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportIncomeStatement()">
            <span class="material-icons">trending_up</span> Income Statement
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportExpenseReport()">
            <span class="material-icons">trending_down</span> Expense Report
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportGeneralLedger()">
            <span class="material-icons">menu_book</span> General Ledger
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportARAgingReport()">
            <span class="material-icons">schedule</span> AR Aging Report
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportAPAgingReport()">
            <span class="material-icons">hourglass_bottom</span> AP Aging Report
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportPayrollJournalEntries()">
            <span class="material-icons">receipt_long</span> Payroll Journal (QBO)
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportBankFeedCSV()">
            <span class="material-icons">account_balance</span> Bank Feed (QBO)
          </button>
          <div style="border-top:1px solid var(--border-light);margin:4px 0;"></div>
          <button class="bi-export-item" onclick="window.BIModule.export1099Summary()">
            <span class="material-icons">receipt_long</span> 1099 Summary
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportContractorPaymentSummary()">
            <span class="material-icons">payments</span> Contractor Payments
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportChartOfAccounts()">
            <span class="material-icons">list_alt</span> Chart of Accounts
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportDepreciationSchedule()">
            <span class="material-icons">trending_down</span> Depreciation Schedule
          </button>
          <div style="border-top:1px solid var(--border-light);margin:4px 0;"></div>
          <div style="padding:4px 12px;font-size:10px;color:var(--text-secondary);font-weight:600;text-transform:uppercase;letter-spacing:0.5px;">QuickBooks Desktop (.iif)</div>
          <button class="bi-export-item" onclick="window.BIModule.exportIIF_CostEntries()">
            <span class="material-icons">receipt</span> Expenses (.iif)
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportIIF_Revenue()">
            <span class="material-icons">payments</span> Revenue (.iif)
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportIIF_ChartOfAccounts()">
            <span class="material-icons">list_alt</span> Chart of Accounts (.iif)
          </button>
          <div style="border-top:1px solid var(--border-light);margin:4px 0;"></div>
          <button class="bi-export-item" disabled style="opacity:0.5;cursor:not-allowed;">
            <span class="material-icons">cloud_sync</span> QBO Sync (Coming Soon)
          </button>
          <button class="bi-export-item" disabled style="opacity:0.5;cursor:not-allowed;">
            <span class="material-icons">cloud_upload</span> E-File 1099s (Coming Soon)
          </button>
        </div>
      </div>
    </div>

    <div class="crm-stats-grid" style="margin-bottom:16px;">
      ${statCard('receipt_long', `Invoiced (${periodRevenue.length})`, formatCurrency(invoicedTotal), 'green', null, trendBadge(invoicedTotal, prevInvoicedTotal))}
      ${statCard('edit_note', `Draft (${draftTickets.length})`, formatCurrency(draftTotal), 'amber', null, trendBadge(draftTotal, prevDraftTotal))}
      ${statCard('check_circle', `Ready (${readyTickets.length})`, formatCurrency(readyTotal), 'blue', null, trendBadge(readyTotal, prevReadyTotal))}
      ${statCard('inventory_2', `Archived (${archivedTickets.length})`, formatCurrency(archivedTotal), 'purple')}
    </div>

    ${agingTotal > 0 ? `
    <div class="crm-section-card" style="margin-bottom:16px;">
      <div class="crm-section-title"><span class="material-icons" style="color:var(--text-secondary);font-size:20px;">hourglass_top</span> Aging Receivables</div>
      ${arNoPaymentsRecorded ? `
      <div style="display:flex;align-items:flex-start;gap:10px;padding:12px 14px;margin:8px 0 0;background:#FFFBEB;border:1px solid #FCD34D;border-radius:8px;font-size:12px;line-height:1.5;color:#92400E;">
        <span class="material-icons" style="font-size:16px;line-height:1.35;color:#B45309;">report_problem</span>
        <span><strong>Read this before you act on the buckets below.</strong> No ticket has ever been marked paid in DispatchDeck, so all ${arNoPaymentsRecorded.count} invoiced tickets (${formatCurrency(arNoPaymentsRecorded.amount)}) count as outstanding and age from their invoice date — most will land in 90+ regardless of whether the customer actually paid. This is a missing-payment-record problem, not necessarily overdue money. Start marking invoices paid and this becomes a real aging report.</span>
      </div>` : ''}
      <div class="crm-stats-grid" style="grid-template-columns:repeat(4,1fr);margin:8px 0 0;">
        ${statCard('check_circle', '0-30 days', formatCurrency(agingBuckets['0-30']), 'green')}
        ${statCard('schedule', '31-60 days', formatCurrency(agingBuckets['31-60']), agingBuckets['31-60'] > 0 ? 'amber' : 'green')}
        ${statCard('warning', '61-90 days', formatCurrency(agingBuckets['61-90']), agingBuckets['61-90'] > 0 ? 'red' : 'green')}
        ${statCard('error', '90+ days', formatCurrency(agingBuckets['90+']), agingBuckets['90+'] > 0 ? 'red' : 'green')}
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px;">
        <span style="font-size:12px;color:var(--text-secondary);">Total outstanding: ${formatCurrency(agingTotal)}</span>
      </div>
      ${arCustomerEntries.length > 0 ? `
      <div style="margin-top:12px;border-top:1px solid var(--border-light);padding-top:12px;">
        <div style="font-size:13px;font-weight:600;color:var(--text-primary);margin-bottom:8px;">AR by Customer</div>
        <table style="width:100%;border-collapse:collapse;font-size:13px;">
          <thead>
            <tr style="border-bottom:2px solid var(--border-light);">
              <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Customer</th>
              <th style="text-align:right;padding:6px 4px;color:#16A34A;font-weight:600;">0-30</th>
              <th style="text-align:right;padding:6px 4px;color:#D97706;font-weight:600;">31-60</th>
              <th style="text-align:right;padding:6px 4px;color:#EA580C;font-weight:600;">61-90</th>
              <th style="text-align:right;padding:6px 4px;color:#DC2626;font-weight:600;">90+</th>
              <th style="text-align:right;padding:6px 8px;font-weight:600;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${arCustomerEntries.map(([cust, data]) => `
              <tr style="border-bottom:1px solid var(--border-light);cursor:pointer;" onclick="this.nextElementSibling.classList.toggle('hidden')">
                <td style="padding:6px 8px;font-weight:500;">${escBI(cust)}</td>
                <td style="text-align:right;padding:6px 4px;color:#16A34A;">${data.buckets['0-30'] > 0 ? formatCurrency(data.buckets['0-30']) : '-'}</td>
                <td style="text-align:right;padding:6px 4px;color:#D97706;">${data.buckets['31-60'] > 0 ? formatCurrency(data.buckets['31-60']) : '-'}</td>
                <td style="text-align:right;padding:6px 4px;color:#EA580C;">${data.buckets['61-90'] > 0 ? formatCurrency(data.buckets['61-90']) : '-'}</td>
                <td style="text-align:right;padding:6px 4px;color:#DC2626;">${data.buckets['90+'] > 0 ? formatCurrency(data.buckets['90+']) : '-'}</td>
                <td style="text-align:right;padding:6px 8px;font-weight:600;">${formatCurrency(data.total)}</td>
              </tr>
              <tr class="hidden">
                <td colspan="6" style="padding:0 8px 8px 24px;">
                  ${data.tickets.sort((a,b) => b.age - a.age).map(tk => `
                    <div style="display:flex;justify-content:space-between;padding:3px 0;font-size:12px;color:var(--text-secondary);border-bottom:1px dotted var(--border-light);">
                      <span>${escBI(tk.ticketNum)}${tk.invoiceNum ? ' / ' + escBI(tk.invoiceNum) : ''} <span style="opacity:0.7;">(${escBI(tk.status)})</span></span>
                      <span>${tk.age}d — <strong style="color:var(--text-primary);">${formatCurrency(tk.amount)}</strong></span>
                    </div>
                  `).join('')}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>` : ''}
    </div>` : ''}

    ${apAgingTotal > 0 ? `
    <div class="crm-section-card" style="margin-bottom:16px;">
      <div class="crm-section-title"><span class="material-icons" style="color:var(--text-secondary);font-size:20px;">hourglass_bottom</span> Aging Payables (AP)</div>
      <div class="crm-stats-grid" style="grid-template-columns:repeat(4,1fr);margin:8px 0 0;">
        ${statCard('check_circle', '0-30 days', formatCurrency(apAgingBuckets['0-30']), 'green')}
        ${statCard('schedule', '31-60 days', formatCurrency(apAgingBuckets['31-60']), apAgingBuckets['31-60'] > 0 ? 'amber' : 'green')}
        ${statCard('warning', '61-90 days', formatCurrency(apAgingBuckets['61-90']), apAgingBuckets['61-90'] > 0 ? 'red' : 'green')}
        ${statCard('error', '90+ days', formatCurrency(apAgingBuckets['90+']), apAgingBuckets['90+'] > 0 ? 'red' : 'green')}
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;margin-top:8px;">
        <span style="font-size:12px;color:var(--text-secondary);">Total outstanding payables: ${formatCurrency(apAgingTotal)}</span>
      </div>
      ${apVendorEntries.length > 0 ? `
      <div style="margin-top:12px;border-top:1px solid var(--border-light);padding-top:12px;">
        <div style="font-size:13px;font-weight:600;color:var(--text-primary);margin-bottom:8px;">AP by Vendor</div>
        <table style="width:100%;border-collapse:collapse;font-size:13px;">
          <thead>
            <tr style="border-bottom:2px solid var(--border-light);">
              <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Vendor</th>
              <th style="text-align:right;padding:6px 4px;color:#16A34A;font-weight:600;">0-30</th>
              <th style="text-align:right;padding:6px 4px;color:#D97706;font-weight:600;">31-60</th>
              <th style="text-align:right;padding:6px 4px;color:#EA580C;font-weight:600;">61-90</th>
              <th style="text-align:right;padding:6px 4px;color:#DC2626;font-weight:600;">90+</th>
              <th style="text-align:right;padding:6px 8px;font-weight:600;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${apVendorEntries.map(([vendor, data]) => `
              <tr style="border-bottom:1px solid var(--border-light);cursor:pointer;" onclick="this.nextElementSibling.classList.toggle('hidden')">
                <td style="padding:6px 8px;font-weight:500;">${escBI(vendor)}</td>
                <td style="text-align:right;padding:6px 4px;color:#16A34A;">${data.buckets['0-30'] > 0 ? formatCurrency(data.buckets['0-30']) : '-'}</td>
                <td style="text-align:right;padding:6px 4px;color:#D97706;">${data.buckets['31-60'] > 0 ? formatCurrency(data.buckets['31-60']) : '-'}</td>
                <td style="text-align:right;padding:6px 4px;color:#EA580C;">${data.buckets['61-90'] > 0 ? formatCurrency(data.buckets['61-90']) : '-'}</td>
                <td style="text-align:right;padding:6px 4px;color:#DC2626;">${data.buckets['90+'] > 0 ? formatCurrency(data.buckets['90+']) : '-'}</td>
                <td style="text-align:right;padding:6px 8px;font-weight:600;">${formatCurrency(data.total)}</td>
              </tr>
              <tr class="hidden">
                <td colspan="6" style="padding:0 8px 8px 24px;">
                  ${data.invoices.sort((a,b) => b.age - a.age).map(inv => `
                    <div style="display:flex;justify-content:space-between;padding:3px 0;font-size:12px;color:var(--text-secondary);border-bottom:1px dotted var(--border-light);">
                      <span>${escBI(inv.invoiceNum) || 'No ref'} <span style="opacity:0.7;">(${inv.age}d past due)</span></span>
                      <span><strong style="color:var(--text-primary);">${formatCurrency(inv.amount)}</strong></span>
                    </div>
                  `).join('')}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>` : ''}
    </div>` : ''}

    <div class="crm-section-card" style="margin-bottom:16px;">
      <div class="crm-section-title"><span class="material-icons" style="color:var(--text-secondary);font-size:20px;">business</span> Revenue by Customer</div>
      ${concentrationRisk ? alertItem(concentrationRisk, 'warning',
        'Customer concentration risk: ' + escBI(custEntries[0][0]) + ' = ' + topCustPct.toFixed(0) + '% of revenue',
        topCustPct > 50 ? 'Over 50% of revenue from a single customer' : 'Over 40% of revenue from a single customer'
      ) : ''}
      ${custEntries.length > 0 ? custEntries.slice(0, 10).map(([name, rev]) => {
        const pctOfTotal = invoicedTotal > 0 ? (rev / invoicedTotal * 100) : 0;
        return `<div class="bi-hbar-row">
          <div class="bi-hbar-label">${escBI(name)}</div>
          <div class="bi-hbar-track"><div class="bi-hbar-fill" style="width:${maxCustRev > 0 ? (rev / maxCustRev * 100) : 0}%;background:#3B82F6;"></div></div>
          <div class="bi-hbar-value">${formatCurrency(rev)} <span style="color:var(--text-secondary);font-size:11px;">(${pctOfTotal.toFixed(0)}%)</span></div>
        </div>`;
      }).join('') : '<p style="color:var(--text-secondary);font-size:13px;padding:8px 0;">No invoiced revenue in period</p>'}
    </div>

    ${renderCustomerProfitability(periodRevenue, periodRevenue)}

    ${renderBillingVelocity()}

    ${renderRevenueByCategorySection(periodRevenue)}

    ${monthEntries.length > 1 ? `
    <div class="crm-section-card">
      <div class="crm-section-title">Revenue by Month</div>
      ${monthEntries.map(([month, rev]) => horizontalBar(month, rev, maxMonthRev, '#06B6D4')).join('')}
    </div>` : ''}
  `;
}

function renderRevenueByCategorySection(periodRevenue) {
  const catTotals = {};
  for (const r of periodRevenue) {
    if (r.revenueBreakdown?.length > 0) {
      for (const b of r.revenueBreakdown) {
        const cat = b.category || 'Uncategorized';
        catTotals[cat] = (catTotals[cat] || 0) + (b.amount || 0);
      }
    } else if (r.revenueCategories?.length > 0) {
      // Legacy: flat list without amounts — split evenly
      const perCat = (r.amount || 0) / r.revenueCategories.length;
      for (const cat of r.revenueCategories) {
        catTotals[cat] = (catTotals[cat] || 0) + perCat;
      }
    }
  }
  const entries = Object.entries(catTotals).filter(([c]) => c !== 'Uncategorized').sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return '';
  const maxCatRev = Math.max(...entries.map(e => e[1]), 1);
  return `
    <div class="crm-section-card">
      <div class="crm-section-title"><span class="material-icons" style="color:var(--text-secondary);font-size:20px;">category</span> Revenue by Category</div>
      ${entries.map(([cat, rev]) => horizontalBar(cat, rev, maxCatRev, '#8B5CF6')).join('')}
    </div>`;
}

function renderCustomerProfitability(periodRevenue) {
  const periodCosts = BiState.costEntries.filter(c => isRecognizedCost(c) && isInPeriod(c.date));
  const periodClock = BiState.clockEntries.filter(c => isInPeriod(c.clockIn));
  const jobCostRates = _biJobCostRates(BiState.people); // burdened; salary allocated to job-clocked hours

  // Map jobId → customerId/customerName
  const jobCustMap = {};
  periodRevenue.forEach(r => { if (r.jobId) jobCustMap[r.jobId] = r.customerId || r.customerName || 'Unknown'; });
  BiState.jobs.forEach(j => { if (j.customerId && !jobCustMap[j.id]) jobCustMap[j.id] = j.customerId; });

  const custProfitData = {};
  // Revenue per customer
  periodRevenue.forEach(r => {
    const cKey = r.customerId || r.customerName || 'Unknown';
    const cName = r.customerName || 'Unknown';
    if (!custProfitData[cKey]) custProfitData[cKey] = { name: cName, revenue: 0, directCosts: 0, laborCost: 0 };
    custProfitData[cKey].revenue += (r.amount || 0);
  });
  // Direct costs per customer
  _costViewsOf(periodCosts).forEach(c => { // TASK-9493 split-aware
    const cKey = c.customerId || (c.jobId ? jobCustMap[c.jobId] : null);
    if (cKey && custProfitData[cKey]) custProfitData[cKey].directCosts += (c.amount || 0);
  });
  // Labor per customer (clock entry's jobId → customer)
  periodClock.forEach(c => {
    const cKey = c.jobId ? jobCustMap[c.jobId] : null;
    if (cKey && custProfitData[cKey]) {
      custProfitData[cKey].laborCost += _BiLaborRate.jobCostForEntry(c, jobCostRates);
    }
  });

  const custProfitEntries = Object.values(custProfitData)
    .map(cp => ({ ...cp, profit: cp.revenue - cp.directCosts - cp.laborCost, margin: cp.revenue > 0 ? ((cp.revenue - cp.directCosts - cp.laborCost) / cp.revenue * 100) : 0 }))
    .sort((a, b) => b.profit - a.profit);
  const maxCustProfit = custProfitEntries.length > 0 ? Math.max(...custProfitEntries.map(e => Math.abs(e.profit)), 1) : 1;

  if (custProfitEntries.length === 0) return '';

  return `
    <div class="crm-section-card" style="margin-bottom:16px;">
      <div class="crm-section-title"><span class="material-icons" style="color:var(--text-secondary);font-size:20px;">leaderboard</span> Customer Profitability</div>
      ${custProfitEntries.slice(0, 10).map(cp => {
        const profitPct = maxCustProfit > 0 ? (Math.abs(cp.profit) / maxCustProfit * 100) : 0;
        const barColor = cp.profit >= 0 ? '#10B981' : '#EF4444';
        return `<div class="bi-hbar-row">
          <div class="bi-hbar-label">${escBI(cp.name)}</div>
          <div class="bi-hbar-track"><div class="bi-hbar-fill" style="width:${profitPct}%;background:${barColor};"></div></div>
          <div class="bi-hbar-value">${formatCurrency(cp.profit)} <span style="color:var(--text-secondary);font-size:11px;">(${cp.margin.toFixed(0)}%)</span></div>
        </div>`;
      }).join('')}
    </div>`;
}

function renderBillingVelocity() {
  const periodTicketsAll = BiState.tickets.filter(t => isInPeriod(t.date));
  let jobToTicketSum = 0, jobToTicketCount = 0;
  let ticketToInvSum = 0, ticketToInvCount = 0;

  periodTicketsAll.forEach(t => {
    const ticketDate = toDate(t.date);
    // Job completion to ticket creation
    if (t.jobId && ticketDate) {
      const job = BiState.jobs.find(j => j.id === t.jobId);
      const jobEnd = job ? toDate(job.completedAt || job.endDate) : null;
      if (jobEnd && ticketDate >= jobEnd) {
        const days = (ticketDate - jobEnd) / (1000 * 60 * 60 * 24);
        if (days >= 0 && days < 365) { jobToTicketSum += days; jobToTicketCount++; }
      }
    }
    // Ticket creation to invoice
    if (t.invoicedAt && ticketDate) {
      const invDate = toDate(t.invoicedAt);
      if (invDate && invDate >= ticketDate) {
        const days = (invDate - ticketDate) / (1000 * 60 * 60 * 24);
        if (days >= 0 && days < 365) { ticketToInvSum += days; ticketToInvCount++; }
      }
    }
  });

  const avgJobToTicket = jobToTicketCount > 0 ? (jobToTicketSum / jobToTicketCount) : null;
  const avgTicketToInv = ticketToInvCount > 0 ? (ticketToInvSum / ticketToInvCount) : null;

  if (avgJobToTicket === null && avgTicketToInv === null) return '';

  return `
    <div class="crm-section-card" style="margin-bottom:16px;">
      <div class="crm-section-title"><span class="material-icons" style="color:var(--text-secondary);font-size:20px;">speed</span> Billing Velocity</div>
      <div class="crm-stats-grid" style="grid-template-columns:repeat(2,1fr);margin:8px 0 0;">
        ${statCard('assignment_turned_in', 'Job → Ticket', avgJobToTicket !== null ? avgJobToTicket.toFixed(1) + 'd' : '—', avgJobToTicket !== null && avgJobToTicket <= 3 ? 'green' : 'amber')}
        ${statCard('send', 'Ticket → Invoice', avgTicketToInv !== null ? avgTicketToInv.toFixed(1) + 'd' : '—', avgTicketToInv !== null && avgTicketToInv <= 3 ? 'green' : 'amber')}
      </div>
    </div>`;
}

function horizontalBar(label, value, max, color, formatter) {
  // TASK-9367 — a cost bucket can now net NEGATIVE (a refund with no charge in
  // the period). A negative CSS width is dropped, and the fill then renders at
  // FULL width — clamp so it draws empty instead. The value label keeps the sign.
  const pct = max > 0 ? Math.max(0, Math.min(100, value / max * 100)) : 0;
  const displayVal = formatter ? formatter(value) : formatCurrency(value);
  return `
    <div class="bi-hbar-row">
      <div class="bi-hbar-label">${escBI(label)}</div>
      <div class="bi-hbar-track"><div class="bi-hbar-fill" style="width:${pct}%;background:${color};"></div></div>
      <div class="bi-hbar-value">${displayVal}</div>
    </div>
  `;
}
// TASK-9287 sibling — `label` is DATA (maintenance `category`, revenue category,
// month), never markup: every caller passes a plain string. A maintenance doc's
// category is member/driver-writable, so it is escaped here, once, for all four
// callers — same as renderHBars already does. escBI (below) is a TEXT-context
// escaper; that is the only context `label` lands in.

// ============================================
// MAINTENANCE TAB
// ============================================

// TASK-4814 — maintenance STATUS vocabulary.
//
// TITS carries 99 maintenance docs written by two one-shot XLSX importers in
// 2026-01 (scripts/import-meters-tasks.js, scripts/seed-accounts.js) whose
// statuses are CAPITALISED: Closed x84, Done x4, Open x11. The running app only
// ever writes lowercase open | in_progress | pending_review | completed.
//
// The old count was `m.status !== 'completed'` — case-BLIND in the wrong
// direction, so all 88 FINISHED legacy docs read as open. Measured live
// 2026-09-08: the founder dashboard said 129 Open Tasks against 41 genuinely
// unfinished ones. Every other tenant was uninflated (the importers only ever ran
// on TITS), so this looked like real work piling up on the one account Chris
// looks at daily.
//
// Deliberately NOT solved by filtering the maintenance subscription (:1153) to an
// open-status vocabulary. BiState.maintenance also feeds cost-by-category and
// Period Spend (right here), avg-resolution, the P&L maintenance OpEx bucket
// (:3017) and the per-truck/vehicle/asset lifetime cost rollups (:1479, :6328,
// :7383). Every one of those needs the COMPLETED docs. An open-only subscription
// would silently zero maintenance cost across the whole dashboard — a bigger,
// quieter bug than the one being fixed.
//
// Membership is by the CLOSED vocabulary, not the open one: an unrecognised
// status counts as OPEN, which over-reports visibly rather than hiding work.
// (Note the sibling surfaces fail the opposite way and are NOT changed here:
// the daily digest at functions/src/services/notifications.js uses a
// case-sensitive `where status in ['open','in_progress']`, which under-counts;
// the 49 CFR 396.3 audit packet keys work orders off truckId, which these
// assetId-only legacy docs never had.)
const MAINT_CLOSED_STATUSES = new Set(['completed', 'closed', 'done']);
function isMaintComplete(m) {
  return MAINT_CLOSED_STATUSES.has(String(m?.status ?? '').trim().toLowerCase());
}
// TASK-9899 — the SAME twin, on window for maintenance.js (Pitfall #1: no
// cross-import with a ?v= buster). Takes the status VALUE like the server
// helper. Not a third copy: tests/maintenance-status-vocabulary.unit.cjs pins it.
window.MaintStatus = Object.freeze({
  isMaintClosed: (status) => isMaintComplete({ status }),
  isMaintOpen: (status) => !isMaintComplete({ status }),
});

// TASK-4814 — maintenance PRIORITY vocabulary.
//
// maintenance.js writes low | normal | high | critical, and `normal` is the
// DEFAULT for every task created without an explicit pick. The old prioColors map
// carried `medium` — a value nothing writes — and had no `normal`, so on EVERY
// tenant with maintenance the "normal (N)" chip resolved to
// `background:undefined15; color:undefined`. Legacy imported docs carry no
// priority at all, which on TITS rendered a chip reading literally
// "undefined (99)". `medium` is retained for any doc that still carries it.
const MAINT_PRIO_COLORS = {
  critical: '#EF4444', high: '#F97316', normal: '#F59E0B',
  medium: '#F59E0B', low: '#3B82F6', unspecified: '#64748B',
};
const MAINT_PRIO_FALLBACK_COLOR = '#64748B';
const MAINT_PRIO_ORDER = ['critical', 'high', 'normal', 'medium', 'low', 'unspecified'];
// The chip label is now DATA-DERIVED (it used to be one of four hardcoded keys),
// so it is normalised to a safe charset here rather than relying on an escape at
// the interpolation site. A missing/blank priority becomes 'unspecified' — a real
// word with a real colour, never the string "undefined".
function maintPriorityKey(t) {
  const p = String(t?.priority ?? '').trim().toLowerCase().replace(/[^a-z0-9 _-]/g, '').slice(0, 24);
  return p || 'unspecified';
}

function renderMaintenanceTab() {
  const el = document.getElementById('bi-tab-maintenance');
  if (!el) return;

  const periodMaint = BiState.maintenance.filter(m => isInPeriod(m.createdAt));
  const openTasks = BiState.maintenance.filter(m => !isMaintComplete(m));
  const completedInPeriod = periodMaint.filter(m => isMaintComplete(m));

  // Open by priority — buckets are DERIVED from the data (normalised), never a
  // hardcoded seed, so a value the map doesn't know gets a labelled chip and a
  // real colour instead of rendering the string "undefined".
  // Object.create(null), not {} — the keys are now DATA-DERIVED, and the sanitiser
  // charset allows underscores, so a doc with priority "__proto__" would set the
  // prototype instead of creating a bucket (its tasks vanish from the chips) and
  // "constructor" would read a Function, rendering "constructor (NaN)". COD
  // Code-Reviewer lane, TASK-4814.
  const byPriority = Object.create(null);
  openTasks.forEach(t => { const k = maintPriorityKey(t); byPriority[k] = (byPriority[k] || 0) + 1; });
  // Count the tasks, don't re-derive from the buckets: the old sum silently
  // agreed with reality only because the undefined bucket absorbed the overflow.
  const totalOpen = openTasks.length;
  const prioEntries = Object.entries(byPriority)
    .filter(([, v]) => v > 0)
    .sort((a, b) => {
      const ra = MAINT_PRIO_ORDER.indexOf(a[0]);
      const rb = MAINT_PRIO_ORDER.indexOf(b[0]);
      return (ra === -1 ? 98 : ra) - (rb === -1 ? 98 : rb) || a[0].localeCompare(b[0]);
    });

  // Average resolution time
  let totalDays = 0, resolved = 0;
  completedInPeriod.forEach(m => {
    const created = toDate(m.createdAt);
    const completed = toDate(m.completedAt);
    if (created && completed) {
      totalDays += (completed - created) / (1000 * 60 * 60 * 24);
      resolved++;
    }
  });
  const avgResolution = resolved > 0 ? (totalDays / resolved) : 0;

  // Cost by category
  const costByCategory = {};
  periodMaint.forEach(m => {
    const cat = m.category || 'Other';
    costByCategory[cat] = (costByCategory[cat] || 0) + (m.costs?.totalCost || 0);
  });
  const catEntries = Object.entries(costByCategory).sort((a, b) => b[1] - a[1]);
  const maxCat = catEntries.length > 0 ? catEntries[0][1] : 1;

  // Recent DVIR defects
  const defects = BiState.pretrips
    .filter(p => isInPeriod(p.createdAt) && !_isPretripPass(p))
    .sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0))
    .slice(0, 10);

  el.innerHTML = `
    <div class="crm-stats-grid" style="margin-bottom:16px;">
      ${statCard('engineering', 'Open Tasks', totalOpen, totalOpen > 0 ? 'red' : 'green')}
      ${statCard('timer', 'Avg Resolution', avgResolution > 0 ? avgResolution.toFixed(1) + 'd' : '—', 'blue')}
      ${statCard('receipt', 'Period Spend', formatCurrency(catEntries.reduce((s, e) => s + e[1], 0)), 'amber')}
    </div>

    <div class="crm-section-card" style="margin-bottom:16px;">
      <div class="crm-section-title">Open Tasks by Priority</div>
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin:8px 0;">
        ${prioEntries.map(([p, v]) => {
          const c = Object.hasOwn(MAINT_PRIO_COLORS, p) ? MAINT_PRIO_COLORS[p] : MAINT_PRIO_FALLBACK_COLOR;
          return `<span style="display:inline-flex;align-items:center;gap:4px;padding:4px 12px;border-radius:20px;font-size:13px;font-weight:600;background:${c}15;color:${c};">
            <span style="width:8px;height:8px;border-radius:50%;background:${c};"></span> ${escBI(p)} (${v})
          </span>`;
        }).join('')}
      </div>
      ${totalOpen === 0 ? '<p style="color:var(--text-secondary);font-size:13px;">No open tasks</p>' : ''}
    </div>

    ${catEntries.length > 0 ? `
    <div class="crm-section-card" style="margin-bottom:16px;">
      <div class="crm-section-title">Cost by Category</div>
      ${catEntries.map(([cat, cost]) => horizontalBar(cat, cost, maxCat, '#F59E0B')).join('')}
    </div>` : ''}

    <div class="crm-section-card">
      <div class="crm-section-title">Recent DVIR Defects (${defects.length})</div>
      ${defects.length > 0 ? `
      <div style="overflow-x:auto;">
        <table class="bi-table">
          <thead><tr><th>Date</th><th>Vehicle</th><th>Defects</th><th>Safety</th></tr></thead>
          <tbody>
            ${defects.map(d => {
              const date = toDate(d.createdAt);
              const defectItems = d.defects || d.items?.filter(i => i.condition === 'defect') || [];
              const defectText = defectItems.map(i => i.description || i.name || i.item).filter(Boolean).join(', ') || 'Defect noted';
              const hasSafety = defectItems.some(i => i.safety || i.isSafety);
              return `<tr>
                <td>${date ? date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</td>
                <td>${escBI(d.truckUnit || d.unit || '—')}</td>
                <td style="max-width:250px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">${escBI(defectText)}</td>
                <td>${hasSafety ? '<span style="color:#EF4444;font-weight:600;">Yes</span>' : 'No'}</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>` : '<p style="color:var(--text-secondary);font-size:13px;padding:8px 0;">No defects in period</p>'}
    </div>
  `;
}

// ============================================
// FINANCING TAB
// ============================================
function renderFinancingTab() {
  const el = document.getElementById('bi-tab-financing');
  if (!el) return;

  // Gather all loan entities
  const allEquip = [
    ...BiState.trucks.map(t => ({ ...t, _type: 'truck', _label: window.getUnitDisplay ? window.getUnitDisplay(t) : `#${t.unit}` })),
    ...BiState.trailers.map(t => ({ ...t, _type: 'trailer', _label: window.getUnitDisplay ? window.getUnitDisplay(t) : `#${t.unit}` })),
    ...BiState.assets.map(a => ({ ...a, _type: 'asset', _label: a.name || a.id }))
  ];
  const loanEntities = allEquip.filter(e => e.loan?.enabled).map(e => {
    const details = computeLoanDetails(e.loan);
    return { ...e, _details: details };
  });

  // Summary stats
  let totalDebt = 0, totalMonthly = 0, totalInterest = 0;
  loanEntities.forEach(e => {
    if (e._details) {
      totalDebt += e._details.remainingBalance;
      totalMonthly += e._details.monthlyPayment;
      totalInterest += e._details.totalInterest;
    }
  });
  // TASK-362 Slice 1d: skip soft-deleted (archived) LOCs so totals don't include
  // cards the admin has effectively removed (Code Reviewer catch in COD).
  BiState.linesOfCredit.filter(loc => !loc.archived).forEach(loc => { totalDebt += (loc.currentBalance || 0); totalMonthly += (loc.minPayment || 0); });
  BiState.investorNotes.forEach(inv => { if (inv.monthlyPayment) totalMonthly += inv.monthlyPayment; totalDebt += (inv.amount || 0); });

  // Owner's equity from cost entries (gate rejected/pending driver+staff — TASK-564;
  // no-op for draws/contributions which are admin-entered category-keyed, no source)
  const periodCosts = BiState.costEntries.filter(c => isRecognizedCost(c) && isInPeriod(c.date));
  // TASK-9917 — personal-for-owner charges are draws; periodCosts dropped them, so read the period un-gated.
  const periodDraws = BiState.costEntries.filter(c => isInPeriod(c.date) && isOwnerDrawCost(c) && (personalChargeOf(c) || isRecognizedCost(c))).reduce((s, c) => s + (c.amount || 0), 0);
  const periodContribs = periodCosts.filter(c => c.category === 'contributions').reduce((s, c) => s + (c.amount || 0), 0);
  const netEquityChange = periodContribs - periodDraws;

  el.innerHTML = `
    <div style="margin-bottom:16px;">
      <div class="crm-stats-grid">
        ${statCard('account_balance', 'Total Debt', formatCurrency(totalDebt), totalDebt > 0 ? 'amber' : 'green')}
        ${statCard('payments', 'Monthly Payments', formatCurrency(totalMonthly), 'blue')}
        ${statCard('trending_up', 'Total Interest', formatCurrency(totalInterest), 'red')}
      </div>
    </div>

    <!-- Vehicle Loans -->
    <div class="crm-section-card" style="margin-bottom:16px;">
      <div class="crm-section-title" style="display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">local_shipping</span>
        Vehicle Loans (${loanEntities.length})
      </div>
      ${loanEntities.length === 0 ? `
        <div style="text-align:center;padding:24px;color:var(--text-secondary);font-size:14px;">
          No vehicle loans configured. Edit a vehicle's loan info from the Visual tab.
        </div>` : loanEntities.map(e => {
    const d = e._details;
    if (!d) return '';
    const pctPaid = d.termMonths > 0 ? (d.monthsElapsed / d.termMonths * 100) : 0;
    return `
        <div class="bi-financing-card">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px;">
            <div>
              <div style="font-weight:600;font-size:15px;color:var(--text-primary);">${escBI(e._label)}</div>
              ${e.loan.lender ? `<div style="font-size:13px;color:var(--text-secondary);">Lender: ${escBI(e.loan.lender)}</div>` : ''}
            </div>
            <div style="display:flex;gap:4px;">
              <button class="btn btn-sm btn-secondary" onclick="window.BIModule.showLoanStatement('${escJsAttr(e.id)}','${escJsAttr(e._type)}')" style="border-radius:6px;font-size:12px;">
                <span class="material-icons" style="font-size:14px;vertical-align:middle;">description</span> Statement
              </button>
              <button class="btn btn-sm btn-secondary" onclick="window.BIModule.editLoanInfo('${escJsAttr(e.id)}','${escJsAttr(e._type)}')" style="border-radius:6px;font-size:12px;">
                <span class="material-icons" style="font-size:14px;vertical-align:middle;">edit</span> Edit
              </button>
            </div>
          </div>
          <div style="display:flex;gap:16px;flex-wrap:wrap;margin-bottom:10px;font-size:13px;">
            <span style="color:var(--text-secondary);">Remaining: <strong style="color:var(--text-primary);">${formatCurrency(d.remainingBalance)}</strong></span>
            <span style="color:var(--text-secondary);">${d.monthsElapsed} of ${d.termMonths} mo</span>
            <span style="color:var(--text-secondary);">APR: ${d.apr}%</span>
          </div>
          <div class="bi-financing-progress-track">
            <div class="bi-financing-progress-fill" style="width:${Math.min(pctPaid, 100)}%;"></div>
          </div>
          <div style="display:flex;justify-content:space-between;margin-top:6px;font-size:12px;color:var(--text-secondary);">
            <span>${Math.round(pctPaid)}% paid</span>
            <span>Monthly: ${formatCurrency(d.monthlyPayment)} | Payoff: ${d.payoffDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
          </div>
          ${(() => {
    const dep = computeDepreciation(e.loan);
    if (!dep) return '';
    return `<div style="margin-top:10px;padding-top:10px;border-top:1px solid var(--border-light);">
      <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;font-size:12px;font-weight:600;color:#7C3AED;">
        <span class="material-icons" style="font-size:14px;">calculate</span> Depreciation (${e.loan.depMethod === 'macrs5' ? 'MACRS 5-Yr' : e.loan.depMethod === 'macrs7' ? 'MACRS 7-Yr' : 'Straight Line'})
      </div>
      <div style="display:flex;gap:16px;flex-wrap:wrap;font-size:12px;color:var(--text-secondary);">
        <span>Annual: <strong style="color:var(--text-primary);">${formatCurrency(dep.annualDep)}</strong></span>
        <span>Book Value: <strong style="color:var(--text-primary);">${formatCurrency(dep.bookValue)}</strong></span>
        <span>Accum: <strong style="color:var(--text-primary);">${formatCurrency(dep.accumulatedDep)}</strong></span>
      </div>
      ${dep.schedule.length > 0 ? `<details style="margin-top:6px;">
        <summary style="font-size:11px;color:var(--text-secondary);cursor:pointer;">Year-by-year schedule</summary>
        <div style="margin-top:4px;font-size:11px;">
          ${dep.schedule.map(s => `<div style="display:flex;justify-content:space-between;padding:2px 0;"><span>${s.year}</span><span>${s.rate ? (s.rate * 100).toFixed(2) + '%' : ''}</span><span>${formatCurrency(s.depreciation)}</span></div>`).join('')}
        </div>
      </details>` : ''}
    </div>`;
  })()}
        </div>`;
  }).join('')}
    </div>

    <!-- Lines of Credit / Credit Cards -->
    <div class="crm-section-card" style="margin-bottom:16px;">
      <div class="crm-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="font-size:20px;color:#7C3AED;">account_balance_wallet</span>
          Debt Accounts (${BiState.linesOfCredit.filter(l => !l.archived).length})
        </div>
        <button class="btn btn-sm btn-secondary" onclick="window.BIModule.editLineOfCredit()" style="border-radius:6px;font-size:12px;">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;">add</span> Add
        </button>
      </div>
      ${(() => {
    // TASK-362 Slice 1d: filter archived (soft-deleted) cards out of the default
    // Financing list. Card-picker downstream (Slice 2) does the same filter.
    const visibleLOCs = BiState.linesOfCredit.filter(l => !l.archived);
    return visibleLOCs.length === 0 ? `
        <div style="text-align:center;padding:24px;color:var(--text-secondary);font-size:14px;">
          No debt accounts. Track bank lines of credit, vendor accounts, or credit cards.
        </div>` : visibleLOCs.map(loc => {
    const utilPct = loc.creditLimit > 0 ? (loc.currentBalance / loc.creditLimit * 100) : 0;
    const utilColor = utilPct > 75 ? '#EF4444' : utilPct > 50 ? '#F59E0B' : '#10B981';
    const st = loc.subtype || 'bank_loc';
    const subtypeIcon = st === 'credit_card' ? 'credit_card' : st === 'vendor_loc' ? 'storefront' : 'account_balance';
    // TASK-4866 — a credit_card row now says WHICH slot it is for, so the list
    // matches the name the office manager typed on the HR Employment tab.
    const subtypeLabel = st === 'credit_card' ? CARD_TYPES[_cardTypeOf(loc)] : st === 'vendor_loc' ? 'Vendor' : 'Bank LOC';
    const subtypeColor = st === 'credit_card' ? '#7C3AED' : st === 'vendor_loc' ? '#F59E0B' : '#3B82F6';
    // TASK-362 Slice 1c: render `· Exp MM/YYYY` next to ****1234. Color the
    // badge red when <=30d, amber when <=60d. Date math mirrors
    // dispatch.js:_hsDaysUntilExpiry — expiry day = last day of MM (so a card
    // marked 2026-08 expires Aug 31, 2026 23:59:59 local).
    let expBadgeHTML = '';
    if (st === 'credit_card' && loc.cardExpiryDate && /^\d{4}-\d{2}$/.test(loc.cardExpiryDate)) {
      const [yr, mo] = loc.cardExpiryDate.split('-').map(Number);
      const lastDayOfMonth = new Date(yr, mo, 0); // day 0 = last day of prev month relative to next
      lastDayOfMonth.setHours(23, 59, 59, 999);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysUntil = Math.ceil((lastDayOfMonth - today) / (1000 * 60 * 60 * 24));
      const isExpired = daysUntil <= 0;
      const isUrgent = daysUntil <= 30;
      const isWarning = daysUntil <= 60;
      const expColor = isExpired ? '#991B1B' : isUrgent ? '#DC2626' : isWarning ? '#D97706' : '#64748B';
      const expBg = isExpired ? '#FEE2E2' : isUrgent ? '#FEE2E2' : isWarning ? '#FEF3C7' : '#F1F5F9';
      const expWeight = (isExpired || isUrgent || isWarning) ? '700' : '500';
      const mmShort = String(mo).padStart(2, '0');
      const expLabel = isExpired ? `Exp ${mmShort}/${yr} • EXPIRED` : `Exp ${mmShort}/${yr}`;
      expBadgeHTML = `<span style="font-size:10px;padding:2px 6px;border-radius:4px;background:${expBg};color:${expColor};font-weight:${expWeight};margin-left:4px;">${expLabel}</span>`;
    }
    // TASK-9503 — who carries this card. Credit AND fuel cards; a bank LOC or a
    // vendor account is not carried by anybody, so those rows get no line.
    // `null` = the shared core is not on window (should never happen — hr.js is
    // in app.js's static graph) → print nothing rather than a false "Unclaimed".
    let holderHTML = '';
    if (st === 'credit_card') {
      const holders = _biCardHolders(loc);
      if (holders && holders.length) {
        holderHTML = `<div class="bi-card-holders" data-card-id="${escBI(loc.id)}" data-held="1" style="font-size:12px;color:var(--text-secondary);margin-left:22px;margin-top:2px;">Held by: <span style="color:var(--text-primary);font-weight:500;">${holders.map(p => escBI(p.name || p.id)).join(', ')}</span></div>`;
      } else if (holders) {
        holderHTML = `<div class="bi-card-holders" data-card-id="${escBI(loc.id)}" data-held="0" style="margin-left:22px;margin-top:4px;"><span class="bi-card-unclaimed" title="Not assigned to a current employee in HR (Employment tab). A last-4 shared by two cards is never matched — assign by card to be sure." style="display:inline-block;font-size:10px;padding:2px 6px;border-radius:4px;background:#FEF3C7;color:#92400E;font-weight:600;">Unclaimed</span></div>`;
      }
    }
    return `
        <div class="bi-financing-card">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:8px;">
            <div>
              <div style="display:flex;align-items:center;gap:6px;flex-wrap:wrap;">
                <span class="material-icons" style="font-size:16px;color:${subtypeColor};">${subtypeIcon}</span>
                <span style="font-weight:600;font-size:15px;color:var(--text-primary);">${escBI(loc.name || 'Untitled')}</span>
                <span style="font-size:10px;padding:2px 6px;border-radius:4px;background:${subtypeColor}15;color:${subtypeColor};font-weight:600;">${subtypeLabel}${st === 'vendor_loc' && loc.netTerms ? ` Net-${escBI(loc.netTerms)}` : ''}${st === 'credit_card' && loc.lastFourDigits ? ` ****${escBI(loc.lastFourDigits)}` : ''}</span>${expBadgeHTML}
              </div>
              ${loc.lender ? `<div style="font-size:13px;color:var(--text-secondary);margin-left:22px;">${escBI(loc.lender)}</div>` : ''}${holderHTML}
            </div>
            <div style="display:flex;gap:4px;">
              <button class="btn btn-sm btn-secondary" onclick="window.BIModule.editLineOfCredit('${escJsAttr(loc.id)}')" style="border-radius:6px;font-size:12px;">
                <span class="material-icons" style="font-size:14px;vertical-align:middle;">edit</span>
              </button>
              <button class="btn btn-sm btn-secondary" onclick="window.BIModule.deleteLineOfCredit('${escJsAttr(loc.id)}')" style="border-radius:6px;font-size:12px;color:#dc2626;">
                <span class="material-icons" style="font-size:14px;vertical-align:middle;">delete</span>
              </button>
            </div>
          </div>
          <div style="font-size:13px;color:var(--text-secondary);margin-bottom:10px;">
            <strong style="color:var(--text-primary);">${formatCurrency(loc.currentBalance || 0)}</strong> drawn / ${formatCurrency(loc.creditLimit || 0)} limit
          </div>
          <div class="bi-financing-progress-track">
            <div class="bi-financing-progress-fill" style="width:${Math.min(utilPct, 100)}%;background:${utilColor};"></div>
          </div>
          <div style="display:flex;justify-content:space-between;margin-top:6px;font-size:12px;color:var(--text-secondary);">
            <span>${Math.round(utilPct)}% utilized</span>
            <span>APR: ${loc.apr || 0}% | Min: ${formatCurrency(loc.minPayment || 0)}/mo</span>
          </div>
        </div>`;
  }).join('');
  })()}
    </div>

    <!-- Owner's Equity -->
    <div class="crm-section-card" style="margin-bottom:16px;">
      <div class="crm-section-title" style="display:flex;align-items:center;gap:8px;">
        <span class="material-icons" style="font-size:20px;color:#059669;">savings</span>
        Owner's Equity
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;padding:8px 0;">
        <div style="text-align:center;">
          <div style="font-size:12px;color:var(--text-secondary);margin-bottom:4px;">Draws (period)</div>
          <div style="font-size:18px;font-weight:600;color:${periodDraws > 0 ? '#dc2626' : 'var(--text-primary)'};">${periodDraws > 0 ? '(' + formatCurrency(periodDraws) + ')' : '$0'}</div>
        </div>
        <div style="text-align:center;">
          <div style="font-size:12px;color:var(--text-secondary);margin-bottom:4px;">Contributions (period)</div>
          <div style="font-size:18px;font-weight:600;color:${periodContribs > 0 ? '#16A34A' : 'var(--text-primary)'};">${periodContribs > 0 ? formatCurrency(periodContribs) : '$0'}</div>
        </div>
        <div style="text-align:center;">
          <div style="font-size:12px;color:var(--text-secondary);margin-bottom:4px;">Net Equity Change</div>
          <div style="font-size:18px;font-weight:600;color:${netEquityChange >= 0 ? '#16A34A' : '#dc2626'};">${netEquityChange >= 0 ? '+' : ''}${formatCurrency(netEquityChange)}</div>
        </div>
      </div>
      <div style="font-size:12px;color:var(--text-secondary);text-align:center;padding-top:8px;border-top:1px solid var(--border-light);">
        Use "Add Cost" → Owner Draws / Contributions to track equity changes.
      </div>
    </div>

    <!-- Investors / Notes -->
    <div class="crm-section-card" style="margin-bottom:16px;">
      <div class="crm-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="font-size:20px;color:#0EA5E9;">people</span>
          Investors / Notes (${BiState.investorNotes.length})
        </div>
        <button class="btn btn-sm btn-secondary" onclick="window.BIModule.editInvestorNote()" style="border-radius:6px;font-size:12px;">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;">add</span> Add
        </button>
      </div>
      ${BiState.investorNotes.length === 0 ? `
        <div style="text-align:center;padding:24px;color:var(--text-secondary);font-size:14px;">
          No investors or notes payable. Click "Add" to track one.
        </div>` : BiState.investorNotes.map(inv => {
    const typeLabel = inv.type === 'investor' ? 'Investor' : inv.type === 'note' ? 'Note Payable' : 'Other';
    return `
        <div class="bi-financing-card">
          <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:6px;">
            <div>
              <div style="font-weight:600;font-size:15px;color:var(--text-primary);">${escBI(inv.name || 'Untitled')}</div>
              <div style="font-size:12px;color:var(--text-secondary);">${typeLabel}${inv.startDate ? ` | Started: ${escBI(inv.startDate)}` : ''}</div>
            </div>
            <div style="display:flex;gap:4px;">
              <button class="btn btn-sm btn-secondary" onclick="window.BIModule.editInvestorNote('${escJsAttr(inv.id)}')" style="border-radius:6px;font-size:12px;">
                <span class="material-icons" style="font-size:14px;vertical-align:middle;">edit</span>
              </button>
              <button class="btn btn-sm btn-secondary" onclick="window.BIModule.deleteInvestorNote('${escJsAttr(inv.id)}')" style="border-radius:6px;font-size:12px;color:#dc2626;">
                <span class="material-icons" style="font-size:14px;vertical-align:middle;">delete</span>
              </button>
            </div>
          </div>
          <div style="display:flex;gap:16px;font-size:13px;color:var(--text-secondary);">
            <span>Amount: <strong style="color:var(--text-primary);">${formatCurrency(inv.amount || 0)}</strong></span>
            ${inv.monthlyPayment ? `<span>Monthly: ${formatCurrency(inv.monthlyPayment)}</span>` : ''}
          </div>
          ${inv.terms ? `<div style="font-size:12px;color:var(--text-secondary);margin-top:4px;">Terms: ${escBI(inv.terms)}</div>` : ''}
          ${inv.notes ? `<div style="font-size:12px;color:var(--text-secondary);margin-top:2px;font-style:italic;">${escBI(inv.notes)}</div>` : ''}
        </div>`;
  }).join('')}
    </div>

    <!-- 1099 Vendor Summary -->
    ${render1099VendorSection()}
  `;
}

// TASK-10145 — 1099 Paid YTD for a CRM vendor: what was PAID it. A vendor
// statement bill paid the full statement, but its cost mirror books only what
// the linked crew charge receipts did not (chargeNettedCents, server-written),
// so the mirror counts at its gross and the linked receipts not at all (they
// are inside that gross; counting both was the TASK-10141 COD r3 double count).
function _vendorPaidYtd(yearCosts, v) {
  const name = (v.name || '').toLowerCase();
  return yearCosts.filter(c => !c.chargeBillId && (c.vendorId === v.id || (c.description || '').toLowerCase().includes(name)))
    .reduce((s, c) => s + (c.amount || 0) + (c.apInvoiceId && Number(c.chargeNettedCents) > 0 ? Number(c.chargeNettedCents) / 100 : 0), 0);
}
// TASK-10145 — bill id -> vendor id, from the bills' ap_ cost mirrors (a linked
// receipt typed by the crew has no vendor id of its own; its bill's vendor is it).
function _chargeBillVendorMap(costEntries) {
  const m = new Map();
  (costEntries || []).forEach(c => { if (c && c.apInvoiceId && c.vendorId) m.set(c.apInvoiceId, c.vendorId); });
  return m;
}

function render1099VendorSection() {
  const vendors1099 = (BiState.customers || []).filter(c => c.is1099 && (c.type === 'subcontractor' || c.type === 'vendor'));
  if (!vendors1099.length) return '';
  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const yearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59);
  const yearCosts = BiState.costEntries.filter(c => {
    const d = toDate(c.date);
    return d && d >= yearStart && d <= yearEnd;
  });
  return `
    <div class="crm-section-card" style="margin-bottom:16px;">
      <div class="crm-section-title" style="display:flex;align-items:center;justify-content:space-between;">
        <div style="display:flex;align-items:center;gap:8px;">
          <span class="material-icons" style="font-size:20px;color:#DC2626;">receipt</span>
          1099 Vendors (${vendors1099.length})
        </div>
        <button class="btn btn-sm btn-secondary" onclick="window.BIModule.export1099Summary()" style="border-radius:6px;font-size:12px;">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;">download</span> Export 1099
        </button>
      </div>
      <table style="width:100%;border-collapse:collapse;font-size:13px;margin-top:8px;">
        <thead>
          <tr style="border-bottom:2px solid var(--border-light);">
            <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);">Vendor</th>
            <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);">Tax ID</th>
            <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);">Paid YTD</th>
            <th style="text-align:center;padding:6px 8px;color:var(--text-secondary);">Status</th>
          </tr>
        </thead>
        <tbody>
          ${vendors1099.map(v => {
    const paid = _vendorPaidYtd(yearCosts, v);
    // TASK-1619 — v.taxId no longer exists on the broadcast `customers` doc
    // (moved to the admin-only customersSecure sibling); this reads the
    // derived, non-sensitive `taxIdOnFile` boolean crm.js now writes instead.
    // No digit of the tax ID — masked or not — belongs on a doc that's
    // broadcast to every driver-PIN session.
    const taxDisplay = v.taxIdOnFile ? 'On file' : 'Not on file';
    const needs1099 = paid >= 600;
    const approaching = paid >= 400 && paid < 600;
    return `
            <tr style="border-bottom:1px solid var(--border-light);">
              <td style="padding:6px 8px;font-weight:500;">${escBI(v.name || '')}</td>
              <td style="padding:6px 8px;color:var(--text-secondary);font-family:monospace;font-size:12px;">${taxDisplay}</td>
              <td style="text-align:right;padding:6px 8px;font-weight:600;">${formatCurrency(paid)}</td>
              <td style="text-align:center;padding:6px 8px;">
                ${needs1099 ? '<span style="background:#FEE2E2;color:#DC2626;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;">1099 Required</span>'
                : approaching ? '<span style="background:#FEF3C7;color:#D97706;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;">Approaching $600</span>'
                : '<span style="background:#F1F5F9;color:var(--text-secondary);padding:2px 8px;border-radius:10px;font-size:11px;">Under threshold</span>'}
              </td>
            </tr>`;
  }).join('')}
        </tbody>
      </table>
      <div style="font-size:11px;color:var(--text-secondary);margin-top:8px;padding-top:8px;border-top:1px solid var(--border-light);">
        IRS requires Form 1099-NEC for vendors paid $600+ in a calendar year. Mark vendors as "1099" in CRM to track.
      </div>
    </div>`;
}

// ============================================
// FINANCING CRUD — Lines of Credit
// ============================================

// TASK-4866 — the ONE writer of `tenants/{tid}.linesOfCredit`.
//
// WHY A TRANSACTION. This field is a whole ARRAY on a single document, so every
// edit is read-modify-write. Until now there was exactly one editor (this
// modal), so `updateDoc({linesOfCredit: [...BiState.linesOfCredit]})` was
// merely fragile. TASK-4866 adds a second editor in a completely different
// view (hr.js Employment Info, via upsertCreditCardByName below) — an office
// manager naming a fuel card while a bookkeeper updates a balance in BI would,
// with the old code, silently drop whichever write landed first. Neither user
// would see an error; the row would just be gone.
//
// `mutate` receives the CURRENT array straight off the server (never
// BiState, which is a snapshot) and returns the array to write. Returning null
// aborts without a write.
async function _writeLinesOfCredit(tenantId, mutate) {
  const ref = doc(db, 'tenants', tenantId);
  let serverSeen = null;
  const committed = await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const current = (snap.exists() && Array.isArray(snap.data().linesOfCredit))
      ? snap.data().linesOfCredit
      : [];
    // What the SERVER actually holds, captured for the abort path below.
    serverSeen = current;
    const next = mutate(current.map(l => ({ ...l })));
    if (next === null || next === undefined) return null;
    tx.update(ref, { linesOfCredit: next });
    return next;
  });

  // TASK-4866 (COD Code Reviewer 🔴, 2026-09-11) — the cache refresh lives OUT
  // HERE, after `runTransaction` RESOLVES. It used to run inside the callback,
  // which is wrong twice over: Firestore RE-RUNS that callback on contention
  // (so a superseded `next` could be cached), and the callback runs BEFORE the
  // commit (so a transaction that ultimately failed still left a phantom card
  // in the UI). Past this await the write is durable.
  // ABORT PATH (COD Code Reviewer, 2026-09-11 second pass). `mutate` returned
  // null — either "nothing to change" or "that card is gone". Nothing was
  // written, but `serverSeen` is a CONSISTENT read of the server's array taken
  // inside the transaction, and it is newer than whatever the caches hold. Sync
  // to it so the caller's "that card is no longer in the list — refreshing"
  // re-render shows the truth instead of re-painting the same stale list.
  //
  // Both caches, one helper:
  //   • AppState.tenantData.linesOfCredit — the login-time tenant snapshot, what
  //     hr.js and jobboard.js read. Fetched ONCE at login (app.js ~:1847), so
  //     without this a card named on the HR Employment tab would not appear in
  //     its own datalist, or in the driver expense picker, until the next full
  //     page load — which reads as "it didn't save".
  //   • BiState.linesOfCredit — `subscribeToTenantFinancing` keeps this live
  //     ONLY while the BI view is mounted; once it unmounts the unsubscriber
  //     fires and it freezes. `listCreditCards` PREFERS it when non-empty.
  const _sync = (arr) => {
    try {
      if (window.AppState && window.AppState.tenantData) {
        window.AppState.tenantData.linesOfCredit = arr;
      }
      BiState.linesOfCredit = arr.map(loc => ({ ...loc, subtype: loc.subtype || 'bank_loc' }));
    } catch (_) { /* cache refresh is best-effort; the write already landed */ }
  };
  _sync(Array.isArray(committed) ? committed : (Array.isArray(serverSeen) ? serverSeen : []));
  return committed;
}

// TASK-4866 (COD Code Reviewer 🔴, 2026-09-11) — PAN-IN-A-TEXT-FIELD.
// `lastFourDigits` is normalised to four digits everywhere, but `name` and
// `lender` are free text sitting on the tenant ROOT doc, which firestore.rules
// `canReadTenant` makes readable by EVERY driver-PIN session. An office manager
// typing the full number into "Card name" — right next to a box labelled "Last
// 4 digits" — would broadcast a live PAN to the whole yard, and nothing would
// error. Refuse a run of 12+ digits (shortest real PAN is 13; 12 leaves margin).
//
// STRIP SEPARATORS FIRST (COD Adversary 🔴 pass 2, COD Code Reviewer 🔴 pass 4,
// 2026-09-11). This guard was wrong twice before it was right, both times in the
// same way — an ALLOWLIST of separators is unwinnable:
//   v1 `/\d(?:[ -]?\d){11,}/`        — tolerated only space and hyphen, so
//                                      `4111.1111.1111.1111` walked through.
//   v2 `.replace(/[\s\-./\\_,]/g,'')` — enumerated ASCII punctuation, so the
//                                      Unicode ones did: U+2011 non-breaking
//                                      hyphen and U+2013 en-dash (what Word and
//                                      Excel autocorrect a typed hyphen INTO —
//                                      i.e. the likeliest real paste), and
//                                      U+200B zero-width space, which `\s`
//                                      does not match.
// So invert it: keep only letters and digits and drop EVERYTHING else, in any
// script. Nothing is left to enumerate and nothing new can be invented. Letters
// are deliberately kept so they still BREAK a digit run — that is what stops
// "Truck 12 Card 3456 Fuel 7890" from being a false positive. It takes twelve
// digits with nothing but punctuation between them to trip this.
//   v3 `.replace(/[^\p{L}\p{N}]/gu,'')` + `/\d{12,}/` — a CATEGORY MISMATCH:
//                                      the strip kept Unicode digits (`\p{N}`)
//                                      but the test was ASCII-only (`\d` is
//                                      ASCII even under /u), so a PAN typed in
//                                      fullwidth digits (４１１１…, U+FF10-FF19)
//                                      survived both steps untouched.
// Hence all three steps below, in order: NFKC-fold compatibility forms down to
// ASCII, drop every non-alphanumeric, then count digits with `\p{Nd}` so the
// test matches the same character class the strip preserved.
function _looksLikePAN(str) {
  const folded = String(str || '').normalize('NFKC').replace(/[^\p{L}\p{N}]/gu, '');
  return /\p{Nd}{12,}/u.test(folded);
}

// TASK-4866 — CARD TYPE. The registry had no way to say "this one is the fuel
// card". Every entry was `subtype:'credit_card'`, which is the DEBT class, not
// what the card is FOR. HR has always had two distinct slots (Company Credit
// Card / Fuel Card) and 36 real assignments across two tenants live in them, so
// without this field a card named in HR could not round-trip back into the
// right slot. Absent `cardType` reads as 'credit' — every pre-existing entry
// (there are none in production, measured 2026-09-10: 0 rows / 95 tenants) and
// every entry created before this field existed.
const CARD_TYPES = { credit: 'Credit Card', fuel: 'Fuel Card' };
function _cardTypeOf(loc) { return (loc && loc.cardType === 'fuel') ? 'fuel' : 'credit'; }

// TASK-9503 — the CURRENT people who carry this registry card, resolved by the
// shared HR card core (`window.HrCardCore.holdersOf`, defined in hr.js —
// window lookup at call time, never an import: Pitfall #1). Read-only over
// data BI already holds: `BiState.people` (the people snapshot, which drops
// `noLongerEmployed` — so a card held only by someone who left is Unclaimed)
// and `BiState.linesOfCredit` (the registry, archived rows included so the
// core can do its own unique-last-4 check against LIVE rows). Returns `null`
// when the core is unavailable so the renderer prints nothing — an "Unclaimed"
// pill produced by a missing function would be a lie. Sorted by name so the
// line is stable across snapshot order.
function _biCardHolders(loc) {
  try {
    const core = window.HrCardCore;
    if (!core || typeof core.holdersOf !== 'function') return null;
    // People snapshot not in yet, or denied: an empty list is "unknown", not
    // "nobody" — print nothing rather than Unclaimed on every card.
    if (!BiState.dataLoaded || BiState.dataLoaded.people !== true) return null;
    const registry = (BiState.linesOfCredit || []).filter(l => l && l.subtype === 'credit_card');
    const held = core.holdersOf(loc, BiState.people || [], registry);
    return held.slice().sort((a, b) => String(a.name || a.id || '').localeCompare(String(b.name || b.id || '')));
  } catch (e) {
    console.warn('[BI] card holder lookup failed:', e?.message);
    return null;
  }
}

// TASK-9312 — WHICH ROW DID YOU MEAN? Card identity resolution for
// upsertCreditCardByName, extracted as a pure factory so
// tests/task9312-card-upsert-conflict.unit.cjs can drive the REAL rule under
// node:vm instead of a copy of it (the TASK-9292 harness, one surface over).
//
// THE BUG THIS REPLACES, and it fired on LIVE trippin-trucking data on
// 2026-09-22. The resolver used to be one line —
//
//     live.find(l => _cardTypeOf(l) === type && String(l.lastFourDigits||'') === last4)
//
// last-4 + cardType, decided BEFORE the typed name was ever consulted — and a
// hit was rewritten in place with `arr[i] = { ...arr[i], name: clean, ... }`.
// So naming a card on the HR Employment tab against a last-4 that some OTHER
// card already owned silently renamed that other card. Between ~11:30 and
// ~12:30 CT the row `Spark Business ****9015` became a SECOND
// `ASB- Michael Goodman` sitting beside the real `****3905` one; only its
// `lender` ("Capital One", not "American State Bank & Trust") still said what
// the card actually was. No new row, no warning, no audit line. That card id is
// the join key for statement reconciliation and for every
// `costEntries.paymentCardId`, so a wrong label is a money error, not cosmetic.
//
// THE FIX IS NOT TO FLIP THE PRECEDENCE. Matching on NAME first reintroduces the
// mirror bug — two genuinely different physical cards collapsing into one row
// because somebody typed the same label twice — and last-4-first is deliberate
// so that renaming a card you already hold updates the right row. The fix is to
// REFUSE the ambiguous case and say what it collided with, which is exactly the
// stance TASK-9292 (77173a0c) took for statement -> card attribution next door.
//
// "The row you meant" is decided by EVIDENCE, never by guessing. A relabel is
// allowed only when one of these holds:
//   • `cardId` — the FK the caller already holds for this slot (hr.js passes the
//     person's creditCardId / fuelCardId). If the last-4 lands on THAT row, this
//     is the rename the user asked for.
//   • the name is already identical — nothing is being relabelled, so there is
//     no question to ask. This is the routine path: an employment save that did
//     not touch the card re-sends the same name + last-4 and still resolves to a
//     silent no-op.
//   • `confirmRelabel` — the caller showed the collision and the user said yes,
//     passed as the SNAPSHOT of the row they read ({id, name, lastFourDigits,
//     cardType, lender}), so the yes cannot transfer to a row that arrived, or
//     changed, while they were reading.
// Anything else returns `action:'conflict'`, writes NOTHING, and hands back the
// row it would have overwritten so the caller can name it in the UI.
function _cardUpsertCoreFactory() {
  const typeOf = (loc) => (loc && loc.cardType === 'fuel') ? 'fuel' : 'credit';
  const norm = (s) => String(s || '').trim().toLowerCase();
  // Only fields the caller can already read through listCreditCards — this
  // object goes back out to a UI message, so it must not widen exposure.
  const describe = (c) => ({
    id: c.id || null,
    name: String(c.name || ''),
    lender: String(c.lender || ''),
    lastFourDigits: String(c.lastFourDigits || ''),
    cardType: typeOf(c),
  });
  // The registry filter, unchanged from the pre-fix line: live company cards
  // only. An archived card must never block naming a live one (same rule
  // TASK-9292 landed for the reconciliation matcher).
  function liveCards(arr) {
    return (arr || []).filter(l => l && l.subtype === 'credit_card' && !l.archived);
  }
  function settle(hit, clean, want4) {
    // Verbatim from the pre-fix "nothing to change" test, so a routine save
    // still writes nothing at all.
    const unchanged = String(hit.name || '') === clean
      && String(hit.lastFourDigits || '') === String(want4 || '');
    return { action: unchanged ? 'noop' : 'update', card: describe(hit) };
  }
  // `cards` is ALWAYS the full candidate list (TASK-9292's shape), `card` the
  // first of it, so a caller that only ever shows one row still works and one
  // that wants to list them all can.
  const conflict = (code, rows = []) => ({
    action: 'conflict', code,
    card: rows[0] ? describe(rows[0]) : null,
    cards: rows.map(describe),
  });
  function resolveUpsertTarget(arr, { name, last4, cardType, cardId = null, confirmRelabel = null } = {}) {
    const type = cardType === 'fuel' ? 'fuel' : 'credit';
    const clean = String(name || '').trim();
    const want4 = last4 ? String(last4) : null;
    const live = liveCards(arr);

    // TASK-9312 (COD Code Reviewer 🔴 + Edge Case Hunter, pass 2) — CONSENT IS
    // BOUND TO THE ROW THAT WAS SHOWN. A bare "yes" is not transferable: while
    // the dialog sits open another session can archive that card or move those
    // four digits onto a different row, and a blanket "relabel approved" would
    // then rename whatever the resolver picks next — the original bug, laundered
    // through a click. So approval carries the id and EVERY FIELD THE DIALOG
    // DISPLAYED, re-compared on EVERY transaction attempt (this resolver runs
    // inside the mutate callback, which Firestore re-runs on contention). A
    // boolean is deliberately NOT accepted.
    //
    // `cardType` and `lender` are in that comparison for a second reason (COD
    // Code Reviewer 🔴, pass 4): the consent path short-circuits the type-scoped
    // lookup below, so without them a card switched to `fuel` in the AP card
    // manager while the dialog sat open would still match the yes and be
    // silently stamped back to `credit` by the relabel write.
    const okd = (confirmRelabel && typeof confirmRelabel === 'object') ? confirmRelabel : null;
    const approved = (c) => !!(okd && okd.id && c.id === okd.id
      && String(c.name || '') === String(okd.name || '')
      && String(c.lastFourDigits || '') === String(okd.lastFourDigits || '')
      && typeOf(c) === (okd.cardType === 'fuel' ? 'fuel' : 'credit')
      && String(c.lender || '') === String(okd.lender || ''));

    // A YES IS SCOPED TO THAT ROW AND NOTHING ELSE (COD Code Reviewer +
    // Independent 🔴, pass 3 — both found the same hole and both were right).
    // Checking `approved` only at the branch points left two ways for a yes to
    // land somewhere the user never saw: archive the approved row while the
    // dialog is open and resolution fell through to `create`; let a same-last-4
    // twin take the typed name and the same-name shortcut settled the TWIN.
    // Both returned ok:true with a different id, which is the original bug with
    // a click in front of it. So consent short-circuits everything: find the
    // approved row exactly as it was read, or fail closed.
    if (okd) {
      const target = live.find(approved);
      return target ? settle(target, clean, want4) : conflict('relabel-superseded');
    }

    // PRECEDENCE UNCHANGED: a physical card is identified by (cardType, last-4).
    // `filter`, not `find` (COD Code Reviewer 🔴, 2026-09-22): a registry typed in
    // by hand can already hold two rows with the same four digits — that is the
    // exact data TASK-9292 was filed for — and `find` would let a confirmation
    // authorise "whichever one sorts first", which is the same silent guess in a
    // different costume.
    const byLast4 = want4
      ? live.filter(l => typeOf(l) === type && String(l.lastFourDigits || '') === want4)
      : [];
    if (byLast4.length) {
      // The FK the caller already holds names exactly one row, so it resolves
      // even a duplicated last-4.
      const own = cardId ? byLast4.find(c => c.id === cardId) : null;
      if (own) return settle(own, clean, want4);
      // NOTHING IS BEING RELABELLED — check this BEFORE ambiguity (COD
      // Independent 🔴, pass 2). On a tenant that already holds two rows with
      // the same four digits, a routine employment save re-sends an UNCHANGED
      // name + last-4; calling that ambiguous would hand hr.js an {ok:false} on
      // every single save, and the toast it fires ("that needs an admin") names
      // an action no admin can take. Exactly one row already carries this name,
      // so there is no question to ask.
      const sameName = byLast4.filter(c => norm(c.name) === norm(clean));
      if (sameName.length === 1) return settle(sameName[0], clean, want4);
      // Two live cards, four digits, no FK and no approval: there is no answer
      // to confirm, and no dialog is offered for one (`isAskable` refuses it) —
      // a user cannot consent to a row they were never shown. Reported with both
      // candidates, exactly as TASK-9292 reports an ambiguous statement last-4.
      if (byLast4.length > 1) return conflict('last4-ambiguous', byLast4);
      // Past the consent short-circuit above, so this is an unapproved relabel
      // of a single identified row: the askable case.
      return conflict('last4-conflict', [byLast4[0]]);
    }

    const byName = live.filter(l => typeOf(l) === type && norm(l.name) === norm(clean));
    if (byName.length) {
      const own = cardId ? byName.find(c => c.id === cardId) : null;
      const target = own || (byName.length === 1 ? byName[0] : null);
      if (!target) return conflict('name-ambiguous', byName);
      // The mirror of the same harm, one field over. This row already carries a
      // DIFFERENT last-4, so writing the typed one RE-IDENTIFIES a physical card
      // — every statement line and expense that keys off those four digits now
      // points somewhere else, just as silently. Filling in a BLANK last-4 is
      // purely additive and stays silent, which is the common first-time case.
      const has4 = String(target.lastFourDigits || '');
      if (!own && want4 && has4 && has4 !== want4) {
        return conflict('name-conflict', [target]);
      }
      return settle(target, clean, want4);
    }
    return { action: 'create' };
  }
  // A conflict the user can be ASKED about names exactly one row. The ambiguous
  // pair cannot be answered with yes/no, so it is reported, never prompted —
  // TASK-9292's stance verbatim.
  function isAskable(code) { return code === 'last4-conflict' || code === 'name-conflict'; }
  return { liveCards, resolveUpsertTarget, isAskable };
}
const _cardUpsert = _cardUpsertCoreFactory();

// TASK-4866 — called by hr.js (via window.BIModule, NEVER a cross-import with a
// `?v=` buster — pitfall #1) when someone names a card on the Employment tab.
//
// This is the whole point of the task. The registry shipped 2026-03-16 and had
// ZERO rows in ALL 95 tenants on 2026-09-10, while the dumb HR checkboxes it
// was supposed to replace collected 36 assignments — because card data gets
// entered where the person is, not under BI -> Financing -> "Debt Accounts".
// So HR seeds the registry, and the BI list / expense card picker /
// reconciliation attribution light up as a side effect of normal HR work.
//
// Match is by (cardType, lastFourDigits) first — the identity of a physical
// card — then by case-insensitive name. Never creates a duplicate for a card
// that is already registered under a different name.
//
// TASK-9312 — and never RENAMES a card that belongs to somebody else. When the
// typed last-4 is already held by a differently-named row this ASKS, naming that
// row and its lender, and relabels only on a yes; see _cardUpsertCoreFactory
// above for why that is a confirmation rather than a precedence flip.
//
//   • `cardId`  — the FK the caller already holds for this slot. If the last-4
//                 lands on THAT row the rename is proven and nothing is asked.
//                 hr.js computes it as `prior` one line before the call and
//                 should pass it; until it does, the confirm carries the flow.
//   • `confirmRelabel` — `{ id, name, lastFourDigits, cardType, lender }` of the
//                 row the user was SHOWN and approved (i.e. the `conflict` this
//                 function handed back). Not a boolean: consent is bound to that
//                 exact row, every displayed field is re-compared inside every
//                 transaction attempt, and nothing else can be written under it,
//                 so a registry that moved while the dialog was open cannot
//                 inherit the yes (code `relabel-superseded`).
//   • `prompt: false`  — never open a dialog; return the conflict instead. For
//                 callers with their own UI, and for the internal re-run.
//
// Returns { ok, id, created, code, conflict? }. Conflict codes: `last4-conflict`
// / `name-conflict` (one identified row — askable), `last4-ambiguous` /
// `name-ambiguous` (two live rows match; never auto-resolved, never prompted —
// TASK-9292's stance), `relabel-declined` (asked, user said no),
// `relabel-superseded` (said yes, but that row changed or went away first).
// NEVER throws: naming a card is a nicety, and it must never be able to fail a
// payroll-bearing employment save.
async function upsertCreditCardByName(tenantIdRaw, { name, lastFourDigits, cardType, cardId = null, confirmRelabel = null, prompt = true } = {}) {
  const tenantId = tenantIdRaw === 'demo-tenant' ? 'demo' : tenantIdRaw;
  const clean = String(name || '').trim().slice(0, 60);
  if (!tenantId || !clean) return { ok: false, id: null, created: false, code: 'bad-args' };
  // TASK-4866 (COD Code Reviewer, 2026-09-11 second pass) — the HR "Card name"
  // box reaches the tenant ROOT doc through here, so it needs the same PAN
  // refusal the AP surface got. hr.js already treats a {ok:false} from this
  // function as "registry not updated, keep the prior FK" and still saves the
  // typed name on the person doc — so this closes the tenant-root half only.
  // The person-doc half is a separate, pre-existing hole; see the Lane M report.
  if (_looksLikePAN(clean)) return { ok: false, id: null, created: false, code: 'pan-in-text' };
  const type = cardType === 'fuel' ? 'fuel' : 'credit';
  // Last-4 only. A full PAN must never reach this function, and a 16-digit
  // paste would otherwise be stored verbatim on a doc every driver session can
  // read. Keep the last four digits of whatever was typed and nothing else.
  const last4 = String(lastFourDigits || '').replace(/\D/g, '').slice(-4) || null;
  let resultId = null, created = false, conflict = null;
  try {
    await _writeLinesOfCredit(tenantId, (arr) => {
      // RESET per attempt — Firestore RE-RUNS this callback on contention (the
      // same trap saveCard/setCardArchived document). A conflict detected on
      // attempt 1 must not survive into a clean attempt 2, and vice versa.
      conflict = null; created = false; resultId = null;
      const r = _cardUpsert.resolveUpsertTarget(arr, {
        name: clean, last4, cardType: type, cardId, confirmRelabel,
      });
      if (r.action === 'conflict') {
        // Return null: the transaction commits NOTHING. The caller gets the row
        // it would have clobbered and owes the user an explanation.
        // `card` is null for `relabel-superseded` — there is no row to name,
        // which is the whole point of that code.
        conflict = { ...(r.card || {}), code: r.code, cards: r.cards || [] };
        return null;
      }
      if (r.action === 'noop') {
        // Already exactly right -> write nothing, but still hand back the id so
        // the caller's FK stays linked.
        resultId = r.card.id;
        return null;
      }
      if (r.action === 'update') {
        resultId = r.card.id;
        const i = arr.findIndex(l => l && l.id === r.card.id);
        // Unreachable — resolveUpsertTarget read THIS array — but a blind
        // arr[-1] write would corrupt the whole field, so refuse instead.
        if (i < 0) return null;
        arr[i] = { ...arr[i], name: clean, cardType: type, ...(last4 ? { lastFourDigits: last4 } : {}) };
        return arr;
      }
      resultId = crypto.randomUUID();
      created = true;
      arr.push({
        id: resultId, name: clean, subtype: 'credit_card', cardType: type,
        lender: '', creditLimit: 0, currentBalance: 0, apr: 0, minPayment: 0,
        notes: 'Added from HR → Employment Info', lastFourDigits: last4,
        statementDate: null, cardExpiryDate: null,
      });
      return arr;
    });
    if (conflict) {
      // ASK — do not just refuse (COD Blast-Radius / Code Reviewer / Independent
      // all 🔴, 2026-09-22, and they were right). The ONLY caller, hr.js
      // `saveEmploymentFields`, hands a {ok:false} to a toast that says the write
      // "needs an admin", which is false for a collision and unfixable by the
      // person reading it. A refusal nobody can act on is the silent failure
      // again with a return code stapled to it. So the question is asked HERE,
      // where the colliding row is known, and a "yes" re-runs the write with the
      // relabel authorised — which keeps the ROUTINE case (renaming the card you
      // already hold, last-4 unchanged) working with one extra tap instead of
      // failing closed.
      //
      // showConfirm() sets title/message with textContent, so the card name and
      // lender go in RAW — an HTML escaper here would render literal &#39;s.
      // The ambiguous codes are never prompted: a user cannot consent to "rename
      // whichever of these two sorts first".
      const askable = !confirmRelabel && prompt && _cardUpsert.isAskable(conflict.code)
        && typeof window !== 'undefined' && typeof window.showConfirm === 'function';
      if (askable) {
        const who = conflict.lender ? `${conflict.name} (${conflict.lender})` : conflict.name;
        const ask = conflict.code === 'last4-conflict'
          ? {
            title: `Rename "${conflict.name}"?`,
            message: `The card ending ${last4} is already in your company card list as "${who}". `
              + `Saving this name RENAMES that same card everywhere — including statement matching `
              + `and any expenses already assigned to it. If this is a different card, cancel and `
              + `check the last 4 digits.`,
            confirmText: 'Rename that card',
          }
          : {
            title: `Change the digits on "${conflict.name}"?`,
            message: `"${who}" is currently saved as ending ${conflict.lastFourDigits}. `
              + `Saving ${last4} re-points that same card, so statement lines matching `
              + `${conflict.lastFourDigits} will stop matching it. If this is a different card, `
              + `cancel and give it its own name.`,
            confirmText: 'Change the digits',
          };
        let said = false;
        try {
          said = await window.showConfirm(ask.title, ask.message, {
            confirmText: ask.confirmText, cancelText: 'Cancel', icon: 'credit_card',
          });
        } catch (_) { said = false; } // a broken dialog must never auto-approve
        if (said) {
          // Consent carries the ROW THE USER READ, not a blanket yes — see
          // `approved()` in the factory. If the registry moved under the dialog
          // the re-run comes back as a conflict again rather than renaming
          // whatever now holds those digits, and the caller is told the list
          // changed instead of being told an admin is needed.
          const again = await upsertCreditCardByName(tenantIdRaw, {
            name, lastFourDigits, cardType, cardId, prompt: false,
            confirmRelabel: {
              id: conflict.id, name: conflict.name, lastFourDigits: conflict.lastFourDigits,
              cardType: conflict.cardType, lender: conflict.lender,
            },
          });
          if (!again.ok && again.conflict) return { ...again, code: 'relabel-superseded' };
          return again;
        }
        return { ok: false, id: null, created: false, code: 'relabel-declined', conflict };
      }
      return { ok: false, id: null, created: false, code: conflict.code, conflict };
    }
    return { ok: true, id: resultId, created, code: null };
  } catch (e) {
    console.warn('[BI] upsertCreditCardByName failed:', e?.code || e?.message || e);
    return { ok: false, id: null, created: false, code: (e && e.code) || 'unknown' };
  }
}

// Read-only accessor for other views (hr.js) that must not import this module.
// Prefers the live BI state when the BI view is open, else the tenant snapshot
// app.js caches at login — the SAME source jobboard.js:10456 already reads.
// TASK-4866 — `includeArchived` is for the AP card manager, which has to show
// retired cards so they can be un-retired (and so a bookkeeper can see WHY a
// card vanished). Every pre-existing caller passes no argument and keeps the
// old active-only behaviour verbatim. The extra fields are additive: hr.js
// reads id/name/lastFourDigits/cardType and ignores the rest.
function listCreditCards({ includeArchived = false } = {}) {
  const arr = (BiState.linesOfCredit && BiState.linesOfCredit.length)
    ? BiState.linesOfCredit
    : (window.AppState?.tenantData?.linesOfCredit || []);
  return (Array.isArray(arr) ? arr : [])
    .filter(l => l && l.subtype === 'credit_card' && (includeArchived || !l.archived))
    .map(l => ({
      id: l.id, name: l.name || '', lastFourDigits: l.lastFourDigits || '',
      cardType: _cardTypeOf(l), archived: !!l.archived,
      lender: l.lender || '', statementDate: l.statementDate || null,
      cardExpiryDate: l.cardExpiryDate || null,
    }));
}

// TASK-4866 — the AP card manager's writer. AP must NEVER open its own
// `updateDoc` on `tenants/{tid}.linesOfCredit`: there are now three editors of
// that one array (BI Financing, HR Employment Info, AP Cards) and a
// snapshot-based whole-array write from any of them silently drops the other
// two's rows with no error anywhere. Everything funnels through
// `_writeLinesOfCredit`'s transaction.
//
// This deliberately writes ONLY the card-shaped fields. A card added in AP is
// not a debt account — creditLimit/currentBalance/apr/minPayment are seeded to
// 0 on create and are LEFT UNTOUCHED on edit, so an office manager naming a
// card in AP can never zero out a balance a bookkeeper entered in BI.
//
// Returns { ok, id, created, code }. NEVER throws.
async function saveCard(tenantIdRaw, payload = {}) {
  const tenantId = tenantIdRaw === 'demo-tenant' ? 'demo' : tenantIdRaw;
  const name = String(payload.name || '').trim().slice(0, 60);
  if (!tenantId || !name) return { ok: false, id: null, created: false, code: 'bad-args' };
  const lenderRaw = String(payload.lender || '').trim().slice(0, 60);
  // Refuse, never silently scrub — a scrubbed name would save "looking wrong"
  // and the person would type it again. See _looksLikePAN above.
  if (_looksLikePAN(name) || _looksLikePAN(lenderRaw)) {
    return { ok: false, id: null, created: false, code: 'pan-in-text' };
  }
  const type = payload.cardType === 'fuel' ? 'fuel' : 'credit';
  // Last-4 ONLY. The tenant ROOT doc is readable by every driver-PIN session
  // (firestore.rules canReadTenant), so a pasted 16-digit PAN must not survive
  // this line. Same normalisation as saveLineOfCredit / upsertCreditCardByName.
  const last4 = String(payload.lastFourDigits || '').replace(/\D/g, '').slice(-4) || null;
  const stmtRaw = parseInt(payload.statementDate, 10);
  const stmt = (Number.isFinite(stmtRaw) && stmtRaw >= 1 && stmtRaw <= 31) ? stmtRaw : null;
  const expiry = /^\d{4}-\d{2}$/.test(String(payload.cardExpiryDate || '')) ? payload.cardExpiryDate : null;
  const lender = lenderRaw;
  const existingId = payload.id || null;
  let resultId = existingId, created = false;
  try {
    let missing = false;
    await _writeLinesOfCredit(tenantId, (arr) => {
      // RESET per attempt — Firestore re-runs this callback on contention.
      missing = false;
      const idx = existingId ? arr.findIndex(l => l && l.id === existingId) : -1;
      // TASK-4866 (COD Code Reviewer, 2026-09-11 third pass) — an EDIT whose id
      // is gone must NOT fall through to the create branch. It would mint a
      // fresh UUID, and every `costEntries.paymentCardId` pointing at the old id
      // would be orphaned — a silent break of the expense→card attribution this
      // whole feature exists to feed. Refuse and let the caller re-render.
      if (existingId && idx < 0) { missing = true; return null; }
      if (idx >= 0) {
        const prev = arr[idx];
        // Changing the expiry clears the cron's dedup stamp so the new date can
        // fire fresh alerts — same rule saveLineOfCredit follows.
        const expiryChanged = (prev.cardExpiryDate || null) !== expiry;
        arr[idx] = {
          ...prev, name, cardType: type, lender,
          lastFourDigits: last4, statementDate: stmt, cardExpiryDate: expiry,
          ...(expiryChanged ? { expiryNotified: null } : {}),
        };
        return arr;
      }
      resultId = crypto.randomUUID();
      created = true;
      arr.push({
        id: resultId, name, subtype: 'credit_card', cardType: type, lender,
        creditLimit: 0, currentBalance: 0, apr: 0, minPayment: 0,
        notes: 'Added from Accounts Payable → Cards',
        lastFourDigits: last4, statementDate: stmt, cardExpiryDate: expiry,
      });
      return arr;
    });
    if (missing) return { ok: false, id: null, created: false, code: 'not-found' };
    return { ok: true, id: resultId, created, code: null };
  } catch (e) {
    console.warn('[BI] saveCard failed:', e?.code || e?.message || e);
    return { ok: false, id: null, created: false, code: (e && e.code) || 'unknown' };
  }
}

// TASK-4866 — retire / un-retire a card. SOFT only, never a splice:
// `costEntries.paymentCardId` is a foreign key into this array, so removing a
// row would orphan every historical expense attributed to that card. Matches
// deleteLineOfCredit's soft-delete contract.
async function setCardArchived(tenantIdRaw, id, archived) {
  const tenantId = tenantIdRaw === 'demo-tenant' ? 'demo' : tenantIdRaw;
  if (!tenantId || !id) return { ok: false, code: 'bad-args' };
  try {
    let found = false;
    await _writeLinesOfCredit(tenantId, (arr) => {
      // RESET per attempt (COD Code Reviewer, 2026-09-11). Firestore RE-RUNS
      // this callback on contention. Without the reset, an attempt that found
      // the card followed by a retry where another session had just removed it
      // would leave `found` true from the first pass and report success for a
      // write that never happened.
      found = false;
      const idx = arr.findIndex(l => l && l.id === id);
      if (idx < 0) return null; // nothing to do → transaction writes nothing
      found = true;
      arr[idx] = archived
        ? { ...arr[idx], archived: true, archivedAt: Timestamp.now() }
        : { ...arr[idx], archived: false, archivedAt: null };
      return arr;
    });
    // TASK-4866 (COD Code Reviewer) — a no-op is not a success. Without this the
    // caller toasts "Card retired" for a card another session had already
    // removed, and the list silently disagrees with the message.
    if (!found) return { ok: false, code: 'not-found' };
    return { ok: true, code: null };
  } catch (e) {
    console.warn('[BI] setCardArchived failed:', e?.code || e?.message || e);
    return { ok: false, code: (e && e.code) || 'unknown' };
  }
}

function editLineOfCredit(id) {
  const existing = id ? BiState.linesOfCredit.find(l => l.id === id) : null;
  const loc = existing || { id: '', name: '', lender: '', creditLimit: 0, currentBalance: 0, apr: 0, minPayment: 0, notes: '', subtype: 'bank_loc' };
  const st = loc.subtype || 'bank_loc';

  const prev = document.getElementById('bi-loc-modal');
  if (prev) prev.remove();

  const modal = document.createElement('div');
  modal.id = 'bi-loc-modal';
  modal.className = 'modal';
  modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;">
      <div class="modal-header">
        <h3 style="margin:0;">${existing ? 'Edit' : 'Add'} Debt Account</h3>
        <button class="btn-icon" onclick="document.getElementById('bi-loc-modal').remove()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="padding:20px;max-height:70vh;overflow-y:auto;">
        <div class="form-group">
          <label>Type *</label>
          <select id="loc-subtype" class="form-control" onchange="window.BIModule._onLocSubtypeChange()">
            <option value="bank_loc" ${st === 'bank_loc' ? 'selected' : ''}>Bank Line of Credit</option>
            <option value="vendor_loc" ${st === 'vendor_loc' ? 'selected' : ''}>Vendor Account (Net Terms)</option>
            <option value="credit_card" ${st === 'credit_card' ? 'selected' : ''}>Credit Card</option>
          </select>
        </div>
        <div class="form-group">
          <label>Name *</label>
          <input type="text" id="loc-name" class="form-control" value="${escBI(loc.name)}" placeholder="${st === 'credit_card' ? 'Chase Visa' : st === 'vendor_loc' ? 'Cat Financial' : 'Operating LOC'}">
        </div>
        <div class="form-group">
          <label>${st === 'credit_card' ? 'Issuer' : st === 'vendor_loc' ? 'Vendor' : 'Lender'}</label>
          <input type="text" id="loc-lender" class="form-control" value="${escBI(loc.lender)}" placeholder="${st === 'credit_card' ? 'Chase' : st === 'vendor_loc' ? 'Caterpillar' : 'Wells Fargo'}">
        </div>
        <div id="loc-vendor-fields" style="display:${st === 'vendor_loc' ? 'block' : 'none'};">
          <div class="form-group">
            <label>Net Terms (days)</label>
            <select id="loc-net-terms" class="form-control">
              <option value="">-- Select --</option>
              <option value="30" ${loc.netTerms == 30 ? 'selected' : ''}>Net 30</option>
              <option value="60" ${loc.netTerms == 60 ? 'selected' : ''}>Net 60</option>
              <option value="90" ${loc.netTerms == 90 ? 'selected' : ''}>Net 90</option>
            </select>
          </div>
        </div>
        <div id="loc-card-fields" style="display:${st === 'credit_card' ? 'grid' : 'none'};grid-template-columns:1fr 1fr;gap:12px;">
          ${/* TASK-4866 — "Used for" is what lets a named card round-trip into the
               right HR slot. HR has always had two card slots (Company Credit Card
               / Fuel Card); the registry had only the DEBT class `subtype`, so
               nothing here could say which slot a card belongs to. Absent = credit. */''}
          <div class="form-group">
            <label>Used for</label>
            <select id="loc-card-type" class="form-control">
              <option value="credit" ${(loc.cardType || 'credit') !== 'fuel' ? 'selected' : ''}>Company Credit Card</option>
              <option value="fuel" ${loc.cardType === 'fuel' ? 'selected' : ''}>Fuel Card</option>
            </select>
          </div>
          <div class="form-group">
            <label>Last 4 Digits</label>
            <input type="text" id="loc-last-four" class="form-control" maxlength="4" value="${escBI(loc.lastFourDigits || '')}" placeholder="1234">
          </div>
          <div class="form-group">
            <label>Statement Day</label>
            <input type="number" id="loc-stmt-day" class="form-control" min="1" max="31" value="${escBI(loc.statementDate || '')}">
          </div>
          <div class="form-group">
            <label>Card Expires</label>
            <input type="month" id="loc-card-expiry" class="form-control" value="${escBI(loc.cardExpiryDate || '')}">
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group">
            <label>Credit Limit ($)</label>
            <input type="number" id="loc-limit" class="form-control" step="0.01" min="0" value="${escBI(loc.creditLimit || '')}">
          </div>
          <div class="form-group">
            <label>Current Balance ($)</label>
            <input type="number" id="loc-balance" class="form-control" step="0.01" min="0" value="${escBI(loc.currentBalance || '')}">
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group">
            <label>APR (%)</label>
            <input type="number" id="loc-apr" class="form-control" step="0.01" min="0" value="${escBI(loc.apr || '')}">
          </div>
          <div class="form-group">
            <label>Min Payment ($/mo)</label>
            <input type="number" id="loc-min" class="form-control" step="0.01" min="0" value="${escBI(loc.minPayment || '')}">
          </div>
        </div>
        <div class="form-group">
          <label>Notes</label>
          <input type="text" id="loc-notes" class="form-control" value="${escBI(loc.notes || '')}" placeholder="Optional notes">
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);display:flex;gap:12px;justify-content:flex-end;">
        <button class="btn btn-secondary" onclick="document.getElementById('bi-loc-modal').remove()">Cancel</button>
        <button class="btn btn-primary" onclick="window.BIModule.saveLineOfCredit('${escJsAttr(id || '')}')">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">save</span> Save
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

function _onLocSubtypeChange() {
  const st = document.getElementById('loc-subtype')?.value || 'bank_loc';
  const vendorFields = document.getElementById('loc-vendor-fields');
  const cardFields = document.getElementById('loc-card-fields');
  if (vendorFields) vendorFields.style.display = st === 'vendor_loc' ? 'block' : 'none';
  if (cardFields) cardFields.style.display = st === 'credit_card' ? 'grid' : 'none';
}

async function saveLineOfCredit(existingId) {
  const name = document.getElementById('loc-name').value.trim();
  if (!name) { window.showToast('Name is required', 'error'); return; }

  const subtype = document.getElementById('loc-subtype')?.value || 'bank_loc';
  // TASK-362 Slice 1: cardExpiryDate (HTML5 month input — 'YYYY-MM' string).
  const newCardExpiryDate = subtype === 'credit_card'
    ? (document.getElementById('loc-card-expiry')?.value?.trim() || null)
    : null;

  // Preserve fields we don't directly edit but must not blow away (archived flag
  // from soft-delete; expiryNotified dedup stamps written by the daily cron;
  // archivedAt timestamp). When the admin changes the expiry date, clear stamps
  // so the new threshold can fire fresh alerts (Slice 1d behavior).
  const existing = existingId ? BiState.linesOfCredit.find(l => l.id === existingId) : null;
  const expiryChanged = !!existing && existing.subtype === 'credit_card'
    && (existing.cardExpiryDate || null) !== newCardExpiryDate;

  const entry = {
    id: existingId || crypto.randomUUID(),
    name,
    subtype,
    lender: document.getElementById('loc-lender').value.trim(),
    creditLimit: parseFloat(document.getElementById('loc-limit').value) || 0,
    currentBalance: parseFloat(document.getElementById('loc-balance').value) || 0,
    apr: parseFloat(document.getElementById('loc-apr').value) || 0,
    minPayment: parseFloat(document.getElementById('loc-min').value) || 0,
    notes: document.getElementById('loc-notes').value.trim(),
    ...(subtype === 'vendor_loc' ? { netTerms: parseInt(document.getElementById('loc-net-terms')?.value) || null } : {}),
    ...(subtype === 'credit_card' ? {
      // TASK-4866 — last-4 ONLY. A pasted full PAN would otherwise be stored
      // verbatim on the tenant root, which firestore.rules `canReadTenant`
      // makes readable by every driver-PIN session. Strip to digits, keep four.
      lastFourDigits: (String(document.getElementById('loc-last-four')?.value || '').replace(/\D/g, '').slice(-4) || null),
      cardType: document.getElementById('loc-card-type')?.value === 'fuel' ? 'fuel' : 'credit',
      statementDate: parseInt(document.getElementById('loc-stmt-day')?.value) || null,
      cardExpiryDate: newCardExpiryDate,
    } : {}),
    // Preserve archived flags + dedup stamps unless expiry changed (then clear stamps so
    // the new expiry date gets fresh threshold alerts from the daily cron).
    ...(existing?.archived ? { archived: true, archivedAt: existing.archivedAt || null } : {}),
    ...(existing?.expiryNotified && !expiryChanged ? { expiryNotified: existing.expiryNotified } : {}),
  };

  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;

  try {
    // TASK-4866 — transaction, not `updateDoc([...BiState.linesOfCredit])`.
    // hr.js now writes this same array; a snapshot-based whole-array write
    // would silently drop the other editor's row with no error anywhere.
    await _writeLinesOfCredit(tenant, (arr) => {
      const idx = arr.findIndex(l => l && l.id === entry.id);
      if (idx >= 0) arr[idx] = { ...arr[idx], ...entry }; else arr.push(entry);
      return arr;
    });
    window.showToast('Line of credit saved', 'success');
    const m = document.getElementById('bi-loc-modal');
    if (m) m.remove();
  } catch (e) {
    console.error('[BI] Save LOC error:', e);
    window.showToast('Error saving: ' + e.message, 'error');
  }
}

async function deleteLineOfCredit(id) {
  if (!confirm('Delete this line of credit?')) return;
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  // TASK-362 Slice 1d: soft-delete. Stamps `archived:true` + `archivedAt`
  // instead of removing from array. The expense-modal card picker filters
  // `!archived`, the BI Financing list filters archived out of the default
  // view, and the daily expiry cron skips archived cards. Audit trail
  // (costEntries.paymentCardId FK) survives soft-delete intact.
  try {
    // TASK-4866 — same transaction as the save path, same reason: a second
    // writer (hr.js) means a snapshot-based whole-array write can clobber.
    await _writeLinesOfCredit(tenant, (arr) => arr.map(l =>
      (l && l.id === id) ? { ...l, archived: true, archivedAt: Timestamp.now() } : l
    ));
    window.showToast('Line of credit deleted', 'success');
  } catch (e) {
    console.error('[BI] Delete LOC error:', e);
    window.showToast('Error deleting: ' + e.message, 'error');
  }
}

// ============================================
// FINANCING CRUD — Investors / Notes
// ============================================
function editInvestorNote(id) {
  const existing = id ? BiState.investorNotes.find(n => n.id === id) : null;
  const inv = existing || { id: '', name: '', type: 'note', amount: 0, terms: '', monthlyPayment: 0, startDate: '', notes: '' };

  const prev = document.getElementById('bi-inv-modal');
  if (prev) prev.remove();

  const modal = document.createElement('div');
  modal.id = 'bi-inv-modal';
  modal.className = 'modal';
  modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;">
      <div class="modal-header">
        <h3 style="margin:0;">${existing ? 'Edit' : 'Add'} Investor / Note</h3>
        <button class="btn-icon" onclick="document.getElementById('bi-inv-modal').remove()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="padding:20px;max-height:70vh;overflow-y:auto;">
        <div class="form-group">
          <label>Name *</label>
          <input type="text" id="inv-name" class="form-control" value="${escBI(inv.name)}" placeholder="Uncle Bob, SBA Loan, etc.">
        </div>
        <div class="form-group">
          <label>Type</label>
          <select id="inv-type" class="form-control">
            <option value="investor" ${inv.type === 'investor' ? 'selected' : ''}>Investor</option>
            <option value="note" ${inv.type === 'note' ? 'selected' : ''}>Note Payable</option>
            <option value="other" ${inv.type === 'other' ? 'selected' : ''}>Other</option>
          </select>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group">
            <label>Amount ($)</label>
            <input type="number" id="inv-amount" class="form-control" step="0.01" min="0" value="${escBI(inv.amount || '')}">
          </div>
          <div class="form-group">
            <label>Monthly Payment ($)</label>
            <input type="number" id="inv-monthly" class="form-control" step="0.01" min="0" value="${escBI(inv.monthlyPayment || '')}">
          </div>
        </div>
        <div class="form-group">
          <label>Terms</label>
          <input type="text" id="inv-terms" class="form-control" value="${escBI(inv.terms || '')}" placeholder="10% equity, 5yr @ 6%, etc.">
        </div>
        <div class="form-group">
          <label>Start Date</label>
          <input type="date" id="inv-start" class="form-control" value="${escBI(inv.startDate || '')}">
        </div>
        <div class="form-group">
          <label>Notes</label>
          <input type="text" id="inv-notes" class="form-control" value="${escBI(inv.notes || '')}" placeholder="Optional notes">
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);display:flex;gap:12px;justify-content:flex-end;">
        <button class="btn btn-secondary" onclick="document.getElementById('bi-inv-modal').remove()">Cancel</button>
        <button class="btn btn-primary" onclick="window.BIModule.saveInvestorNote('${escJsAttr(id || '')}')">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">save</span> Save
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

async function saveInvestorNote(existingId) {
  const name = document.getElementById('inv-name').value.trim();
  if (!name) { window.showToast('Name is required', 'error'); return; }

  const entry = {
    id: existingId || crypto.randomUUID(),
    name,
    type: document.getElementById('inv-type').value,
    amount: parseFloat(document.getElementById('inv-amount').value) || 0,
    monthlyPayment: parseFloat(document.getElementById('inv-monthly').value) || 0,
    terms: document.getElementById('inv-terms').value.trim(),
    startDate: document.getElementById('inv-start').value,
    notes: document.getElementById('inv-notes').value.trim()
  };

  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const arr = [...BiState.investorNotes];
  const idx = arr.findIndex(n => n.id === entry.id);
  if (idx >= 0) arr[idx] = entry; else arr.push(entry);

  try {
    await updateDoc(doc(db, 'tenants', tenant), { investorNotes: arr });
    window.showToast('Investor/note saved', 'success');
    const m = document.getElementById('bi-inv-modal');
    if (m) m.remove();
  } catch (e) {
    console.error('[BI] Save investor note error:', e);
    window.showToast('Error saving: ' + e.message, 'error');
  }
}

async function deleteInvestorNote(id) {
  if (!confirm('Delete this investor/note?')) return;
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const arr = BiState.investorNotes.filter(n => n.id !== id);
  try {
    await updateDoc(doc(db, 'tenants', tenant), { investorNotes: arr });
    window.showToast('Investor/note deleted', 'success');
  } catch (e) {
    console.error('[BI] Delete investor note error:', e);
    window.showToast('Error deleting: ' + e.message, 'error');
  }
}

// ============================================
// DRIVERS TAB
// ============================================
function renderDriversTab() {
  const el = document.getElementById('bi-tab-drivers');
  if (!el) return;

  const periodDays = getPeriodDays();
  const periodPretrips = BiState.pretrips.filter(p => isInPeriod(p.createdAt));
  const periodClock = BiState.clockEntries.filter(e => isInPeriod(e.clockIn));
  const periodJobDays = BiState.jobDays.filter(jd => isInPeriod(jd.date));
  const periodRevenue = rentalAwareRevenueEntries(isInPeriod); // TASK-842-C

  // Build per-driver scorecard
  const driverData = BiState.people.map(person => {
    const name = person.name || 'Unknown';
    const role = (person.crewType || person.role || person.roles?.[0] || '').toLowerCase();

    // DVIR completion
    const driverDvirs = periodPretrips.filter(p => p.driverId === person.id);
    const dvirCount = driverDvirs.length;
    const dvirPassCount = driverDvirs.filter(p => _isPretripPass(p)).length;
    const dvirPassRate = dvirCount > 0 ? (dvirPassCount / dvirCount * 100) : null;
    const defectsReported = driverDvirs.filter(p => p.status === 'defects' || (p.result && p.result !== 'pass' && p.result !== 'Pass')).length;

    // Clock hours
    const driverClock = periodClock.filter(c => c.personId === person.id);
    let totalHours = 0, otHours = 0;
    // Group by week for OT calc
    const weeklyHours = {};
    driverClock.forEach(c => {
      const hrs = c.totalHours || c.hours || 0;
      totalHours += hrs;
      const d = toDate(c.clockIn);
      const wk = d ? _biOtWeekKey(d) : null;
      if (wk) weeklyHours[wk] = (weeklyHours[wk] || 0) + hrs;
    });
    const _otTh = _biOtThreshold();
    Object.values(weeklyHours).forEach(wh => {
      if (wh > _otTh) otHours += (wh - _otTh);
    });

    // Days clocked in
    const clockDays = new Set();
    driverClock.forEach(c => {
      const d = toDate(c.clockIn);
      if (d) clockDays.add(d.toISOString().slice(0, 10));
    });

    // Revenue attribution (from jobs this person clocked into)
    const driverJobIds = new Set(driverClock.filter(c => c.jobId).map(c => c.jobId));
    let revenueAttr = 0;
    periodRevenue.forEach(r => {
      if (r.jobId && driverJobIds.has(r.jobId)) {
        // Split revenue evenly among workers on that job
        const workersOnJob = periodClock.filter(c => c.jobId === r.jobId).map(c => c.personId);
        const uniqueWorkers = new Set(workersOnJob);
        if (uniqueWorkers.has(person.id) && uniqueWorkers.size > 0) {
          revenueAttr += (r.amount || 0) / uniqueWorkers.size;
        }
      }
    });

    // Jobs worked
    const jobsWorked = driverJobIds.size;

    return { person, name, role, dvirCount, dvirPassRate, defectsReported, totalHours, otHours, clockDays: clockDays.size, revenueAttr, jobsWorked };
  });

  // Filter to people with any activity (clock entries, DVIRs, or assigned)
  const activeDrivers = driverData.filter(d => d.totalHours > 0 || d.dvirCount > 0 || d.jobsWorked > 0);
  activeDrivers.sort((a, b) => b.revenueAttr - a.revenueAttr);

  // Fleet totals
  const totalFleetHours = activeDrivers.reduce((s, d) => s + d.totalHours, 0);
  const totalFleetOT = activeDrivers.reduce((s, d) => s + d.otHours, 0);
  const totalFleetRevenue = activeDrivers.reduce((s, d) => s + d.revenueAttr, 0);

  el.innerHTML = `
    <div class="crm-stats-grid" style="margin-bottom:16px;">
      ${statCard('groups', 'Active Crew', activeDrivers.length, 'blue')}
      ${statCard('schedule', 'Total Hours', Math.round(totalFleetHours).toLocaleString(), 'purple')}
      ${statCard('more_time', 'OT Hours', Math.round(totalFleetOT).toLocaleString(), totalFleetOT > 0 ? 'amber' : 'green')}
      ${statCard('attach_money', 'Revenue Attributed', formatCurrency(totalFleetRevenue), 'green')}
    </div>

    <div style="font-size:13px;color:var(--text-secondary);margin-bottom:12px;">${activeDrivers.length} crew with activity in period &middot; ${periodDays} days</div>

    <div class="bi-vehicle-grid">
      ${activeDrivers.map(d => driverScorecard(d)).join('')}
    </div>

    ${activeDrivers.length === 0 ? `
    <div class="crm-section-card" style="text-align:center;padding:40px 20px;">
      <span class="material-icons" style="font-size:48px;color:var(--text-secondary);">badge</span>
      <div style="font-size:16px;font-weight:600;color:var(--text-primary);margin:12px 0 4px;">No driver activity in period</div>
      <div style="font-size:14px;color:var(--text-secondary);">Clock entries and DVIR inspections will appear here.</div>
    </div>` : ''}
  `;
}

function driverScorecard(d) {
  const initials = getInitialsBI(d.name);
  const color = avatarColor(d.name);
  const roleBadge = d.role ? `<span class="bi-type-badge">${escBI(d.role)}</span>` : '';
  const revenuePerHour = d.totalHours > 0 ? d.revenueAttr / d.totalHours : 0;

  return `
    <div class="bi-vehicle-card" style="border-left-color:${color};cursor:pointer;" onclick="window.BIModule.showPersonAnalytics('${escJsAttr(d.person.id)}')">
      <div style="display:flex;justify-content:space-between;align-items:start;margin-bottom:8px;">
        <div style="display:flex;align-items:center;gap:8px;">
          <div style="width:32px;height:32px;border-radius:50%;background:${color};color:#fff;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;">${escBI(initials)}</div>
          <div>
            <div style="font-weight:600;font-size:14px;color:var(--text-primary);">${escBI(d.name)}</div>
            ${roleBadge}
          </div>
        </div>
        <div style="text-align:right;font-size:18px;font-weight:700;color:#10B981;">${formatCurrency(d.revenueAttr)}</div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;font-size:12px;margin-bottom:8px;">
        <div><div style="color:var(--text-secondary);">Hours</div><div style="font-weight:600;color:var(--text-primary);">${d.totalHours.toFixed(1)}</div></div>
        <div><div style="color:var(--text-secondary);">OT Hours</div><div style="font-weight:600;color:${d.otHours > 0 ? '#F59E0B' : 'var(--text-primary)'};">${d.otHours.toFixed(1)}</div></div>
        <div><div style="color:var(--text-secondary);">$/Hour</div><div style="font-weight:600;color:var(--text-primary);">${revenuePerHour > 0 ? '$' + revenuePerHour.toFixed(0) : '—'}</div></div>
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;font-size:12px;">
        <div><div style="color:var(--text-secondary);">Jobs</div><div style="font-weight:600;color:var(--text-primary);">${d.jobsWorked}</div></div>
        <div><div style="color:var(--text-secondary);">Days Worked</div><div style="font-weight:600;color:var(--text-primary);">${d.clockDays}</div></div>
        <div><div style="color:var(--text-secondary);">DVIRs</div><div style="font-weight:600;color:var(--text-primary);">${d.dvirCount}</div></div>
      </div>
    </div>
  `;
}

// ============================================
// PROFITABILITY TAB
// ============================================
// P&L sub-view state
let _plView = 'statement';
let _plDrillBy = 'customer';
let _plSortCol = 'profit';
let _plSortDir = 'desc';

const CATEGORY_LABELS = { labor: 'Labor', fuel: 'Fuel', tolls: 'Tolls', lodging: 'Lodging', materials: 'Materials', parts: 'Parts', supplies: 'Supplies / PPE', permits: 'Permits', maintenance: 'Maintenance', shop: 'Shop', insurance: 'Insurance', other: 'Other', subcontractor: 'Subcontractor', per_diem: 'Per Diem', interest: 'Interest Expense', taxes: 'Taxes & Fees', bank_fees: 'Bank & Processing Fees', dot_compliance: 'DOT / Compliance', benefits: 'Benefits & Insurance', penalties: 'Fines & Penalties', fines: 'Fines & Citations', other_income: 'Other Income', draws: 'Owner Draws', contributions: 'Owner Contributions', equipment_purchase: 'Equipment Purchase', loan_payment: 'Loan Payment' };
const CATEGORY_COLORS = { labor: '#3B82F6', fuel: '#F59E0B', tolls: '#8B5CF6', lodging: '#06B6D4', materials: '#10B981', parts: '#0D9488', supplies: '#65A30D', permits: '#EC4899', maintenance: '#EF4444', shop: '#F97316', insurance: '#6366F1', other: '#64748B', subcontractor: '#14B8A6', per_diem: '#0EA5E9', interest: '#DC2626', taxes: '#7C3AED', bank_fees: '#A855F7', dot_compliance: '#059669', benefits: '#2563EB', penalties: '#E11D48', fines: '#DC2626', other_income: '#16A34A', draws: '#92400E', contributions: '#047857' };

function getAccountingBasis() {
  return window.AppState?.settings?.accountingBasis || 'cash';
}

function computePLData(range) { // TASK-9701 S2: optional { inPeriod, start, end } (the print's prior-period column)
  // TASK-9701 S1 — the statement math lives in ONE place:
  // functions/src/shared/pnlStatement.js (browser mirror window.PnlStatement,
  // GENERATED by scripts/build-shared.cjs). This adapter only hands it BiState.
  // The connector P&L runs the same function server-side. Source rules (Chris
  // 2026-10-25): cash basis = the revenue report's paid_date rule; loans =
  // interest only, principal as a cash note; a QuickBooks company's statement
  // stands on QuickBooks figures — BI does not load those yet (`books: null`),
  // so it stays a DispatchDeck estimate and says so (pl.estimate / pl.label).
  const basis = getAccountingBasis();
  const jobCostRates = _biJobCostRates(BiState.people); // burdened; salary allocated to job-clocked hours
  const allEquip = [...BiState.trucks.map(t => ({...t, _type: 'truck'})), ...BiState.trailers.map(t => ({...t, _type: 'trailer'})), ...BiState.assets.map(a => ({...a, _type: 'asset'}))];
  // TASK-10172-A — a unit deactivated (sold) during or after this period still
  // belongs to it: its loan interest (clipped at its end date by the statement)
  // and its per-vehicle revenue stay. pl.allEquip stays the ACTIVE fleet, so the
  // forecast / break-even never project a sold truck forward.
  const ended = BiState.endedUnits || {};
  const endedHere = (list, type) => (list || []).filter(u => window.PnlStatement.pnlUnitInPeriod(u, range ? range.start : BiState.periodStart)).map(u => ({...u, _type: type}));
  const periodEquip = [...allEquip, ...endedHere(ended.trucks, 'truck'), ...endedHere(ended.trailers, 'trailer'), ...endedHere(ended.assets, 'asset')];
  const pl = window.PnlStatement.computePnlStatement({
    basis,
    inPeriod: range ? range.inPeriod : isInPeriod,
    periodStart: range ? range.start : BiState.periodStart,
    periodEnd: range ? range.end : BiState.periodEnd,
    revenueEntries: BiState.revenueEntries,
    selectAccrualRevenue: rentalAwareRevenueEntries, // TASK-842-C rental slices
    costEntries: BiState.costEntries,
    clockEntries: BiState.clockEntries,
    maintenance: BiState.maintenance,
    equipment: periodEquip,
    isRecognizedCost,
    personalChargeOf,
    personalOwedOutstanding,
    laborCostForEntry: c => _BiLaborRate.jobCostForEntry(c, jobCostRates),
    loanDetails: computeLoanDetails,
    quickBooksConnected: _biQbConnection() === 'yes',
    books: null,
  });
  pl.jobCostRates = jobCostRates;
  pl.allEquip = allEquip;
  pl.periodEquip = periodEquip;
  return pl;
}

// Bucket data into time periods for sparklines/trends
function bucketByMonth(entries, dateFn) {
  const buckets = {};
  entries.forEach(e => {
    const d = dateFn(e);
    if (!d) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    buckets[key] = (buckets[key] || 0) + (e._amount || e.amount || 0);
  });
  // Return sorted array of values
  const keys = Object.keys(buckets).sort();
  return { keys, values: keys.map(k => buckets[k]) };
}

function renderSparkline(values, color = '#3B82F6') {
  if (!values || values.length < 2) return '';
  const w = 52, h = 18;
  const max = Math.max(...values, 0.01), min = Math.min(...values, 0);
  const range = max - min || 1;
  const pts = values.map((v, i) => `${(i / (values.length - 1)) * w},${h - 2 - ((v - min) / range) * (h - 4)}`).join(' ');
  return `<svg width="${w}" height="${h}" style="vertical-align:middle;margin-left:6px;"><polyline points="${pts}" fill="none" stroke="${color}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
}

function fmtAcct(val) {
  if (val == null || val === 0) return '$0';
  const abs = Math.abs(val);
  const str = '$' + abs.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return val < 0 ? `(${str})` : str;
}

function renderProfitabilityTab() {
  const el = document.getElementById('bi-tab-profitability');
  if (!el) return;
  // TASK-9701 S1 — the statement module loads lazily; every other P&L entry point
  // (sub-views, balance sheet, exports) is reached from this rendered tab.
  if (!window.PnlStatement) {
    el.innerHTML = '<div class="loading-state"><div class="loading-spinner"></div><p>Loading P&amp;L...</p></div>';
    _pnlStatementReady.then((ok) => {
      if (!el.isConnected) return;
      if (ok && window.PnlStatement) renderProfitabilityTab();
      else el.textContent = 'The P&L could not be loaded — refresh and try again.';
    });
    return;
  }

  const pl = computePLData();
  const profitColor = pl.netIncome >= 0 ? 'green' : 'red';

  // Previous period P&L for trends
  const prevRevEntries = rentalAwareRevenueEntries(isInPreviousPeriod); // TASK-842-C
  const prevTotalRevenue = prevRevEntries.reduce((s, r) => s + (r.amount || 0), 0);
  const prevCostEntries = BiState.costEntries.filter(c => isRecognizedCost(c) && isInPreviousPeriod(c.date));
  const prevClockEntries = BiState.clockEntries.filter(c => isInPreviousPeriod(c.clockIn));
  const prevJobCostRates = _biJobCostRates(BiState.people); // same basis as the current-period P&L
  let prevLaborCost = 0;
  prevClockEntries.forEach(c => { prevLaborCost += _BiLaborRate.jobCostForEntry(c, prevJobCostRates); });
  let prevDirectCosts = 0;
  prevCostEntries.forEach(c => {
    const cat = c.category || 'other';
    if (['fuel', 'tolls', 'lodging', 'materials', 'subcontractor'].includes(cat)) prevDirectCosts += (c.amount || 0);
  });
  const prevTotalCOGS = prevLaborCost + prevDirectCosts;
  const prevGrossProfit = prevTotalRevenue - prevTotalCOGS;
  const prevGrossMargin = prevTotalRevenue > 0 ? (prevGrossProfit / prevTotalRevenue * 100) : 0;

  el.innerHTML = `
    <div class="crm-stats-grid" style="grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));">
      ${statCard('payments', 'Revenue', formatCurrency(pl.totalRevenue), 'green', null, trendBadge(pl.totalRevenue, prevTotalRevenue))}
      ${statCard('inventory_2', 'COGS', formatCurrency(pl.totalCOGS), 'amber', null, trendBadge(pl.totalCOGS, prevTotalCOGS, true))}
      ${statCard('trending_up', 'Gross Profit', formatCurrency(pl.grossProfit), pl.grossProfit >= 0 ? 'green' : 'red', null, trendBadge(pl.grossProfit, prevGrossProfit))}
      ${statCard('account_balance_wallet', 'OpEx', formatCurrency(pl.totalOpex), 'purple')}
      ${statCard('savings', 'Net Income', formatCurrency(pl.netIncome), profitColor)}
      ${statCard('percent', 'Net Margin', formatPct(pl.netMargin), profitColor, null, trendBadge(pl.netMargin, prevGrossMargin))}
      ${window.AppState?.tenantData?.plaidConnected ? `<div id="bi-bank-balance-card" class="crm-stat-card" style="cursor:pointer;" onclick="window.BIModule.refreshBankBalance()">
        <div class="stat-icon" style="background:rgba(16,185,129,0.1);"><span class="material-icons" style="color:#10B981;">account_balance</span></div>
        <div class="stat-label">Bank Balance</div>
        <div class="stat-value" id="bi-bank-balance-val" style="font-size:0.9rem;">Loading...</div>
      </div>` : ''}
    </div>

    <!-- Sub-navigation -->
    <div class="pl-sub-nav">
      <button class="pl-sub-pill ${_plView === 'statement' ? 'active' : ''}" onclick="window.BIModule.switchPLView('statement')">
        <span class="material-icons">description</span> P&L Statement
      </button>
      <button class="pl-sub-pill ${_plView === 'drilldown' ? 'active' : ''}" onclick="window.BIModule.switchPLView('drilldown')">
        <span class="material-icons">pivot_table_chart</span> Drill-Down
      </button>
      <button class="pl-sub-pill ${_plView === 'charges' ? 'active' : ''}" onclick="window.BIModule.switchPLView('charges')">
        <span class="material-icons">receipt_long</span> Charges
      </button>
      <button class="pl-sub-pill ${_plView === 'rankings' ? 'active' : ''}" onclick="window.BIModule.switchPLView('rankings')">
        <span class="material-icons">emoji_events</span> Rankings
      </button>
      <button class="pl-sub-pill ${_plView === 'forecast' ? 'active' : ''}" onclick="window.BIModule.switchPLView('forecast')">
        <span class="material-icons">trending_up</span> Forecast
      </button>
      <button class="pl-sub-pill ${_plView === 'balancesheet' ? 'active' : ''}" onclick="window.BIModule.switchPLView('balancesheet')">
        <span class="material-icons">account_balance</span> Balance Sheet
      </button>
    </div>

    <!-- Action bar -->
    <div style="display:flex;justify-content:flex-end;gap:8px;margin:0 0 8px;flex-wrap:wrap;">
      <div style="position:relative;display:inline-block;" id="bi-export-dropdown">
        <button class="btn btn-secondary btn-sm" onclick="document.getElementById('bi-export-menu').classList.toggle('hidden')" style="border-radius:8px;">
          <span class="material-icons" style="font-size:18px;vertical-align:middle;">download</span> Export
        </button>
        <div id="bi-export-menu" class="hidden" style="position:absolute;right:0;top:100%;margin-top:4px;background:#fff;border:1px solid var(--border-light);border-radius:8px;box-shadow:var(--shadow-lg);z-index:100;min-width:220px;">
          <button class="bi-export-item" onclick="window.BIModule.exportPLStatement()">
            <span class="material-icons">description</span> P&L Statement
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportIncomeStatement()">
            <span class="material-icons">trending_up</span> Income Statement
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportExpenseReport()">
            <span class="material-icons">trending_down</span> Expense Report
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportGeneralLedger()">
            <span class="material-icons">menu_book</span> General Ledger
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportARAgingReport()">
            <span class="material-icons">schedule</span> AR Aging Report
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportAPAgingReport()">
            <span class="material-icons">hourglass_bottom</span> AP Aging Report
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportPayrollJournalEntries()">
            <span class="material-icons">receipt_long</span> Payroll Journal (QBO)
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportBankFeedCSV()">
            <span class="material-icons">account_balance</span> Bank Feed (QBO)
          </button>
          <div style="border-top:1px solid var(--border-light);margin:4px 0;"></div>
          <button class="bi-export-item" onclick="window.BIModule.export1099Summary()">
            <span class="material-icons">receipt_long</span> 1099 Summary
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportContractorPaymentSummary()">
            <span class="material-icons">payments</span> Contractor Payments
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportChartOfAccounts()">
            <span class="material-icons">list_alt</span> Chart of Accounts
          </button>
          <button class="bi-export-item" onclick="window.BIModule.exportDepreciationSchedule()">
            <span class="material-icons">trending_down</span> Depreciation Schedule
          </button>
        </div>
      </div>
      <button class="btn btn-secondary btn-sm" onclick="window.BIModule.showFuelCardImport()" style="border-radius:8px;">
        <span class="material-icons" style="font-size:18px;vertical-align:middle;">local_gas_station</span> Fuel Card
      </button>
      <button class="btn btn-primary btn-sm" onclick="window.BIModule.showAddCostModal()" style="border-radius:8px;">
        <span class="material-icons" style="font-size:18px;vertical-align:middle;">add</span> Add Cost
      </button>
    </div>

    <div id="pl-content-area"></div>
  `;

  renderPLSubView(pl);
}

function renderPLSubView(pl) {
  if (!pl && !window.PnlStatement) return; // TASK-9701 S1 — the P&L tab re-renders once the statement module lands
  if (!pl) pl = computePLData();
  const area = document.getElementById('pl-content-area');
  if (!area) return;

  if (_plView === 'statement') renderPLStatement(area, pl);
  else if (_plView === 'drilldown') renderPLDrilldown(area, pl);
  else if (_plView === 'charges') renderPLCharges(area); // TASK-9917 s4
  else if (_plView === 'rankings') renderPLRankings(area, pl);
  else if (_plView === 'forecast') renderPLForecast(area, pl);
  else if (_plView === 'balancesheet') renderBalanceSheet(area, pl);
}

function switchPLView(view) {
  _plView = view;
  document.querySelectorAll('.pl-sub-pill').forEach(b => b.classList.toggle('active', b.textContent.toLowerCase().includes(
    view === 'statement' ? 'p&l' : view === 'drilldown' ? 'drill' : view === 'charges' ? 'charges' : view === 'rankings' ? 'rank' : view === 'balancesheet' ? 'balance' : 'forecast'
  )));
  renderPLSubView();
}

// ============================================
// TASK-9917 slice 4 (a) — P&L → CHARGES. The pane (list, search, Personal
// button + its refusal predicates) lives in the LAZY views/biPlCharges.js
// (integ/2100, perf budget appColdLoadGzip). bi.js keeps the action
// (markCostPersonal → the shared personalRepay dialog + callable) and hands
// the module its helpers (pitfall #1: deps passed, not imported).
// ============================================
let _plChargesMod = null;
let _plChargesLoad = null;
function _plChargesDeps() { return { BiState, personalChargeOf, personalOwedOutstanding, isRecognizedCost, isInPeriod, toDate, CATEGORY_LABELS, escBI, escJsAttr }; }
function _loadPlCharges() {
  if (_plChargesMod) return Promise.resolve(_plChargesMod);
  if (!_plChargesLoad) _plChargesLoad = __ddChunkWrap(import('./biPlCharges.js?v=9917405')).then((m) => (_plChargesMod = m)).catch((e) => { _plChargesLoad = null; throw e; });
  return _plChargesLoad;
}
function renderPLCharges(area) {
  if (!area) return;
  if (_plChargesMod) { _plChargesMod.renderPLCharges(area, _plChargesDeps()); return; }
  area.innerHTML = '<div class="pl-section"><div class="pl-section-header">Charges in this period</div><p style="margin:6px 0 10px;font-size:0.85rem;color:var(--text-secondary);">Loading…</p></div>';
  _loadPlCharges().then(() => {
    if (_plView === 'charges' && area.isConnected) renderPLCharges(area);
  }).catch((err) => {
    console.error('[BI] biPlCharges load', err);
    if (area.isConnected) area.innerHTML = '<div class="pl-section"><div class="pl-section-header">Charges in this period</div><p style="margin:6px 0 10px;font-size:0.85rem;color:var(--text-secondary);">The list could not be loaded — refresh and try again.</p></div>';
  });
}
function setPLChargesQuery(q) {
  if (_plChargesMod) _plChargesMod.setPLChargesQuery(q, _plChargesDeps());
}
async function markCostPersonal(costEntryId) {
  const c = BiState.costEntries.find(x => x.id === costEntryId);
  if (!c) return;
  const expectedStatus = typeof c.status === 'string' ? c.status : undefined;
  try {
    const CH = await _loadPlCharges();
    if (!CH.canMarkPersonal(c, _plChargesDeps())) return;
    const PR = await _loadPersonalRepay();
    await PR.markPersonal(c, {
      tenantId: BiState.tenantId,
      people: (window.AppState?.people || BiState.people || []),
      isOwner: CH.isOwnerLogin(),
      expectedStatus,
      amountOf: (e) => Number(e?.amount) || 0,
      escapeHtml: escBI,
      toast: (m, k) => window.showToast?.(m, k),
      callFn: (name, payload) => httpsCallable(functions, name)(payload),
      decisionToast: (res, okText, okKind) => {
        const d = res?.data || {};
        if (d.applied === false && d.message) window.showToast?.(d.message, 'info');
        else window.showToast?.(okText, okKind);
      },
      decisionErrToast: (err, failLead) => {
        const refusal = err?.code === 'functions/permission-denied' || err?.code === 'permission-denied' || err?.details?.alreadyDecided;
        window.showToast?.(refusal && err?.message ? err.message : failLead + (err?.message || 'try again'), refusal ? 'warning' : 'error');
      },
    });
  } catch (err) {
    console.error('[BI] personalRepay load', err);
    window.showToast?.('Could not open the Personal form — refresh and try again.', 'error');
  }
}

// ============================================
// DEPRECIATION CALCULATOR
// ============================================
const MACRS_RATES = {
  5: [0.2000, 0.3200, 0.1920, 0.1152, 0.1152, 0.0576],
  7: [0.1429, 0.2449, 0.1749, 0.1249, 0.0893, 0.0892, 0.0893, 0.0446]
};

function computeDepreciation(loan) {
  if (!loan || !loan.depMethod || loan.depMethod === 'none') return null;
  const price = loan.purchasePrice || 0;
  const purchaseDate = loan.purchaseDate ? new Date(loan.purchaseDate) : null;
  if (!price || !purchaseDate) return null;

  const now = new Date();
  const yearsElapsed = now.getFullYear() - purchaseDate.getFullYear() + (now >= new Date(purchaseDate.getFullYear() + (now.getFullYear() - purchaseDate.getFullYear()), purchaseDate.getMonth(), purchaseDate.getDate()) ? 0 : -1);
  const fullYears = Math.max(0, yearsElapsed + 1); // inclusive of partial first year
  const schedule = [];
  let accumulatedDep = 0;
  let annualDep = 0;

  if (loan.depMethod === 'macrs5' || loan.depMethod === 'macrs7') {
    const rates = loan.depMethod === 'macrs5' ? MACRS_RATES[5] : MACRS_RATES[7];
    for (let i = 0; i < rates.length; i++) {
      const dep = price * rates[i];
      const year = purchaseDate.getFullYear() + i;
      schedule.push({ year, rate: rates[i], depreciation: dep });
      if (i < fullYears) accumulatedDep += dep;
      if (i === Math.min(fullYears - 1, rates.length - 1)) annualDep = dep;
    }
  } else if (loan.depMethod === 'straight') {
    const useful = loan.usefulLife || 5;
    const salvage = loan.salvageValue || 0;
    annualDep = (price - salvage) / useful;
    for (let i = 0; i < useful; i++) {
      const year = purchaseDate.getFullYear() + i;
      schedule.push({ year, rate: null, depreciation: annualDep });
      if (i < fullYears) accumulatedDep += annualDep;
    }
  }
  accumulatedDep = Math.min(accumulatedDep, price);
  const bookValue = Math.max(0, price - accumulatedDep);
  return { annualDep, accumulatedDep, bookValue, schedule, purchasePrice: price };
}

// ============================================
// BALANCE SHEET VIEW
// ============================================
function renderBalanceSheet(area, pl) {
  if (!pl) pl = computePLData();
  const basis = getAccountingBasis();
  const bsConfig = BiState.balanceSheetConfig || {};

  // CURRENT ASSETS
  let accountsReceivable = 0;
  if (basis !== 'cash') {
    const outstanding = BiState.tickets.filter(t =>
      t.status === 'draft' || t.status === 'ready' || (_biTicketInvoiced(t) && !t.paid && !t.writtenOff && t.status !== 'deleted')
    );
    accountsReceivable = outstanding.reduce((s, t) => s + (t.grandTotal || 0), 0);
  }
  const cashBalance = BiState.bankAccounts.length > 0
    ? BiState.bankAccounts.reduce((s, a) => s + (a.balance || 0), 0)
    : (BiState.balanceSheetCash || 0);
  const prepaidExpenses = bsConfig.prepaidExpenses || 0;
  const otherCurrentAssets = bsConfig.otherCurrentAssets || 0;
  // TASK-9917 — personal charges employees still owe back: a receivable.
  const employeeOwed = BiState.costEntries.reduce((s, c) => s + personalOwedOutstanding(c), 0);
  const totalCurrentAssets = cashBalance + accountsReceivable + prepaidExpenses + otherCurrentAssets + employeeOwed;

  // FIXED ASSETS
  let vehicleGross = 0, accumulatedDep = 0;
  const allEquip = [
    ...BiState.trucks.map(t => ({...t, _type: 'truck'})),
    ...BiState.trailers.map(t => ({...t, _type: 'trailer'})),
    ...BiState.assets.map(a => ({...a, _type: 'asset'}))
  ];
  allEquip.forEach(e => {
    if (e.loan?.enabled) {
      const price = e.loan.purchasePrice || 0;
      vehicleGross += price;
      const dep = computeDepreciation(e.loan);
      if (dep) accumulatedDep += dep.accumulatedDep;
    }
  });
  const netFixedAssets = vehicleGross - accumulatedDep;
  const totalAssets = totalCurrentAssets + netFixedAssets;

  // CURRENT LIABILITIES
  const accountsPayable = bsConfig.accountsPayable || 0;
  // Accrued wages: clock entries EXPLICITLY marked unpaid × comp-resolved rate.
  // 🔴 Not `!c.paid` — nothing writes `paid` onto a clockEntry (0 of 1,847 on
  // TITS carry the key), so the old form counted every shift ever worked as
  // owed. With a real rate that would have booked $317K of already-paid 2026
  // payroll as a current liability. See jcLaborRateCoreFactory (functions/src/shared/jobCost.js).
  // 🔴 WAGE map on purpose: this is money OWED to the worker. Burden is
  // employer-side cost and allocated salary is not a payment — neither
  // belongs in a wage liability. See jcLaborRateCoreFactory (functions/src/shared/jobCost.js).
  const personRateMap = _biRateMap(BiState.people);
  let accruedWages = 0;
  BiState.clockEntries.forEach(c => {
    if (_BiLaborRate.isUnpaidClockEntry(c)) accruedWages += (c.totalHours || c.hours || 0) * (personRateMap[c.personId] || 0);
  });
  // Current portion of LTD: next 12 months of loan payments
  let currentPortionLTD = 0;
  allEquip.forEach(e => {
    if (e.loan?.enabled && e.loan.monthlyPayment > 0) {
      const details = computeLoanDetails(e.loan);
      if (details && details.monthsRemaining > 0) {
        currentPortionLTD += Math.min(12, details.monthsRemaining) * e.loan.monthlyPayment;
      }
    }
  });
  const otherCurrentLiabilities = bsConfig.otherCurrentLiabilities || 0;
  // TASK-362 Slice 1d: Balance Sheet liabilities must also exclude soft-deleted
  // (archived) LOCs so totals match the Financing tab "Total Debt" stat. Without
  // this filter, deleted cards would inflate the long-term liability and 1099
  // reconciliation views (Code Reviewer ⚠️ in pre-deploy COD V2).
  const _activeLOCs = BiState.linesOfCredit.filter(l => !l.archived);
  const locBankBalance = _activeLOCs.filter(l => (l.subtype || 'bank_loc') === 'bank_loc').reduce((s, l) => s + (l.currentBalance || 0), 0);
  const locVendorBalance = _activeLOCs.filter(l => l.subtype === 'vendor_loc').reduce((s, l) => s + (l.currentBalance || 0), 0);
  const locCardBalance = _activeLOCs.filter(l => l.subtype === 'credit_card').reduce((s, l) => s + (l.currentBalance || 0), 0);
  const totalCurrentLiabilities = accountsPayable + accruedWages + currentPortionLTD + otherCurrentLiabilities + locVendorBalance + locCardBalance;

  // LONG-TERM LIABILITIES
  let vehicleLoansLT = 0;
  allEquip.forEach(e => {
    if (e.loan?.enabled) {
      const details = computeLoanDetails(e.loan);
      if (details) {
        const ltPortion = details.remainingBalance - (Math.min(12, details.monthsRemaining) * e.loan.monthlyPayment);
        vehicleLoansLT += Math.max(0, ltPortion);
      }
    }
  });
  const investorNotes = BiState.investorNotes.filter(n => n.type === 'note').reduce((s, n) => s + (n.amount || 0), 0);
  const totalLTLiabilities = vehicleLoansLT + locBankBalance + investorNotes;
  const totalLiabilities = totalCurrentLiabilities + totalLTLiabilities;

  // EQUITY
  const allCosts = BiState.costEntries;
  const allRevenue = BiState.revenueEntries;
  const openingEquity = bsConfig.openingEquity || 0;
  const totalContribs = allCosts.filter(c => c.category === 'contributions').reduce((s, c) => s + (c.amount || 0), 0);
  const totalDraws = allCosts.filter(isOwnerDrawCost).reduce((s, c) => s + (c.amount || 0), 0); // TASK-9917
  const totalRev = allRevenue.reduce((s, r) => s + (r.amount || 0), 0);
  const totalExp = allCosts.filter(c => !personalChargeOf(c) && !['draws', 'contributions', 'other_income'].includes(c.category)).reduce((s, c) => s + (c.amount || 0), 0);
  const retainedEarnings = totalRev - totalExp;
  const totalEquity = openingEquity + totalContribs + retainedEarnings - totalDraws;

  const difference = totalAssets - totalLiabilities - totalEquity;
  const isBalanced = Math.abs(difference) < 0.01;

  function bsRow(label, amount, indent = false, bold = false) {
    return `<div style="display:flex;justify-content:space-between;padding:${bold ? '8px' : '4px'} ${indent ? '24px' : '8px'} ${bold ? '8px' : '4px'} 8px;${bold ? 'font-weight:700;border-top:2px solid var(--border-light);' : ''}">
      <span>${label}</span><span>${formatCurrency(amount)}</span>
    </div>`;
  }

  area.innerHTML = `
    <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
      <div style="font-size:16px;font-weight:600;">Balance Sheet <span style="font-size:13px;color:var(--text-secondary);font-weight:400;">as of ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span> <span class="basis-badge">${basis === 'cash' ? 'CASH BASIS' : 'ACCRUAL BASIS'}</span></div>
      <div style="display:flex;gap:8px;">
        <button class="btn btn-secondary btn-sm" onclick="window.BIModule.showBalanceSheetSetup()" style="border-radius:6px;font-size:12px;" title="Balance Sheet Setup">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;">settings</span>
        </button>
        <button class="btn btn-secondary btn-sm" onclick="window.BIModule.manageBankAccounts()" style="border-radius:6px;font-size:12px;">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;">account_balance</span> Bank Accounts
        </button>
        <button class="btn btn-secondary btn-sm" onclick="window.BIModule.exportBalanceSheet()" style="border-radius:6px;font-size:12px;">
          <span class="material-icons" style="font-size:14px;vertical-align:middle;">download</span> Export
        </button>
      </div>
    </div>

    ${!isBalanced ? `<div style="background:#FEF2F2;border:1px solid #FECACA;border-radius:8px;padding:10px 16px;margin-bottom:12px;display:flex;align-items:center;gap:8px;">
      <span class="material-icons" style="color:#DC2626;font-size:18px;">warning</span>
      <span style="font-size:13px;color:#DC2626;">Not balanced — difference of ${formatCurrency(Math.abs(difference))}. This is normal without full double-entry bookkeeping.</span>
    </div>` : `<div style="background:#F0FDF4;border:1px solid #BBF7D0;border-radius:8px;padding:10px 16px;margin-bottom:12px;display:flex;align-items:center;gap:8px;">
      <span class="material-icons" style="color:#16A34A;font-size:18px;">check_circle</span>
      <span style="font-size:13px;color:#16A34A;">Balanced! Assets = Liabilities + Equity</span>
    </div>`}

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
      <!-- Assets -->
      <div class="crm-section-card" style="margin:0;">
        <div class="crm-section-title" style="color:#16A34A;"><span class="material-icons" style="font-size:18px;">account_balance</span> Assets</div>
        <div style="font-size:12px;font-weight:600;color:var(--text-secondary);padding:6px 8px 2px;text-transform:uppercase;letter-spacing:0.5px;">Current Assets</div>
        ${bsRow('Cash & Equivalents', cashBalance, true)}
        ${BiState.bankAccounts.length > 1 ? BiState.bankAccounts.map(a =>
          `<div style="display:flex;justify-content:space-between;padding:2px 36px;font-size:11px;color:var(--text-secondary);">
            <span>${escBI(a.name)}${a.lastFour ? ' ****' + escBI(a.lastFour) : ''}${a.holdingCompany ? ' <span style="opacity:0.6;">(' + escBI(a.holdingCompany) + ')</span>' : ''}</span>
            <span>${formatCurrency(a.balance || 0)}</span>
          </div>`).join('') : ''}
        ${bsRow('Accounts Receivable', accountsReceivable, true)}
        ${prepaidExpenses ? bsRow('Prepaid Expenses', prepaidExpenses, true) : ''}
        ${otherCurrentAssets ? bsRow('Other Current Assets', otherCurrentAssets, true) : ''}
        ${employeeOwed ? bsRow('Owed by Employees (personal charges)', employeeOwed, true) : ''}
        ${bsRow('Total Current Assets', totalCurrentAssets, false, true)}
        <div style="font-size:12px;font-weight:600;color:var(--text-secondary);padding:10px 8px 2px;text-transform:uppercase;letter-spacing:0.5px;">Fixed Assets</div>
        ${bsRow('Vehicles & Equipment (gross)', vehicleGross, true)}
        ${bsRow('Less: Accumulated Depreciation', -accumulatedDep, true)}
        ${bsRow('Net Fixed Assets', netFixedAssets, false, true)}
        <div style="margin-top:4px;"></div>
        ${bsRow('TOTAL ASSETS', totalAssets, false, true)}
      </div>

      <!-- Liabilities + Equity -->
      <div>
        <div class="crm-section-card" style="margin:0 0 12px;">
          <div class="crm-section-title" style="color:#DC2626;"><span class="material-icons" style="font-size:18px;">credit_card</span> Liabilities</div>
          <div style="font-size:12px;font-weight:600;color:var(--text-secondary);padding:6px 8px 2px;text-transform:uppercase;letter-spacing:0.5px;">Current Liabilities</div>
          ${accountsPayable ? bsRow('Accounts Payable', accountsPayable, true) : ''}
          ${accruedWages ? bsRow('Accrued Wages', accruedWages, true) : ''}
          ${currentPortionLTD ? bsRow('Current Portion of LTD', currentPortionLTD, true) : ''}
          ${locVendorBalance ? bsRow('Vendor Accounts', locVendorBalance, true) : ''}
          ${locCardBalance ? bsRow('Credit Cards', locCardBalance, true) : ''}
          ${otherCurrentLiabilities ? bsRow('Other Current Liabilities', otherCurrentLiabilities, true) : ''}
          ${bsRow('Total Current Liabilities', totalCurrentLiabilities, false, true)}
          <div style="font-size:12px;font-weight:600;color:var(--text-secondary);padding:10px 8px 2px;text-transform:uppercase;letter-spacing:0.5px;">Long-Term Liabilities</div>
          ${bsRow('Vehicle Loans', vehicleLoansLT, true)}
          ${locBankBalance ? bsRow('Bank Lines of Credit', locBankBalance, true) : ''}
          ${investorNotes ? bsRow('Notes Payable', investorNotes, true) : ''}
          ${bsRow('Total Long-Term Liabilities', totalLTLiabilities, false, true)}
          <div style="margin-top:4px;"></div>
          ${bsRow('TOTAL LIABILITIES', totalLiabilities, false, true)}
        </div>
        <div class="crm-section-card" style="margin:0;">
          <div class="crm-section-title" style="color:#7C3AED;"><span class="material-icons" style="font-size:18px;">savings</span> Owner\'s Equity</div>
          ${openingEquity ? bsRow('Opening Equity', openingEquity, true) : ''}
          ${bsRow('Contributions', totalContribs, true)}
          ${bsRow('Retained Earnings', retainedEarnings, true)}
          ${bsRow('Less: Owner Draws', -totalDraws, true)}
          ${bsRow('TOTAL EQUITY', totalEquity, false, true)}
        </div>
      </div>
    </div>
  `;
}

async function editBalanceSheetCash() {
  const current = BiState.balanceSheetCash || 0;
  const val = await window.showInputModal('Cash & equivalents', 'Enter current balance', String(current), { type: 'number' });
  if (val === null) return;
  const amount = parseFloat(val) || 0;
  BiState.balanceSheetCash = amount;
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  setDoc(doc(db, 'tenants', tenant), { balanceSheetCash: amount }, { merge: true })
    .then(() => { showToast('Cash balance updated', 'success'); renderPLSubView(); })
    .catch(e => { console.error('[BI] Save cash error:', e); showToast('Error saving', 'error'); });
}

// ============================================
// BANK ACCOUNTS MANAGEMENT
// ============================================
function manageBankAccounts() {
  const prev = document.getElementById('bi-bank-modal');
  if (prev) prev.remove();

  // Auto-migrate single cash balance to bank account
  if (!BiState.bankAccounts.length && BiState.balanceSheetCash > 0) {
    BiState.bankAccounts = [{
      id: crypto.randomUUID(), name: 'Primary Account', type: 'checking',
      institution: '', lastFour: '', balance: BiState.balanceSheetCash,
      holdingCompany: '', isPrimary: true, notes: ''
    }];
  }

  const plaidConnected = window.AppState?.tenantData?.plaidConnected;
  const plaidInst = window.AppState?.tenantData?.plaidInstitution || 'Bank';
  const plaidLast4 = window.AppState?.tenantData?.plaidAccountLast4 || '';

  function renderAccountList() {
    const list = document.getElementById('bi-bank-list');
    if (!list) return;
    if (!BiState.bankAccounts.length) {
      list.innerHTML = '<div style="text-align:center;padding:24px;color:var(--text-secondary);">No bank accounts. Click "Add Account" to get started.</div>';
      return;
    }
    const total = BiState.bankAccounts.reduce((s, a) => s + (a.balance || 0), 0);
    list.innerHTML = BiState.bankAccounts.map(a => {
      const typeIcon = a.type === 'savings' ? 'savings' : a.type === 'money_market' ? 'trending_up' : 'account_balance';
      return `<div style="display:flex;justify-content:space-between;align-items:center;padding:10px 12px;border:1px solid var(--border-light);border-radius:8px;margin-bottom:8px;">
        <div style="display:flex;align-items:center;gap:10px;">
          <span class="material-icons" style="font-size:20px;color:var(--brand-blue);">${typeIcon}</span>
          <div>
            <div style="font-weight:600;font-size:14px;">${escBI(a.name)}${a.lastFour ? ' <span style="font-size:12px;color:var(--text-secondary);">****' + escBI(a.lastFour) + '</span>' : ''}${a.isPrimary ? ' <span style="font-size:10px;padding:1px 5px;border-radius:3px;background:#3B82F615;color:#3B82F6;font-weight:600;">PRIMARY</span>' : ''}</div>
            <div style="font-size:12px;color:var(--text-secondary);">${escBI(a.institution || a.type || 'Checking')}${a.holdingCompany ? ' · ' + escBI(a.holdingCompany) : ''}</div>
          </div>
        </div>
        <div style="display:flex;align-items:center;gap:8px;">
          <span style="font-weight:700;font-size:15px;">${formatCurrency(a.balance || 0)}</span>
          <button class="btn btn-sm btn-secondary" onclick="window.BIModule.editBankAccount('${escJsAttr(a.id)}')" style="border-radius:6px;padding:4px 6px;">
            <span class="material-icons" style="font-size:14px;">edit</span>
          </button>
          <button class="btn btn-sm btn-secondary" onclick="window.BIModule.deleteBankAccount('${escJsAttr(a.id)}')" style="border-radius:6px;padding:4px 6px;color:#dc2626;">
            <span class="material-icons" style="font-size:14px;">delete</span>
          </button>
        </div>
      </div>`;
    }).join('') + `<div style="display:flex;justify-content:flex-end;padding:8px 0;font-weight:700;font-size:14px;">Total: ${formatCurrency(total)}</div>`;
  }

  const modal = document.createElement('div');
  modal.id = 'bi-bank-modal';
  modal.className = 'modal';
  modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
  modal.innerHTML = `
    <div class="modal-content" style="max-width:640px;">
      <div class="modal-header">
        <h3 style="margin:0;">Bank Setup</h3>
        <button class="btn-icon" onclick="document.getElementById('bi-bank-modal').remove()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="padding:20px;max-height:70vh;overflow-y:auto;">
        ${plaidConnected ? `
        <!-- Connected Bank Card -->
        <div style="background:linear-gradient(135deg,#F0FDF4,#ECFDF5);border:1px solid #BBF7D0;border-radius:12px;padding:16px;margin-bottom:16px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;">
            <div style="display:flex;align-items:center;gap:10px;">
              <span class="material-icons" style="font-size:28px;color:#10B981;">account_balance</span>
              <div>
                <div style="font-weight:700;font-size:15px;color:#065F46;">${escBI(plaidInst)}</div>
                <div style="font-size:12px;color:#047857;">${plaidLast4 ? 'Account ••' + escBI(plaidLast4) : 'Connected'} <span style="display:inline-block;width:6px;height:6px;background:#10B981;border-radius:50%;margin-left:4px;vertical-align:middle;"></span></div>
              </div>
            </div>
            <div style="text-align:right;">
              <div id="bi-bank-modal-balance" style="font-weight:700;font-size:20px;color:#065F46;">Loading...</div>
              <div id="bi-bank-modal-updated" style="font-size:11px;color:#047857;"></div>
            </div>
          </div>
          <div style="display:flex;gap:8px;">
            <button class="btn btn-sm btn-secondary" onclick="window.BIModule.refreshBankModalBalance()" style="border-radius:6px;font-size:12px;">
              <span class="material-icons" style="font-size:14px;vertical-align:middle;">refresh</span> Refresh
            </button>
            <button class="btn btn-sm btn-secondary" onclick="window.BIModule.disconnectPlaid()" style="border-radius:6px;font-size:12px;color:#dc2626;">
              <span class="material-icons" style="font-size:14px;vertical-align:middle;">link_off</span> Disconnect
            </button>
          </div>
        </div>

        <!-- Recent Transactions -->
        <div style="margin-bottom:16px;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
            <div style="font-weight:600;font-size:14px;color:var(--text-primary);">Recent Transactions</div>
            <select id="bi-bank-txn-days" class="control-auto" onchange="window.BIModule.loadBankTransactions()" style="font-size:12px;padding:4px 8px;border:1px solid var(--border-light);border-radius:6px;">
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30" selected>Last 30 days</option>
              <option value="90">Last 90 days</option>
            </select>
          </div>
          <div id="bi-bank-txn-list" style="max-height:300px;overflow-y:auto;border:1px solid var(--border-light);border-radius:8px;">
            <div style="text-align:center;padding:24px;color:var(--text-secondary);"><div class="loading-spinner" style="margin:0 auto 8px;"></div>Loading transactions...</div>
          </div>
        </div>
        ` : `
        <!-- Not Connected -->
        <div style="text-align:center;padding:32px 20px;margin-bottom:16px;">
          <span class="material-icons" style="font-size:56px;color:var(--text-secondary);margin-bottom:12px;">account_balance</span>
          <div style="font-size:16px;font-weight:600;margin-bottom:4px;">Connect Your Bank</div>
          <div style="font-size:13px;color:var(--text-secondary);margin-bottom:16px;">Securely link your bank account to see live balances and pull transactions for reconciliation.</div>
          <button class="btn btn-primary" onclick="window.BIModule.connectBankPlaid()" style="border-radius:8px;background:linear-gradient(135deg,#3B82F6,#2563EB);">
            <span class="material-icons" style="font-size:18px;vertical-align:middle;">link</span> Connect with Plaid
          </button>
        </div>
        `}

        <!-- Manual Accounts Section -->
        <div style="border-top:1px solid var(--border-light);padding-top:16px;">
          <div style="font-weight:600;font-size:14px;color:var(--text-primary);margin-bottom:8px;">Manual Accounts</div>
          <div id="bi-bank-list"></div>
        </div>
      </div>
      <div class="form-actions" style="padding:12px 20px;border-top:1px solid var(--border-light);display:flex;gap:12px;justify-content:flex-end;">
        <button class="btn btn-secondary" onclick="document.getElementById('bi-bank-modal').remove()">Close</button>
        <button class="btn btn-primary" onclick="window.BIModule.editBankAccount()">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">add</span> Add Manual
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  renderAccountList();

  // If Plaid connected, fetch balance and transactions
  if (plaidConnected) {
    refreshBankModalBalance();
    loadBankTransactions();
  }
}

function editBankAccount(id) {
  const existing = id ? BiState.bankAccounts.find(a => a.id === id) : null;
  const acct = existing || { id: '', name: '', type: 'checking', institution: '', lastFour: '', balance: 0, holdingCompany: '', isPrimary: false, notes: '' };

  const prev = document.getElementById('bi-bank-edit-modal');
  if (prev) prev.remove();

  const modal = document.createElement('div');
  modal.id = 'bi-bank-edit-modal';
  modal.className = 'modal';
  modal.style.zIndex = '1070';
  modal.onclick = (e) => { if (e.target === modal) modal.remove(); };
  modal.innerHTML = `
    <div class="modal-content" style="max-width:440px;">
      <div class="modal-header">
        <h3 style="margin:0;">${existing ? 'Edit' : 'Add'} Bank Account</h3>
        <button class="btn-icon" onclick="document.getElementById('bi-bank-edit-modal').remove()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="padding:20px;">
        <div class="form-group">
          <label>Account Name *</label>
          <input type="text" id="bank-name" class="form-control" value="${escBI(acct.name)}" placeholder="Operating Account">
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group">
            <label>Type</label>
            <select id="bank-type" class="form-control">
              <option value="checking" ${acct.type === 'checking' ? 'selected' : ''}>Checking</option>
              <option value="savings" ${acct.type === 'savings' ? 'selected' : ''}>Savings</option>
              <option value="money_market" ${acct.type === 'money_market' ? 'selected' : ''}>Money Market</option>
              <option value="other" ${acct.type === 'other' ? 'selected' : ''}>Other</option>
            </select>
          </div>
          <div class="form-group">
            <label>Last 4 Digits</label>
            <input type="text" id="bank-last-four" class="form-control" maxlength="4" value="${escBI(acct.lastFour || '')}" placeholder="4567">
          </div>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
          <div class="form-group">
            <label>Institution</label>
            <input type="text" id="bank-institution" class="form-control" value="${escBI(acct.institution || '')}" placeholder="Wells Fargo">
          </div>
          <div class="form-group">
            <label>Current Balance ($)</label>
            <input type="number" id="bank-balance" class="form-control" step="0.01" value="${escBI(acct.balance || '')}">
          </div>
        </div>
        <div class="form-group">
          <label>Holding Company <span style="font-size:11px;color:var(--text-secondary);">(optional — for multi-entity tracking)</span></label>
          <input type="text" id="bank-holding" class="form-control" value="${escBI(acct.holdingCompany || '')}" placeholder="ABC Holdings LLC">
        </div>
        <div style="display:flex;align-items:center;gap:8px;">
          <input type="checkbox" id="bank-primary" ${acct.isPrimary ? 'checked' : ''}>
          <label for="bank-primary" style="margin:0;font-size:13px;cursor:pointer;">Primary account (used as default for payments)</label>
        </div>
      </div>
      <div class="form-actions" style="padding:16px 20px;border-top:1px solid var(--border-light);display:flex;gap:12px;justify-content:flex-end;">
        <button class="btn btn-secondary" onclick="document.getElementById('bi-bank-edit-modal').remove()">Cancel</button>
        <button class="btn btn-primary" onclick="window.BIModule.saveBankAccount('${escJsAttr(id || '')}')">
          <span class="material-icons" style="font-size:16px;vertical-align:middle;">save</span> Save
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

async function saveBankAccount(existingId) {
  const name = document.getElementById('bank-name')?.value.trim();
  if (!name) { showToast('Account name is required', 'error'); return; }

  const isPrimary = document.getElementById('bank-primary')?.checked || false;
  const entry = {
    id: existingId || crypto.randomUUID(),
    name,
    type: document.getElementById('bank-type')?.value || 'checking',
    institution: document.getElementById('bank-institution')?.value.trim() || '',
    lastFour: document.getElementById('bank-last-four')?.value.trim() || '',
    balance: parseFloat(document.getElementById('bank-balance')?.value) || 0,
    holdingCompany: document.getElementById('bank-holding')?.value.trim() || '',
    isPrimary,
    notes: ''
  };

  const arr = [...BiState.bankAccounts];
  // If marking as primary, unmark others
  if (isPrimary) arr.forEach(a => { a.isPrimary = false; });
  const idx = arr.findIndex(a => a.id === entry.id);
  if (idx >= 0) arr[idx] = entry; else arr.push(entry);

  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  try {
    // TASK-1620 -- writes to the financeSecure/bankAccounts sibling doc now,
    // not the broadcast tenant root (see subscribeToTenantFinancing above).
    await setDoc(doc(db, 'tenants', tenant, 'financeSecure', 'bankAccounts'), { accounts: arr }, { merge: true });
    BiState.bankAccounts = arr;
    showToast('Bank account saved', 'success');
    document.getElementById('bi-bank-edit-modal')?.remove();
    // Refresh list if management modal is open
    manageBankAccounts();
  } catch (e) {
    console.error('[BI] Save bank account error:', e);
    showToast('Error saving: ' + e.message, 'error');
  }
}

async function deleteBankAccount(id) {
  if (!confirm('Delete this bank account?')) return;
  const arr = BiState.bankAccounts.filter(a => a.id !== id);
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  try {
    // TASK-1620 -- see saveBankAccount above.
    await setDoc(doc(db, 'tenants', tenant, 'financeSecure', 'bankAccounts'), { accounts: arr }, { merge: true });
    BiState.bankAccounts = arr;
    showToast('Bank account deleted', 'success');
    manageBankAccounts();
  } catch (e) {
    console.error('[BI] Delete bank account error:', e);
    showToast('Error deleting: ' + e.message, 'error');
  }
}

function showBalanceSheetSetup() {
  const existing = document.getElementById('bs-setup-modal');
  if (existing) existing.remove();

  const cfg = BiState.balanceSheetConfig || {};
  const modal = document.createElement('div');
  modal.id = 'bs-setup-modal';
  modal.className = 'modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;">
      <div class="modal-header">
        <h3>Balance Sheet Setup</h3>
        <button class="btn-icon" onclick="document.getElementById('bs-setup-modal').remove()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:20px;max-height:70vh;overflow-y:auto;">
        <p style="font-size:0.8rem;color:var(--text-secondary);margin-bottom:16px;">Fill in what you know — skip what you don't. These values supplement the auto-calculated data from your fleet, tickets, and cost entries.</p>
        <div style="font-weight:600;font-size:0.85rem;margin-bottom:8px;color:var(--text-secondary);">Current Assets</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;">
          <div class="form-group" style="margin:0;">
            <label>Prepaid Expenses</label>
            <input type="number" id="bs-prepaid" step="0.01" min="0" class="form-control" value="${escBI(cfg.prepaidExpenses || '')}">
          </div>
          <div class="form-group" style="margin:0;">
            <label>Other Current Assets</label>
            <input type="number" id="bs-other-current" step="0.01" min="0" class="form-control" value="${escBI(cfg.otherCurrentAssets || '')}">
          </div>
        </div>
        <div style="font-weight:600;font-size:0.85rem;margin-bottom:8px;color:var(--text-secondary);">Current Liabilities</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:16px;">
          <div class="form-group" style="margin:0;">
            <label>Accounts Payable</label>
            <input type="number" id="bs-ap" step="0.01" min="0" class="form-control" value="${escBI(cfg.accountsPayable || '')}">
          </div>
          <div class="form-group" style="margin:0;">
            <label>Other Current Liabilities</label>
            <input type="number" id="bs-other-current-liab" step="0.01" min="0" class="form-control" value="${escBI(cfg.otherCurrentLiabilities || '')}">
          </div>
        </div>
        <div style="font-weight:600;font-size:0.85rem;margin-bottom:8px;color:var(--text-secondary);">Equity</div>
        <div class="form-group">
          <label>Opening Equity</label>
          <input type="number" id="bs-opening-equity" step="0.01" class="form-control" value="${escBI(cfg.openingEquity || '')}">
          <p style="font-size:0.75rem;color:var(--text-muted);margin-top:2px;">Owner's equity balance when you started using DispatchDeck.</p>
        </div>
      </div>
      <div class="form-actions" style="padding:16px 24px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom, #F8FAFC, #fff);box-shadow:0 -4px 12px rgba(0,0,0,0.04);">
        <button type="button" class="btn btn-secondary" onclick="document.getElementById('bs-setup-modal').remove()" style="border-radius:8px;">Cancel</button>
        <button type="button" class="btn btn-primary" onclick="window.BIModule.saveBalanceSheetConfig()" style="border-radius:8px;">
          <span class="material-icons" style="font-size:18px;vertical-align:middle;">save</span> Save
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

async function saveBalanceSheetConfig() {
  const cfg = {
    prepaidExpenses: parseFloat(document.getElementById('bs-prepaid')?.value) || 0,
    otherCurrentAssets: parseFloat(document.getElementById('bs-other-current')?.value) || 0,
    accountsPayable: parseFloat(document.getElementById('bs-ap')?.value) || 0,
    otherCurrentLiabilities: parseFloat(document.getElementById('bs-other-current-liab')?.value) || 0,
    openingEquity: parseFloat(document.getElementById('bs-opening-equity')?.value) || 0,
    lastUpdated: Timestamp.now(),
    updatedBy: window.AppState?.uid || 'unknown'
  };
  try {
    const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
    await setDoc(doc(db, 'tenants', tenant), { balanceSheetConfig: cfg }, { merge: true });
    BiState.balanceSheetConfig = cfg;
    showToast('Balance sheet config saved', 'success');
    document.getElementById('bs-setup-modal')?.remove();
    renderPLSubView();
  } catch (err) {
    console.error('[BI] Save BS config error:', err);
    showToast('Error saving config', 'error');
  }
}

function exportBalanceSheet() {
  const now = new Date();
  const basis = getAccountingBasis();
  const bsConfig = BiState.balanceSheetConfig || {};
  let ar = 0;
  if (basis !== 'cash') {
    const outstanding = BiState.tickets.filter(t => t.status === 'draft' || t.status === 'ready' || (_biTicketInvoiced(t) && !t.paid && !t.writtenOff && t.status !== 'deleted'));
    ar = outstanding.reduce((s, t) => s + (t.grandTotal || 0), 0);
  }
  const cash = BiState.balanceSheetCash || 0;
  const prepaid = bsConfig.prepaidExpenses || 0;
  const otherCA = bsConfig.otherCurrentAssets || 0;
  const employeeOwedCA = BiState.costEntries.reduce((s, c) => s + personalOwedOutstanding(c), 0); // TASK-9917
  const totalCA = cash + ar + prepaid + otherCA + employeeOwedCA;

  let vehicleGross = 0, accumDep = 0;
  const allEquip = [...BiState.trucks, ...BiState.trailers, ...BiState.assets];
  allEquip.forEach(e => {
    if (e.loan?.enabled) {
      vehicleGross += (e.loan.purchasePrice || 0);
      const dep = computeDepreciation(e.loan);
      if (dep) accumDep += dep.accumulatedDep;
    }
  });
  const netFixed = vehicleGross - accumDep;
  const totalAssets = totalCA + netFixed;

  const ap = bsConfig.accountsPayable || 0;
  const otherCL = bsConfig.otherCurrentLiabilities || 0;
  let currentLTD = 0, ltLoans = 0;
  allEquip.forEach(e => {
    if (e.loan?.enabled && e.loan.monthlyPayment > 0) {
      const d = computeLoanDetails(e.loan);
      if (d) {
        const cur = Math.min(12, d.monthsRemaining) * e.loan.monthlyPayment;
        currentLTD += cur;
        ltLoans += Math.max(0, d.remainingBalance - cur);
      }
    }
  });
  const totalCL = ap + currentLTD + otherCL;
  // TASK-362 Slice 1d: exclude archived (soft-deleted) LOCs from liability total.
  const loc = BiState.linesOfCredit.filter(l => !l.archived).reduce((s, l) => s + (l.currentBalance || 0), 0);
  const notes = BiState.investorNotes.filter(n => n.type === 'note').reduce((s, n) => s + (n.amount || 0), 0);
  const totalLTL = ltLoans + loc + notes;
  const totalLiab = totalCL + totalLTL;

  const openEq = bsConfig.openingEquity || 0;
  const contribs = BiState.costEntries.filter(c => c.category === 'contributions').reduce((s, c) => s + (c.amount || 0), 0);
  const draws = BiState.costEntries.filter(isOwnerDrawCost).reduce((s, c) => s + (c.amount || 0), 0); // TASK-9917
  const totalRev = BiState.revenueEntries.reduce((s, r) => s + (r.amount || 0), 0);
  const totalExp = BiState.costEntries.filter(c => isRecognizedCost(c) && !['draws','contributions','other_income'].includes(c.category)).reduce((s, c) => s + (c.amount || 0), 0);
  const retained = totalRev - totalExp;
  const totalEq = openEq + contribs + retained - draws;

  const rows = [
    [basis === 'cash' ? 'CASH BASIS' : 'ACCRUAL BASIS', '', ''], ['', '', ''],
    ['CURRENT ASSETS', '', ''],
    ['  Cash & Equivalents', '', cash.toFixed(2)],
    ['  Accounts Receivable', '', ar.toFixed(2)],
    ...(prepaid ? [['  Prepaid Expenses', '', prepaid.toFixed(2)]] : []),
    ...(otherCA ? [['  Other Current Assets', '', otherCA.toFixed(2)]] : []),
    ...(employeeOwedCA ? [['  Owed by Employees (personal charges)', '', employeeOwedCA.toFixed(2)]] : []),
    ['Total Current Assets', '', totalCA.toFixed(2)], ['', '', ''],
    ['FIXED ASSETS', '', ''],
    ['  Vehicles & Equipment (gross)', '', vehicleGross.toFixed(2)],
    ['  Less: Accumulated Depreciation', '', (-accumDep).toFixed(2)],
    ['Net Fixed Assets', '', netFixed.toFixed(2)], ['', '', ''],
    ['TOTAL ASSETS', '', totalAssets.toFixed(2)], ['', '', ''],
    ['CURRENT LIABILITIES', '', ''],
    ...(ap ? [['  Accounts Payable', '', ap.toFixed(2)]] : []),
    ...(currentLTD ? [['  Current Portion of LTD', '', currentLTD.toFixed(2)]] : []),
    ...(otherCL ? [['  Other Current Liabilities', '', otherCL.toFixed(2)]] : []),
    ['Total Current Liabilities', '', totalCL.toFixed(2)], ['', '', ''],
    ['LONG-TERM LIABILITIES', '', ''],
    ['  Vehicle Loans', '', ltLoans.toFixed(2)],
    ...(loc ? [['  Lines of Credit', '', loc.toFixed(2)]] : []),
    ...(notes ? [['  Notes Payable', '', notes.toFixed(2)]] : []),
    ['Total Long-Term Liabilities', '', totalLTL.toFixed(2)], ['', '', ''],
    ['TOTAL LIABILITIES', '', totalLiab.toFixed(2)], ['', '', ''],
    ['EQUITY', '', ''],
    ...(openEq ? [['  Opening Equity', '', openEq.toFixed(2)]] : []),
    ['  Contributions', '', contribs.toFixed(2)],
    ['  Retained Earnings', '', retained.toFixed(2)],
    ['  Less: Owner Draws', '', (-draws).toFixed(2)],
    ['TOTAL EQUITY', '', totalEq.toFixed(2)]
  ];
  downloadCSV(`DispatchDeck_Balance_Sheet_${now.toISOString().slice(0,10)}.csv`, generateCSV(['Line Item', '', 'Amount'], rows));
}

// ============================================
// P&L STATEMENT — CPA-ready income statement
// ============================================
// TASK-9701 S2 — the P&L Statement sub-view and its cash-basis banner moved, unchanged
// apart from the S2 lines, into the lazy views/biPnlPrint.js (renderPLStatementHtml) so
// the printable statement does not grow the cold-load bundle. bi.js hands it its
// helpers (pitfall #1: deps passed, never imported).
let _pnlPrintMod = null;
function _plStatementDeps() { return { bucketByMonth, toDate, renderSparkline, escBI, CATEGORY_LABELS, fmtAcct, formatPct, getAccountingBasis, getPeriodLabel, _biCompDeniedBanner, _biLaborBasisNote, _biIsOwnerOrAdmin }; }
function renderPLStatement(area, pl) {
  if (_pnlPrintMod) { area.innerHTML = _pnlPrintMod.renderPLStatementHtml(pl, _plStatementDeps()); return; }
  area.innerHTML = '<div class="loading-state"><div class="loading-spinner"></div><p>Loading P&amp;L...</p></div>';
  _loadPnlPrint().then((m) => {
    _pnlPrintMod = m;
    if (_plView === 'statement' && area.isConnected) renderPLStatement(area, computePLData());
  }).catch((err) => {
    console.error('[BI] biPnlPrint load', err);
    if (area.isConnected) area.textContent = 'The P&L could not be loaded — refresh and try again.';
  });
}

// ============================================
// DRILL-DOWN — breakdown explorer
// ============================================
function setPLDrillBy(dim) {
  _plDrillBy = dim;
  _plSortCol = dim === 'period' ? 'period' : 'profit';
  _plSortDir = 'desc';
  renderPLSubView();
}

function sortPLTable(col) {
  if (_plSortCol === col) _plSortDir = _plSortDir === 'desc' ? 'asc' : 'desc';
  else { _plSortCol = col; _plSortDir = 'desc'; }
  renderPLSubView();
}

// TASK-9512 — P&L drilldown label resolvers (read-only lookups into the
// customers/jobs BI already subscribes to). A key that is a customer id resolves
// to its name; a legacy name-string key (GAS imports) prints as itself; an id we
// cannot resolve prints "Unknown customer", never the raw "CUST-…" id.
function _plLooksLikeId(s) {
  return /^CUST-/i.test(s) || /^[A-Za-z0-9]{20}$/.test(s);
}
function _plCustomerName(key) {
  if (!key || key === 'Unassigned') return 'Unassigned';
  const c = (BiState.customers || []).find(x => x && x.id === key);
  if (c) return c.name || c.companyName || 'Unnamed customer';
  return _plLooksLikeId(String(key)) ? 'Unknown customer' : String(key);
}
function _plJobCustomerKey(jobId) {
  if (!jobId) return '';
  const j = (BiState.jobs || []).find(x => x && x.id === jobId);
  return j ? (j.customerId || j.customerName || '') : '';
}
function _plJobInfo(jobId) {
  const j = jobId ? (BiState.jobs || []).find(x => x && x.id === jobId) : null;
  if (!j) return { name: '', customer: '' };
  return {
    name: j.title || j.jobName || j.name || '',
    customer: j.customerName || (j.customerId ? _plCustomerName(j.customerId) : ''),
  };
}

// TASK-10130 S3 (BI follow-up) — on the CASH basis the Drill-Down and Rankings put
// what was BILLED beside the cash figure, as the Claude connector does
// (get_job_cost / get_profit_rankings). The logic lives in the LAZY
// views/biPlBilled.js (perf budget appColdLoadGzip), loaded only on the cash
// basis; until it lands the views show the cash figures as before and re-render
// once it does. Accrual never loads it and its HTML is unchanged.
// The invoice-date rows are the accrual P&L's own selector (rentalAwareRevenueEntries).
let _plBilledMod = null;
let _plBilledLoad = null;
function _plBilled(pl) {
  if (getAccountingBasis() !== 'cash') return null;
  if (_plBilledMod) return _plBilledMod.make(pl, { BiState, rentalAwareRevenueEntries, isInPeriod, getPeriodDays, labor: _BiLaborRate, JobCost: window.JobCost, fmtAcct, escBI });
  if (!_plBilledLoad) {
    _plBilledLoad = __ddChunkWrap(import('./biPlBilled.js?v=10130201')).then((m) => {
      _plBilledMod = m;
      if (_plView === 'drilldown' || _plView === 'rankings') renderPLSubView();
    }).catch((err) => { _plBilledLoad = null; console.error('[BI] biPlBilled', err); });
  }
  return null;
}

function renderPLDrilldown(area, pl) {
  const dims = [
    { key: 'customer', label: 'Customer', icon: 'business' },
    { key: 'job', label: 'Job', icon: 'work' },
    { key: 'vehicle', label: 'Vehicle', icon: 'local_shipping' },
    { key: 'crew', label: 'Crew Member', icon: 'person' },
    { key: 'project', label: 'Project', icon: 'folder_special' },
    { key: 'category', label: 'Cost Category', icon: 'category' },
    { key: 'period', label: 'Monthly', icon: 'calendar_month' },
  ];

  let rows = [];
  let headers = [];
  // Crew Member + Vehicle (TASK-9878): rows AND sort come from window.JobCost.
  let sharedRows = null;
  let toRow = null;
  let billedEt = null; // TASK-10130 — the cash view's entityTotals + options, for biPlBilled
  let billedOpts;

  if (_plDrillBy === 'customer') {
    headers = ['Customer', 'Revenue', 'Labor', 'Direct', 'Profit', 'Margin', 'Jobs'];
    // TASK-9878 S2 — WHO each dollar and hour belongs to is window.JobCost.entityTotals
    // (functions/src/shared/jobCost.js), the SAME rule the Claude connector's
    // get_job_cost runs. The TASK-9512 rules live there: rows are KEYED by
    // customerId (a legacy name-string key stays its own row); a cost or labor
    // row with no customer of its own takes the job doc's customer before
    // "Unassigned"; labor follows the customer a revenue row this period names
    // for its job; a cost- or labor-only job still counts (COD r1). TASK-9493:
    // costs arrive split-aware (_costViewsOf). Names come from the
    // customers/jobs the BI view already holds, never a raw "CUST-…" id.
    billedOpts = { jobCustomerKey: _plJobCustomerKey, labor: _BiLaborRate };
    const et = billedEt = window.JobCost.entityTotals('customer', { revenue: pl.periodRevenue, costViews: _costViewsOf(pl.periodCosts), clock: pl.periodClock, rates: pl.jobCostRates }, billedOpts);
    rows = et.rows.map(t => {
      let name = t.nameHint || _plCustomerName(t.key);
      // The leftover bucket is money the P&L cannot place, not a customer — say so.
      if (t.key === 'Unassigned' && (t.labor > 0 || t.direct > 0)) name = 'Unassigned — costs not tied to a customer';
      const { profit, margin } = window.JobCost.profit(t.revenue, t.labor, t.direct);
      const jobs = t.jobs.length;
      return { name, revenue: t.revenue, labor: t.labor, direct: t.direct, profit, margin, jobs, _sort: { revenue: t.revenue, labor: t.labor, direct: t.direct, profit, margin, jobs } };
    });
  } else if (_plDrillBy === 'job') {
    headers = ['Job', 'Customer', 'Revenue', 'Labor', 'Direct', 'Profit', 'Margin'];
    // TASK-9878 S2 — window.JobCost.entityTotals (shared with get_job_cost).
    // TASK-9512 — a job with labor/costs but no period revenue showed a blank
    // customer (and sometimes a raw job id). Resolve both from the job doc
    // (_plJobInfo); a revenue row's customerName wins when there is one.
    billedOpts = { labor: _BiLaborRate };
    const et = billedEt = window.JobCost.entityTotals('job', { revenue: pl.periodRevenue, costViews: _costViewsOf(pl.periodCosts), clock: pl.periodClock, rates: pl.jobCostRates }, billedOpts);
    rows = et.rows.map(t => {
      const info = _plJobInfo(t.key);
      const { profit, margin } = window.JobCost.profit(t.revenue, t.labor, t.direct);
      return { name: t.nameHint || info.name || t.key, customer: t.customerHint || info.customer || '', revenue: t.revenue, labor: t.labor, direct: t.direct, profit, margin, _sort: { revenue: t.revenue, labor: t.labor, direct: t.direct, profit, margin } };
    });
  } else if (_plDrillBy === 'vehicle') {
    headers = ['Vehicle', 'Type', 'Revenue', 'Costs', 'Loan/mo', 'Maint', 'Net', 'Utilization'];
    // TASK-9878 — window.JobCost.vehicleTotals (shared with get_profit_rankings):
    // revenue follows a job to every vehicle on any of its job-days (split
    // evenly), split-aware costs, maintenance, loan, utilization over the period.
    sharedRows = window.JobCost.vehicleTotals({
      revenue: pl.periodRevenue, costViews: _costViewsOf(pl.statementCosts), maint: pl.periodMaint, maintCost: window.PnlStatement.woCountedCost, // TASK-10171 shop mirrors in maint; item 4
      jobDays: BiState.jobDays, equipment: pl.periodEquip || pl.allEquip, periodDays: getPeriodDays(), inPeriod: isInPeriod, // TASK-10172-A sold units stay in their periods
    });
    toRow = (v) => ({ name: v.name, type: v.type, revenue: v.revenue, costs: v.costs, loan: v.loan, maint: v.maint, net: v.net, util: v.util, bl: v.bl, pc: v.pc });
  } else if (_plDrillBy === 'crew') {
    headers = ['Name', 'Role', 'Hours', 'Labor Cost', 'Revenue Attr.', '$/Hour'];
    // TASK-9878 — window.JobCost.crewTotals (shared with get_profit_rankings):
    // a person's revenue is each job's revenue split by their share of its
    // clocked hours. Hours are read as a number (an entry stored as text "4"
    // used to be glued on as text and crash this view).
    sharedRows = window.JobCost.crewTotals({ revenue: pl.periodRevenue, clock: pl.periodClock, rates: pl.jobCostRates, people: BiState.people }, { labor: _BiLaborRate });
    toRow = (r) => ({ name: r.name, role: r.role, hours: r.hours, labor: r.labor, revAttr: r.revAttr, perHr: r.perHr, bl: r.bl });
  } else if (_plDrillBy === 'project') {
    headers = ['Project', 'Customer', 'Revenue', 'Labor', 'Direct', 'Profit', 'Margin', 'Jobs'];
    // Build job→project mapping from BiState.jobs
    const jobProjectMap = {};
    const projectNames = {};
    const projectCustomers = {};
    // TASK-9726 — every project, completed ones too (getActiveProjects named
    // only active ones, so a finished project showed its raw id). The list is
    // started for this view on demand (Dispatch's listener is torn down on
    // navigate); no Projects access = no names (the rules refuse the read),
    // said in words, never an id.
    const canProj = window.canAccessProjects?.() !== false;
    const booting = canProj && !window.AppState?.viewCleanup?.__projectsBootTenant;
    if (booting) {
      window.ensureProjectsModule?.().then(m => { if (m && _plDrillBy === 'project') renderPLSubView(); }).catch(() => {});
    }
    const allProjects = window.ProjectsModule?.getState?.()?.projects || [];
    BiState.jobs.forEach(j => {
      if (j.projectId) {
        jobProjectMap[j.id] = j.projectId;
        if (!projectNames[j.projectId]) {
          const proj = allProjects.find(p => p.id === j.projectId);
          projectNames[j.projectId] = proj?.name || (booting ? 'Loading…' : canProj ? 'Project not found' : 'Project (name not available)');
          projectCustomers[j.projectId] = proj?.primaryCustomerName || j.customer || '';
        }
      }
    });
    // TASK-9878 S2 — revenue, labor and split-aware direct cost by project through
    // each row's job: window.JobCost.entityTotals (shared with get_job_cost).
    billedOpts = {
      jobProjectId: (jobId) => (Object.prototype.hasOwnProperty.call(jobProjectMap, jobId) ? jobProjectMap[jobId] : null),
      labor: _BiLaborRate,
    };
    const et = billedEt = window.JobCost.entityTotals('project', { revenue: pl.periodRevenue, costViews: _costViewsOf(pl.periodCosts), clock: pl.periodClock, rates: pl.jobCostRates }, billedOpts);
    rows = et.rows.map(t => {
      const { profit, margin } = window.JobCost.profit(t.revenue, t.labor, t.direct);
      const jobs = t.jobs.length;
      return { name: projectNames[t.key] || t.key, customer: projectCustomers[t.key] || '', revenue: t.revenue, labor: t.labor, direct: t.direct, profit, margin, jobs, _sort: { revenue: t.revenue, labor: t.labor, direct: t.direct, profit, margin, jobs } };
    });
  } else if (_plDrillBy === 'category') {
    headers = ['Category', 'Amount', '% of Total', 'Entries', 'Avg/Entry'];
    // TASK-10171 — statementCosts: the statement's rows (a work order's
    // shop mirror is left out; its dollars are in Maintenance).
    const allCosts = pl.totalLaborCost + pl.statementCosts.reduce((s, c) => s + (c.amount || 0), 0) + pl.totalMaintCosts;
    const map = { labor: { amount: pl.totalLaborCost, count: pl.periodClock.length } };
    pl.statementCosts.forEach(c => {
      const cat = c.category || 'other';
      if (!map[cat]) map[cat] = { amount: 0, count: 0 };
      map[cat].amount += (c.amount || 0);
      map[cat].count++;
    });
    if (pl.totalMaintCosts > 0) map.maintenance = { amount: pl.totalMaintCosts, count: pl.periodMaint.length };
    rows = Object.entries(map).filter(([, v]) => v.amount > 0).map(([cat, v]) => {
      const pctTotal = allCosts > 0 ? (v.amount / allCosts * 100) : 0;
      const avg = v.count > 0 ? (v.amount / v.count) : 0;
      return { name: CATEGORY_LABELS[cat] || cat, amount: v.amount, pctTotal, count: v.count, avg, _sort: { amount: v.amount, pctTotal, count: v.count, avg, profit: -v.amount } };
    });
  } else if (_plDrillBy === 'period') {
    headers = ['Month', 'Revenue', 'COGS', 'Gross Profit', 'OpEx', 'Net Income', 'Margin'];
    const months = {};
    const addToMonth = (d, field, val) => {
      if (!d) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!months[key]) months[key] = { revenue: 0, cogs: 0, opex: 0 };
      months[key][field] += val;
    };
    pl.periodRevenue.forEach(r => addToMonth(toDate(r.invoicedAt), 'revenue', r.amount || 0));
    pl.periodClock.forEach(c => addToMonth(toDate(c.clockIn), 'cogs', _BiLaborRate.jobCostForEntry(c, pl.jobCostRates)));
    pl.statementCosts.forEach(c => { // TASK-10171 — shop mirrors are in periodMaint
      const cat = c.category || 'other';
      const field = ['fuel', 'tolls', 'lodging', 'materials', 'subcontractor'].includes(cat) ? 'cogs' : 'opex';
      addToMonth(toDate(c.date), field, c.amount || 0);
    });
    pl.periodMaint.forEach(m => addToMonth(toDate(m.createdAt), 'opex', window.PnlStatement.woCountedCost(m)));

    rows = Object.entries(months).sort((a, b) => a[0].localeCompare(b[0])).map(([key, v]) => {
      const gp = v.revenue - v.cogs;
      const net = gp - v.opex;
      const margin = v.revenue > 0 ? (net / v.revenue * 100) : 0;
      const label = new Date(key + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      return { name: label, period: key, revenue: v.revenue, cogs: v.cogs, grossProfit: gp, opex: v.opex, net, margin, _sort: { revenue: v.revenue, cogs: v.cogs, grossProfit: gp, opex: v.opex, net, margin, profit: net, period: key } };
    });
  }

  // TASK-10130 — cash basis: billed beside cash (rows decorated in place, before sorting).
  const B = _plBilled(pl);
  const billedBanner = B ? B.decorate(_plDrillBy, { et: billedEt, opts: billedOpts, rows, shared: sharedRows }) : '';

  // Sort
  const sortKey = _plSortCol === 'name' ? null : _plSortCol;
  if (sharedRows) {
    rows = (sortKey ? window.JobCost.rank(sharedRows, sortKey, _plSortDir === 'asc' ? 'asc' : 'desc') : sharedRows).map(toRow);
  } else if (sortKey) {
    rows.sort((a, b) => {
      const av = a._sort?.[sortKey] ?? a[sortKey] ?? 0;
      const bv = b._sort?.[sortKey] ?? b[sortKey] ?? 0;
      if (typeof av === 'string') return _plSortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return _plSortDir === 'asc' ? av - bv : bv - av;
    });
  }

  // Render dimension selector + table
  const sortIcon = col => _plSortCol === col ? (_plSortDir === 'desc' ? ' ▾' : ' ▴') : '';

  area.innerHTML = `
    <div class="pl-drill-selector">
      ${dims.map(d => `
        <button class="pl-dim-pill ${_plDrillBy === d.key ? 'active' : ''}" onclick="window.BIModule.setPLDrillBy('${escJsAttr(d.key)}')">
          <span class="material-icons" style="font-size:16px;">${d.icon}</span> ${d.label}
        </button>
      `).join('')}
    </div>

    ${billedBanner}${rows.length === 0 ? `
      <div class="crm-section-card" style="text-align:center;padding:40px;">
        <span class="material-icons" style="font-size:48px;color:var(--text-secondary);">search_off</span>
        <div style="margin-top:8px;color:var(--text-secondary);">No data for this breakdown in the selected period.</div>
      </div>
    ` : `
      <div class="crm-section-card" style="overflow-x:auto;">
        <table class="pl-drill-table">
          <thead><tr>
            ${_plDrillBy === 'customer' ? `
              <th onclick="window.BIModule.sortPLTable('name')" class="pl-th-sort">Customer${sortIcon('name')}</th>
              <th onclick="window.BIModule.sortPLTable('revenue')" class="pl-th-sort pl-th-right">Revenue${sortIcon('revenue')}</th>
              <th onclick="window.BIModule.sortPLTable('labor')" class="pl-th-sort pl-th-right">Labor${sortIcon('labor')}</th>
              <th onclick="window.BIModule.sortPLTable('direct')" class="pl-th-sort pl-th-right">Direct${sortIcon('direct')}</th>
              <th onclick="window.BIModule.sortPLTable('profit')" class="pl-th-sort pl-th-right">Profit${sortIcon('profit')}</th>
              <th onclick="window.BIModule.sortPLTable('margin')" class="pl-th-sort pl-th-right">Margin${sortIcon('margin')}</th>
              <th onclick="window.BIModule.sortPLTable('jobs')" class="pl-th-sort pl-th-right">Jobs${sortIcon('jobs')}</th>
            ` : _plDrillBy === 'job' ? `
              <th onclick="window.BIModule.sortPLTable('name')" class="pl-th-sort">Job${sortIcon('name')}</th>
              <th>Customer</th>
              <th onclick="window.BIModule.sortPLTable('revenue')" class="pl-th-sort pl-th-right">Revenue${sortIcon('revenue')}</th>
              <th onclick="window.BIModule.sortPLTable('labor')" class="pl-th-sort pl-th-right">Labor${sortIcon('labor')}</th>
              <th onclick="window.BIModule.sortPLTable('direct')" class="pl-th-sort pl-th-right">Direct${sortIcon('direct')}</th>
              <th onclick="window.BIModule.sortPLTable('profit')" class="pl-th-sort pl-th-right">Profit${sortIcon('profit')}</th>
              <th onclick="window.BIModule.sortPLTable('margin')" class="pl-th-sort pl-th-right">Margin${sortIcon('margin')}</th>
            ` : _plDrillBy === 'vehicle' ? `
              <th onclick="window.BIModule.sortPLTable('name')" class="pl-th-sort">Vehicle${sortIcon('name')}</th>
              <th>Type</th>
              <th onclick="window.BIModule.sortPLTable('revenue')" class="pl-th-sort pl-th-right">Revenue${sortIcon('revenue')}</th>
              <th onclick="window.BIModule.sortPLTable('costs')" class="pl-th-sort pl-th-right">Costs${sortIcon('costs')}</th>
              <th onclick="window.BIModule.sortPLTable('loan')" class="pl-th-sort pl-th-right">Loan/mo${sortIcon('loan')}</th>
              <th onclick="window.BIModule.sortPLTable('maint')" class="pl-th-sort pl-th-right">Maint${sortIcon('maint')}</th>
              <th onclick="window.BIModule.sortPLTable('net')" class="pl-th-sort pl-th-right">Net${sortIcon('net')}</th>
              <th onclick="window.BIModule.sortPLTable('util')" class="pl-th-sort pl-th-right">Util%${sortIcon('util')}</th>
            ` : _plDrillBy === 'crew' ? `
              <th onclick="window.BIModule.sortPLTable('name')" class="pl-th-sort">Name${sortIcon('name')}</th>
              <th>Role</th>
              <th onclick="window.BIModule.sortPLTable('hours')" class="pl-th-sort pl-th-right">Hours${sortIcon('hours')}</th>
              <th onclick="window.BIModule.sortPLTable('labor')" class="pl-th-sort pl-th-right">Labor Cost${sortIcon('labor')}</th>
              <th onclick="window.BIModule.sortPLTable('revAttr')" class="pl-th-sort pl-th-right">Revenue Attr.${sortIcon('revAttr')}</th>
              <th onclick="window.BIModule.sortPLTable('perHr')" class="pl-th-sort pl-th-right">$/Hour${sortIcon('perHr')}</th>
            ` : _plDrillBy === 'project' ? `
              <th onclick="window.BIModule.sortPLTable('name')" class="pl-th-sort">Project${sortIcon('name')}</th>
              <th>Customer</th>
              <th onclick="window.BIModule.sortPLTable('revenue')" class="pl-th-sort pl-th-right">Revenue${sortIcon('revenue')}</th>
              <th onclick="window.BIModule.sortPLTable('labor')" class="pl-th-sort pl-th-right">Labor${sortIcon('labor')}</th>
              <th onclick="window.BIModule.sortPLTable('direct')" class="pl-th-sort pl-th-right">Direct${sortIcon('direct')}</th>
              <th onclick="window.BIModule.sortPLTable('profit')" class="pl-th-sort pl-th-right">Profit${sortIcon('profit')}</th>
              <th onclick="window.BIModule.sortPLTable('margin')" class="pl-th-sort pl-th-right">Margin${sortIcon('margin')}</th>
              <th onclick="window.BIModule.sortPLTable('jobs')" class="pl-th-sort pl-th-right">Jobs${sortIcon('jobs')}</th>
            ` : _plDrillBy === 'category' ? `
              <th onclick="window.BIModule.sortPLTable('name')" class="pl-th-sort">Category${sortIcon('name')}</th>
              <th onclick="window.BIModule.sortPLTable('amount')" class="pl-th-sort pl-th-right">Amount${sortIcon('amount')}</th>
              <th onclick="window.BIModule.sortPLTable('pctTotal')" class="pl-th-sort pl-th-right">% of Total${sortIcon('pctTotal')}</th>
              <th onclick="window.BIModule.sortPLTable('count')" class="pl-th-sort pl-th-right">Entries${sortIcon('count')}</th>
              <th onclick="window.BIModule.sortPLTable('avg')" class="pl-th-sort pl-th-right">Avg/Entry${sortIcon('avg')}</th>
            ` : `
              <th>Month</th>
              <th onclick="window.BIModule.sortPLTable('revenue')" class="pl-th-sort pl-th-right">Revenue${sortIcon('revenue')}</th>
              <th onclick="window.BIModule.sortPLTable('cogs')" class="pl-th-sort pl-th-right">COGS${sortIcon('cogs')}</th>
              <th onclick="window.BIModule.sortPLTable('grossProfit')" class="pl-th-sort pl-th-right">Gross Profit${sortIcon('grossProfit')}</th>
              <th onclick="window.BIModule.sortPLTable('opex')" class="pl-th-sort pl-th-right">OpEx${sortIcon('opex')}</th>
              <th onclick="window.BIModule.sortPLTable('net')" class="pl-th-sort pl-th-right">Net Income${sortIcon('net')}</th>
              <th onclick="window.BIModule.sortPLTable('margin')" class="pl-th-sort pl-th-right">Margin${sortIcon('margin')}</th>
            `}
          </tr></thead>
          <tbody>
            ${rows.map(r => `<tr>
              ${_plDrillBy === 'customer' ? `
                <td class="pl-td-name">${escBI(r.name)}${r.bl || ''}</td>
                <td class="pl-td-right">${fmtAcct(r.revenue)}</td>
                <td class="pl-td-right">${fmtAcct(r.labor)}</td>
                <td class="pl-td-right">${fmtAcct(r.direct)}</td>
                <td class="pl-td-right pl-td-bold ${r.pc ?? (r.profit >= 0 ? 'pl-positive' : 'pl-negative')}">${fmtAcct(r.profit)}</td>
                <td class="pl-td-right">${formatPct(r.margin)}</td>
                <td class="pl-td-right">${r.jobs}</td>
              ` : _plDrillBy === 'job' ? `
                <td class="pl-td-name">${escBI(r.name)}${r.bl || ''}</td>
                <td style="color:var(--text-secondary);font-size:13px;">${escBI(r.customer)}</td>
                <td class="pl-td-right">${fmtAcct(r.revenue)}</td>
                <td class="pl-td-right">${fmtAcct(r.labor)}</td>
                <td class="pl-td-right">${fmtAcct(r.direct)}</td>
                <td class="pl-td-right pl-td-bold ${r.pc ?? (r.profit >= 0 ? 'pl-positive' : 'pl-negative')}">${fmtAcct(r.profit)}</td>
                <td class="pl-td-right">${formatPct(r.margin)}</td>
              ` : _plDrillBy === 'vehicle' ? `
                <td class="pl-td-name">${escBI(r.name)}${r.bl || ''}</td>
                <td style="color:var(--text-secondary);font-size:13px;">${escBI(r.type || '')}</td>
                <td class="pl-td-right">${fmtAcct(r.revenue)}</td>
                <td class="pl-td-right">${fmtAcct(r.costs)}</td>
                <td class="pl-td-right">${r.loan > 0 ? fmtAcct(r.loan) : '—'}</td>
                <td class="pl-td-right">${fmtAcct(r.maint)}</td>
                <td class="pl-td-right pl-td-bold ${r.pc ?? (r.net >= 0 ? 'pl-positive' : 'pl-negative')}">${fmtAcct(r.net)}</td>
                <td class="pl-td-right">${formatPct(r.util)}</td>
              ` : _plDrillBy === 'crew' ? `
                <td class="pl-td-name">${escBI(r.name)}${r.bl || ''}</td>
                <td style="color:var(--text-secondary);font-size:13px;">${escBI(r.role)}</td>
                <td class="pl-td-right">${r.hours.toFixed(1)}</td>
                <td class="pl-td-right">${fmtAcct(r.labor)}</td>
                <td class="pl-td-right">${fmtAcct(r.revAttr)}</td>
                <td class="pl-td-right pl-td-bold">${fmtAcct(Math.round(r.perHr))}</td>
              ` : _plDrillBy === 'project' ? `
                <td class="pl-td-name">${escBI(r.name)}${r.bl || ''}</td>
                <td style="color:var(--text-secondary);font-size:13px;">${escBI(r.customer)}</td>
                <td class="pl-td-right">${fmtAcct(r.revenue)}</td>
                <td class="pl-td-right">${fmtAcct(r.labor)}</td>
                <td class="pl-td-right">${fmtAcct(r.direct)}</td>
                <td class="pl-td-right pl-td-bold ${r.pc ?? (r.profit >= 0 ? 'pl-positive' : 'pl-negative')}">${fmtAcct(r.profit)}</td>
                <td class="pl-td-right">${formatPct(r.margin)}</td>
                <td class="pl-td-right">${r.jobs}</td>
              ` : _plDrillBy === 'category' ? `
                <td class="pl-td-name"><span class="bi-cost-cat-badge" style="background:${CATEGORY_COLORS[r.name.toLowerCase()] || '#64748B'}20;color:${CATEGORY_COLORS[r.name.toLowerCase()] || '#64748B'};">${escBI(r.name)}</span></td>
                <td class="pl-td-right">${fmtAcct(r.amount)}</td>
                <td class="pl-td-right">${formatPct(r.pctTotal)}</td>
                <td class="pl-td-right">${r.count}</td>
                <td class="pl-td-right">${fmtAcct(Math.round(r.avg))}</td>
              ` : `
                <td class="pl-td-name">${escBI(r.name)}</td>
                <td class="pl-td-right">${fmtAcct(r.revenue)}</td>
                <td class="pl-td-right">${fmtAcct(r.cogs)}</td>
                <td class="pl-td-right ${r.grossProfit >= 0 ? 'pl-positive' : 'pl-negative'}">${fmtAcct(r.grossProfit)}</td>
                <td class="pl-td-right">${fmtAcct(r.opex)}</td>
                <td class="pl-td-right pl-td-bold ${r.net >= 0 ? 'pl-positive' : 'pl-negative'}">${fmtAcct(r.net)}</td>
                <td class="pl-td-right">${formatPct(r.margin)}</td>
              `}
            </tr>`).join('')}
          </tbody>
        </table>
      </div>
    `}
  `;
}

// ============================================
// RANKINGS — insights & leaderboards
// ============================================
// TASK-10130 S3 BI follow-up — Rankings lives in the LAZY views/biPlRankings.js (perf budget).
let _plRankingsLoad = null;
function renderPLRankings(area, pl) {
  area.textContent = 'Loading…';
  if (!_plRankingsLoad) _plRankingsLoad = __ddChunkWrap(import('./biPlRankings.js?v=10171004')).catch((e) => { _plRankingsLoad = null; throw e; });
  _plRankingsLoad.then((m) => {
    if (_plView === 'rankings' && area.isConnected) m.renderPLRankings(area, pl, { BiState, _BiLaborRate, _costViewsOf, computeLoanDetails, getPeriodDays, rentalAwareRevenueEntries, toDate, isRecognizedCost, fmtAcct, formatPct, escBI, billed: _plBilled(pl) });
  }).catch((err) => {
    console.error('[BI] biPlRankings load', err);
    if (area.isConnected) area.textContent = 'The rankings could not be loaded — refresh and try again.';
  });
}

// ============================================
// FORECAST — cash flow projections
// ============================================
// TASK-9726 — Forecast lives in the LAZY views/biPlForecast.js (perf budget).
let _plForecastLoad = null;
function renderPLForecast(area, pl) {
  area.textContent = 'Loading…';
  if (!_plForecastLoad) _plForecastLoad = __ddChunkWrap(import('./biPlForecast.js?v=10171004')).catch((e) => { _plForecastLoad = null; throw e; });
  _plForecastLoad.then((m) => {
    if (_plView === 'forecast' && area.isConnected) m.renderPLForecast(area, pl, { BiState, rentalAwareRevenueEntries, toDate, isRecognizedCost, _BiLaborRate, computeLoanDetails, fmtAcct, formatPct, escBI, PnlStatement: window.PnlStatement }); // TASK-9701 — loans split by the statement's own rule
  }).catch((err) => {
    console.error('[BI] biPlForecast load', err);
    if (area.isConnected) area.textContent = 'The forecast could not be loaded — refresh and try again.';
  });
}

// ============================================
// P&L STATEMENT EXPORT
// ============================================
function exportPLStatement() {
  // TASK-9701 S1 (COD) — reachable from the Financial tab before the P&L tab has
  // rendered; wait for the lazily loaded statement module. TASK-9701 S2 — the rows
  // are built in the lazy views/biPnlPrint.js (unchanged, pinned by its test).
  Promise.all([_pnlStatementReady, _loadPnlPrint()]).then(([ok, m]) => {
    if (!ok || !window.PnlStatement) throw new Error('pnl-statement not loaded');
    const rows = m.buildPnlCsvRows(computePLData(), { companyName: window.AppState?.tenantData?.companyName, periodLabel: getPeriodLabel(), labels: CATEGORY_LABELS, formatPct, toDate });
    const csv = generateCSV(['', '', 'Amount'], rows);
    downloadCSV(`DispatchDeck_PL_Statement_${getPeriodSlug()}.csv`, csv);
  }).catch((err) => {
    console.error('[BI] P&L export', err);
    window.showToast?.('The P&L could not be loaded — refresh and try again.', 'error');
  });
}

// TASK-9701 S2 — the printable, branded P&L (logo, period, basis, prior-period
// column, estimate / not-final banners, notes). The window opens ON the click so
// a popup blocker never eats it; the lazy module fills it.
function printPLStatement() {
  const win = window.open('', '_blank');
  if (!win) { window.showToast?.('Allow pop-ups for DispatchDeck to print the P&L.', 'warning'); return; }
  try { win.document.write('<p style="font-family:sans-serif;padding:24px;">Preparing the P&amp;L…</p>'); } catch (e) { /* closed */ }
  Promise.all([_pnlStatementReady, _loadPnlPrint()]).then(([ok, m]) => {
    if (!ok || !window.PnlStatement) throw new Error('pnl-statement not loaded');
    if (win.closed) return;
    const cur = computePLData();
    const prior = m.priorPeriodFor(BiState.period, BiState.periodStart, BiState.periodEnd);
    const prev = prior ? computePLData({ start: prior.start, end: prior.end, inPeriod: (v) => { const d = toDate(v); return !!d && d >= prior.start && d <= prior.end; } }) : null;
    const td = window.AppState?.tenantData || {};
    m.writePnlPrint(win, { cur, prev, prior, period: { start: BiState.periodStart, end: BiState.periodEnd }, basis: getAccountingBasis(), companyName: td.companyName, logoUrl: td.companyLogoUrl, labels: CATEGORY_LABELS, unmappedLabel: window.PnlStatement.PNL_LINE_UNMAPPED, preparedAt: new Date() });
  }).catch((err) => {
    console.error('[BI] P&L print', err);
    try { win.document.body.textContent = 'The P&L could not be loaded — close this tab, refresh DispatchDeck and try again.'; } catch (e) { /* closed */ }
  });
}

// ============================================
// ADD COST MODAL
// ============================================
function showAddCostModal() {
  // Remove any existing modal
  const existing = document.getElementById('bi-cost-modal');
  if (existing) existing.remove();

  // TASK-9548 slice 2 — Trucks / Trailers / Assets under labeled <optgroup>s
  // via window.PickerGroups (Pitfall #1: never a ?v= import). Values are still
  // the vehicle ids and data-type still rides on each option — saveCostEntry
  // reads `.value`, `selectedOptions[0].dataset.type` and `.textContent`, none
  // of which an optgroup wrapper changes. Labels are escaped by the helper;
  // data-type is a fixed literal.
  const unitDisplay = t => (window.getUnitDisplay ? window.getUnitDisplay(t) : `#${t.unit}`);
  const vehicleItem = (v, type) => ({ value: v.id, label: v.display, attrs: ` data-type="${escBI(type)}"` });
  const vehicleOptions = window.PickerGroups.groupedOptionsHtml([
    { label: '', items: [{ value: '', label: 'None' }] },
    { label: 'Trucks', items: BiState.trucks.map(t => vehicleItem({ id: t.id, display: unitDisplay(t) }, 'truck')) },
    { label: 'Trailers', items: BiState.trailers.map(t => vehicleItem({ id: t.id, display: unitDisplay(t) }, 'trailer')) },
    { label: 'Assets', items: BiState.assets.filter(a => a.hoursTracking?.enabled).map(a => vehicleItem({ id: a.id, display: a.name }, 'asset')) },
  ], { alwaysGroup: true });

  const jobs = BiState.jobs.map(j => ({
    id: j.id,
    title: j.title || j.customerName || j.id,
    customerId: j.customerId || null,
    customerName: j.customerName || ''
  }));

  const modal = document.createElement('div');
  modal.id = 'bi-cost-modal';
  modal.className = 'modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:520px;">
      <div class="modal-header">
        <h3>Add Cost Entry</h3>
        <button class="btn-icon" onclick="window.BIModule.closeAddCostModal()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="modal-body" style="padding:20px;max-height:70vh;overflow-y:auto;">
        <div class="form-group">
          <label for="cost-category">Category *</label>
          <select id="cost-category" class="form-control" onchange="window.BIModule.onCostCategoryChange()">
            <optgroup label="Direct Costs (COGS)">
              <option value="fuel">Fuel</option>
              <option value="tolls">Tolls</option>
              <option value="lodging">Lodging</option>
              <option value="materials">Materials</option>
              <option value="subcontractor">Subcontractor</option>
              <option value="per_diem">Per Diem</option>
            </optgroup>
            <optgroup label="Operating Expenses">
              <option value="permits">Permits</option>
              <option value="shop">Shop</option>
              <option value="insurance">Insurance</option>
              <option value="interest">Interest Expense</option>
              <option value="taxes">Taxes & Fees</option>
              <option value="bank_fees">Bank & Processing Fees</option>
              <option value="dot_compliance">DOT / Compliance</option>
              <option value="benefits">Benefits & Insurance</option>
              <option value="penalties">Fines & Penalties</option>
              <option value="other">Other</option>
            </optgroup>
            <optgroup label="Other">
              <option value="other_income">Other Income</option>
              <option value="draws">Owner Draws / Distributions</option>
              <option value="contributions">Owner Contributions</option>
            </optgroup>
          </select>
        </div>
        <div class="form-group">
          <label for="cost-amount">Amount ($) *</label>
          <input type="number" id="cost-amount" step="0.01" min="0" class="form-control" placeholder="0.00">
        </div>
        <div class="form-group">
          <label for="cost-date">Date *</label>
          <input type="date" id="cost-date" class="form-control" value="${new Date().toISOString().split('T')[0]}">
        </div>
        <div class="form-group">
          <label for="cost-paid-date">Date Paid</label>
          <input type="date" id="cost-paid-date" class="form-control">
          <p style="font-size:0.75rem;color:var(--text-muted);margin-top:2px;">When actually paid. Leave blank = same as date above.</p>
        </div>
        <div id="cost-fuel-fields" class="form-group" style="display:none;">
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
            <div>
              <label for="cost-fuel-gallons">Gallons</label>
              <input type="number" id="cost-fuel-gallons" step="0.01" min="0" class="form-control" placeholder="0.00">
            </div>
            <div>
              <label for="cost-fuel-ppg">$/Gallon</label>
              <input type="number" id="cost-fuel-ppg" step="0.01" min="0" class="form-control" placeholder="0.00">
            </div>
          </div>
        </div>
        <div class="form-group">
          <label for="cost-vehicle">Vehicle</label>
          <select id="cost-vehicle" class="form-control">
            ${vehicleOptions}
          </select>
        </div>
        <div class="form-group">
          <label for="cost-job">Job</label>
          <select id="cost-job" class="form-control" onchange="window.BIModule.onCostJobChange()">
            <option value="">None</option>
            ${jobs.map(j => `<option value="${escBI(j.id)}" data-cust-id="${escBI(j.customerId || '')}" data-cust-name="${escBI(j.customerName || '')}">${escBI(j.title)}</option>`).join('')}
          </select>
        </div>
        <div class="form-group" id="cost-contractor-group" style="display:none;">
          <label for="cost-contractor">Contractor</label>
          <select id="cost-contractor" class="form-control">
            <option value="">None</option>
            ${(BiState.people || []).filter(p => window.WorkerClass?.resolve(p).is1099).map(p => `<option value="${escBI(p.id)}">${escBI(p.name)}</option>`).join('')}
          </select>
        </div>
        <div class="form-group">
          <label for="cost-description">Description</label>
          <input type="text" id="cost-description" class="form-control" placeholder="e.g. Diesel fill-up at Pilot #342">
        </div>
        <div class="form-group">
          <label for="cost-notes">Notes</label>
          <textarea id="cost-notes" class="form-control" rows="2" placeholder="Optional notes"></textarea>
        </div>
      </div>
      <div class="form-actions" style="padding:16px 24px 20px;border-top:1px solid var(--border-light);background:linear-gradient(to bottom, #F8FAFC, #fff);box-shadow:0 -4px 12px rgba(0,0,0,0.04);">
        <button type="button" class="btn btn-secondary" onclick="window.BIModule.closeAddCostModal()" style="border-radius:8px;">Cancel</button>
        <button type="button" class="btn btn-primary" onclick="window.BIModule.saveCostEntry()" style="border-radius:8px;">
          <span class="material-icons" style="font-size:18px;vertical-align:middle;">save</span> Save
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);

  // Show fuel fields by default (fuel is first option)
  onCostCategoryChange();
}

function closeAddCostModal() {
  const modal = document.getElementById('bi-cost-modal');
  if (modal) modal.remove();
}

function onCostCategoryChange() {
  const cat = document.getElementById('cost-category')?.value;
  const fuelFields = document.getElementById('cost-fuel-fields');
  if (fuelFields) fuelFields.style.display = cat === 'fuel' ? '' : 'none';
  const contractorGroup = document.getElementById('cost-contractor-group');
  if (contractorGroup) contractorGroup.style.display = cat === 'subcontractor' ? '' : 'none';
}

function onCostJobChange() {
  // Auto-fill customer from job selection (info stored in data attributes)
  // No UI for customer field — it's stored in the cost entry automatically
}

async function saveCostEntry() {
  const category = document.getElementById('cost-category')?.value;
  const amount = parseFloat(document.getElementById('cost-amount')?.value);
  const dateVal = document.getElementById('cost-date')?.value;

  if (!category || isNaN(amount) || amount <= 0) {
    window.showToast?.('Amount is required', 'error');
    return;
  }
  if (!dateVal) {
    window.showToast?.('Date is required', 'error');
    return;
  }

  const vehicleSelect = document.getElementById('cost-vehicle');
  const vehicleId = vehicleSelect?.value || null;
  const vehicleOption = vehicleSelect?.selectedOptions?.[0];
  const vehicleDisplay = vehicleId ? vehicleOption?.textContent : null;
  const vehicleType = vehicleId ? (vehicleOption?.dataset?.type || null) : null;

  const jobSelect = document.getElementById('cost-job');
  const jobId = jobSelect?.value || null;
  const jobOption = jobSelect?.selectedOptions?.[0];
  const jobName = jobId ? jobOption?.textContent : null;
  const customerId = jobId ? (jobOption?.dataset?.custId || null) : null;
  const customerName = jobId ? (jobOption?.dataset?.custName || null) : null;

  const paidDateVal = document.getElementById('cost-paid-date')?.value || '';
  const contractorSelect = document.getElementById('cost-contractor');
  const contractorId = contractorSelect?.value || null;
  const contractorOption = contractorSelect?.selectedOptions?.[0];
  const contractorName = contractorId ? contractorOption?.textContent : null;

  const entry = {
    category,
    amount,
    date: Timestamp.fromDate(new Date(dateVal + 'T12:00:00')),
    paidDate: paidDateVal ? Timestamp.fromDate(new Date(paidDateVal + 'T12:00:00')) : null,
    description: document.getElementById('cost-description')?.value?.trim() || '',
    notes: document.getElementById('cost-notes')?.value?.trim() || '',
    vehicleId,
    vehicleDisplay,
    vehicleType,
    personId: null,
    personName: null,
    contractorId,
    contractorName,
    jobId,
    jobName,
    customerId,
    customerName,
    receiptUrl: null,
    receiptFileName: null,
    createdAt: Timestamp.now(),
    createdBy: window.AppState?.uid || 'unknown'
  };

  // Fuel-specific fields
  if (category === 'fuel') {
    const gal = parseFloat(document.getElementById('cost-fuel-gallons')?.value);
    const ppg = parseFloat(document.getElementById('cost-fuel-ppg')?.value);
    if (!isNaN(gal) && gal > 0) entry.fuelGallons = gal;
    if (!isNaN(ppg) && ppg > 0) entry.fuelPricePerGallon = ppg;
  }

  try {
    const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
    await addDoc(collection(db, 'tenants', tenant, 'costEntries'), entry);
    window.showToast?.('Cost entry saved', 'success');
    closeAddCostModal();
  } catch (err) {
    console.error('[BI] Error saving cost entry:', err);
    window.showToast?.('Error saving cost entry', 'error');
  }
}

async function deleteCostEntry(entryId) {
  if (!confirm('Delete this cost entry?')) return;
  try {
    const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
    await deleteDoc(doc(db, 'tenants', tenant, 'costEntries', entryId));
    window.showToast?.('Cost entry deleted', 'success');
  } catch (err) {
    console.error('[BI] Error deleting cost entry:', err);
    // TASK-9947 — firestore.rules keep a refund, and an expense that has a
    // refund recorded against it; say so instead of a bare error.
    const e = (BiState.costEntries || []).find(x => x && x.id === entryId) || {};
    const kept = err?.code === 'permission-denied' && (e.entryType === 'credit' || (BiState.costEntries || []).some(x => x && x.creditOf === entryId));
    window.showToast?.(kept ? 'This cost has a refund recorded against it (or is one), so it is kept. Reject the refund in AP instead.' : 'Error deleting entry', 'error');
  }
}

// ============================================
// RESERVE SETTINGS
// ============================================
function showReserveSettings() {
  const typeList = window.getTypeList ? window.getTypeList('truckTypes') : [
    { slug: 'semi', label: 'Semi' },
    { slug: 'vac', label: 'Vac Truck' }
  ];

  const reserves = BiState.biConfig.reserves || {};
  const defaultPct = reserves._default ?? 12;

  const rows = typeList.map(t => {
    const pct = reserves[t.slug] ?? defaultPct;
    return `<div style="display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid var(--border-light);">
      <span style="font-size:14px;color:var(--text-primary);">${escBI(t.label)}</span>
      <div style="display:flex;align-items:center;gap:4px;">
        <input type="number" min="0" max="50" value="${pct}" data-slug="${escBI(t.slug)}" class="bi-reserve-input" style="width:60px;padding:6px 8px;border:1px solid var(--border-light);border-radius:6px;font-size:14px;text-align:center;">
        <span style="font-size:13px;color:var(--text-secondary);">%</span>
      </div>
    </div>`;
  }).join('');

  // Create inline modal
  const existing = document.getElementById('bi-reserve-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'bi-reserve-modal';
  modal.className = 'modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:420px;">
      <div class="modal-header">
        <h3>Maintenance Reserve %</h3>
        <button class="btn-icon" onclick="window.BIModule.closeReserveSettings()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="padding:16px 24px;">
        <p style="font-size:13px;color:var(--text-secondary);margin:0 0 12px;">Set the target maintenance reserve as a percentage of revenue per vehicle type.</p>
        <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid var(--border-light);">
          <span style="font-size:14px;font-weight:600;color:var(--text-primary);">Default (all types)</span>
          <div style="display:flex;align-items:center;gap:4px;">
            <input type="number" min="0" max="50" value="${defaultPct}" data-slug="_default" class="bi-reserve-input" style="width:60px;padding:6px 8px;border:1px solid var(--border-light);border-radius:6px;font-size:14px;text-align:center;">
            <span style="font-size:13px;color:var(--text-secondary);">%</span>
          </div>
        </div>
        ${rows}
      </div>
      <div class="form-actions" style="display:flex;justify-content:flex-end;gap:8px;">
        <button class="btn btn-secondary" onclick="window.BIModule.closeReserveSettings()">Cancel</button>
        <button class="btn btn-primary" onclick="window.BIModule.saveReserveSettings()">
          <span class="material-icons">save</span> Save
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

// ============================================
// LABOR BURDEN SETTINGS (tenant-level job-cost knobs)
// ============================================
// Lives on tenants/{id}/settings/general.biConfig, next to the maintenance
// reserves. Default burden is 1.0 — see resolveBurden() in
// jcLaborRateCoreFactory (shared/jobCost.js) for why this is opt-in and not an industry guess.
function showLaborBurdenSettings() {
  const cfg = BiState.biConfig || {};
  const burden = _BiLaborRate.resolveBurden(cfg);
  const salaryHours = _BiLaborRate.resolveSalaryHours(cfg);

  const existing = document.getElementById('bi-burden-modal');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.id = 'bi-burden-modal';
  modal.className = 'modal';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;">
      <div class="modal-header">
        <h3>Labor Cost Basis</h3>
        <button class="btn-icon" onclick="window.BIModule.closeLaborBurdenSettings()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="padding:16px 24px;">
        <p style="font-size:13px;color:var(--text-secondary);margin:0 0 4px;font-weight:600;">Labor burden</p>
        <p style="font-size:12px;color:var(--text-secondary);margin:0 0 10px;line-height:1.5;">
          An hour of crew costs more than the wage — employer payroll taxes, workers' comp,
          benefits and PTO on top. Enter that as a percent over base and every job-cost report
          uses it. Leave it at 0 and reports show the wage only, which understates what the
          work actually cost. Industry burden runs roughly 30–45% for field labor and higher
          in trucking, where workers' comp is expensive. Use your own number, not the range.
        </p>
        <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid var(--border-light);">
          <span style="font-size:14px;font-weight:600;color:var(--text-primary);">Burden over base wage</span>
          <div style="display:flex;align-items:center;gap:4px;">
            <input type="number" id="bi-burden-pct" min="0" max="200" step="1" value="${burden.pct}" style="width:70px;padding:6px 8px;border:1px solid var(--border-light);border-radius:6px;font-size:14px;text-align:center;">
            <span style="font-size:13px;color:var(--text-secondary);">%</span>
          </div>
        </div>
        <p style="font-size:13px;color:var(--text-secondary);margin:16px 0 4px;font-weight:600;">Salaried hours</p>
        <p style="font-size:12px;color:var(--text-secondary);margin:0 0 10px;line-height:1.5;">
          Salaried people are costed at annual salary ÷ productive hours, and only for hours
          clocked to a job — unassigned hours stay in overhead. 2,080 is 40h × 52 weeks; drop
          it to about 1,880 if you want PTO and holidays taken out first.
        </p>
        <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid var(--border-light);">
          <span style="font-size:14px;font-weight:600;color:var(--text-primary);">Productive hours / year</span>
          <div style="display:flex;align-items:center;gap:4px;">
            <input type="number" id="bi-salary-hours" min="500" max="8760" step="10" value="${salaryHours}" style="width:80px;padding:6px 8px;border:1px solid var(--border-light);border-radius:6px;font-size:14px;text-align:center;">
            <span style="font-size:13px;color:var(--text-secondary);">h</span>
          </div>
        </div>
        <p style="font-size:11px;color:var(--text-secondary);margin:14px 0 0;line-height:1.5;">
          Internal reporting only. This never changes a rate on a ticket or an invoice.
        </p>
      </div>
      <div class="form-actions" style="display:flex;justify-content:flex-end;gap:8px;">
        <button class="btn btn-secondary" onclick="window.BIModule.closeLaborBurdenSettings()">Cancel</button>
        <button class="btn btn-primary" onclick="window.BIModule.saveLaborBurdenSettings()">
          <span class="material-icons">save</span> Save
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

function closeLaborBurdenSettings() {
  const modal = document.getElementById('bi-burden-modal');
  if (modal) modal.remove();
}

async function saveLaborBurdenSettings() {
  const D = _BiLaborRate.DEFAULTS;
  const rawPct = parseFloat(document.getElementById('bi-burden-pct')?.value);
  const rawHours = parseFloat(document.getElementById('bi-salary-hours')?.value);
  const laborBurdenPct = (Number.isFinite(rawPct) && rawPct > 0) ? Math.min(rawPct, D.maxBurdenPct) : 0;
  const salaryProductiveHours = (Number.isFinite(rawHours) && rawHours >= D.minSalaryHours && rawHours <= D.maxSalaryHours)
    ? rawHours : D.salaryProductiveHours;

  BiState.biConfig = { ...BiState.biConfig, laborBurdenPct, salaryProductiveHours };

  try {
    const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
    await setDoc(doc(db, 'tenants', tenant, 'settings', 'general'), {
      biConfig: { laborBurdenPct, salaryProductiveHours }
    }, { merge: true });
    window.showToast?.('Labor cost basis saved', 'success');
  } catch (e) {
    console.error('[BI] Save labor burden error:', e);
    window.showToast?.('Error saving settings', 'error');
  }

  closeLaborBurdenSettings();
  _renderActiveTabNow(); // user saved settings — instant paint
}

// Plain-English one-liner for the P&L, so the labor number never sits there
// unlabelled. A wage-only figure is a defensible number; an unlabelled one is
// not, which is the whole reason the burden default is 1.0.
// The Labor Cost Basis write lands on tenants/{id}/settings/general, and
// firestore.rules:6012 gates that document `allow write: if isTenantAdmin`.
// A BI-module manager who is not an admin could open the modal, type a burden
// percentage and press Save straight into a permission-denied — COD's two code
// lanes both landed on this as the concrete Monday failure. Hide the control
// for them instead. Mirrors hr.js `_hrIsOwnerOrAdmin`: no member doc means
// owner/legacy, which is full access.
function _biIsOwnerOrAdmin() {
  const m = window.AppState?.memberAccess;
  if (!m) return true;
  if (['revoked', 'inactive', 'pending'].includes(m.status)) return false;
  return m.role === 'owner' || m.role === 'admin';
}

// 🔴 The refusal. When the viewer cannot read comp for people who actually
// clocked hours in this period, their labor is MISSING from COGS and gross
// margin is overstated. Say it in red, above the basis note, with the number
// of people and hours — never let the statement imply the labor was free.
function _biCompDeniedBanner(rates, entries) {
  const impact = _BiLaborRate.compDeniedImpact(entries, rates);
  if (!impact.people) return '';
  const hrs = Math.round(impact.hours).toLocaleString('en-US');
  const who = impact.people === 1 ? '1 person' : (impact.people + ' people');
  return '<div style="display:flex;align-items:flex-start;gap:8px;padding:10px 12px;margin:8px 0 2px;background:#FEF2F2;border:1px solid #FCA5A5;border-radius:8px;font-size:11px;line-height:1.5;color:#991B1B;">'
    + '<span class="material-icons" style="font-size:14px;line-height:1.4;">error_outline</span>'
    + '<span><strong>Labor cost is incomplete — this margin is overstated.</strong> '
    + escBI(who + ' clocked ' + hrs + ' hours in this period whose pay rate your account cannot read, '
      + 'so those hours are costed at $0. Compensation lives in an admin-gated collection; ask an owner '
      + 'for the Workforce or HR module to see the real number.')
    + '</span></div>';
}

function _biLaborBasisNote(rates) {
  if (!rates) return '';
  const b = rates.burden;
  const hrs = Math.round(rates.salaryHours).toLocaleString('en-US');
  const lead = b.isDefault
    ? 'Labor is priced at wage only — employer payroll taxes, workers\u2019 comp and benefits are NOT included, so this line understates true cost.'
    : ('Labor is priced at wage + ' + b.pct + '% burden (employer taxes, workers\u2019 comp, benefits).');
  return lead + ' Salaried hours clocked to a job are costed at salary \u00F7 ' + hrs + ' h/yr; unassigned salaried hours stay in overhead.';
}

function closeReserveSettings() {
  const modal = document.getElementById('bi-reserve-modal');
  if (modal) modal.remove();
}

async function saveReserveSettings() {
  const inputs = document.querySelectorAll('.bi-reserve-input');
  const reserves = {};
  inputs.forEach(inp => {
    const slug = inp.dataset.slug;
    const val = parseInt(inp.value, 10);
    if (!isNaN(val) && val >= 0 && val <= 50) reserves[slug] = val;
  });

  BiState.biConfig.reserves = reserves;

  try {
    const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
    await setDoc(doc(db, 'tenants', tenant, 'settings', 'general'), {
      biConfig: { reserves }
    }, { merge: true });
    window.showToast?.('Reserve settings saved', 'success');
  } catch (e) {
    console.error('[BI] Save reserve settings error:', e);
    window.showToast?.('Error saving settings', 'error');
  }

  closeReserveSettings();
  _renderActiveTabNow(); // user saved settings — instant paint
}

// ============================================
// VISUAL BI TAB
// ============================================
function escBI(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// TASK-9344 — escBI above is a TEXT escaper and does not touch apostrophes, so it
// is never correct inside `onclick="window.BIModule.fn('${…}')"`. That position
// (a JS string inside a double-quoted attribute) takes escJsAttr: delegating
// wrapper for the shared one in sharedFormat.js (TASK-4838), hoisted, window
// lookup deferred to call time (Pitfall #1 — never import sharedFormat).
function escJsAttr(str) {
  return window.escJsAttr(str);
}

let visualCategory = 'people';
const VISUAL_CATEGORIES = [
  { key: 'people', icon: 'group', label: 'People' },
  { key: 'vehicles', icon: 'local_shipping', label: 'Vehicles' },
  { key: 'trailers', icon: 'rv_hookup', label: 'Trailers' },
  { key: 'assets', icon: 'inventory_2', label: 'Assets' },
  { key: 'customers', icon: 'business', label: 'Customers' },
  { key: 'vendors', icon: 'store', label: 'Vendors' }
];

const AVATAR_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#EC4899', '#F97316'];

function renderVisualTab() {
  const el = document.getElementById('bi-tab-visual');
  if (!el) return;

  const filter = (el.querySelector('#bi-visual-search')?.value || '').toLowerCase();

  // Build pill counts
  const counts = {
    people: BiState.people.length,
    vehicles: BiState.trucks.length,
    trailers: BiState.trailers.length,
    assets: BiState.assets.length,
    customers: BiState.customers.filter(c => c.type !== 'vendor' && c.type !== 'subcontractor').length,
    vendors: BiState.customers.filter(c => c.type === 'vendor' || c.type === 'subcontractor').length
  };

  el.innerHTML = `
    <div style="max-width:400px;margin:0 auto 16px auto;">
      <div style="position:relative;">
        <span class="material-icons" style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--text-secondary);font-size:18px;">search</span>
        <input type="text" id="bi-visual-search" class="form-control" placeholder="Search ${VISUAL_CATEGORIES.find(c => c.key === visualCategory)?.label.toLowerCase() || ''}..." value="${escBI(filter)}" oninput="window.BIModule.filterVisualCards()" style="padding-left:38px;border-radius:10px;">
      </div>
    </div>
    <div class="bi-visual-pills">
      ${VISUAL_CATEGORIES.map(c => `
        <button class="bi-visual-pill ${visualCategory === c.key ? 'active' : ''}" onclick="window.BIModule.switchVisualCategory('${escJsAttr(c.key)}')">
          <span class="material-icons" style="font-size:16px;">${c.icon}</span>
          ${c.label}
          <span class="bi-visual-pill-count">${counts[c.key]}</span>
        </button>
      `).join('')}
    </div>
    <div id="bi-visual-chart">${renderVisualChart()}</div>
    <div class="bi-visual-grid" id="bi-visual-grid">
      ${renderVisualCards(filter)}
    </div>
  `;

}

function renderVisualChart() {
  if (visualCategory === 'people') {
    // Crew Hours column bar chart
    const periodClock = BiState.clockEntries.filter(e => isInPeriod(e.clockIn));
    const hoursByPerson = {};
    periodClock.forEach(e => {
      const name = e.personName || e.personId || 'Unknown';
      const start = toDate(e.clockIn);
      const end = toDate(e.clockOut);
      if (start && end) {
        hoursByPerson[name] = (hoursByPerson[name] || 0) + ((end - start) / (1000 * 60 * 60));
      }
    });
    const crewEntries = Object.entries(hoursByPerson).sort((a, b) => b[1] - a[1]).slice(0, 10);
    if (crewEntries.length === 0) return '';
    const maxHrs = crewEntries[0][1] || 1;
    return `
      <div class="crm-section-card" style="margin-bottom:16px;padding:12px 16px;">
        <div class="crm-section-title" style="font-size:13px;margin-bottom:12px;">Crew Hours (Clock)</div>
        <div style="display:flex;flex-direction:column;gap:8px;">
          ${crewEntries.map(([name, hrs]) => {
            const pct = Math.max(3, (hrs / maxHrs) * 100);
            const firstName = name.split(' ')[0];
            return `<div style="display:flex;align-items:center;gap:10px;">
              <div style="width:60px;font-size:12px;font-weight:500;color:var(--text-secondary);text-align:right;flex-shrink:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escBI(firstName)}</div>
              <div style="flex:1;height:22px;background:var(--bg-secondary);border-radius:6px;overflow:hidden;">
                <div style="height:100%;width:${pct}%;background:linear-gradient(90deg,var(--brand-blue),var(--accent-teal));border-radius:6px;min-width:8px;"></div>
              </div>
              <div style="width:36px;font-size:12px;font-weight:600;color:var(--text-primary);text-align:right;flex-shrink:0;">${hrs.toFixed(0)}h</div>
            </div>`;
          }).join('')}
        </div>
      </div>`;
  }

  if (visualCategory === 'customers') {
    // Revenue by Customer column bar chart
    const customers = BiState.customers.filter(c => c.type !== 'vendor' && c.type !== 'subcontractor');
    const pMode = BiState.visualCardPeriod.customers || 'ytd';
    const { start, end } = getCardPeriodRange(pMode);
    const custRevenue = customers.map(c => ({
      name: c.name || 'Unknown',
      rev: getCustomerRevenueInRange(c.id, start, end)
    })).filter(c => c.rev > 0).sort((a, b) => b.rev - a.rev).slice(0, 12);
    if (custRevenue.length === 0) return '';
    const maxRev = custRevenue[0].rev || 1;
    return `
      <div class="crm-section-card" style="margin-bottom:16px;padding:12px 16px;">
        <div class="crm-section-title" style="font-size:13px;margin-bottom:8px;">Revenue by Customer (${CARD_PERIOD_LABELS[pMode]})</div>
        <div style="display:flex;align-items:flex-end;gap:4px;height:120px;">
          ${custRevenue.map(c => {
            const pct = Math.max(4, (c.rev / maxRev) * 100);
            const shortName = c.name.length > 10 ? c.name.substring(0, 9) + '...' : c.name;
            return `<div style="flex:1;display:flex;flex-direction:column;align-items:center;min-width:0;">
              <div style="font-size:10px;font-weight:600;color:var(--text-primary);margin-bottom:2px;">${formatCompactCurrency(c.rev)}</div>
              <div style="width:100%;max-width:36px;height:${pct}%;background:#10B981;border-radius:4px 4px 0 0;min-height:3px;"></div>
              <div style="font-size:8px;color:var(--text-secondary);margin-top:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:56px;text-align:center;" title="${escBI(c.name)}">${escBI(shortName)}</div>
            </div>`;
          }).join('')}
        </div>
      </div>`;
  }

  if (visualCategory === 'vehicles') {
    // Maintenance Cost by Category
    const periodMaint = BiState.maintenance.filter(m => isInPeriod(m.createdAt));
    const costByCategory = {};
    periodMaint.forEach(m => {
      const cost = m.costs?.totalCost || 0;
      if (cost <= 0) return; // skip zero-cost tasks
      const cat = m.category || 'Other';
      costByCategory[cat] = (costByCategory[cat] || 0) + cost;
    });
    const catEntries = Object.entries(costByCategory).sort((a, b) => b[1] - a[1]);
    if (catEntries.length === 0) return '';
    const maxCat = catEntries[0][1] || 1;
    return `
      <div class="crm-section-card" style="margin-bottom:16px;padding:12px 16px;">
        <div class="crm-section-title" style="font-size:13px;margin-bottom:8px;">Maintenance Cost by Category</div>
        ${catEntries.map(([cat, cost]) => horizontalBar(cat, cost, maxCat, '#F59E0B')).join('')}
      </div>`;
  }

  return '';
}

function switchVisualCategory(cat) {
  visualCategory = cat;
  renderVisualTab();
}

function filterVisualCards() {
  const el = document.getElementById('bi-tab-visual');
  const filter = (el?.querySelector('#bi-visual-search')?.value || '').toLowerCase();
  const grid = document.getElementById('bi-visual-grid');
  if (grid) grid.innerHTML = renderVisualCards(filter);
}

function renderVisualCards(filter) {
  switch (visualCategory) {
    case 'people': return renderVisualPeople(filter);
    case 'vehicles': return renderVisualTrucks(filter);
    case 'trailers': return renderVisualTrailers(filter);
    case 'assets': return renderVisualAssets(filter);
    case 'customers': return renderVisualCustomers(filter);
    case 'vendors': return renderVisualVendors(filter);
    default: return '';
  }
}

function getInitialsBI(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : (parts[0][0] || '?').toUpperCase();
}

function getUnitDisplayBI(item) {
  return window.getUnitDisplay ? window.getUnitDisplay(item) : `#${item.unit || '?'}`;
}

function avatarColor(name) {
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// Type-based color palette for visual variety
const TYPE_COLORS = {
  // Vehicle types
  'trucking': { bg: '#3B82F6', accent: '#3B82F6' },
  'hydrovac': { bg: '#06B6D4', accent: '#06B6D4' },
  'pickup': { bg: '#F59E0B', accent: '#F59E0B' },
  'hotshot': { bg: '#EF4444', accent: '#EF4444' },
  'service': { bg: '#8B5CF6', accent: '#8B5CF6' },
  'winch': { bg: '#EC4899', accent: '#EC4899' },
  // Trailer types
  'flatbed': { bg: '#6366F1', accent: '#6366F1' },
  'step deck': { bg: '#8B5CF6', accent: '#8B5CF6' },
  'stepdeck': { bg: '#8B5CF6', accent: '#8B5CF6' },
  'side dump': { bg: '#F97316', accent: '#F97316' },
  'sidedump': { bg: '#F97316', accent: '#F97316' },
  'gooseneck': { bg: '#14B8A6', accent: '#14B8A6' },
  'booster': { bg: '#EC4899', accent: '#EC4899' },
  'scissor neck': { bg: '#EF4444', accent: '#EF4444' },
  'scissorneck': { bg: '#EF4444', accent: '#EF4444' },
  'jeep': { bg: '#84CC16', accent: '#84CC16' },
  'lowboy': { bg: '#A855F7', accent: '#A855F7' },
  // Crew roles
  'owner': { bg: '#D97706', accent: '#D97706' },
  'cdl driver': { bg: '#3B82F6', accent: '#3B82F6' },
  'driver': { bg: '#3B82F6', accent: '#3B82F6' },
  'laborer': { bg: '#10B981', accent: '#10B981' },
  'swamper': { bg: '#14B8A6', accent: '#14B8A6' },
  'mechanic': { bg: '#F59E0B', accent: '#F59E0B' },
  'admin': { bg: '#8B5CF6', accent: '#8B5CF6' },
  'supervisor': { bg: '#EC4899', accent: '#EC4899' },
  'manager': { bg: '#EC4899', accent: '#EC4899' },
  'operator': { bg: '#06B6D4', accent: '#06B6D4' },
};
function getTypeColor(type) {
  if (!type) return { bg: '#64748B', accent: '#94A3B8' };
  const key = type.toLowerCase().trim();
  return TYPE_COLORS[key] || { bg: '#64748B', accent: '#94A3B8' };
}

function formatCompactCurrency(val) {
  if (val >= 1000) return '$' + (val / 1000).toFixed(val >= 10000 ? 0 : 1) + 'K';
  return '$' + Math.round(val).toLocaleString();
}

// ============================================
// VISUAL CARD PERIOD HELPERS (YTD / MTD / WTD)
// ============================================
const CARD_PERIODS = ['ytd', 'mtd', 'wtd'];
const CARD_PERIOD_LABELS = { ytd: 'YTD', mtd: 'MTD', wtd: 'WTD' };

function getCardPeriodRange(mode) {
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  let start;
  switch (mode) {
    case 'wtd': {
      const day = now.getDay();
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day);
      break;
    }
    case 'mtd':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'ytd':
    default:
      start = new Date(now.getFullYear(), 0, 1);
      break;
  }
  return { start, end };
}

function getPrevPeriodRange(mode) {
  const now = new Date();
  switch (mode) {
    case 'mom': {
      // Previous month same date range
      const thisStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const prevStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const prevEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59); // last day of prev month
      return { start: prevStart, end: prevEnd, curStart: thisStart, curEnd: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59) };
    }
    case 'yoy': {
      // Same YTD range last year
      const curStart = new Date(now.getFullYear(), 0, 1);
      const curEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
      const prevStart = new Date(now.getFullYear() - 1, 0, 1);
      const prevEnd = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate(), 23, 59, 59);
      return { start: prevStart, end: prevEnd, curStart: curStart, curEnd: curEnd };
    }
    default:
      return null;
  }
}

function getPersonCostInRange(personId, start, end) {
  // TASK-1481 — comp-first resolution; null = "cannot know" (denied + no
  // legacy fields), which the card renders as an explicit no-access state
  // instead of a silent $0 labor cost.
  const rawPerson = BiState.people.find(p => p.id === personId);
  if (!rawPerson) return 0;
  if (_biCompUnknown(rawPerson)) return null;
  const person = _biComp(rawPerson);
  const clockEntries = (BiState.clockEntries || []).filter(c =>
    c.personId === personId && c.status === 'completed'
  );
  let totalHours = 0;
  clockEntries.forEach(c => {
    const d = c.clockIn?.toDate?.() || new Date(c.clockIn);
    if (d >= start && d <= end) totalHours += (c.totalHours || 0);
  });
  const rate = _BiLaborRate.laborRate(person);
  if (person.hrEmploymentType === 'salary' && person.hrAnnualSalary) {
    // Pro-rate salary to period
    const days = Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    return (Number(person.hrAnnualSalary) / 365) * days;
  }
  return totalHours * rate;
}

// 🔴 A READ WITH NO WRITER — the third one found in BI in one day, same family
// as `costRate` and the cash-basis `paid` flag. This matched `truckId`,
// `trailerId` and `assetId`; the writer (`saveCostEntry`, and the driver-expense
// and reconciliation paths) writes **`vehicleId`**. Measured live 2026-08-23 on
// TITS: 0 of 65 cost entries carry any of the three matched fields, 13 carry
// `vehicleId`. And because the Visual card is gated on `cost > 0` it did not even
// render — so it read as "this truck has no costs", not as a bug.
//
// The old three are KEPT in the match, not replaced: they cost nothing, and if
// any legacy or imported row ever carried one, dropping it would trade a
// silent-zero for a silent-loss.
function getEntityCostInRange(entityId, start, end) {
  return _costViewsOf(BiState.costEntries || []).reduce((sum, c) => { // TASK-9493 split-aware
    const d = c.date?.toDate?.() || new Date(c.date);
    if (d >= start && d <= end) {
      if (c.vehicleId === entityId || c.truckId === entityId || c.trailerId === entityId || c.assetId === entityId) {
        return sum + (c.amount || 0);
      }
    }
    return sum;
  }, 0);
}

// 🔴 SAME FAMILY, WORSE BLAST RADIUS. `revenueEntries` has NO `date` field —
// measured live: 0 of 685 on TITS, while all 685 carry `invoicedAt` and
// `workDate`. `new Date(undefined)` is an Invalid Date, and EVERY comparison
// against an Invalid Date is false, so this returned 0 for every customer,
// every period — $1.5M invisible, and every MoM/YoY badge built on it stuck at
// 0%. The demo seeder does not write `date` either, which is why it never
// surfaced in a demo.
//
// `invoicedAt` first, matching what computePLData's accrual arm recognises, so
// a customer card and the P&L agree about which period a dollar falls in.
// `toDate` is the shared parser — never `new Date()` on a Firestore timestamp.
// The ONE answer to "when did this revenue land". Extracted because COD found a
// FOURTH site reading the non-existent `r.date` (exportIIF_Revenue) after three
// were already fixed — a second inline copy of this chain would have been the
// fifth. `invoicedAt` first, matching computePLData's accrual arm, so a customer
// card, a QuickBooks export and the P&L all agree which period a dollar is in.
function _biRevenueDate(r) {
  if (!r) return null;
  return toDate(r.invoicedAt) || toDate(r.date) || toDate(r.workDate) || toDate(r.createdAt);
}

function getCustomerRevenueInRange(customerId, start, end) {
  return (BiState.revenueEntries || []).reduce((sum, r) => {
    const d = _biRevenueDate(r);
    if (d && d >= start && d <= end && r.customerId === customerId) {
      return sum + (r.amount || 0);
    }
    return sum;
  }, 0);
}

// 🔴 NO TICKET HAS `status === 'invoiced'`. Measured live on TITS: 881 tickets,
// statuses are archived 709 / deleted 114 / draft 48 / ready 10 — the string
// never appears. Meanwhile **602 tickets ARE invoiced** by the canonical test.
// So every BI surface keyed on that status silently saw an empty set: AR aging
// counted no receivable it had actually billed, and the outstanding-work rollups
// showed only drafts. Exactly the drift the `_isPretripPass` comment near the
// top of this file already warns about.
//
// `billing.js:665` owns the real predicate and exports it on `window.BillingDedup`
// (a `?v=` cross-import would give us a SECOND module instance — Pitfall #1).
// We prefer the export and fall back to a verbatim copy, because BI can render
// before billing.js has ever been loaded and a missing predicate must not
// silently re-empty the set. The fallback is byte-identical to the source of
// truth ON PURPOSE — if that one changes, this must change with it.
function _biTicketInvoiced(t) {
  const canonical = window.BillingDedup && window.BillingDedup.isTicketInvoiced;
  if (typeof canonical === 'function') return canonical(t);
  t = t || {};
  return t.invoiced === true || !!t.invoiceNumber || !!t.invoicedAt;
}

function cycleCardPeriod(section, event) {
  if (event) { event.stopPropagation(); event.preventDefault(); }
  const cur = BiState.visualCardPeriod[section] || 'ytd';
  const idx = CARD_PERIODS.indexOf(cur);
  BiState.visualCardPeriod[section] = CARD_PERIODS[(idx + 1) % CARD_PERIODS.length];
  renderVisualTab();
}

function formatPctChange(cur, prev) {
  if (!prev || prev === 0) return cur > 0 ? '+∞' : '—';
  const pct = ((cur - prev) / prev) * 100;
  const sign = pct >= 0 ? '+' : '';
  return `${sign}${pct.toFixed(0)}%`;
}

function pctChangeColor(cur, prev, invertColors) {
  if (!prev || prev === 0) return cur > 0 ? '#10B981' : '#94A3B8';
  const pct = ((cur - prev) / prev) * 100;
  if (invertColors) return pct <= 0 ? '#10B981' : '#EF4444'; // for costs: down is good
  return pct >= 0 ? '#10B981' : '#EF4444'; // for revenue: up is good
}

// --- Placard Renderers ---
function renderVisualPeople(filter) {
  const items = BiState.people.filter(p => !filter || p.name?.toLowerCase().includes(filter) || (p.crewType || p.role || p.roles?.[0] || '').toLowerCase().includes(filter));
  if (!items.length) return emptyVisual('No people found');
  const pMode = BiState.visualCardPeriod.people || 'ytd';
  const pLabel = CARD_PERIOD_LABELS[pMode];
  const { start, end } = getCardPeriodRange(pMode);

  // Pre-compute driver scorecard data
  const periodPretrips = BiState.pretrips.filter(p => isInPeriod(p.createdAt));
  const periodClock = BiState.clockEntries.filter(e => isInPeriod(e.clockIn));

  function renderPersonCard(rawP) {
    const p = _biComp(rawP); // TASK-1481 — pay badges below read comp fields
    const role = p.crewType || p.role || p.roles?.[0] || '';
    const roleLabel = window.getTypeLabel ? window.getTypeLabel(role, 'crewTypes') : role;
    const tc = getTypeColor(role);
    const isOwner = role.toLowerCase() === 'owner';

    // Clock hours + OT
    const driverClock = periodClock.filter(c => c.personId === p.id);
    let totalHours = 0, otHours = 0;
    const weeklyHours = {};
    driverClock.forEach(c => {
      const hrs = c.totalHours || c.hours || 0;
      totalHours += hrs;
      const d = toDate(c.clockIn);
      const wk = d ? _biOtWeekKey(d) : null;
      if (wk) weeklyHours[wk] = (weeklyHours[wk] || 0) + hrs;
    });
    const _otTh = _biOtThreshold();
    Object.values(weeklyHours).forEach(wh => { if (wh > _otTh) otHours += (wh - _otTh); });

    // DVIR count
    const dvirCount = periodPretrips.filter(pt => pt.driverId === p.id).length;

    // Jobs worked
    const driverJobIds = new Set(driverClock.filter(c => c.jobId).map(c => c.jobId));
    const jobsWorked = driverJobIds.size;

    // Revenue attribution
    let revenueAttr = 0;
    const periodRevenue = rentalAwareRevenueEntries(isInPeriod); // TASK-842-C
    periodRevenue.forEach(r => {
      if (r.jobId && driverJobIds.has(r.jobId)) {
        const workersOnJob = periodClock.filter(c => c.jobId === r.jobId).map(c => c.personId);
        const uniqueWorkers = new Set(workersOnJob);
        if (uniqueWorkers.has(p.id) && uniqueWorkers.size > 0) {
          revenueAttr += (r.amount || 0) / uniqueWorkers.size;
        }
      }
    });

    // Cost for selected card period
    const cost = getPersonCostInRange(p.id, start, end);
    return `
    <div class="bi-visual-card" style="border-left:3px solid ${isOwner ? '#D97706' : tc.accent};" onclick="window.BIModule.showPersonAnalytics('${escJsAttr(p.id)}')">
      <div class="bi-visual-card-header">
        <div class="bi-visual-avatar" style="background:${escBI(p.color || avatarColor(p.name))}">${escBI(getInitialsBI(p.name))}</div>
        <div class="bi-visual-card-title">
          <div class="bi-visual-card-name">${escBI(p.name)}</div>
          ${roleLabel ? `<div class="bi-visual-card-subtitle">${escBI(roleLabel)}</div>` : ''}
        </div>
        ${revenueAttr > 0 ? `<div style="text-align:right;font-size:16px;font-weight:700;color:#10B981;">${formatCurrency(revenueAttr)}</div>` : ''}
      </div>
      <div class="bi-visual-card-badges">
        ${_biBadgeSalary(p) ? `<span class="bi-vis-badge" style="background:${tc.accent}15;color:${tc.accent};"><span class="material-icons" style="font-size:12px;">payments</span> $${Number(_biBadgeSalary(p)).toLocaleString()}/yr</span>` : _biBadgeRate(p) ? `<span class="bi-vis-badge" style="background:${tc.accent}15;color:${tc.accent};"><span class="material-icons" style="font-size:12px;">payments</span> $${_biBadgeRate(p).toFixed(2)}/hr</span>` : false ? `<span class="bi-vis-badge" style="background:${tc.accent}15;color:${tc.accent};"><span class="material-icons" style="font-size:12px;">payments</span> $${''}/hr</span>` : ''}
        ${totalHours > 0 ? `<span class="bi-vis-badge" style="background:#10B98115;color:#10B981;"><span class="material-icons" style="font-size:12px;">schedule</span> ${totalHours.toFixed(1)}h</span>` : ''}
        ${otHours > 0 ? `<span class="bi-vis-badge" style="background:#F59E0B15;color:#F59E0B;"><span class="material-icons" style="font-size:12px;">more_time</span> ${otHours.toFixed(1)} OT</span>` : ''}
        ${dvirCount > 0 ? `<span class="bi-vis-badge" style="background:#3B82F615;color:#3B82F6;"><span class="material-icons" style="font-size:12px;">fact_check</span> ${dvirCount} DVIRs</span>` : ''}
        ${jobsWorked > 0 ? `<span class="bi-vis-badge" style="background:#8B5CF615;color:#8B5CF6;"><span class="material-icons" style="font-size:12px;">work</span> ${jobsWorked} jobs</span>` : ''}
      </div>
      ${cost === null ? `<div class="bi-visual-card-metric" title="Labor cost needs HR Directory access">
        <span class="material-icons" style="font-size:14px;color:#94A3B8;">lock</span>
        <span style="font-weight:600;color:#94A3B8;">n/a — needs HR access</span>
      </div>` : cost > 0 ? `<div class="bi-visual-card-metric" onclick="event.stopPropagation();window.BIModule.cycleCardPeriod('people',event)">
        <span class="material-icons" style="font-size:14px;color:#EF4444;">trending_down</span>
        <span style="font-weight:600;color:#EF4444;">${formatCompactCurrency(cost)}</span>
        <span class="bi-metric-period-btn">${pLabel}</span>
      </div>` : ''}
      <div class="bi-visual-card-actions">
        <button class="bi-action-pill blue">
          <span class="material-icons">bar_chart</span> Analytics
        </button>
      </div>
    </div>`;
  }

  // Split owners from crew for visual separation
  const owners = items.filter(p => (p.crewType || p.role || '').toLowerCase() === 'owner');
  const crew = items.filter(p => (p.crewType || p.role || '').toLowerCase() !== 'owner');
  let html = '';
  if (owners.length) {
    html += `<div style="grid-column:1/-1;padding:8px 0 4px;"><span style="font-size:13px;font-weight:600;color:#D97706;">
      <span class="material-icons" style="font-size:16px;vertical-align:middle;">star</span> Owners (${owners.length})</span></div>`;
    html += owners.map(renderPersonCard).join('');
    html += `<div style="grid-column:1/-1;padding:8px 0 4px;border-top:1px solid var(--border-light);margin-top:8px;">
      <span style="font-size:13px;font-weight:600;color:var(--text-secondary);">Crew (${crew.length})</span></div>`;
  }
  html += (crew.length ? crew : items).map(renderPersonCard).join('');
  return html;
}

function renderVisualTrucks(filter) {
  const items = BiState.trucks.filter(t => !filter || (t.unit || '').toLowerCase().includes(filter) || (t.nickname || '').toLowerCase().includes(filter) || (t.make || '').toLowerCase().includes(filter));
  if (!items.length) return emptyVisual('No vehicles found');
  const pMode = BiState.visualCardPeriod.vehicles || 'ytd';
  const pLabel = CARD_PERIOD_LABELS[pMode];
  const { start, end } = getCardPeriodRange(pMode);
  return items.map(t => {
    const typeLabel = window.getTypeLabel ? window.getTypeLabel(t.type, 'truckTypes') : (t.type || 'Truck');
    const hasLoan = t.loan?.enabled;
    const tc = getTypeColor(t.type);
    const cost = getEntityCostInRange(t.id, start, end);
    return `
    <div class="bi-visual-card" style="border-left:3px solid ${tc.accent};" onclick="window.BIModule.showVehicleAnalytics('${escJsAttr(t.id)}')">
      <div class="bi-visual-card-header">
        <div class="bi-visual-avatar icon" style="background:${tc.bg};"><span class="material-icons">local_shipping</span></div>
        <div class="bi-visual-card-title">
          <div class="bi-visual-card-name">${escBI(getUnitDisplayBI(t))}</div>
          ${t.make ? `<div class="bi-visual-card-subtitle">${escBI(t.make)} ${escBI(t.model || '')} ${escBI(t.year || '')}</div>` : ''}
        </div>
      </div>
      <div class="bi-visual-card-badges">
        <span class="bi-vis-badge" style="background:${tc.accent}15;color:${tc.accent};">${escBI(typeLabel)}</span>
        ${t.vin ? `<span class="bi-vis-badge"><span class="material-icons" style="font-size:12px;">pin</span> ${escBI(t.vin.slice(-6))}</span>` : ''}
        ${hasLoan ? `<span class="bi-vis-badge green"><span class="material-icons" style="font-size:12px;">account_balance</span> Loan</span>` : ''}
        ${t.licensePlate ? `<span class="bi-vis-badge">${escBI(t.licensePlate)}</span>` : ''}
      </div>
      ${cost > 0 ? `<div class="bi-visual-card-metric" onclick="event.stopPropagation();window.BIModule.cycleCardPeriod('vehicles',event)">
        <span class="material-icons" style="font-size:14px;color:#EF4444;">trending_down</span>
        <span style="font-weight:600;color:#EF4444;">${formatCompactCurrency(cost)}</span>
        <span class="bi-metric-period-btn">${pLabel}</span>
      </div>` : ''}
      <div class="bi-visual-card-actions">
        <button class="bi-action-pill blue">
          <span class="material-icons">bar_chart</span> Analytics
        </button>
        ${!hasLoan ? `<button class="bi-action-pill teal" onclick="event.stopPropagation();window.BIModule.editLoanInfo('${escJsAttr(t.id)}','truck')">
          <span class="material-icons">add</span> Loan Info
        </button>` : ''}
      </div>
    </div>`;
  }).join('');
}

function renderVisualTrailers(filter) {
  const items = BiState.trailers.filter(t => !filter || (t.unit || '').toLowerCase().includes(filter) || (t.nickname || '').toLowerCase().includes(filter));
  if (!items.length) return emptyVisual('No trailers found');
  const pMode = BiState.visualCardPeriod.trailers || 'ytd';
  const pLabel = CARD_PERIOD_LABELS[pMode];
  const { start, end } = getCardPeriodRange(pMode);
  return items.map(t => {
    const typeLabel = window.getTypeLabel ? window.getTypeLabel(t.type, 'trailerTypes') : (t.type || 'Trailer');
    const hasLoan = t.loan?.enabled;
    const tc = getTypeColor(t.type);
    const cost = getEntityCostInRange(t.id, start, end);
    return `
    <div class="bi-visual-card" style="border-left:3px solid ${tc.accent};" onclick="window.BIModule.showTrailerAnalytics('${escJsAttr(t.id)}')">
      <div class="bi-visual-card-header">
        <div class="bi-visual-avatar icon" style="background:${tc.bg};"><span class="material-icons">rv_hookup</span></div>
        <div class="bi-visual-card-title">
          <div class="bi-visual-card-name">${escBI(getUnitDisplayBI(t))}</div>
          ${t.make ? `<div class="bi-visual-card-subtitle">${escBI(t.make)} ${escBI(t.model || '')}</div>` : ''}
        </div>
      </div>
      <div class="bi-visual-card-badges">
        <span class="bi-vis-badge" style="background:${tc.accent}15;color:${tc.accent};">${escBI(typeLabel)}</span>
        ${hasLoan ? `<span class="bi-vis-badge green"><span class="material-icons" style="font-size:12px;">account_balance</span> Loan</span>` : ''}
        ${t.licensePlate ? `<span class="bi-vis-badge">${escBI(t.licensePlate)}</span>` : ''}
      </div>
      ${cost > 0 ? `<div class="bi-visual-card-metric" onclick="event.stopPropagation();window.BIModule.cycleCardPeriod('trailers',event)">
        <span class="material-icons" style="font-size:14px;color:#EF4444;">trending_down</span>
        <span style="font-weight:600;color:#EF4444;">${formatCompactCurrency(cost)}</span>
        <span class="bi-metric-period-btn">${pLabel}</span>
      </div>` : ''}
      <div class="bi-visual-card-actions">
        <button class="bi-action-pill blue">
          <span class="material-icons">bar_chart</span> Analytics
        </button>
        ${!hasLoan ? `<button class="bi-action-pill teal" onclick="event.stopPropagation();window.BIModule.editLoanInfo('${escJsAttr(t.id)}','trailer')">
          <span class="material-icons">add</span> Loan Info
        </button>` : ''}
      </div>
    </div>`;
  }).join('');
}

function renderVisualAssets(filter) {
  const items = BiState.assets.filter(a => !filter || (a.name || '').toLowerCase().includes(filter) || (a.partNumber || '').toLowerCase().includes(filter) || (a.category || '').toLowerCase().includes(filter));
  if (!items.length) return emptyVisual('No assets found');
  const pMode = BiState.visualCardPeriod.assets || 'ytd';
  const pLabel = CARD_PERIOD_LABELS[pMode];
  const { start, end } = getCardPeriodRange(pMode);
  return items.map(a => {
    const catLabel = window.getTypeLabel ? window.getTypeLabel(a.category, 'assetCategories') : (a.category || 'Asset');
    const hasLoan = a.loan?.enabled;
    const cost = getEntityCostInRange(a.id, start, end);
    return `
    <div class="bi-visual-card" onclick="window.BIModule.showAssetAnalytics('${escJsAttr(a.id)}')">
      <div class="bi-visual-card-header">
        <div class="bi-visual-avatar icon" style="background:linear-gradient(135deg,#F59E0B,#F97316);"><span class="material-icons">inventory_2</span></div>
        <div class="bi-visual-card-title">
          <div class="bi-visual-card-name">${escBI(a.name)}</div>
          ${a.partNumber ? `<div class="bi-visual-card-subtitle">${escBI(a.partNumber)}</div>` : ''}
        </div>
      </div>
      <div class="bi-visual-card-badges">
        <span class="bi-vis-badge amber">${escBI(catLabel)}</span>
        ${a.trackingMode === 'quantity' ? `<span class="bi-vis-badge"><span class="material-icons" style="font-size:12px;">inventory</span> Qty: ${a.quantity || 0}</span>` : ''}
        ${(a.quantityType === 'rental' || a.isRental) ? (() => {
          const deployed = a.deployedQty || 0;
          const total = a.trackingMode === 'quantity' ? (a.quantity || 0) : 1;
          const available = total - deployed;
          // TASK-4810 — more out than owned: the available count is unknown, so
          // say "needs recount" instead of printing "3765 out / -2168 avail".
          if (deployed > total) {
            return `<span class="bi-vis-badge amber" title="${escBI(`${deployed} out against ${total} owned — recount in Roster › Assets`)}"><span class="material-icons" style="font-size:12px;">rule</span> ${deployed} out · needs recount</span>`;
          }
          return `<span class="bi-vis-badge blue"><span class="material-icons" style="font-size:12px;">output</span> ${deployed} out / ${available} avail</span>`;
        })() : ''}
        ${a.hoursTracking?.enabled ? `<span class="bi-vis-badge"><span class="material-icons" style="font-size:12px;">schedule</span> ${(a.hoursTracking.currentHours || 0).toFixed(0)} hrs</span>` : ''}
        ${hasLoan ? `<span class="bi-vis-badge green"><span class="material-icons" style="font-size:12px;">account_balance</span> Loan</span>` : ''}
      </div>
      ${cost > 0 ? `<div class="bi-visual-card-metric" onclick="event.stopPropagation();window.BIModule.cycleCardPeriod('assets',event)">
        <span class="material-icons" style="font-size:14px;color:#EF4444;">trending_down</span>
        <span style="font-weight:600;color:#EF4444;">${formatCompactCurrency(cost)}</span>
        <span class="bi-metric-period-btn">${pLabel}</span>
      </div>` : ''}
      <div class="bi-visual-card-actions">
        <button class="bi-action-pill blue">
          <span class="material-icons">bar_chart</span> Analytics
        </button>
        ${!hasLoan ? `<button class="bi-action-pill teal" onclick="event.stopPropagation();window.BIModule.editLoanInfo('${escJsAttr(a.id)}','asset')">
          <span class="material-icons">add</span> Loan Info
        </button>` : ''}
      </div>
    </div>`;
  }).join('');
}

function renderVisualCustomers(filter) {
  const items = BiState.customers.filter(c => c.type !== 'vendor' && c.type !== 'subcontractor' && (!filter || (c.name || '').toLowerCase().includes(filter)));
  if (!items.length) return emptyVisual('No customers found');
  const pMode = BiState.visualCardPeriod.customers || 'ytd';
  const pLabel = CARD_PERIOD_LABELS[pMode];
  const { start, end } = getCardPeriodRange(pMode);
  // MoM and YoY ranges
  const mom = getPrevPeriodRange('mom');
  const yoy = getPrevPeriodRange('yoy');
  const statusColors = { active: '#10B981', lead: '#F59E0B', prospect: '#3B82F6', inactive: '#94A3B8' };
  return items.map(c => {
    const st = (c.status || 'active').toLowerCase();
    const sc = statusColors[st] || '#64748B';
    const rev = getCustomerRevenueInRange(c.id, start, end);
    const ac = avatarColor(c.name);
    // MoM: this month vs last month
    const momCur = getCustomerRevenueInRange(c.id, mom.curStart, mom.curEnd);
    const momPrev = getCustomerRevenueInRange(c.id, mom.start, mom.end);
    const momPct = formatPctChange(momCur, momPrev);
    const momColor = pctChangeColor(momCur, momPrev, false);
    // YoY: this YTD vs last year same period
    const yoyCur = getCustomerRevenueInRange(c.id, yoy.curStart, yoy.curEnd);
    const yoyPrev = getCustomerRevenueInRange(c.id, yoy.start, yoy.end);
    const yoyPct = formatPctChange(yoyCur, yoyPrev);
    const yoyColor = pctChangeColor(yoyCur, yoyPrev, false);
    return `
    <div class="bi-visual-card" style="border-left:3px solid ${ac};" onclick="window.BIModule.showCustomerAnalytics('${escJsAttr(c.id)}')">
      <div class="bi-visual-card-header">
        <div class="bi-visual-avatar" style="background:${ac};color:#fff;font-weight:700;font-size:13px;">${escBI(getInitialsBI(c.name))}</div>
        <div class="bi-visual-card-title">
          <div class="bi-visual-card-name">${escBI(c.name)}</div>
          ${c.contactName ? `<div class="bi-visual-card-subtitle">${escBI(c.contactName)}</div>` : ''}
        </div>
      </div>
      <div class="bi-visual-card-badges">
        <span class="bi-vis-badge" style="background:${sc}15;color:${sc};">${st}</span>
      </div>
      ${rev > 0 || momCur > 0 || yoyCur > 0 ? `<div class="bi-visual-card-metric" onclick="event.stopPropagation();window.BIModule.cycleCardPeriod('customers',event)">
        <span class="material-icons" style="font-size:14px;color:#10B981;">trending_up</span>
        <span style="font-weight:600;color:#10B981;">${formatCompactCurrency(rev)}</span>
        <span class="bi-metric-period-btn">${pLabel}</span>
        ${momPrev > 0 || momCur > 0 ? `<span class="bi-metric-compare" style="color:${momColor};">MoM ${momPct}</span>` : ''}
        ${yoyPrev > 0 || yoyCur > 0 ? `<span class="bi-metric-compare" style="color:${yoyColor};">YoY ${yoyPct}</span>` : ''}
      </div>` : ''}
      <div class="bi-visual-card-actions">
        <button class="bi-action-pill blue">
          <span class="material-icons">bar_chart</span> Analytics
        </button>
      </div>
    </div>`;
  }).join('');
}

function renderVisualVendors(filter) {
  const items = BiState.customers.filter(c => (c.type === 'vendor' || c.type === 'subcontractor') && (!filter || (c.name || '').toLowerCase().includes(filter)));
  if (!items.length) return emptyVisual('No vendors or subcontractors found');
  return items.map(v => {
    const isSub = v.type === 'subcontractor';
    const color = isSub ? '#F59E0B' : '#8B5CF6';
    const icon = isSub ? 'engineering' : 'store';
    const label = isSub ? 'Subcontractor' : 'Vendor';
    return `
    <div class="bi-visual-card" style="border-left:3px solid ${color};" onclick="window.BIModule.showVendorAnalytics('${escJsAttr(v.id)}')">
      <div class="bi-visual-card-header">
        <div class="bi-visual-avatar icon" style="background:${color};"><span class="material-icons">${icon}</span></div>
        <div class="bi-visual-card-title">
          <div class="bi-visual-card-name">${escBI(v.name)}</div>
          ${v.contactName ? `<div class="bi-visual-card-subtitle">${escBI(v.contactName)}</div>` : ''}
        </div>
      </div>
      <div class="bi-visual-card-badges">
        <span class="bi-vis-badge" style="background:${color}15;color:${color};">${label}</span>
      </div>
      <div class="bi-visual-card-actions">
        <button class="bi-action-pill blue">
          <span class="material-icons">bar_chart</span> Analytics
        </button>
      </div>
    </div>`;
  }).join('');
}

function emptyVisual(msg) {
  return `<div style="grid-column:1/-1;text-align:center;padding:40px 20px;color:var(--text-secondary);">
    <span class="material-icons" style="font-size:48px;margin-bottom:8px;display:block;">search_off</span>
    <div style="font-size:14px;">${msg}</div>
  </div>`;
}

// ============================================
// ANALYTICS HELPERS
// ============================================
function getPersonPeriodData(personId) {
  const clock = BiState.clockEntries.filter(c => c.personId === personId && isInPeriod(c.clockIn || c.date));
  const totalHours = clock.reduce((s, c) => s + (c.totalHours || c.hours || 0), 0);
  const otHours = clock.reduce((s, c) => s + (c.otHours || 0), 0);
  // TASK-1481 — comp-first resolution + explicit "cannot know" state (denied
  // peopleComp AND no legacy fields on the person doc). compDenied makes the
  // analytics modal say "needs HR access" rather than $0.00 labor cost.
  const rawPerson = BiState.people.find(p => p.id === personId);
  const compDenied = _biCompUnknown(rawPerson);
  const person = _biComp(rawPerson);
  // Pay rate: hrHourlyRate (live) → costRate (legacy). Still never derived from salary.
  const hourlyRate = _BiLaborRate.laborRate(person);
  const laborCost = totalHours * hourlyRate;
  const isSalary = person?.hrEmploymentType === 'salary';
  const annualSalary = person?.hrAnnualSalary ? parseFloat(person.hrAnnualSalary) : 0;
  const payDisplay = isSalary && annualSalary ? `$${annualSalary.toLocaleString()}/yr` : hourlyRate ? `$${hourlyRate.toFixed(2)}/hr` : '';

  // Jobs worked
  const jobIds = new Set(clock.filter(c => c.jobId).map(c => c.jobId));

  // Revenue from jobs this person worked on
  const periodRevenue = rentalAwareRevenueEntries(isInPeriod).filter(r => r.jobId && jobIds.has(r.jobId)); // TASK-842-C
  const revenue = periodRevenue.reduce((s, r) => s + (r.amount || 0), 0);

  // Hours by customer
  const hoursByCustomer = {};
  clock.forEach(c => {
    const cName = c.customerName || c.jobName || 'Unknown';
    hoursByCustomer[cName] = (hoursByCustomer[cName] || 0) + (c.totalHours || c.hours || 0);
  });

  // Revenue by customer
  const revByCustomer = {};
  periodRevenue.forEach(r => {
    const cName = r.customerName || r.jobName || 'Unknown';
    revByCustomer[cName] = (revByCustomer[cName] || 0) + (r.amount || 0);
  });

  return { totalHours, otHours, laborCost, revenue, jobsWorked: jobIds.size, hoursByCustomer, revByCustomer, payDisplay, hourlyRate, compDenied };
}

function getVehiclePeriodData(vehicleId) {
  const periodDays = getPeriodDays();
  const entity = BiState.trucks.find(t => t.id === vehicleId) || BiState.trailers.find(t => t.id === vehicleId);

  // Find jobs that use this vehicle — assignments are on jobDay, not job
  const assignedDays = new Set();
  const vehicleJobIds = new Set();
  const refs = [vehicleId, entity?.unit, entity?.name].filter(Boolean);
  BiState.jobDays.filter(jd => isInPeriod(jd.date)).forEach(jd => {
    // TASK-10171 (perf budget): one .some over trucks + trailers, same result.
    if ([...(jd.trucks || []), ...(jd.trailers || [])].some(r => refs.includes(r))) {
      const dayStr = typeof jd.date === 'string' ? jd.date : toDate(jd.date)?.toISOString?.()?.slice(0, 10);
      if (dayStr) assignedDays.add(dayStr);
      vehicleJobIds.add(jd.jobId);
    }
  });

  const utilPct = periodDays > 0 ? (assignedDays.size / periodDays * 100) : 0;

  // Revenue from ticket lines — the Fleet tab's rule (TASK-9490 shared function)
  const vRev = _biVehicleRevenueByAsset(BiState.tickets, BiState.ticketLinesCache, isInPeriod)[vehicleId];
  const totalRevenue = vRev ? vRev.amount : 0;
  const revByCustomer = vRev ? { ...vRev.byCustomer } : {};

  // Costs: costEntries + maintenance (gate rejected/pending driver+staff — TASK-564)
  // TASK-10171 — a work order's shop mirror row is left out (its dollars are in totalMaintCosts).
  const directCosts = _costViewsOf(window.PnlStatement.withoutWoShopMirrors(BiState.costEntries, BiState.maintenance)).filter(c => isRecognizedCost(c) && isInPeriod(c.date) && (c.vehicleId === vehicleId || c.truckId === vehicleId)); // TASK-9493
  const totalDirectCosts = directCosts.reduce((s, c) => s + (c.amount || 0), 0);

  const maintCosts = BiState.maintenance.filter(m => (m.truckId === vehicleId || m.trailerId === vehicleId) && isInPeriod(m.createdAt));
  const totalMaintCosts = maintCosts.reduce((s, m) => s + window.PnlStatement.woCountedCost(m), 0); // TASK-10171 item 4

  const totalCosts = totalDirectCosts + totalMaintCosts;
  const netPL = totalRevenue - totalCosts;

  // Cost by category
  const costByCat = {};
  directCosts.forEach(c => {
    const cat = c.category || 'other';
    costByCat[cat] = (costByCat[cat] || 0) + (c.amount || 0);
  });
  if (totalMaintCosts > 0) costByCat.maintenance = (costByCat.maintenance || 0) + totalMaintCosts;

  return { totalRevenue, totalCosts, totalDirectCosts, totalMaintCosts, netPL, utilPct, jobCount: vehicleJobIds.size, revByCustomer, costByCat };
}

function getCustomerPeriodData(customerId) {
  const customer = BiState.customers.find(c => c.id === customerId);
  const cName = customer?.name || '';

  // Revenue from revenueEntries (TASK-842-C: rental slices by billed period)
  const periodRevenue = rentalAwareRevenueEntries(isInPeriod).filter(r => (r.customerId === customerId || r.customerName === cName));
  const totalRevenue = periodRevenue.reduce((s, r) => s + (r.amount || 0), 0);

  // Tickets
  const customerTickets = BiState.tickets.filter(t => t.customerId === customerId || t.customerName === cName);
  const periodTickets = customerTickets.filter(t => isInPeriod(t.date));
  const outstandingTickets = customerTickets.filter(t => t.status === 'draft' || t.status === 'ready');
  const outstanding = outstandingTickets.reduce((s, t) => s + (t.grandTotal || 0), 0);
  const avgTicketVal = periodTickets.length > 0 ? periodTickets.reduce((s, t) => s + (t.grandTotal || 0), 0) / periodTickets.length : 0;

  // DSO estimate
  let dsoSum = 0, dsoCount = 0;
  customerTickets.filter(t => t.invoicedAt && t.date).forEach(t => {
    const inv = toDate(t.invoicedAt);
    const created = toDate(t.date);
    if (inv && created) {
      const days = (inv - created) / (1000 * 60 * 60 * 24);
      if (days >= 0 && days < 365) { dsoSum += days; dsoCount++; }
    }
  });
  const dso = dsoCount > 0 ? Math.round(dsoSum / dsoCount) : 0;

  // Revenue by month
  const revByMonth = {};
  periodRevenue.forEach(r => {
    const d = toDate(r.invoicedAt);
    if (d) {
      const key = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      revByMonth[key] = (revByMonth[key] || 0) + (r.amount || 0);
    }
  });

  // Jobs for this customer
  const custJobs = BiState.jobs.filter(j => j.customerId === customerId || j.customerName === cName);
  const custJobIds = new Set(custJobs.map(j => j.id));

  // Revenue by job
  const revByJob = {};
  periodRevenue.forEach(r => {
    const label = r.jobName || r.jobTitle || r.jobId || 'Unassigned';
    if (!revByJob[label]) revByJob[label] = { amount: 0, jobId: r.jobId };
    revByJob[label].amount += (r.amount || 0);
  });

  // Per-job ticket totals (for avg ticket drill-down)
  const ticketsByJob = {};
  periodTickets.forEach(t => {
    const job = custJobs.find(j => j.id === t.jobId);
    const label = job?.name || t.jobName || t.jobId || 'Unassigned';
    if (!ticketsByJob[label]) ticketsByJob[label] = { count: 0, total: 0 };
    ticketsByJob[label].count++;
    ticketsByJob[label].total += (t.grandTotal || 0);
  });

  // Outstanding per job
  const outstandingByJob = {};
  outstandingTickets.forEach(t => {
    const job = custJobs.find(j => j.id === t.jobId);
    const label = job?.name || t.jobName || t.jobId || 'Unassigned';
    if (!outstandingByJob[label]) outstandingByJob[label] = { amount: 0, tickets: [] };
    outstandingByJob[label].amount += (t.grandTotal || 0);
    outstandingByJob[label].tickets.push(t);
  });

  // Avg cost per job — labor (clock hours × rate) + direct cost entries
  let totalLaborCost = 0;
  const custJobCostRates = _biJobCostRates(BiState.people); // burdened; salary allocated to job-clocked hours
  const clockEntries = BiState.clockEntries.filter(c => c.jobId && custJobIds.has(c.jobId) && isInPeriod(c.clockIn || c.date));
  clockEntries.forEach(c => {
    totalLaborCost += _BiLaborRate.jobCostForEntry(c, custJobCostRates);
  });
  const directCosts = _costViewsOf(BiState.costEntries).filter(c => isRecognizedCost(c) && isInPeriod(c.date) && c.jobId && custJobIds.has(c.jobId)); // TASK-9493
  const totalDirectCosts = directCosts.reduce((s, c) => s + (c.amount || 0), 0);
  const totalCost = totalLaborCost + totalDirectCosts;
  const jobCount = custJobs.filter(j => isInPeriod(j.date || j.createdAt)).length;
  const avgCostPerJob = jobCount > 0 ? totalCost / jobCount : 0;

  // Cost breakdown by job (for drill-down)
  const costByJob = {};
  clockEntries.forEach(c => {
    const job = custJobs.find(j => j.id === c.jobId);
    const label = job?.name || c.jobName || c.jobId || 'Unknown';
    if (!costByJob[label]) costByJob[label] = { labor: 0, expenses: 0 };
    costByJob[label].labor += _BiLaborRate.jobCostForEntry(c, custJobCostRates);
  });
  directCosts.forEach(c => {
    const job = custJobs.find(j => j.id === c.jobId);
    const label = job?.name || c.jobName || c.jobId || 'Unknown';
    if (!costByJob[label]) costByJob[label] = { labor: 0, expenses: 0 };
    costByJob[label].expenses += (c.amount || 0);
  });

  // Top vehicles
  const topVehicles = {};
  custJobs.forEach(j => {
    (j.trucks || []).forEach(ref => {
      const truck = _resolveFleetRef(ref, BiState.trucks);
      const label = truck ? (window.getUnitDisplay ? window.getUnitDisplay(truck) : `#${truck.unit}`) : ref;
      topVehicles[label] = (topVehicles[label] || 0) + 1;
    });
  });

  // Top crew
  const topCrew = {};
  clockEntries.forEach(c => {
    const person = BiState.people.find(p => p.id === c.personId);
    const name = person?.name || c.personName || 'Unknown';
    topCrew[name] = (topCrew[name] || 0) + (c.totalHours || c.hours || 0);
  });

  return {
    totalRevenue, ticketCount: periodTickets.length, outstanding, avgTicketVal, dso,
    revByMonth, topVehicles, topCrew, revByJob, ticketsByJob, outstandingByJob,
    avgCostPerJob, totalLaborCost, totalDirectCosts, totalCost, costByJob,
    periodTickets, outstandingTickets, custJobs
  };
}

function getVendorPeriodData(vendorId) {
  const vendor = BiState.customers.find(c => c.id === vendorId);
  const vName = vendor?.name || '';

  // TASK-10145 — a vendor statement bill's cost mirror books only what the crew
  // charge receipts on it did not; those receipts are this vendor's spend too
  // (charged at it, or linked to its bill), so the vendor total is the statement.
  const billVendor = _chargeBillVendorMap(BiState.costEntries);
  const costs = BiState.costEntries.filter(c => isRecognizedCost(c) && isInPeriod(c.date) && (c.vendorId === vendorId || c.vendorName === vName
    || (c.paidOnAccount === true && (c.chargeVendorId === vendorId || (c.chargeBillId && billVendor.get(c.chargeBillId) === vendorId)))));
  const totalSpend = costs.reduce((s, c) => s + (c.amount || 0), 0);

  const spendByCat = {};
  costs.forEach(c => {
    const cat = c.category || 'other';
    spendByCat[cat] = (spendByCat[cat] || 0) + (c.amount || 0);
  });

  const spendByMonth = {};
  costs.forEach(c => {
    const d = toDate(c.date);
    if (d) {
      const key = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      spendByMonth[key] = (spendByMonth[key] || 0) + (c.amount || 0);
    }
  });

  return { totalSpend, paymentCount: costs.length, spendByCat, spendByMonth };
}

// ============================================
// ANALYTICS MODALS
// ============================================
function renderAnalyticsModal(title, icon, content) {
  // Remove any existing modal first
  closeAnalyticsModal();
  // Create overlay directly on document.body — NO CSS classes to avoid style conflicts
  const overlay = document.createElement('div');
  overlay.id = 'bi-analytics-overlay';
  overlay.style.cssText = 'position:fixed;top:0;left:0;width:100vw;height:100vh;height:100dvh;background:rgba(15,23,42,0.5);display:flex;align-items:center;justify-content:center;z-index:9999;padding:16px;';
  overlay.onclick = function(e) { if (e.target === overlay) window.BIModule.closeAnalyticsModal(); };
  overlay.innerHTML = `
    <div id="bi-modal-box" style="max-width:600px;width:100%;max-height:85vh;background:#fff;border-radius:12px;box-shadow:0 25px 50px rgba(0,0,0,0.25);display:flex;flex-direction:column;overflow:hidden;">
      <div style="display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid #e2e8f0;flex-shrink:0;">
        <h3 style="display:flex;align-items:center;gap:8px;margin:0;font-size:16px;font-weight:600;"><span class="material-icons" style="color:var(--brand-blue);">${icon}</span>${escBI(title)}</h3>
        <button style="background:none;border:none;cursor:pointer;padding:4px;border-radius:6px;display:flex;align-items:center;" onclick="window.BIModule.closeAnalyticsModal()" onmouseover="this.style.background='#f1f5f9'" onmouseout="this.style.background='none'"><span class="material-icons" style="font-size:20px;color:#64748b;">close</span></button>
      </div>
      <div id="bi-modal-body" style="padding:20px;overflow-y:auto;flex:1;">
        ${content}
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
}

function closeAnalyticsModal() {
  const el = document.getElementById('bi-analytics-overlay');
  if (el) el.remove();
}

function renderHBars(data, color, isCurrency) {
  const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return '<div class="empty-state-small" style="color:var(--text-secondary);font-size:13px;padding:8px 0;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.55;">bar_chart</span>No data in this slice</div>';
  const max = Math.max(...entries.map(e => e[1]), 1);
  return entries.slice(0, 10).map(([label, val]) => {
    const pct = Math.max(0, val / max * 100); // TASK-9367 — see horizontalBar
    const display = isCurrency ? formatCurrency(val) : (typeof val === 'number' && val % 1 !== 0 ? val.toFixed(1) : val);
    return `
      <div class="bi-hbar-row">
        <div class="bi-hbar-label">${escBI(label)}</div>
        <div class="bi-hbar-track"><div class="bi-hbar-fill" style="width:${pct}%;background:${color};"></div></div>
        <div class="bi-hbar-value">${escBI(String(display))}</div>
      </div>`;
  }).join('');
}

// Drill-down state — stores context for the current analytics modal
let drillCtx = {};

function drillDown(key) {
  console.log('[BI] drillDown called with key:', key);
  // Modal analytics use bi-modal-drill; overview tab uses bi-drill-panel
  const isModal = !!document.getElementById('bi-analytics-overlay');
  const container = document.getElementById(isModal ? 'bi-modal-drill' : 'bi-drill-panel');
  console.log('[BI] drill panel found:', !!container, 'isModal:', isModal);
  if (!container) return;
  // Toggle: clicking same key closes it
  if (container.dataset.activeKey === key) {
    container.innerHTML = '';
    container.dataset.activeKey = '';
    document.querySelectorAll('.bi-stat-clickable.active').forEach(el => el.classList.remove('active'));
    return;
  }
  container.dataset.activeKey = key;
  document.querySelectorAll('.bi-stat-clickable.active').forEach(el => el.classList.remove('active'));
  // Highlight active card — search up from drill panel to find stat cards
  const modal = container.closest('#bi-analytics-overlay') || container.parentElement;
  const cards = modal?.querySelectorAll('.bi-stat-clickable');
  cards?.forEach(c => { if (c.getAttribute('onclick')?.includes(`'${key}'`)) c.classList.add('active'); });

  const html = renderDrillContent(key);
  console.log('[BI] drillDown content length:', html?.length || 0, 'drillCtx:', Object.keys(drillCtx));
  container.innerHTML = html ? `<div class="crm-section-card" style="margin-top:0;">${html}</div>` : '';
  // Auto-scroll drill panel into view within the modal body
  if (html) setTimeout(() => {
    const scrollBox = document.getElementById('bi-modal-body');
    if (scrollBox) scrollBox.scrollTop = container.offsetTop - 10;
  }, 50);
}

function renderDrillContent(key) {
  const ctx = drillCtx;
  // Overview drill-downs
  if (key === 'ov-revenue') return drillOverviewRevenue(ctx);
  if (key === 'ov-jobs') return drillOverviewJobs(ctx);
  if (key === 'ov-fleet') return drillOverviewFleet(ctx);
  if (key === 'ov-dvir') return drillOverviewDvir(ctx);
  // Vehicle/trailer drill-downs
  if (key === 'veh-revenue') return drillVehicleRevenue(ctx);
  if (key === 'veh-costs') return drillVehicleCosts(ctx);
  if (key === 'veh-pl') return drillVehiclePL(ctx);
  if (key === 'veh-util') return drillVehicleUtil(ctx);
  if (key === 'veh-jobs') return drillVehicleJobs(ctx);
  // Person drill-downs
  if (key === 'person-hours') return drillPersonHours(ctx);
  if (key === 'person-ot') return drillPersonOT(ctx);
  if (key === 'person-jobs') return drillPersonJobs(ctx);
  if (key === 'person-revenue') return drillPersonRevenue(ctx);
  if (key === 'person-labor') return drillPersonLabor(ctx);
  // Customer drill-downs
  if (key === 'cust-revenue') return drillCustomerRevenue(ctx);
  if (key === 'cust-tickets') return drillCustomerTickets(ctx);
  if (key === 'cust-outstanding') return drillCustomerOutstanding(ctx);
  if (key === 'cust-avgticket') return drillCustomerAvgTicket(ctx);
  if (key === 'cust-avgcost') return drillCustomerAvgCost(ctx);
  return '';
}

// --- Overview drill-downs ---
function drillOverviewRevenue(ctx) {
  const revenue = ctx.periodRevenue;
  if (!revenue.length) return '<div style="padding:12px;color:var(--text-secondary);font-size:13px;">No revenue data this period</div>';

  // Revenue by month
  const byMonth = {};
  revenue.forEach(r => {
    const d = toDate(r._periodDate || r.invoicedAt); // TASK-9242 — performed rows carry a real Date work day (drafts have no invoicedAt)
    if (d) {
      const key = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      byMonth[key] = (byMonth[key] || 0) + (r.amount || 0);
    }
  });

  // Revenue by customer
  const byCust = {};
  revenue.forEach(r => {
    const name = r.customerName || 'Unknown';
    byCust[name] = (byCust[name] || 0) + (r.amount || 0);
  });

  // Revenue by job
  const byJob = {};
  revenue.forEach(r => {
    const label = r.jobName || r.jobTitle || r.jobId || 'Unassigned';
    byJob[label] = (byJob[label] || 0) + (r.amount || 0);
  });

  // Mini bar chart for monthly trend
  const monthEntries = Object.entries(byMonth);
  const maxMonth = Math.max(...monthEntries.map(e => e[1]), 1);

  return `
    <div class="crm-section-title" style="margin-bottom:12px;"><span class="material-icons" style="color:#10B981;font-size:18px;">attach_money</span> Revenue Breakdown</div>
    ${monthEntries.length > 1 ? `
    <div style="margin-bottom:16px;">
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:8px;">Monthly Trend</div>
      <div style="display:flex;align-items:flex-end;gap:4px;height:80px;">
        ${monthEntries.map(([label, val]) => {
          const pct = (val / maxMonth * 100);
          return `<div style="flex:1;display:flex;flex-direction:column;align-items:center;gap:2px;">
            <div style="font-size:10px;font-weight:600;color:var(--text-secondary);">${formatCurrency(val)}</div>
            <div style="width:100%;background:#10B981;border-radius:4px 4px 0 0;height:${Math.max(pct, 5)}%;min-height:4px;transition:height 0.3s;"></div>
            <div style="font-size:10px;color:var(--text-secondary);white-space:nowrap;">${label}</div>
          </div>`;
        }).join('')}
      </div>
    </div>` : ''}

    <div style="margin-bottom:16px;">
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">By Customer</div>
      ${renderHBars(byCust, '#10B981', true)}
    </div>

    ${Object.keys(byJob).length > 0 ? `
    <div>
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">By Job</div>
      ${renderHBars(byJob, '#3B82F6', true)}
    </div>` : ''}
  `;
}

function drillOverviewJobs(ctx) {
  const jobIds = Array.from(ctx.uniqueJobIds);
  if (!jobIds.length) return '<div class="empty-state-small" style="padding:12px;color:var(--text-secondary);font-size:13px;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.55;">event_busy</span>No jobs in this period</div>';

  // Build job details
  const jobRows = jobIds.map(jid => {
    const job = BiState.jobs.find(j => j.id === jid);
    if (!job) return null;
    const jdCount = ctx.periodJobDays.filter(jd => jd.jobId === jid).length;
    const trucks = (job.trucks || []).map(ref => {
      const truck = _resolveFleetRef(ref, BiState.trucks);
      return truck ? (window.getUnitDisplay ? window.getUnitDisplay(truck) : `#${truck.unit}`) : ref;
    });
    return { name: job.name || job.title || jid, customer: job.customerName || '—', days: jdCount, trucks: trucks.join(', ') || '—' };
  }).filter(Boolean).sort((a, b) => b.days - a.days);

  // Jobs by customer
  const jobsByCust = {};
  jobRows.forEach(j => {
    jobsByCust[j.customer] = (jobsByCust[j.customer] || 0) + 1;
  });

  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:#3B82F6;font-size:18px;">work</span> Jobs (${jobIds.length})</div>
    ${Object.keys(jobsByCust).length > 1 ? `
    <div style="margin-bottom:12px;">
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">By Customer</div>
      ${renderHBars(jobsByCust, '#3B82F6', false)}
    </div>` : ''}
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Job</th>
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Customer</th>
        <th style="text-align:center;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Days</th>
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Vehicles</th>
      </tr></thead>
      <tbody>${jobRows.slice(0, 20).map(j => `<tr style="border-bottom:1px solid var(--border-light);">
        <td style="padding:8px;font-weight:500;">${escBI(j.name)}</td>
        <td style="padding:8px;color:var(--text-secondary);">${escBI(j.customer)}</td>
        <td style="text-align:center;padding:8px;">${j.days}</td>
        <td style="padding:8px;font-size:12px;color:var(--text-secondary);">${escBI(j.trucks)}</td>
      </tr>`).join('')}</tbody>
    </table>
    ${jobRows.length > 20 ? `<div style="padding:8px;font-size:12px;color:var(--text-secondary);text-align:center;">+ ${jobRows.length - 20} more jobs</div>` : ''}
  `;
}

function drillOverviewFleet(ctx) {
  const fu = ctx.fleetUtil;
  if (!fu || !fu.activeTrucks) return '<div style="padding:12px;color:var(--text-secondary);font-size:13px;">No vehicles</div>';

  // TASK-4860 — per-vehicle DISTINCT days worked (a vehicle on 2 jobs one day = 1 day),
  // from the same biFleetUtilization() pass the card uses, so the rows add up to the card.
  const rows = fu.perTruck.map(p => ({
    label: window.getUnitDisplay ? window.getUnitDisplay(p.truck) : `#${p.truck.unit}`,
    days: p.days, pct: p.pct,
  })).sort((a, b) => b.days - a.days);

  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:#F59E0B;font-size:18px;">local_shipping</span> Fleet Utilization</div>
    <div style="font-size:12px;color:var(--text-secondary);margin-bottom:4px;"><strong style="color:var(--text-primary);">Vehicles used ${formatPct(fu.usedPct)}</strong> — ${fu.vehiclesUsed} of ${fu.activeTrucks} active vehicles ran at least one day this period.</div>
    <div style="font-size:12px;color:var(--text-secondary);margin-bottom:10px;"><strong style="color:var(--text-primary);">Vehicle-days worked ${formatPct(fu.dayPct)}</strong> — ${fu.vehicleDays} days worked out of ${fu.availableDays} possible (${fu.activeTrucks} vehicles × ${fu.periodDays} days). Two jobs on the same day count as one day.</div>
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Vehicle</th>
        <th style="text-align:center;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Days Worked</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">of ${fu.periodDays} days</th>
      </tr></thead>
      <tbody>${rows.map(t => {
        const color = t.pct >= 75 ? '#10B981' : t.pct >= 25 ? '#F59E0B' : '#EF4444';
        return `<tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:8px;font-weight:500;">${escBI(t.label)}</td>
          <td style="text-align:center;padding:8px;">${t.days}</td>
          <td style="text-align:right;padding:8px;"><span style="color:${color};font-weight:600;">${formatPct(t.pct)}</span></td>
        </tr>`;
      }).join('')}</tbody>
    </table>
  `;
}

function drillOverviewDvir(ctx) {
  const pretrips = ctx.periodPretrips;
  if (!pretrips.length) return '<div style="padding:12px;color:var(--text-secondary);font-size:13px;">No DVIRs this period</div>';

  const fails = pretrips.filter(p => !_isPretripPass(p));
  const passes = pretrips.length - fails.length;

  // Fails by vehicle
  const failsByVehicle = {};
  fails.forEach(p => {
    const truck = BiState.trucks.find(t => t.id === p.truckId || t.id === p.vehicleId);
    const label = truck ? (window.getUnitDisplay ? window.getUnitDisplay(truck) : `#${truck.unit}`) : (p.truckUnit || p.vehicleUnit || 'Unknown');
    failsByVehicle[label] = (failsByVehicle[label] || 0) + 1;
  });

  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:#8B5CF6;font-size:18px;">verified</span> DVIR Summary</div>
    <div style="display:flex;gap:16px;margin-bottom:12px;font-size:13px;">
      <span class="bi-vis-badge green"><span class="material-icons" style="font-size:12px;">check_circle</span> ${passes} Pass</span>
      <span class="bi-vis-badge red"><span class="material-icons" style="font-size:12px;">cancel</span> ${fails.length} Fail</span>
    </div>
    ${fails.length > 0 ? `
    <div style="margin-bottom:12px;">
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">Failures by Vehicle</div>
      ${renderHBars(failsByVehicle, '#EF4444', false)}
    </div>
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Date</th>
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Vehicle</th>
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Driver</th>
      </tr></thead>
      <tbody>${fails.slice(0, 10).map(p => {
        const dt = toDate(p.createdAt);
        const dateStr = dt ? dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
        const truck = BiState.trucks.find(t => t.id === p.truckId || t.id === p.vehicleId);
        const vLabel = truck ? (window.getUnitDisplay ? window.getUnitDisplay(truck) : `#${truck.unit}`) : (p.truckUnit || '—');
        return `<tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:8px;">${dateStr}</td>
          <td style="padding:8px;font-weight:500;">${escBI(vLabel)}</td>
          <td style="padding:8px;color:var(--text-secondary);">${escBI(p.driverName || p.inspectorName || '—')}</td>
        </tr>`;
      }).join('')}</tbody>
    </table>
    ${fails.length > 10 ? `<div style="padding:8px;font-size:12px;color:var(--text-secondary);text-align:center;">+ ${fails.length - 10} more</div>` : ''}
    ` : '<div style="padding:16px;text-align:center;color:var(--text-secondary);font-size:13px;"><span class="material-icons" style="font-size:32px;display:block;margin-bottom:4px;color:#10B981;">check_circle</span>100% pass rate — no failures this period</div>'}
  `;
}

// --- Pay period helpers ---
function getPayPeriodWeeks(clockEntries) {
  // TASK-9979 — group by PAYROLL's workweek (company zone, settings start day),
  // not a hardcoded Monday keyed by the UTC date of the clock-in's time of day.
  const weeks = {};
  clockEntries.forEach(c => {
    const d = toDate(c.clockIn || c.date);
    if (!d) return;
    const key = _biOtWeekKey(d);
    if (!key) return;
    if (!weeks[key]) weeks[key] = { start: window.TenantLocalTime.ymdCarrier(key), hours: 0, otHours: 0, entries: [] };
    weeks[key].hours += (c.totalHours || c.hours || 0);
    weeks[key].otHours += (c.otHours || 0);
    weeks[key].entries.push(c);
  });
  return Object.values(weeks).sort((a, b) => a.start - b.start);
}

function formatWeekLabel(start) {
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  const opts = { month: 'short', day: 'numeric' };
  return `${start.toLocaleDateString('en-US', opts)} – ${end.toLocaleDateString('en-US', opts)}`;
}

function drillPersonHours(ctx) {
  const weeks = getPayPeriodWeeks(ctx.clock);
  if (!weeks.length) return '<div style="padding:12px;color:var(--text-secondary);font-size:13px;">No hours this period</div>';
  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">date_range</span> Hours by Pay Period</div>
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Week</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Regular</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">OT</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Total</th>
      </tr></thead>
      <tbody>${weeks.map(w => {
        const reg = Math.max(0, w.hours - w.otHours);
        return `<tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:8px;font-weight:500;">${formatWeekLabel(w.start)}</td>
          <td style="text-align:right;padding:8px;">${reg.toFixed(1)}</td>
          <td style="text-align:right;padding:8px;color:${w.otHours > 0 ? '#F59E0B' : 'var(--text-secondary)'};">${w.otHours.toFixed(1)}</td>
          <td style="text-align:right;padding:8px;font-weight:700;">${w.hours.toFixed(1)}</td>
        </tr>`;
      }).join('')}</tbody>
    </table>
  `;
}

function drillPersonOT(ctx) {
  const weeks = getPayPeriodWeeks(ctx.clock).filter(w => w.otHours > 0);
  if (!weeks.length) return '<div style="padding:16px;text-align:center;color:var(--text-secondary);font-size:13px;"><span class="material-icons" style="font-size:32px;display:block;margin-bottom:4px;">check_circle</span>No overtime this period</div>';
  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:#F59E0B;font-size:18px;">more_time</span> Overtime by Pay Period</div>
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Week</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">OT Hours</th>
        ${ctx.hourlyRate ? '<th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">OT Cost</th>' : ''}
      </tr></thead>
      <tbody>${weeks.map(w => `<tr style="border-bottom:1px solid var(--border-light);">
        <td style="padding:8px;font-weight:500;">${formatWeekLabel(w.start)}</td>
        <td style="text-align:right;padding:8px;color:#F59E0B;font-weight:600;">${w.otHours.toFixed(1)}</td>
        ${ctx.hourlyRate ? `<td style="text-align:right;padding:8px;font-weight:600;">${formatCurrency(w.otHours * ctx.hourlyRate * 1.5)}</td>` : ''}
      </tr>`).join('')}</tbody>
    </table>
  `;
}

function drillPersonJobs(ctx) {
  // Hours per job
  const jobHours = {};
  ctx.clock.forEach(c => {
    if (!c.jobId) return;
    const label = c.jobName || c.jobTitle || c.customerName || c.jobId;
    if (!jobHours[label]) jobHours[label] = { hours: 0, customer: c.customerName || '' };
    jobHours[label].hours += (c.totalHours || c.hours || 0);
  });
  const entries = Object.entries(jobHours).sort((a, b) => b[1].hours - a[1].hours);
  if (!entries.length) return '<div style="padding:16px;text-align:center;color:var(--text-secondary);font-size:13px;">No job assignments this period</div>';
  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">work</span> Jobs Worked</div>
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Job</th>
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Customer</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Hours</th>
      </tr></thead>
      <tbody>${entries.map(([name, d]) => `<tr style="border-bottom:1px solid var(--border-light);">
        <td style="padding:8px;font-weight:500;">${escBI(name)}</td>
        <td style="padding:8px;color:var(--text-secondary);">${escBI(d.customer)}</td>
        <td style="text-align:right;padding:8px;font-weight:600;">${d.hours.toFixed(1)}</td>
      </tr>`).join('')}</tbody>
    </table>
  `;
}

function drillPersonRevenue(ctx) {
  // Revenue by job
  const revByJob = {};
  ctx.periodRevenue.forEach(r => {
    const label = r.jobName || r.jobId || 'Unassigned';
    const cust = r.customerName || '';
    if (!revByJob[label]) revByJob[label] = { amount: 0, customer: cust };
    revByJob[label].amount += (r.amount || 0);
  });
  const jobEntries = Object.entries(revByJob).sort((a, b) => b[1].amount - a[1].amount);

  // Revenue by customer
  const revByCust = {};
  ctx.periodRevenue.forEach(r => {
    const cust = r.customerName || 'Unknown';
    revByCust[cust] = (revByCust[cust] || 0) + (r.amount || 0);
  });

  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">payments</span> Revenue Breakdown</div>
    ${Object.keys(revByCust).length > 0 ? `
      <div style="margin-bottom:12px;"><div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">By Customer</div>${renderHBars(revByCust, '#10B981', true)}</div>` : ''}
    ${jobEntries.length > 0 ? `
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">By Job</div>
      <table style="width:100%;font-size:13px;border-collapse:collapse;">
        <thead><tr style="border-bottom:2px solid var(--border-light);">
          <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Job</th>
          <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Customer</th>
          <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Amount</th>
        </tr></thead>
        <tbody>${jobEntries.map(([name, d]) => `<tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:8px;font-weight:500;">${escBI(name)}</td>
          <td style="padding:8px;color:var(--text-secondary);">${escBI(d.customer)}</td>
          <td style="text-align:right;padding:8px;font-weight:600;">${formatCurrency(d.amount)}</td>
        </tr>`).join('')}</tbody>
      </table>` : '<div style="padding:12px;color:var(--text-secondary);font-size:13px;">No revenue data</div>'}
  `;
}

function drillPersonLabor(ctx) {
  const weeks = getPayPeriodWeeks(ctx.clock);
  if (!weeks.length) return '<div style="padding:12px;color:var(--text-secondary);font-size:13px;">No labor data this period</div>';
  const rate = ctx.hourlyRate;
  const otRate = rate * 1.5;

  // Raise history
  const person = ctx.person;
  const raiseInfo = person?.hrRaiseHistory?.length ? person.hrRaiseHistory : null;

  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">money_off</span> Labor Cost Breakdown</div>
    ${rate ? `<div style="display:flex;gap:16px;margin-bottom:12px;font-size:13px;">
      <span class="bi-vis-badge blue"><span class="material-icons" style="font-size:12px;">payments</span> Regular: $${rate.toFixed(2)}/hr</span>
      <span class="bi-vis-badge amber"><span class="material-icons" style="font-size:12px;">more_time</span> OT: $${otRate.toFixed(2)}/hr (1.5x)</span>
    </div>` : ''}
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Week</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Reg Cost</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">OT Cost</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Total</th>
      </tr></thead>
      <tbody>${weeks.map(w => {
        const reg = Math.max(0, w.hours - w.otHours);
        const regCost = reg * rate;
        const otCost = w.otHours * otRate;
        return `<tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:8px;font-weight:500;">${formatWeekLabel(w.start)}</td>
          <td style="text-align:right;padding:8px;">${formatCurrency(regCost)}</td>
          <td style="text-align:right;padding:8px;color:${otCost > 0 ? '#F59E0B' : 'var(--text-secondary)'};">${formatCurrency(otCost)}</td>
          <td style="text-align:right;padding:8px;font-weight:700;">${formatCurrency(regCost + otCost)}</td>
        </tr>`;
      }).join('')}</tbody>
    </table>
    ${raiseInfo ? `
    <div style="margin-top:16px;">
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">Raise History</div>
      ${raiseInfo.map(r => `<div style="display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border-light);font-size:13px;">
        <span>${r.date ? new Date(r.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
        <span style="font-weight:600;">$${parseFloat(r.oldRate || 0).toFixed(2)} → $${parseFloat(r.newRate || 0).toFixed(2)}/hr ${r.raisePercent ? `(+${r.raisePercent}%)` : ''}</span>
      </div>`).join('')}
    </div>` : ''}
  `;
}

// --- Vehicle/trailer drill-downs ---
function drillVehicleRevenue(ctx) {
  const d = ctx.vehicleData;
  if (d.totalRevenue === 0) return '<div style="padding:12px;color:var(--text-secondary);font-size:13px;">No revenue data this period</div>';
  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:#10B981;font-size:18px;">payments</span> Revenue Breakdown</div>
    ${Object.keys(d.revByCustomer).length > 0 ? `
    <div style="margin-bottom:12px;">
      <div style="font-size:12px;font-weight:600;color:var(--text-secondary);margin-bottom:6px;">By Customer</div>
      ${renderHBars(d.revByCustomer, '#10B981', true)}
    </div>` : ''}
    <div style="font-size:13px;color:var(--text-secondary);padding:4px 0;">Total: <strong style="color:var(--text-primary);">${formatCurrency(d.totalRevenue)}</strong> across ${d.jobCount} jobs</div>
  `;
}

function drillVehicleCosts(ctx) {
  const d = ctx.vehicleData;
  if (d.totalCosts === 0) return '<div style="padding:12px;color:var(--text-secondary);font-size:13px;">No costs this period</div>';
  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:#EF4444;font-size:18px;">money_off</span> Cost Breakdown</div>
    <div style="display:flex;gap:16px;margin-bottom:12px;font-size:13px;">
      <span class="bi-vis-badge red"><span class="material-icons" style="font-size:12px;">receipt</span> Direct: ${formatCurrency(d.totalDirectCosts)}</span>
      <span class="bi-vis-badge amber"><span class="material-icons" style="font-size:12px;">build</span> Maintenance: ${formatCurrency(d.totalMaintCosts)}</span>
    </div>
    ${Object.keys(d.costByCat).length > 0 ? renderHBars(d.costByCat, '#EF4444', true) : ''}
  `;
}

function drillVehiclePL(ctx) {
  const d = ctx.vehicleData;
  const margin = d.totalRevenue > 0 ? ((d.netPL / d.totalRevenue) * 100).toFixed(1) : '0.0';
  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:${d.netPL >= 0 ? '#10B981' : '#EF4444'};font-size:18px;">trending_up</span> Profit / Loss</div>
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <tbody>
        <tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:8px;font-weight:500;">Revenue</td>
          <td style="text-align:right;padding:8px;color:#10B981;font-weight:600;">${formatCurrency(d.totalRevenue)}</td>
        </tr>
        <tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:8px;font-weight:500;">Direct Costs</td>
          <td style="text-align:right;padding:8px;color:#EF4444;">${formatCurrency(d.totalDirectCosts)}</td>
        </tr>
        <tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:8px;font-weight:500;">Maintenance</td>
          <td style="text-align:right;padding:8px;color:#EF4444;">${formatCurrency(d.totalMaintCosts)}</td>
        </tr>
        <tr style="border-top:2px solid var(--border-light);">
          <td style="padding:8px;font-weight:700;">Net P/L</td>
          <td style="text-align:right;padding:8px;font-weight:700;color:${d.netPL >= 0 ? '#10B981' : '#EF4444'};">${formatCurrency(d.netPL)}</td>
        </tr>
      </tbody>
    </table>
    <div style="margin-top:8px;font-size:12px;color:var(--text-secondary);">Margin: <strong>${margin}%</strong></div>
  `;
}

function drillVehicleUtil(ctx) {
  const d = ctx.vehicleData;
  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:#F59E0B;font-size:18px;">speed</span> Utilization Detail</div>
    <div style="font-size:13px;color:var(--text-secondary);line-height:1.8;">
      <div>Utilization: <strong style="color:var(--text-primary);">${formatPct(d.utilPct)}</strong></div>
      <div>Jobs assigned: <strong style="color:var(--text-primary);">${d.jobCount}</strong></div>
      <div style="margin-top:8px;font-size:12px;">Utilization = days with job assignments / days in period</div>
    </div>
  `;
}

function drillVehicleJobs(ctx) {
  const d = ctx.vehicleData;
  if (d.jobCount === 0) return '<div class="empty-state-small" style="padding:12px;color:var(--text-secondary);font-size:13px;"><span class="material-icons" style="font-size:18px;vertical-align:middle;margin-right:4px;opacity:0.55;">local_shipping</span>No jobs for this vehicle in the period</div>';
  // Find jobs for this vehicle
  const vehicleId = ctx.vehicleId;
  const entity = ctx.entityType === 'trailer' ? BiState.trailers.find(t => t.id === vehicleId) : BiState.trucks.find(t => t.id === vehicleId);
  const refs = [vehicleId, entity?.unit, entity?.name].filter(Boolean);
  const periodJobDays = BiState.jobDays.filter(jd => isInPeriod(jd.date));
  const jobIds = new Set();
  periodJobDays.forEach(jd => {
    if ((jd.trucks || []).some(ref => refs.includes(ref)) || (jd.trailers || []).some(ref => refs.includes(ref))) jobIds.add(jd.jobId);
  });
  const jobs = Array.from(jobIds).map(jid => {
    const job = BiState.jobs.find(j => j.id === jid);
    return job ? { name: job.name || job.title || jid, customer: job.customerName || '—' } : null;
  }).filter(Boolean);

  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:#3B82F6;font-size:18px;">work</span> Jobs (${jobs.length})</div>
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Job</th>
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Customer</th>
      </tr></thead>
      <tbody>${jobs.map(j => `<tr style="border-bottom:1px solid var(--border-light);">
        <td style="padding:8px;font-weight:500;">${escBI(j.name)}</td>
        <td style="padding:8px;color:var(--text-secondary);">${escBI(j.customer)}</td>
      </tr>`).join('')}</tbody>
    </table>
  `;
}

// --- Customer drill-downs ---
function drillCustomerRevenue(ctx) {
  const d = ctx.customerData;
  const entries = Object.entries(d.revByJob).sort((a, b) => b[1].amount - a[1].amount);
  if (!entries.length) return '<div style="padding:12px;color:var(--text-secondary);font-size:13px;">No revenue data this period</div>';
  const total = entries.reduce((s, e) => s + e[1].amount, 0);
  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">payments</span> Revenue by Job</div>
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Job</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Revenue</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">%</th>
      </tr></thead>
      <tbody>${entries.map(([name, info]) => `<tr style="border-bottom:1px solid var(--border-light);">
        <td style="padding:8px;font-weight:500;">${escBI(name)}</td>
        <td style="text-align:right;padding:8px;font-weight:600;color:#10B981;">${formatCurrency(info.amount)}</td>
        <td style="text-align:right;padding:8px;color:var(--text-secondary);">${total > 0 ? ((info.amount / total) * 100).toFixed(0) + '%' : '—'}</td>
      </tr>`).join('')}
      <tr style="border-top:2px solid var(--border-light);font-weight:700;">
        <td style="padding:8px;">Total</td>
        <td style="text-align:right;padding:8px;color:#10B981;">${formatCurrency(total)}</td>
        <td></td>
      </tr></tbody>
    </table>
  `;
}

function drillCustomerTickets(ctx) {
  const d = ctx.customerData;
  const tickets = d.periodTickets;
  if (!tickets.length) return '<div style="padding:12px;color:var(--text-secondary);font-size:13px;">No tickets this period</div>';
  const sorted = [...tickets].sort((a, b) => {
    const da = toDate(a.date), db = toDate(b.date);
    return (db || 0) - (da || 0);
  });
  const statusColors = { draft: '#94A3B8', ready: '#F59E0B', invoiced: '#3B82F6', paid: '#10B981' };
  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">receipt</span> Tickets (${tickets.length})</div>
    <div style="font-size:12px;color:var(--text-secondary);margin-bottom:8px;">View & edit tickets in the Billing tab</div>
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Date</th>
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Job</th>
        <th style="text-align:center;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Status</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Amount</th>
      </tr></thead>
      <tbody>${sorted.map(t => {
        const dt = toDate(t.date);
        const dateStr = dt ? dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
        const jobName = t.jobName || t.jobTitle || '—';
        const status = t.status || 'draft';
        const sColor = statusColors[status] || '#94A3B8';
        return `<tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:8px;">${dateStr}</td>
          <td style="padding:8px;font-weight:500;">${escBI(jobName)}</td>
          <td style="text-align:center;padding:8px;"><span style="display:inline-block;padding:2px 8px;border-radius:10px;font-size:11px;font-weight:600;background:${sColor}15;color:${sColor};">${escBI(status)}</span></td>
          <td style="text-align:right;padding:8px;font-weight:600;">${formatCurrency(t.grandTotal || 0)}</td>
        </tr>`;
      }).join('')}</tbody>
    </table>
  `;
}

function drillCustomerOutstanding(ctx) {
  const d = ctx.customerData;
  const entries = Object.entries(d.outstandingByJob);
  if (!entries.length) return '<div style="padding:16px;text-align:center;color:var(--text-secondary);font-size:13px;"><span class="material-icons" style="font-size:32px;display:block;margin-bottom:4px;">check_circle</span>No outstanding balance</div>';
  const sorted = entries.sort((a, b) => b[1].amount - a[1].amount);
  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:#F59E0B;font-size:18px;">pending</span> Outstanding by Job</div>
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Job</th>
        <th style="text-align:center;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Tickets</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Amount</th>
      </tr></thead>
      <tbody>${sorted.map(([name, info]) => `<tr style="border-bottom:1px solid var(--border-light);">
        <td style="padding:8px;font-weight:500;">${escBI(name)}</td>
        <td style="text-align:center;padding:8px;">${info.tickets.length}</td>
        <td style="text-align:right;padding:8px;font-weight:600;color:#F59E0B;">${formatCurrency(info.amount)}</td>
      </tr>`).join('')}
      <tr style="border-top:2px solid var(--border-light);font-weight:700;">
        <td style="padding:8px;">Total</td>
        <td></td>
        <td style="text-align:right;padding:8px;color:#F59E0B;">${formatCurrency(d.outstanding)}</td>
      </tr></tbody>
    </table>
  `;
}

function drillCustomerAvgTicket(ctx) {
  const d = ctx.customerData;
  const entries = Object.entries(d.ticketsByJob);
  if (!entries.length) return '<div style="padding:12px;color:var(--text-secondary);font-size:13px;">No tickets this period</div>';
  const sorted = entries.sort((a, b) => b[1].total - a[1].total);
  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">attach_money</span> Ticket Totals by Job</div>
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Job</th>
        <th style="text-align:center;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Tickets</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Total</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Avg</th>
      </tr></thead>
      <tbody>${sorted.map(([name, info]) => {
        const avg = info.count > 0 ? info.total / info.count : 0;
        return `<tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:8px;font-weight:500;">${escBI(name)}</td>
          <td style="text-align:center;padding:8px;">${info.count}</td>
          <td style="text-align:right;padding:8px;font-weight:600;">${formatCurrency(info.total)}</td>
          <td style="text-align:right;padding:8px;color:var(--text-secondary);">${formatCurrency(avg)}</td>
        </tr>`;
      }).join('')}</tbody>
    </table>
  `;
}

function drillCustomerAvgCost(ctx) {
  const d = ctx.customerData;
  const entries = Object.entries(d.costByJob);
  if (!entries.length && d.totalCost === 0) return '<div style="padding:12px;color:var(--text-secondary);font-size:13px;">No cost data this period</div>';

  return `
    <div class="crm-section-title" style="margin-bottom:10px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">money_off</span> Cost Breakdown</div>
    <div style="display:flex;gap:16px;margin-bottom:12px;font-size:13px;">
      <span class="bi-vis-badge blue"><span class="material-icons" style="font-size:12px;">group</span> Labor: ${formatCurrency(d.totalLaborCost)}</span>
      <span class="bi-vis-badge amber"><span class="material-icons" style="font-size:12px;">receipt</span> Expenses: ${formatCurrency(d.totalDirectCosts)}</span>
    </div>
    ${entries.length > 0 ? `
    <table style="width:100%;font-size:13px;border-collapse:collapse;">
      <thead><tr style="border-bottom:2px solid var(--border-light);">
        <th style="text-align:left;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Job</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Labor</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Expenses</th>
        <th style="text-align:right;padding:6px 8px;color:var(--text-secondary);font-weight:600;">Total</th>
      </tr></thead>
      <tbody>${entries.sort((a, b) => (b[1].labor + b[1].expenses) - (a[1].labor + a[1].expenses)).map(([name, info]) => {
        const total = info.labor + info.expenses;
        return `<tr style="border-bottom:1px solid var(--border-light);">
          <td style="padding:8px;font-weight:500;">${escBI(name)}</td>
          <td style="text-align:right;padding:8px;">${formatCurrency(info.labor)}</td>
          <td style="text-align:right;padding:8px;">${formatCurrency(info.expenses)}</td>
          <td style="text-align:right;padding:8px;font-weight:700;">${formatCurrency(total)}</td>
        </tr>`;
      }).join('')}
      <tr style="border-top:2px solid var(--border-light);font-weight:700;">
        <td style="padding:8px;">Total</td>
        <td style="text-align:right;padding:8px;">${formatCurrency(d.totalLaborCost)}</td>
        <td style="text-align:right;padding:8px;">${formatCurrency(d.totalDirectCosts)}</td>
        <td style="text-align:right;padding:8px;">${formatCurrency(d.totalCost)}</td>
      </tr></tbody>
    </table>` : ''}
    ${d.custJobs.length > 0 ? `<div style="margin-top:8px;font-size:12px;color:var(--text-secondary);">Avg cost per job: <strong>${formatCurrency(d.avgCostPerJob)}</strong> across ${d.custJobs.filter(j => isInPeriod(j.date || j.createdAt)).length} jobs</div>` : ''}
  `;
}

function showPersonAnalytics(id) {
  try {
  const person = BiState.people.find(p => p.id === id);
  if (!person) return;
  const d = getPersonPeriodData(id);
  const role = person.crewType || person.role || person.roles?.[0] || '';
  const roleLabel = window.getTypeLabel ? window.getTypeLabel(role, 'crewTypes') : role;
  const dept = person.department || '';

  // Build clock entries for drill-down context
  const clock = BiState.clockEntries.filter(c => c.personId === id && isInPeriod(c.clockIn || c.date));
  const jobIds = new Set(clock.filter(c => c.jobId).map(c => c.jobId));
  const periodRevenue = rentalAwareRevenueEntries(isInPeriod).filter(r => r.jobId && jobIds.has(r.jobId)); // TASK-842-C

  drillCtx = { person, clock, periodRevenue, hourlyRate: d.hourlyRate, payDisplay: d.payDisplay };

  renderAnalyticsModal(person.name, 'person', `
    ${roleLabel || dept || d.payDisplay ? `
    <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:16px;">
      ${dept ? `<span class="bi-vis-badge blue">${escBI(dept)}</span>` : ''}
      ${roleLabel ? `<span class="bi-vis-badge">${escBI(roleLabel)}</span>` : ''}
      ${d.payDisplay ? `<span class="bi-vis-badge green"><span class="material-icons" style="font-size:12px;">payments</span> ${escBI(d.payDisplay)}</span>` : ''}
    </div>` : ''}
    <div class="crm-stats-grid" style="grid-template-columns:repeat(3,1fr);margin-bottom:8px;">
      ${statCard('schedule', 'Hours', d.totalHours.toFixed(1), 'blue', 'person-hours')}
      ${statCard('more_time', 'OT Hours', d.otHours.toFixed(1), 'amber', 'person-ot')}
      ${statCard('work', 'Jobs', d.jobsWorked, 'purple', 'person-jobs')}
    </div>
    <div class="crm-stats-grid" style="grid-template-columns:repeat(2,1fr);margin-bottom:16px;">
      ${statCard('payments', 'Revenue', formatCurrency(d.revenue), 'green', 'person-revenue')}
      ${d.compDenied
        ? statCard('lock', 'Labor Cost', 'n/a — needs HR access', 'red', 'person-labor')
        : statCard('money_off', 'Labor Cost', formatCurrency(d.laborCost), 'red', 'person-labor')}
    </div>
    <div id="bi-modal-drill" data-active-key="" style="margin-top:4px;"></div>
    ${Object.keys(d.hoursByCustomer).length > 0 ? `
    <div class="crm-section-card" style="margin-bottom:12px;">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">schedule</span> Hours by Customer</div>
      ${renderHBars(d.hoursByCustomer, '#3B82F6', false)}
    </div>` : ''}
    ${Object.keys(d.revByCustomer).length > 0 ? `
    <div class="crm-section-card">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">payments</span> Revenue by Customer</div>
      ${renderHBars(d.revByCustomer, '#10B981', true)}
    </div>` : ''}
  `);
  } catch(e) { console.error('[BI] showPersonAnalytics error:', e); window.showToast?.('Error: ' + e.message, 'error'); }
}

async function showVehicleAnalytics(id) {
  try {
  const truck = BiState.trucks.find(t => t.id === id);
  if (!truck) return;
  renderAnalyticsModal(getUnitDisplayBI(truck), 'local_shipping', '<div class="loading-state"><div class="loading-spinner"></div><p>Loading...</p></div>');
  await loadTicketLinesForPeriod();
  await _pnlStatementReady; // TASK-10171: getVehiclePeriodData reads window.PnlStatement
  const d = getVehiclePeriodData(id);
  const plColor = d.netPL >= 0 ? 'green' : 'red';
  drillCtx = { vehicleId: id, vehicleData: d, vehicleName: getUnitDisplayBI(truck), entityType: 'truck' };

  renderAnalyticsModal(getUnitDisplayBI(truck), 'local_shipping', `
    <div class="crm-stats-grid" style="grid-template-columns:repeat(2,1fr);margin-bottom:8px;">
      ${statCard('payments', 'Revenue', formatCurrency(d.totalRevenue), 'green', 'veh-revenue')}
      ${statCard('money_off', 'Costs', formatCurrency(d.totalCosts), 'red', 'veh-costs')}
    </div>
    <div class="crm-stats-grid" style="grid-template-columns:repeat(3,1fr);margin-bottom:16px;">
      ${statCard('trending_up', 'Net P/L', formatCurrency(d.netPL), plColor, 'veh-pl')}
      ${statCard('speed', 'Utilization', formatPct(d.utilPct), 'amber', 'veh-util')}
      ${statCard('work', 'Jobs', d.jobCount, 'blue', 'veh-jobs')}
    </div>
    <div id="bi-modal-drill" data-active-key="" style="margin-top:4px;"></div>
    ${Object.keys(d.revByCustomer).length > 0 ? `
    <div class="crm-section-card" style="margin-bottom:12px;">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">payments</span> Revenue by Customer</div>
      ${renderHBars(d.revByCustomer, '#10B981', true)}
    </div>` : ''}
    ${Object.keys(d.costByCat).length > 0 ? `
    <div class="crm-section-card">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">pie_chart</span> Cost by Category</div>
      ${renderHBars(d.costByCat, '#EF4444', true)}
    </div>` : ''}
  `);
  } catch(e) { console.error('[BI] showVehicleAnalytics error:', e); window.showToast?.('Error: ' + e.message, 'error'); }
}

async function showTrailerAnalytics(id) {
  try {
  const trailer = BiState.trailers.find(t => t.id === id);
  if (!trailer) return;
  renderAnalyticsModal(getUnitDisplayBI(trailer), 'rv_hookup', '<div class="loading-state"><div class="loading-spinner"></div><p>Loading...</p></div>');
  await loadTicketLinesForPeriod();
  await _pnlStatementReady; // TASK-10171
  const d = getVehiclePeriodData(id);
  const plColor = d.netPL >= 0 ? 'green' : 'red';
  drillCtx = { vehicleId: id, vehicleData: d, vehicleName: getUnitDisplayBI(trailer), entityType: 'trailer' };

  renderAnalyticsModal(getUnitDisplayBI(trailer), 'rv_hookup', `
    <div class="crm-stats-grid" style="grid-template-columns:repeat(2,1fr);margin-bottom:8px;">
      ${statCard('payments', 'Revenue', formatCurrency(d.totalRevenue), 'green', 'veh-revenue')}
      ${statCard('money_off', 'Costs', formatCurrency(d.totalCosts), 'red', 'veh-costs')}
    </div>
    <div class="crm-stats-grid" style="grid-template-columns:repeat(3,1fr);margin-bottom:16px;">
      ${statCard('trending_up', 'Net P/L', formatCurrency(d.netPL), plColor, 'veh-pl')}
      ${statCard('speed', 'Utilization', formatPct(d.utilPct), 'amber', 'veh-util')}
      ${statCard('work', 'Jobs', d.jobCount, 'blue', 'veh-jobs')}
    </div>
    <div id="bi-modal-drill" data-active-key="" style="margin-top:4px;"></div>
    ${Object.keys(d.revByCustomer).length > 0 ? `
    <div class="crm-section-card" style="margin-bottom:12px;">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">payments</span> Revenue by Customer</div>
      ${renderHBars(d.revByCustomer, '#10B981', true)}
    </div>` : ''}
    ${Object.keys(d.costByCat).length > 0 ? `
    <div class="crm-section-card">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">pie_chart</span> Cost by Category</div>
      ${renderHBars(d.costByCat, '#EF4444', true)}
    </div>` : ''}
  `);
  } catch(e) { console.error('[BI] showTrailerAnalytics error:', e); window.showToast?.('Error: ' + e.message, 'error'); }
}

function showAssetAnalytics(id) {
  const asset = BiState.assets.find(a => a.id === id);
  if (!asset) return;

  const maintCosts = BiState.maintenance.filter(m => m.assetId === id && isInPeriod(m.createdAt));
  const totalMaintCost = maintCosts.reduce((s, m) => s + (m.costs?.totalCost || m.totalCost || 0), 0);

  // Jobs assigned
  const periodJobDays = BiState.jobDays.filter(jd => isInPeriod(jd.date));
  const assetJobIds = new Set();
  periodJobDays.forEach(jd => {
    const job = BiState.jobs.find(j => j.id === jd.jobId);
    if (job?.assets?.some(ref => ref === id || ref === asset.name)) assetJobIds.add(jd.jobId);
  });

  const costByCat = {};
  maintCosts.forEach(m => {
    const cat = m.category || 'maintenance';
    costByCat[cat] = (costByCat[cat] || 0) + (m.costs?.totalCost || m.totalCost || 0);
  });

  const loan = asset.loan;
  const loanSection = loan?.enabled ? renderLoanSection(loan, id, 'asset') : `
    <div style="text-align:center;padding:12px;">
      <button class="btn btn-sm btn-secondary" onclick="window.BIModule.editLoanInfo('${escJsAttr(id)}','asset')" style="border-radius:6px;">
        <span class="material-icons" style="font-size:16px;vertical-align:middle;">add</span> Add Loan Info
      </button>
    </div>`;

  renderAnalyticsModal(asset.name, 'inventory_2', `
    <div class="crm-stats-grid" style="grid-template-columns:repeat(auto-fit,minmax(120px,1fr));margin-bottom:16px;">
      ${statCard('build', 'Maint Cost', formatCurrency(totalMaintCost), 'red')}
      ${statCard('work', 'Jobs Assigned', assetJobIds.size, 'blue')}
      ${asset.hoursTracking?.enabled ? statCard('schedule', 'Hours', (asset.hoursTracking.currentHours || 0).toFixed(0), 'amber') : ''}
    </div>
    <div class="crm-section-card" style="margin-bottom:12px;">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">account_balance</span> Loan</div>
      ${loanSection}
    </div>
    ${Object.keys(costByCat).length > 0 ? `
    <div class="crm-section-card">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">pie_chart</span> Cost by Category</div>
      ${renderHBars(costByCat, '#EF4444', true)}
    </div>` : ''}
  `);
}

function showCustomerAnalytics(id) {
  const customer = BiState.customers.find(c => c.id === id);
  if (!customer) return;
  const d = getCustomerPeriodData(id);

  // Set drill context for customer
  drillCtx = { customerId: id, customerData: d, customerName: customer.name };

  renderAnalyticsModal(customer.name, 'business', `
    <div class="crm-stats-grid" style="grid-template-columns:repeat(auto-fit,minmax(140px,1fr));margin-bottom:8px;">
      ${statCard('payments', 'Revenue', formatCurrency(d.totalRevenue), 'green', 'cust-revenue')}
      ${statCard('receipt', 'Tickets', d.ticketCount, 'blue', 'cust-tickets')}
      ${statCard('pending', 'Outstanding', formatCurrency(d.outstanding), 'amber', 'cust-outstanding')}
    </div>
    <div class="crm-stats-grid" style="grid-template-columns:repeat(2,1fr);margin-bottom:16px;">
      ${statCard('attach_money', 'Avg Ticket', formatCurrency(d.avgTicketVal), 'teal', 'cust-avgticket')}
      ${statCard('money_off', 'Avg Cost', formatCurrency(d.avgCostPerJob), 'purple', 'cust-avgcost')}
    </div>
    <div id="bi-modal-drill" data-active-key="" style="margin-top:4px;"></div>
    ${Object.keys(d.revByMonth).length > 0 ? `
    <div class="crm-section-card" style="margin-bottom:12px;">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">trending_up</span> Revenue by Month</div>
      ${renderHBars(d.revByMonth, '#10B981', true)}
    </div>` : ''}
    ${Object.keys(d.topVehicles).length > 0 ? `
    <div class="crm-section-card" style="margin-bottom:12px;">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">local_shipping</span> Top Vehicles</div>
      ${renderHBars(d.topVehicles, '#3B82F6', false)}
    </div>` : ''}
    ${Object.keys(d.topCrew).length > 0 ? `
    <div class="crm-section-card">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">group</span> Top Crew (hours)</div>
      ${renderHBars(d.topCrew, '#8B5CF6', false)}
    </div>` : ''}
  `);
}

function showVendorAnalytics(id) {
  const vendor = BiState.customers.find(c => c.id === id);
  if (!vendor) return;
  const d = getVendorPeriodData(id);

  renderAnalyticsModal(vendor.name, 'store', `
    <div class="crm-stats-grid" style="grid-template-columns:repeat(auto-fit,minmax(140px,1fr));margin-bottom:16px;">
      ${statCard('money_off', 'Total Spend', formatCurrency(d.totalSpend), 'red')}
      ${statCard('receipt', 'Payments', d.paymentCount, 'blue')}
    </div>
    ${Object.keys(d.spendByCat).length > 0 ? `
    <div class="crm-section-card" style="margin-bottom:12px;">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">pie_chart</span> Spend by Category</div>
      ${renderHBars(d.spendByCat, '#F59E0B', true)}
    </div>` : ''}
    ${Object.keys(d.spendByMonth).length > 0 ? `
    <div class="crm-section-card">
      <div class="crm-section-title" style="margin-bottom:8px;"><span class="material-icons" style="color:var(--text-secondary);font-size:18px;">trending_up</span> Spend by Month</div>
      ${renderHBars(d.spendByMonth, '#F59E0B', true)}
    </div>` : ''}
  `);
}

// ============================================
// LOAN FEATURE
// ============================================
function computeLoanDetails(loan) {
  if (!loan || !loan.enabled) return null;
  const { loanAmount = 0, apr = 0, termMonths = 0, monthlyPayment = 0, startDate } = loan;
  const r = apr / 12 / 100;
  const n = termMonths;
  const start = startDate ? new Date(startDate) : new Date();

  // Months elapsed
  const now = new Date();
  const monthsElapsed = Math.max(0, (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth()));
  const N = Math.min(monthsElapsed, n);

  // Remaining balance
  let remainingBalance;
  if (r > 0 && n > 0) {
    const factor = Math.pow(1 + r, n);
    const factorN = Math.pow(1 + r, N);
    remainingBalance = loanAmount * (factor - factorN) / (factor - 1);
  } else {
    remainingBalance = loanAmount - (monthlyPayment * N);
  }
  remainingBalance = Math.max(0, remainingBalance);

  const totalInterest = (monthlyPayment * n) - loanAmount;
  const monthsRemaining = Math.max(0, n - N);

  const payoffDate = new Date(start);
  payoffDate.setMonth(payoffDate.getMonth() + n);

  return { remainingBalance, totalInterest, monthsRemaining, monthsElapsed: N, payoffDate, loanAmount, apr, termMonths, monthlyPayment };
}

function generateAmortizationSchedule(loan) {
  const { loanAmount = 0, apr = 0, termMonths = 0, monthlyPayment = 0, startDate } = loan;
  const r = apr / 12 / 100;
  const start = startDate ? new Date(startDate) : new Date();
  let balance = loanAmount;
  const schedule = [];
  for (let i = 1; i <= termMonths && balance > 0.01; i++) {
    const interest = r > 0 ? balance * r : 0;
    const principal = Math.min(monthlyPayment - interest, balance);
    balance = Math.max(0, balance - principal);
    const payDate = new Date(start);
    payDate.setMonth(payDate.getMonth() + i);
    schedule.push({ num: i, date: payDate, payment: monthlyPayment, principal, interest, balance });
  }
  return schedule;
}

function showLoanStatement(entityId, entityType) {
  const allEquip = [
    ...BiState.trucks.map(t => ({ ...t, _type: 'truck', _label: window.getUnitDisplay ? window.getUnitDisplay(t) : `#${t.unit}` })),
    ...BiState.trailers.map(t => ({ ...t, _type: 'trailer', _label: window.getUnitDisplay ? window.getUnitDisplay(t) : `#${t.unit}` })),
    ...BiState.assets.map(a => ({ ...a, _type: 'asset', _label: a.name || a.id }))
  ];
  const entity = allEquip.find(e => e.id === entityId && e._type === entityType);
  if (!entity?.loan?.enabled) { showToast('No loan data for this vehicle', 'error'); return; }

  const loan = entity.loan;
  const details = computeLoanDetails(loan);
  if (!details) return;

  const schedule = generateAmortizationSchedule(loan);
  const startDate = loan.startDate ? new Date(loan.startDate) : new Date();
  const maturityDate = new Date(startDate);
  maturityDate.setMonth(maturityDate.getMonth() + loan.termMonths);

  // YTD calculations
  const yearStart = new Date(new Date().getFullYear(), 0, 1);
  const now = new Date();
  const ytdPayments = schedule.filter(s => s.date >= yearStart && s.date <= now);
  const ytdInterest = ytdPayments.reduce((s, p) => s + p.interest, 0);
  const ytdPrincipal = ytdPayments.reduce((s, p) => s + p.principal, 0);
  const totalInterestPaid = schedule.filter(s => s.date <= now).reduce((s, p) => s + p.interest, 0);

  const prev = document.getElementById('bi-loan-stmt-modal');
  if (prev) prev.remove();

  const modal = document.createElement('div');
  modal.id = 'bi-loan-stmt-modal';
  modal.className = 'modal loan-statement-print';
  modal.onclick = (e) => { if (e.target === modal) modal.remove(); };

  const fmtDate = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const fmtMo = (d) => d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

  modal.innerHTML = `
    <div class="modal-content" style="max-width:700px;max-height:90vh;display:flex;flex-direction:column;">
      <div class="modal-header no-print">
        <h3 style="margin:0;">Loan Statement — ${escBI(entity._label)}</h3>
        <div style="display:flex;gap:8px;align-items:center;">
          <button class="btn btn-sm btn-secondary" onclick="window.print()" style="border-radius:6px;">
            <span class="material-icons" style="font-size:14px;vertical-align:middle;">print</span> Print
          </button>
          <button class="btn-icon" onclick="document.getElementById('bi-loan-stmt-modal').remove()"><span class="material-icons">close</span></button>
        </div>
      </div>
      <div class="modal-body" style="padding:24px;overflow-y:auto;flex:1;">
        <div style="text-align:center;margin-bottom:20px;">
          <div style="font-size:18px;font-weight:700;">Loan Statement</div>
          <div style="font-size:14px;color:var(--text-secondary);">${escBI(entity._label)}</div>
          <div style="font-size:12px;color:var(--text-secondary);">As of ${fmtDate(now)}</div>
        </div>

        <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:20px;">
          <div class="crm-section-card" style="margin:0;padding:16px;">
            <div style="font-size:13px;font-weight:600;margin-bottom:10px;color:var(--text-secondary);">LOAN DETAILS</div>
            <div style="font-size:13px;line-height:2;">
              <div style="display:flex;justify-content:space-between;"><span>Lender:</span><strong>${escBI(loan.lender || 'N/A')}</strong></div>
              ${loan.accountNumber ? `<div style="display:flex;justify-content:space-between;"><span>Account #:</span><strong>${escBI(loan.accountNumber)}</strong></div>` : ''}
              <div style="display:flex;justify-content:space-between;"><span>Original Amount:</span><strong>${formatCurrency(loan.loanAmount)}</strong></div>
              <div style="display:flex;justify-content:space-between;"><span>APR:</span><strong>${loan.apr}%</strong></div>
              <div style="display:flex;justify-content:space-between;"><span>Term:</span><strong>${loan.termMonths} months</strong></div>
              <div style="display:flex;justify-content:space-between;"><span>Monthly Payment:</span><strong>${formatCurrency(loan.monthlyPayment)}</strong></div>
            </div>
          </div>
          <div class="crm-section-card" style="margin:0;padding:16px;">
            <div style="font-size:13px;font-weight:600;margin-bottom:10px;color:var(--text-secondary);">KEY DATES & STATUS</div>
            <div style="font-size:13px;line-height:2;">
              <div style="display:flex;justify-content:space-between;"><span>Start Date:</span><strong>${fmtDate(startDate)}</strong></div>
              <div style="display:flex;justify-content:space-between;"><span>Maturity Date:</span><strong>${fmtDate(maturityDate)}</strong></div>
              ${loan.escrowAmount ? `<div style="display:flex;justify-content:space-between;"><span>Escrow/Mo:</span><strong>${formatCurrency(loan.escrowAmount)}</strong></div>` : ''}
              <div style="display:flex;justify-content:space-between;"><span>Remaining Balance:</span><strong style="color:#DC2626;">${formatCurrency(details.remainingBalance)}</strong></div>
              <div style="display:flex;justify-content:space-between;"><span>Payments Made:</span><strong>${details.monthsElapsed} of ${details.termMonths}</strong></div>
              <div style="display:flex;justify-content:space-between;"><span>Months Remaining:</span><strong>${details.monthsRemaining}</strong></div>
            </div>
          </div>
        </div>

        <div class="crm-section-card" style="margin-bottom:20px;padding:16px;">
          <div style="font-size:13px;font-weight:600;margin-bottom:10px;color:var(--text-secondary);">INTEREST SUMMARY</div>
          <div style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px;text-align:center;">
            <div>
              <div style="font-size:11px;color:var(--text-secondary);">YTD Interest</div>
              <div style="font-size:18px;font-weight:700;color:#DC2626;">${formatCurrency(ytdInterest)}</div>
            </div>
            <div>
              <div style="font-size:11px;color:var(--text-secondary);">YTD Principal</div>
              <div style="font-size:18px;font-weight:700;color:#10B981;">${formatCurrency(ytdPrincipal)}</div>
            </div>
            <div>
              <div style="font-size:11px;color:var(--text-secondary);">Total Interest Paid</div>
              <div style="font-size:18px;font-weight:700;color:#F59E0B;">${formatCurrency(totalInterestPaid)}</div>
            </div>
          </div>
        </div>

        <div class="crm-section-card" style="padding:16px;">
          <div style="font-size:13px;font-weight:600;margin-bottom:10px;color:var(--text-secondary);">AMORTIZATION SCHEDULE</div>
          <div style="max-height:300px;overflow-y:auto;">
            <table style="width:100%;font-size:12px;border-collapse:collapse;">
              <thead>
                <tr style="position:sticky;top:0;background:var(--bg-primary);border-bottom:2px solid var(--border-light);">
                  <th style="text-align:left;padding:6px 4px;">#</th>
                  <th style="text-align:left;padding:6px 4px;">Date</th>
                  <th style="text-align:right;padding:6px 4px;">Payment</th>
                  <th style="text-align:right;padding:6px 4px;">Principal</th>
                  <th style="text-align:right;padding:6px 4px;">Interest</th>
                  <th style="text-align:right;padding:6px 4px;">Balance</th>
                </tr>
              </thead>
              <tbody>
                ${schedule.map(s => {
                  const isPast = s.date <= now;
                  return `<tr style="${isPast ? '' : 'color:var(--text-secondary);'}${s.num === details.monthsElapsed ? 'background:#3B82F610;font-weight:600;' : ''}border-bottom:1px solid var(--border-light);">
                    <td style="padding:4px;">${s.num}</td>
                    <td style="padding:4px;">${fmtMo(s.date)}</td>
                    <td style="text-align:right;padding:4px;">${formatCurrency(s.payment)}</td>
                    <td style="text-align:right;padding:4px;">${formatCurrency(s.principal)}</td>
                    <td style="text-align:right;padding:4px;">${formatCurrency(s.interest)}</td>
                    <td style="text-align:right;padding:4px;">${formatCurrency(s.balance)}</td>
                  </tr>`;
                }).join('')}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

function renderLoanSection(loan, entityId, entityType) {
  const details = computeLoanDetails(loan);
  if (!details) return '';
  return `
    <div class="bi-loan-grid">
      <div class="bi-loan-item">
        <div class="bi-loan-label">Monthly Payment</div>
        <div class="bi-loan-value">${formatCurrency(details.monthlyPayment)}</div>
      </div>
      <div class="bi-loan-item">
        <div class="bi-loan-label">APR</div>
        <div class="bi-loan-value">${details.apr}%</div>
      </div>
      <div class="bi-loan-item">
        <div class="bi-loan-label">Remaining Balance</div>
        <div class="bi-loan-value">${formatCurrency(details.remainingBalance)}</div>
      </div>
      <div class="bi-loan-item">
        <div class="bi-loan-label">Months Remaining</div>
        <div class="bi-loan-value">${details.monthsRemaining}</div>
      </div>
      <div class="bi-loan-item">
        <div class="bi-loan-label">Total Interest</div>
        <div class="bi-loan-value">${formatCurrency(details.totalInterest)}</div>
      </div>
      <div class="bi-loan-item">
        <div class="bi-loan-label">Payoff Date</div>
        <div class="bi-loan-value">${details.payoffDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>
      </div>
    </div>
    ${loan.lender ? `<div style="font-size:12px;color:var(--text-secondary);margin-top:8px;">Lender: ${escBI(loan.lender)}</div>` : ''}
    ${loan.notes ? `<div style="font-size:12px;color:var(--text-secondary);margin-top:4px;">Notes: ${escBI(loan.notes)}</div>` : ''}
    <div style="text-align:right;margin-top:8px;">
      <button class="btn btn-sm btn-secondary" onclick="window.BIModule.editLoanInfo('${escJsAttr(entityId)}','${escJsAttr(entityType)}')" style="border-radius:6px;font-size:12px;">
        <span class="material-icons" style="font-size:14px;vertical-align:middle;">edit</span> Edit Loan
      </button>
    </div>
  `;
}

// TASK-10174 — units of one kind; `loan` reads by the dual-read rule
// (= services/vehicleLoans.js pickLoan): the vehicleLoans home doc wins, else
// the legacy map still on the unit doc.
function _biUnits(snap, k) {
  return snap.docs.map(d => {
    const v = d.data();
    return { id: d.id, ...v, get loan() { const h = BiState.vehicleLoans.get(k + '_' + d.id); return h ? h.loan : v.loan; } };
  });
}

function editLoanInfo(entityId, entityType) {
  const stateMap = { truck: BiState.trucks, trailer: BiState.trailers, asset: BiState.assets };
  const entity = stateMap[entityType]?.find(e => e.id === entityId);
  const loan = entity?.loan || {};

  closeLoanEdit();
  const modal = document.createElement('div');
  modal.id = 'bi-loan-modal';
  modal.className = 'modal';
  modal.onclick = (e) => { if (e.target === modal) closeLoanEdit(); };
  modal.innerHTML = `
    <div class="modal-content" style="max-width:480px;">
      <div class="modal-header">
        <h3 style="margin:0;">Loan Information</h3>
        <button class="btn-icon" onclick="window.BIModule.closeLoanEdit()"><span class="material-icons">close</span></button>
      </div>
      <div class="modal-body" style="padding:20px;max-height:70vh;overflow-y:auto;">
        <div class="form-group">
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;">
            <input type="checkbox" id="loan-enabled" ${loan.enabled ? 'checked' : ''}>
            <span>Loan tracking enabled</span>
          </label>
        </div>
        <div class="form-group">
          <label for="loan-amount">Loan Amount ($)</label>
          <input type="number" id="loan-amount" class="form-control" step="0.01" min="0" value="${escBI(loan.loanAmount || '')}">
        </div>
        <div class="form-group">
          <label for="loan-apr">APR (%)</label>
          <input type="number" id="loan-apr" class="form-control" step="0.01" min="0" max="100" value="${escBI(loan.apr || '')}">
        </div>
        <div class="form-group">
          <label for="loan-term">Term (months)</label>
          <input type="number" id="loan-term" class="form-control" step="1" min="1" value="${escBI(loan.termMonths || '')}">
        </div>
        <div class="form-group">
          <label for="loan-payment">Monthly Payment ($)</label>
          <input type="number" id="loan-payment" class="form-control" step="0.01" min="0" value="${escBI(loan.monthlyPayment || '')}">
        </div>
        <div class="form-group">
          <label for="loan-start">Start Date</label>
          <input type="date" id="loan-start" class="form-control" value="${escBI(loan.startDate || '')}">
        </div>
        <div class="form-group">
          <label for="loan-lender">Lender</label>
          <input type="text" id="loan-lender" class="form-control" value="${escBI(loan.lender || '')}">
        </div>
        <div class="form-group">
          <label for="loan-notes">Notes</label>
          <textarea id="loan-notes" class="form-control" rows="2">${escBI(loan.notes || '')}</textarea>
        </div>
        <div style="border-top:1px solid var(--border-light);margin-top:12px;padding-top:12px;">
          <div style="font-weight:600;font-size:14px;margin-bottom:12px;display:flex;align-items:center;gap:6px;">
            <span class="material-icons" style="font-size:18px;color:#7C3AED;">calculate</span> Depreciation
          </div>
          <div class="form-group">
            <label for="loan-purchase-price">Purchase Price ($)</label>
            <input type="number" id="loan-purchase-price" class="form-control" step="0.01" min="0" value="${escBI(loan.purchasePrice || '')}">
          </div>
          <div class="form-group">
            <label for="loan-purchase-date">Date Acquired</label>
            <input type="date" id="loan-purchase-date" class="form-control" value="${escBI(loan.purchaseDate || '')}">
          </div>
          <div class="form-group">
            <label for="loan-dep-method">Depreciation Method</label>
            <select id="loan-dep-method" class="form-control">
              <option value="none" ${(!loan.depMethod || loan.depMethod === 'none') ? 'selected' : ''}>None</option>
              <option value="macrs5" ${loan.depMethod === 'macrs5' ? 'selected' : ''}>MACRS 5-Year</option>
              <option value="macrs7" ${loan.depMethod === 'macrs7' ? 'selected' : ''}>MACRS 7-Year</option>
              <option value="straight" ${loan.depMethod === 'straight' ? 'selected' : ''}>Straight Line</option>
            </select>
          </div>
          <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">
            <div class="form-group">
              <label for="loan-useful-life">Useful Life (years)</label>
              <input type="number" id="loan-useful-life" class="form-control" step="1" min="1" value="${escBI(String(loan.usefulLife || 5))}">
            </div>
            <div class="form-group">
              <label for="loan-salvage">Salvage Value ($)</label>
              <input type="number" id="loan-salvage" class="form-control" step="0.01" min="0" value="${escBI(String(loan.salvageValue || 0))}">
            </div>
          </div>
        </div>
      </div>
      <div class="form-actions" style="padding:16px 24px 20px;border-top:1px solid var(--border-light);display:flex;gap:12px;justify-content:flex-end;">
        <button class="btn btn-secondary" onclick="window.BIModule.closeLoanEdit()" style="border-radius:8px;">Cancel</button>
        <button class="btn btn-primary" onclick="window.BIModule.saveLoanInfo('${escJsAttr(entityId)}','${escJsAttr(entityType)}')" style="border-radius:8px;">
          <span class="material-icons" style="font-size:18px;vertical-align:middle;">save</span> Save
        </button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

async function saveLoanInfo(entityId, entityType) {

  const loanData = {
    enabled: document.getElementById('loan-enabled')?.checked || false,
    loanAmount: parseFloat(document.getElementById('loan-amount')?.value) || 0,
    apr: parseFloat(document.getElementById('loan-apr')?.value) || 0,
    termMonths: parseInt(document.getElementById('loan-term')?.value, 10) || 0,
    monthlyPayment: parseFloat(document.getElementById('loan-payment')?.value) || 0,
    startDate: document.getElementById('loan-start')?.value || '',
    lender: document.getElementById('loan-lender')?.value?.trim() || '',
    notes: document.getElementById('loan-notes')?.value?.trim() || '',
    purchasePrice: parseFloat(document.getElementById('loan-purchase-price')?.value) || 0,
    purchaseDate: document.getElementById('loan-purchase-date')?.value || '',
    depMethod: document.getElementById('loan-dep-method')?.value || 'none',
    usefulLife: parseInt(document.getElementById('loan-useful-life')?.value, 10) || 5,
    salvageValue: parseFloat(document.getElementById('loan-salvage')?.value) || 0
  };

  try {
    const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
    // TASK-10174 — the server-only home vehicleLoans/{kind}_{id}; the server also
    // drops the legacy map off the driver-readable unit doc. The shared demo
    // tenant cannot reach a callable (anonymous, no member doc) and holds no real
    // money: it keeps the old write on the unit doc (truck -> trucks …).
    if (tenant === 'demo') await setDoc(doc(db, 'tenants', tenant, entityType + 's', entityId), { loan: loanData }, { merge: true });
    else await httpsCallable(functions, 'setVehicleLoan')({ kind: entityType, vehicleId: entityId, loan: loanData });
    window.showToast?.('Loan info saved', 'success');
    closeLoanEdit();
    // Refresh analytics modal if open
    closeAnalyticsModal();
  } catch (e) {
    console.error('[BI] Save loan error:', e);
    window.showToast?.('Error saving loan info', 'error');
  }
}

function closeLoanEdit() {
  const m = document.getElementById('bi-loan-modal');
  if (m) m.remove();
}

// ============================================
// CSV EXPORT UTILITIES
// ============================================
function escapeCSV(val) {
  if (val == null) return '';
  let str = String(val);
  // Prevent Excel formula injection
  // TASK-9701 S2 — except a plain number: "-1234.50" (a loss) stays a number, not text.
  if (/^[=+\-@\t\r]/.test(str) && !/^-\d+(\.\d+)?$/.test(str)) str = "'" + str;
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes("'"))
    return '"' + str.replace(/"/g, '""') + '"';
  return str;
}

function generateCSV(headers, rows) {
  const lines = [headers.map(escapeCSV).join(',')];
  rows.forEach(row => lines.push(row.map(escapeCSV).join(',')));
  return lines.join('\r\n');
}

function downloadCSV(filename, csvContent) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  showToast('Export downloaded', 'success');
  // Close dropdown
  const menu = document.getElementById('bi-export-menu');
  if (menu) menu.classList.add('hidden');
}

function getPeriodLabel() {
  const s = BiState.periodStart, e = BiState.periodEnd;
  const fmt = d => d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
  return `${fmt(s)} - ${fmt(e)}`;
}

// Filename-safe period slug for CSV/IIF exports. For custom ranges, stamp the
// concrete dates so exported files stay auditable (e.g. CUSTOM_2026-06-01_to_2026-06-10).
function getPeriodSlug() {
  if (BiState.period === 'custom') {
    return `CUSTOM_${isoDateLocal(BiState.periodStart)}_to_${isoDateLocal(BiState.periodEnd)}`;
  }
  return BiState.period.toUpperCase();
}

function exportIncomeStatement() {
  const entries = rentalAwareRevenueEntries(isInPeriod); // TASK-842-C
  entries.sort((a, b) => (toDate(a.invoicedAt) || 0) - (toDate(b.invoicedAt) || 0));
  const headers = ['Date', 'Invoice #', 'Customer', 'Job', 'Category', 'Amount'];
  const rows = entries.map(r => {
    const d = toDate(r.invoicedAt);
    return [
      d ? d.toLocaleDateString('en-US') : '',
      r.invoiceNumber || '',
      r.customerName || '',
      r.jobName || '',
      (r.revenueCategories || []).join(', ') || '',
      (r.amount || 0).toFixed(2)
    ];
  });
  // Add total row
  const total = entries.reduce((s, r) => s + (r.amount || 0), 0);
  rows.push(['', '', '', '', 'TOTAL', total.toFixed(2)]);
  const csv = generateCSV(headers, rows);
  const period = getPeriodSlug();
  downloadCSV(`DispatchDeck_Income_Statement_${period}.csv`, csv);
}

function exportExpenseReport() {
  // TASK-9493 — a split receipt exports ONE ROW PER LINE (its own Job /
  // Vehicle / amount); the lines sum to the receipt, so the TOTAL is unchanged.
  const entries = _costViewsOf(BiState.costEntries).filter(c => isInPeriod(c.date));
  entries.sort((a, b) => (toDate(a.date) || 0) - (toDate(b.date) || 0));
  const headers = ['Date', 'Category', 'Vendor/Source', 'Description', 'Job', 'Vehicle', 'Amount'];
  const rows = entries.map(c => {
    const d = toDate(c.date);
    return [
      d ? d.toLocaleDateString('en-US') : '',
      exportCategoryOf(c), // TASK-9917 — personal charges: draws / employee_receivable
      c.vendorName || (c.source === 'ap_invoice' ? 'AP Invoice' : ''),
      (c.description || '') + (c._splitOf !== undefined ? ` (split ${c._splitIndex + 1}/${(c.allocations || []).length})` : ''),
      c.jobName || '',
      c.vehicleDisplay || '',
      (c.amount || 0).toFixed(2)
    ];
  });
  // Add labor costs
  const periodClock = BiState.clockEntries.filter(c => isInPeriod(c.clockIn));
  const jobCostRates = _biJobCostRates(BiState.people); // burdened; salary allocated to job-clocked hours
  let totalLabor = 0;
  periodClock.forEach(c => {
    const hrs = c.totalHours || c.hours || 0;
    const rate = _BiLaborRate.jobCostRateForEntry(c, jobCostRates);
    const cost = hrs * rate;
    if (cost > 0) {
      totalLabor += cost;
      const d = toDate(c.clockIn);
      const personName = BiState.people.find(p => p.id === c.personId)?.name || c.personId;
      rows.push([
        d ? d.toLocaleDateString('en-US') : '',
        'labor',
        '',
        `Labor: ${personName} (${hrs.toFixed(1)}h @ $${rate.toFixed(2)}/hr${jobCostRates.hourly[c.personId] ? '' : ' salary allocation'}${jobCostRates.burden.isDefault ? '' : ', incl ' + jobCostRates.burden.pct + '% burden'})`,
        c.jobName || c.jobTitle || '',
        '',
        cost.toFixed(2)
      ]);
    }
  });
  rows.sort((a, b) => new Date(a[0]) - new Date(b[0]));
  const total = rows.reduce((s, r) => s + parseFloat(r[6] || 0), 0);
  rows.push(['', '', '', '', '', 'TOTAL', total.toFixed(2)]);
  const csv = generateCSV(headers, rows);
  downloadCSV(`DispatchDeck_Expense_Report_${getPeriodSlug()}.csv`, csv);
}

async function exportGeneralLedger() {
  const PR = await _loadPersonalRepay(); // TASK-9917 slice 2
  const basis = getAccountingBasis();
  const rows = [];
  // Income entries
  const incomeEntries = basis === 'cash'
    ? BiState.revenueEntries.filter(r => r.paid && isInPeriod(r.paidAt))
    : rentalAwareRevenueEntries(isInPeriod); // TASK-842-C (accrual only)
  incomeEntries.forEach(r => {
    const d = basis === 'cash' ? toDate(r.paidAt) : toDate(r.invoicedAt);
    rows.push([
      d ? d.toLocaleDateString('en-US') : '',
      'Income',
      r.invoiceNumber || '',
      `${r.customerName || ''} - ${r.jobName || ''}`.trim().replace(/^- | -$/g, ''),
      (r.revenueCategories || []).join(', ') || '',
      (r.amount || 0).toFixed(2),
      '',
      (r.amount || 0).toFixed(2)
    ]);
  });
  // Expense entries
  const expEntries = basis === 'cash'
    ? BiState.costEntries.filter(c => isInPeriod(c.paidDate || c.date))
    : BiState.costEntries.filter(c => isInPeriod(c.date));
  expEntries.forEach(c => {
    const d = basis === 'cash' ? toDate(c.paidDate || c.date) : toDate(c.date);
    rows.push([
      d ? d.toLocaleDateString('en-US') : '',
      personalChargeOf(c) ? 'Personal charge' : 'Expense', // TASK-9917
      exportCategoryOf(c),
      c.description || '',
      '',
      '',
      (c.amount || 0).toFixed(2),
      (-c.amount || 0).toFixed(2)
    ]);
  });
  // TASK-9917 slice 2 — an employee paying back a personal charge reduces Due
  // from Employees (dated on the day it was repaid, either basis: it is cash or
  // a paycheck deduction on that day); an undone one is a reversal on the undo day.
  rows.push(...PR.glRows(BiState.costEntries, isInPeriod, _personalRepayDeps()));
  rows.sort((a, b) => new Date(a[0]) - new Date(b[0]));
  const headers = ['Date', 'Type', 'Reference', 'Description', 'Category', 'Credit', 'Debit', 'Net'];
  const csv = generateCSV(headers, rows);
  downloadCSV(`DispatchDeck_General_Ledger_${getPeriodSlug()}.csv`, csv);
}


function exportPayrollJournalEntries() {
  const periodClock = BiState.clockEntries.filter(c => isInPeriod(c.clockIn));
  // 🔴 WAGE map: a payroll journal entry books WAGES, not burdened job cost.
  const personRateMap = _biRateMap(BiState.people);
  const personNameMap = {};
  BiState.people.forEach(p => { if (p && p.id) personNameMap[p.id] = p.name || p.id; });

  // Group clock entries by person
  const byPerson = {};
  periodClock.forEach(c => {
    const pid = c.personId || 'unknown';
    if (!byPerson[pid]) byPerson[pid] = { totalHours: 0, otHours: 0, entries: [] };
    byPerson[pid].totalHours += (c.totalHours || c.hours || 0);
    byPerson[pid].otHours += (c.otHours || 0);
    byPerson[pid].entries.push(c);
  });

  const period = getPeriodSlug();
  const periodLabel = getPeriodLabel();
  const jeDate = BiState.periodEnd ? (BiState.periodEnd.getMonth() + 1 + '/' + BiState.periodEnd.getDate() + '/' + BiState.periodEnd.getFullYear()) : new Date().toLocaleDateString('en-US');
  const jeDateKey = BiState.periodEnd ? BiState.periodEnd.toISOString().slice(0, 10).replace(/-/g, '') : new Date().toISOString().slice(0, 10).replace(/-/g, '');

  const headers = ['JournalNo', 'JournalDate', 'AccountName', 'Debits', 'Credits', 'Description', 'Name', 'Memo'];
  const rows = [];
  let entryNum = 0;

  Object.entries(byPerson).forEach(([pid, data]) => {
    const rate = personRateMap[pid] || 0;
    if (rate === 0 && data.totalHours === 0) return;
    const name = personNameMap[pid] || pid;
    const regHours = Math.max(0, data.totalHours - data.otHours);
    const regPay = regHours * rate;
    const otPay = data.otHours * rate * 1.5;
    const totalPay = regPay + otPay;
    if (totalPay === 0) return;

    entryNum++;
    const journalNo = `PRL-${jeDateKey}-${String(entryNum).padStart(3, '0')}`;
    const memo = `Payroll ${periodLabel}`;

    // DEBIT: Wage Expense for regular pay
    if (regPay > 0) {
      rows.push([journalNo, jeDate, 'Wage Expense', regPay.toFixed(2), '', `Regular pay: ${name} (${regHours.toFixed(1)}h @ $${rate}/hr)`, name, memo]);
    }
    // DEBIT: Overtime Expense for OT pay
    if (otPay > 0) {
      rows.push([journalNo, jeDate, 'Overtime Expense', otPay.toFixed(2), '', `OT pay: ${name} (${data.otHours.toFixed(1)}h @ $${(rate * 1.5).toFixed(2)}/hr)`, name, memo]);
    }
    // CREDIT: Payroll Liabilities for the total
    rows.push([journalNo, jeDate, 'Payroll Liabilities', '', totalPay.toFixed(2), `Payroll: ${name}`, name, memo]);
  });

  if (rows.length === 0) {
    showToast('No payroll data for this period', 'error');
    return;
  }

  const csv = generateCSV(headers, rows);
  downloadCSV(`DispatchDeck_Payroll_JE_${period}.csv`, csv);
}

async function exportBankFeedCSV() {
  const PR = await _loadPersonalRepay(); // TASK-9917 slice 2
  const basis = getAccountingBasis();
  const rows = [];

  // Revenue (money IN) — from revenueEntries
  const revFilter = basis === 'cash'
    ? BiState.revenueEntries.filter(r => r.paid && isInPeriod(r.paidAt))
    : BiState.revenueEntries.filter(r => isInPeriod(r.invoicedAt));
  revFilter.forEach(r => {
    const d = basis === 'cash' ? toDate(r.paidAt) : toDate(r.invoicedAt);
    if (!d) return;
    const dateStr = (d.getMonth() + 1) + '/' + d.getDate() + '/' + d.getFullYear();
    const desc = [r.invoiceNumber ? `Invoice ${r.invoiceNumber}` : '', r.ticketNumber ? `Ticket #${r.ticketNumber}` : '', r.customerName || ''].filter(Boolean).join(' - ') || 'Revenue';
    rows.push([dateStr, desc, (r.amount || 0).toFixed(2), d.getTime()]);
  });

  // Expenses (money OUT) — from costEntries
  const costFilter = basis === 'cash'
    ? BiState.costEntries.filter(c => isInPeriod(c.paidDate || c.date))
    : BiState.costEntries.filter(c => isInPeriod(c.date));
  costFilter.forEach(c => {
    const d = basis === 'cash' ? toDate(c.paidDate || c.date) : toDate(c.date);
    if (!d) return;
    const dateStr = (d.getMonth() + 1) + '/' + d.getDate() + '/' + d.getFullYear();
    const catLabel = CATEGORY_LABELS[c.category] || c.category || 'Expense';
    const desc = [catLabel, c.vendorName || '', c.description || ''].filter(Boolean).join(' - ');
    // TASK-9367 — negate, don't -abs(): a charge stays money OUT (negative), a
    // vendor refund / credit (stored negative) becomes money IN (positive).
    rows.push([dateStr, desc, (-(Number(c.amount) || 0)).toFixed(2), d.getTime()]);
  });

  // TASK-9917 slice 2 — personal-charge repayments paid in cash / check / other
  // (money IN; an undone one money OUT). A payroll deduction never reaches the bank.
  rows.push(...PR.bankRows(BiState.costEntries, isInPeriod, _personalRepayDeps()));

  // Labor costs (money OUT) — from clock entries
  const personRateMap = _biRateMap(BiState.people); // 🔴 WAGE map: a bank feed shows cash paid, never burdened cost
  const personNameMap = {};
  BiState.people.forEach(p => { if (p && p.id) personNameMap[p.id] = p.name || p.id; });
  BiState.clockEntries.filter(c => isInPeriod(c.clockIn)).forEach(c => {
    const hrs = c.totalHours || c.hours || 0;
    const rate = personRateMap[c.personId] || 0;
    const cost = hrs * rate;
    if (cost <= 0) return;
    const d = toDate(c.clockIn);
    if (!d) return;
    const dateStr = (d.getMonth() + 1) + '/' + d.getDate() + '/' + d.getFullYear();
    const name = personNameMap[c.personId] || c.personId;
    rows.push([dateStr, `Labor - ${name} (${hrs.toFixed(1)}h)`, (-cost).toFixed(2), d.getTime()]);
  });

  if (rows.length === 0) {
    showToast('No transactions for this period', 'error');
    return;
  }

  // Sort by date ascending
  rows.sort((a, b) => a[3] - b[3]);

  const headers = ['Date', 'Description', 'Amount'];
  const csvRows = rows.map(r => [r[0], r[1], r[2]]);
  const csv = generateCSV(headers, csvRows);
  const period = getPeriodSlug();
  downloadCSV(`DispatchDeck_Bank_Feed_${period}.csv`, csv);
}

// TASK-9354 — the TIN (ssn / ein), tinType and businessName live ONLY in the
// peopleTax sibling since the April 2026 tax migration (hr.js SECURE_TAX_FIELDS);
// the broadcast people doc no longer carries them (0 of 466 live docs carry ssn, ssnFull, ein or ssnHash, measured
// 2026-09-23) and firestore.rules deny them there (TASK-9350 / TASK-9354). The
// export used to read p.ssn / p.ein off `people`, so "Tax ID On File" was blank
// for every contractor. Reads ONLY the contractors' docs, one getDoc each.
// NO new read path: peopleTax READ stays isPartnerElevated || hasModule(
// 'hr-directory') in the rules, which is the boundary — a BI viewer without it
// gets permission-denied here, and the column says so instead of reading as
// "no TIN on file" (DENIED is not ABSENT, same as _biCompUnknown).
async function _biLoadContractorTax(people) {
  const tenant = BiState.tenantId === 'demo-tenant' ? 'demo' : BiState.tenantId;
  const byId = {};
  let denied = false;
  const deniedIds = new Set(); // per person: a denied read and a network failure say different things
  await Promise.all(people.map(async (p) => {
    try {
      const snap = await getDoc(doc(db, 'tenants', tenant, 'peopleTax', p.id));
      byId[p.id] = snap.exists() ? (snap.data() || {}) : {};
    } catch (e) {
      if (e && e.code === 'permission-denied') { denied = true; deniedIds.add(p.id); }
      else console.warn('[BI] 1099 peopleTax read failed', p.id, e?.message || e);
    }
  }));
  return { byId, denied, deniedIds };
}

async function export1099Summary() {
  const now = new Date();
  const yearStart = new Date(now.getFullYear(), 0, 1);
  const yearEnd = new Date(now.getFullYear(), 11, 31, 23, 59, 59);

  // Source 1: CRM vendors with is1099 flag (existing)
  const vendors1099 = (BiState.customers || []).filter(c => c.is1099 && (c.type === 'subcontractor' || c.type === 'vendor'));
  const yearCosts = BiState.costEntries.filter(c => {
    const d = toDate(c.date);
    return d && d >= yearStart && d <= yearEnd;
  });

  // Source 2: People with a 1099 classification (TASK-9178: window.WorkerClass, the ONE rule)
  const contractorPeople = (BiState.people || []).filter(p =>
    window.WorkerClass.resolve(p).is1099
  );

  // Clock entries for contractor labor cost
  const yearClock = BiState.clockEntries.filter(c => {
    const d = toDate(c.clockIn);
    return d && d >= yearStart && d <= yearEnd;
  });
  // 🔴 WAGE map — this number goes on an IRS filing. Never burdened, never
  // salary-allocated: "Total Paid" must be what was actually paid.
  const personRateMap = _biRateMap(BiState.people);

  const headers = ['Name', 'Tax ID On File', 'Total Paid YTD', 'Needs 1099 (>=$600)', 'Classification', 'Business Name'];
  const rows = [];
  const { byId: taxById, denied: taxDenied, deniedIds: taxDeniedIds } = await _biLoadContractorTax(contractorPeople);
  if (taxDenied) showToast('Tax IDs hidden — your account needs HR Directory permission to see them. Paid totals exported.', 'warning');

  // CRM vendors
  // TASK-1619 — v.taxId no longer exists here (moved to the admin-only
  // customersSecure sibling); get the real digits from the CRM customer
  // record's ACH/Tax ID section (admin-gated) if you need them for filing.
  // This export shows on-file STATUS only, never a digit of the value.
  vendors1099.forEach(v => {
    const paid = _vendorPaidYtd(yearCosts, v);
    const taxDisplay = v.taxIdOnFile ? 'Yes' : '';
    rows.push([v.name || '', taxDisplay, paid.toFixed(2), paid >= 600 ? 'Yes' : 'No', 'CRM Vendor', '']);
  });

  // People contractors
  contractorPeople.forEach(p => {
    // Sum cost entries matched by contractorId or personId
    const costPaid = yearCosts.filter(c => c.contractorId === p.id || c.personId === p.id)
      .reduce((s, c) => s + (c.amount || 0), 0);
    // Sum labor cost from clock entries
    const laborPaid = yearClock.filter(c => c.personId === p.id)
      .reduce((s, c) => s + ((c.totalHours || c.hours || 0) * (personRateMap[p.id] || 0)), 0);
    const totalPaid = costPaid + laborPaid;
    // TASK-9354 — TIN from peopleTax, never the broadcast doc. Last 4 only.
    const tax = taxById[p.id];
    const tinDigits = !tax ? '' : String((tax.tinType === 'ein' && tax.ein) ? tax.ein : (tax.ssn || '')).replace(/\D/g, '');
    const taxDisplay = !tax ? (taxDeniedIds.has(p.id) ? 'Restricted - HR permission required' : 'Unavailable - try again')
      : (tinDigits.length >= 4 ? (tax.tinType === 'ein' && tax.ein ? '**-***' : '***-**-') + tinDigits.slice(-4) : '');
    const classLabel = window.WorkerClass.resolve(p).classification === 'owner_operator' ? 'Owner-Operator' : 'Contractor';
    rows.push([p.name || p.legalName || '', taxDisplay, totalPaid.toFixed(2), totalPaid >= 600 ? 'Yes' : 'No', classLabel, (tax && tax.businessName) || '']);
  });

  downloadCSV(`DispatchDeck_1099_Summary_${now.getFullYear()}.csv`, generateCSV(headers, rows));
}

function exportContractorPaymentSummary() {
  const now = new Date();
  const year = now.getFullYear();
  const contractorPeople = (BiState.people || []).filter(p =>
    window.WorkerClass.resolve(p).is1099
  );
  if (contractorPeople.length === 0) {
    showToast('No contractors found. Set tax classification in HR first.', 'error');
    return;
  }

  const yearCosts = BiState.costEntries.filter(c => {
    const d = toDate(c.date);
    return d && d.getFullYear() === year;
  });
  const yearClock = BiState.clockEntries.filter(c => {
    const d = toDate(c.clockIn);
    return d && d.getFullYear() === year;
  });
  const personRateMap = _biRateMap(BiState.people); // 🔴 WAGE map — payments actually made, never burdened job cost

  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const headers = ['Contractor Name', ...months, 'YTD Total'];
  const rows = [];

  contractorPeople.forEach(p => {
    const monthlyTotals = new Array(12).fill(0);
    // Cost entries
    yearCosts.filter(c => c.contractorId === p.id || c.personId === p.id).forEach(c => {
      const d = toDate(c.date);
      if (d) monthlyTotals[d.getMonth()] += (c.amount || 0);
    });
    // Labor
    yearClock.filter(c => c.personId === p.id).forEach(c => {
      const d = toDate(c.clockIn);
      if (d) monthlyTotals[d.getMonth()] += ((c.totalHours || c.hours || 0) * (personRateMap[p.id] || 0));
    });
    const ytd = monthlyTotals.reduce((s, v) => s + v, 0);
    rows.push([p.name || '', ...monthlyTotals.map(v => v.toFixed(2)), ytd.toFixed(2)]);
  });

  downloadCSV(`DispatchDeck_Contractor_Payments_${year}.csv`, generateCSV(headers, rows));
}



function exportChartOfAccounts() {
  const headers = ['Account Number', 'Account Name', 'Type', 'Detail Type'];
  const rows = [
    ['1000', 'Cash & Equivalents', 'Bank', 'Checking'],
    ['1100', 'Accounts Receivable', 'Accounts Receivable', 'Accounts Receivable'],
    ['1200', 'Prepaid Expenses', 'Other Current Asset', 'Prepaid Expenses'],
    ['1300', 'Other Current Assets', 'Other Current Asset', 'Other Current Assets'],
    ['1500', 'Vehicles & Equipment', 'Fixed Asset', 'Machinery & Equipment'],
    ['1510', 'Accumulated Depreciation', 'Fixed Asset', 'Accumulated Depreciation'],
    ['2000', 'Accounts Payable', 'Accounts Payable', 'Accounts Payable'],
    ['2050', 'Accrued Wages', 'Other Current Liability', 'Other Current Liabilities'],
    ['2100', 'Current Portion of LTD', 'Other Current Liability', 'Current Portion of Long-Term Debt'],
    ['2200', 'Other Current Liabilities', 'Other Current Liability', 'Other Current Liabilities'],
    ['2500', 'Vehicle Loans', 'Long Term Liability', 'Notes Payable'],
    ['2600', 'Lines of Credit', 'Long Term Liability', 'Line of Credit'],
    ['2700', 'Notes Payable', 'Long Term Liability', 'Notes Payable'],
    ['3000', 'Owner\'s Equity', 'Equity', 'Opening Balance Equity'],
    ['3100', 'Owner Contributions', 'Equity', 'Partner Contributions'],
    ['3200', 'Owner Draws', 'Equity', 'Partner Distributions'],
    ['3300', 'Retained Earnings', 'Equity', 'Retained Earnings'],
    ['4000', 'Service Revenue', 'Income', 'Service/Fee Income'],
    ['4100', 'Other Income', 'Other Income', 'Other Miscellaneous Income'],
    ['5000', 'Fuel', 'Cost of Goods Sold', 'Supplies & Materials - COGS'],
    ['5010', 'Tolls', 'Cost of Goods Sold', 'Other Costs of Services - COGS'],
    ['5020', 'Lodging', 'Cost of Goods Sold', 'Other Costs of Services - COGS'],
    ['5030', 'Materials', 'Cost of Goods Sold', 'Supplies & Materials - COGS'],
    ['5040', 'Subcontractor', 'Cost of Goods Sold', 'Subcontractors - COGS'],
    ['5050', 'Per Diem', 'Cost of Goods Sold', 'Other Costs of Services - COGS'],
    ['5060', 'Labor (Direct)', 'Cost of Goods Sold', 'Labor - COGS'],
    ['6000', 'Permits', 'Expense', 'Taxes & Licenses'],
    ['6010', 'Shop', 'Expense', 'Repair & Maintenance'],
    ['6020', 'Insurance', 'Expense', 'Insurance'],
    ['6030', 'Interest Expense', 'Expense', 'Interest Paid'],
    ['6040', 'Taxes & Fees', 'Expense', 'Taxes Paid'],
    ['6050', 'Bank & Processing Fees', 'Expense', 'Bank Charges'],
    ['6060', 'DOT / Compliance', 'Expense', 'Taxes & Licenses'],
    ['6070', 'Benefits & Insurance', 'Expense', 'Insurance - Employees'],
    ['6080', 'Fines & Penalties', 'Expense', 'Penalties & Settlements'],
    ['6090', 'Maintenance', 'Expense', 'Repair & Maintenance'],
    ['6100', 'Other Expense', 'Expense', 'Other Miscellaneous Expense'],
    // TASK-9701 (decision 4) — loan PRINCIPAL is not an expense: it pays down 2500
    // Vehicle Loans. Loan interest books to 6030 Interest Expense. 6200 holds only
    // payments whose interest could not be split (no term / rate on file), the
    // same line the P&L shows.
    ['6200', 'Loan Payments (interest not split)', 'Expense', 'Other Miscellaneous Expense'],
    ['6300', 'Bad Debt (Write-Offs)', 'Expense', 'Bad Debts']
  ];
  downloadCSV('DispatchDeck_Chart_of_Accounts.csv', generateCSV(headers, rows));
}

function exportDepreciationSchedule() {
  const allEquip = [
    ...BiState.trucks.map(t => ({...t, _type: 'truck'})),
    ...BiState.trailers.map(t => ({...t, _type: 'trailer'})),
    ...BiState.assets.map(a => ({...a, _type: 'asset'}))
  ].filter(e => e.loan?.enabled && e.loan.depMethod && e.loan.depMethod !== 'none');

  if (allEquip.length === 0) {
    showToast('No assets with depreciation configured', 'error');
    return;
  }

  const headers = ['Asset', 'Unit #', 'Method', 'Purchase Price', 'Purchase Date', 'Year', 'Depreciation', 'Accumulated', 'Book Value'];
  const rows = [];
  allEquip.forEach(e => {
    const dep = computeDepreciation(e.loan);
    if (!dep) return;
    const name = e.unit ? `#${e.unit}` : (e.name || e.id);
    const method = e.loan.depMethod === 'macrs5' ? 'MACRS 5-Year' : e.loan.depMethod === 'macrs7' ? 'MACRS 7-Year' : 'Straight-Line';
    let accum = 0;
    dep.schedule.forEach(s => {
      accum += s.depreciation;
      const bv = Math.max(0, dep.purchasePrice - accum);
      rows.push([
        e.name || name,
        e.unit || '',
        method,
        dep.purchasePrice.toFixed(2),
        e.loan.purchaseDate || '',
        s.year.toString(),
        s.depreciation.toFixed(2),
        accum.toFixed(2),
        bv.toFixed(2)
      ]);
    });
    rows.push(['', '', '', '', '', '', '', '', '']); // blank row between assets
  });

  downloadCSV(`DispatchDeck_Depreciation_Schedule_${new Date().getFullYear()}.csv`, generateCSV(headers, rows));
}

// ============================================
// QUICKBOOKS DESKTOP — IIF EXPORT
// ============================================
const CATEGORY_TO_QB_ACCOUNT = {
  fuel: 'Fuel', tolls: 'Tolls', lodging: 'Lodging', materials: 'Materials',
  subcontractor: 'Subcontractor', per_diem: 'Per Diem', permits: 'Permits',
  shop: 'Shop', insurance: 'Insurance', maintenance: 'Maintenance',
  interest: 'Interest Expense', taxes: 'Taxes & Fees', bank_fees: 'Bank & Processing Fees',
  dot: 'DOT / Compliance', benefits: 'Benefits & Insurance', fines: 'Fines & Penalties',
  draws: 'Owner Draws', contributions: 'Owner Contributions', other: 'Other Expense',
  employee_receivable: 'Due from Employees', // TASK-9917
};

function downloadIIF(filename, content) {
  const blob = new Blob([content], { type: 'text/plain' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function exportIIF_ChartOfAccounts() {
  const lines = ['!ACCNT\tNAME\tACCNTTYPE\tDESC'];
  const accounts = [
    ['Checking', 'BANK', 'Primary checking account'],
    ['Accounts Receivable', 'AR', 'Customer receivables'],
    ['Accounts Payable', 'AP', 'Vendor payables'],
    ['Fuel', 'COGS', 'Fuel expense'], ['Tolls', 'COGS', 'Tolls & highway'],
    ['Lodging', 'COGS', 'Travel & lodging'], ['Materials', 'COGS', 'Materials & supplies'],
    ['Subcontractor', 'COGS', 'Subcontractor costs'], ['Per Diem', 'COGS', 'Per diem'],
    ['Permits', 'EXP', 'Licenses & permits'], ['Shop', 'EXP', 'Repair & maintenance'],
    ['Insurance', 'EXP', 'Insurance'], ['Maintenance', 'EXP', 'Vehicle maintenance'],
    ['Interest Expense', 'EXP', 'Loan interest'], ['Taxes & Fees', 'EXP', 'Taxes paid'],
    ['Bank & Processing Fees', 'EXP', 'Bank charges'], ['DOT / Compliance', 'EXP', 'DOT compliance'],
    ['Other Expense', 'EXP', 'Miscellaneous'], ['Payroll Expense', 'EXP', 'Wages & salaries'],
    ['Owner Draws', 'EQUITY', 'Owner distributions'], ['Owner Contributions', 'EQUITY', 'Owner capital'],
    // TASK-9917 — personal charges employees owe back, and the payroll side of a deduction.
    ['Due from Employees', 'OCASSET', 'Personal charges on the company card owed back by employees'],
    ['Payroll Liabilities', 'OCLIAB', 'Wages owed to employees'],
    ['Service Revenue', 'INC', 'Service income'], ['Vehicle Loans', 'LTLIAB', 'Vehicle loan payables'],
    ['Lines of Credit', 'LTLIAB', 'Revolving credit lines']
  ];
  accounts.forEach(([name, type, desc]) => {
    lines.push(`ACCNT\t${name}\t${type}\t${desc}`);
  });
  downloadIIF('DispatchDeck_ChartOfAccounts.iif', lines.join('\r\n'));
  showToast('Chart of Accounts IIF exported', 'success');
}

async function exportIIF_CostEntries() {
  const PR = await _loadPersonalRepay(); // TASK-9917 slice 2
  const costs = BiState.costEntries.filter(c => isInPeriod(c.date));
  const repays = PR.repaymentRows(BiState.costEntries, isInPeriod, _personalRepayDeps());
  if (!costs.length && !repays.length) { showToast('No cost entries in selected period', 'error'); return; }

  const lines = ['!TRNS\tTRNSID\tTRNSTYPE\tDATE\tACCNT\tNAME\tAMOUNT\tMEMO', '!SPL\tSPLID\tTRNSTYPE\tDATE\tACCNT\tNAME\tAMOUNT\tMEMO', '!ENDTRNS'];
  let txnId = 1;

  costs.forEach(c => {
    const date = c.date?.toDate ? c.date.toDate() : new Date(c.date);
    const fmtD = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    const acct = CATEGORY_TO_QB_ACCOUNT[exportCategoryOf(c)] || 'Other Expense'; // TASK-9917
    const memo = (c.description || c.category || '').replace(/\t/g, ' ').replace(/\n/g, ' ');
    const vendor = (c.vendor || '').replace(/\t/g, ' ');
    const amt = (c.amount || 0).toFixed(2);

    // TRNS: debit the expense account
    lines.push(`TRNS\t${txnId}\tCHECK\t${fmtD}\tChecking\t${vendor}\t-${amt}\t${memo}`);
    // SPL: credit (expense side)
    lines.push(`SPL\t${txnId}\tCHECK\t${fmtD}\t${acct}\t${vendor}\t${amt}\t${memo}`);
    lines.push('ENDTRNS');
    txnId++;
  });

  // TASK-9917 slice 2 — repayments of personal charges: Dr Checking (cash /
  // check / other) or Payroll Liabilities (payroll deduction) / Cr Due from
  // Employees; an undone one reversed on the undo day (personalRepay.js iifLines).
  lines.push(...PR.iifLines(BiState.costEntries, isInPeriod, txnId, _personalRepayDeps()).lines);

  downloadIIF(`DispatchDeck_Expenses_${getPeriodSlug()}.iif`, lines.join('\r\n'));
  showToast(`Exported ${costs.length} cost entries${repays.length ? ` + ${repays.length} repayment${repays.length === 1 ? '' : 's'}` : ''} as IIF`, 'success');
}

// 🔴 THE FOURTH READ WITH NO WRITER, found by COD after the other three were
// fixed. `isInPeriod(r.date)` was ALWAYS false — revenueEntries carry no `date`
// field (0 of 685 on TITS) — so this export bailed with "No revenue entries in
// selected period" every single time, for every tenant, forever. A QuickBooks
// export that has never once been able to export anything, and it fails with a
// message that blames the period instead of admitting it cannot read the data.
function exportIIF_Revenue() {
  const revenue = BiState.revenueEntries.filter(r => { const d = _biRevenueDate(r); return d && isInPeriod(d); });
  if (!revenue.length) { showToast('No revenue entries in selected period', 'error'); return; }

  const lines = ['!TRNS\tTRNSID\tTRNSTYPE\tDATE\tACCNT\tNAME\tAMOUNT\tMEMO', '!SPL\tSPLID\tTRNSTYPE\tDATE\tACCNT\tNAME\tAMOUNT\tMEMO', '!ENDTRNS'];
  let txnId = 1;

  revenue.forEach(r => {
    const date = _biRevenueDate(r);
    if (!date) return; // cannot date it, cannot book it — never emit an Invalid Date to QuickBooks
    const fmtD = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    const customer = (r.customer || '').replace(/\t/g, ' ');
    const memo = (r.description || r.ticketNumber || '').replace(/\t/g, ' ');
    const amt = (r.amount || 0).toFixed(2);

    lines.push(`TRNS\t${txnId}\tINVOICE\t${fmtD}\tAccounts Receivable\t${customer}\t${amt}\t${memo}`);
    lines.push(`SPL\t${txnId}\tINVOICE\t${fmtD}\tService Revenue\t${customer}\t-${amt}\t${memo}`);
    lines.push('ENDTRNS');
    txnId++;
  });

  downloadIIF(`DispatchDeck_Revenue_${getPeriodSlug()}.iif`, lines.join('\r\n'));
  showToast(`Exported ${revenue.length} revenue entries as IIF`, 'success');
}

function exportARAgingReport() {
  const now = new Date();
  const outstanding = BiState.tickets.filter(t =>
    t.status === 'draft' || t.status === 'ready' ||
    (_biTicketInvoiced(t) && !t.paid && !t.writtenOff && t.status !== 'deleted')
  );

  // Group by customer with aging buckets
  const byCustomer = {};
  outstanding.forEach(t => {
    const ageDate = (_biTicketInvoiced(t) && t.invoicedAt) ? toDate(t.invoicedAt) : toDate(t.date);
    if (!ageDate) return;
    const age = Math.floor((now - ageDate) / (1000 * 60 * 60 * 24));
    const amt = t.grandTotal || 0;
    const name = t.customerName || 'Unknown';
    if (!byCustomer[name]) byCustomer[name] = { current: 0, '31-60': 0, '61-90': 0, '91-120': 0, '120+': 0, total: 0 };
    if (age <= 30) byCustomer[name].current += amt;
    else if (age <= 60) byCustomer[name]['31-60'] += amt;
    else if (age <= 90) byCustomer[name]['61-90'] += amt;
    else if (age <= 120) byCustomer[name]['91-120'] += amt;
    else byCustomer[name]['120+'] += amt;
    byCustomer[name].total += amt;
  });

  const headers = ['Customer', 'Current (0-30)', '31-60 Days', '61-90 Days', '91-120 Days', '120+ Days', 'Total'];
  const rows = [];
  rows.push([window.AppState?.tenantData?.companyName || 'DispatchDeck', '', '', '', '', '', '']);
  rows.push(['AR Aging Report', '', '', '', '', '', '']);
  rows.push([`As of ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`, '', '', '', '', '', '']);
  rows.push(['', '', '', '', '', '', '']);

  const totals = { current: 0, '31-60': 0, '61-90': 0, '91-120': 0, '120+': 0, total: 0 };
  Object.entries(byCustomer).sort((a, b) => b[1].total - a[1].total).forEach(([name, b]) => {
    rows.push([name, b.current.toFixed(2), b['31-60'].toFixed(2), b['61-90'].toFixed(2), b['91-120'].toFixed(2), b['120+'].toFixed(2), b.total.toFixed(2)]);
    totals.current += b.current;
    totals['31-60'] += b['31-60'];
    totals['61-90'] += b['61-90'];
    totals['91-120'] += b['91-120'];
    totals['120+'] += b['120+'];
    totals.total += b.total;
  });

  rows.push(['', '', '', '', '', '', '']);
  rows.push(['TOTAL', totals.current.toFixed(2), totals['31-60'].toFixed(2), totals['61-90'].toFixed(2), totals['91-120'].toFixed(2), totals['120+'].toFixed(2), totals.total.toFixed(2)]);

  const csv = generateCSV(headers, rows);
  downloadCSV(`DispatchDeck_AR_Aging_${now.toISOString().slice(0, 10)}.csv`, csv);
}

function exportAPAgingReport() {
  const now = new Date();
  const outstanding = (BiState.apInvoices || []).filter(i => i.paymentStatus !== 'paid');

  const byVendor = {};
  outstanding.forEach(inv => {
    const dueDate = inv.dueDate ? toDate(inv.dueDate) : toDate(inv.invoiceDate);
    if (!dueDate) return;
    const age = Math.max(0, Math.floor((now - dueDate) / (1000 * 60 * 60 * 24)));
    const amt = (inv.grandTotal || 0) - (inv.paidAmount || 0);
    if (amt <= 0) return;
    const name = inv.vendorName || 'Unknown';
    if (!byVendor[name]) byVendor[name] = { current: 0, '31-60': 0, '61-90': 0, '91-120': 0, '120+': 0, total: 0 };
    if (age <= 30) byVendor[name].current += amt;
    else if (age <= 60) byVendor[name]['31-60'] += amt;
    else if (age <= 90) byVendor[name]['61-90'] += amt;
    else if (age <= 120) byVendor[name]['91-120'] += amt;
    else byVendor[name]['120+'] += amt;
    byVendor[name].total += amt;
  });

  const headers = ['Vendor', 'Current (0-30)', '31-60 Days', '61-90 Days', '91-120 Days', '120+ Days', 'Total'];
  const rows = [];
  rows.push([window.AppState?.tenantData?.companyName || 'DispatchDeck', '', '', '', '', '', '']);
  rows.push(['AP Aging Report', '', '', '', '', '', '']);
  rows.push([`As of ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`, '', '', '', '', '', '']);
  rows.push(['', '', '', '', '', '', '']);

  const totals = { current: 0, '31-60': 0, '61-90': 0, '91-120': 0, '120+': 0, total: 0 };
  Object.entries(byVendor).sort((a, b) => b[1].total - a[1].total).forEach(([name, b]) => {
    rows.push([name, b.current.toFixed(2), b['31-60'].toFixed(2), b['61-90'].toFixed(2), b['91-120'].toFixed(2), b['120+'].toFixed(2), b.total.toFixed(2)]);
    totals.current += b.current;
    totals['31-60'] += b['31-60'];
    totals['61-90'] += b['61-90'];
    totals['91-120'] += b['91-120'];
    totals['120+'] += b['120+'];
    totals.total += b.total;
  });

  rows.push(['', '', '', '', '', '', '']);
  rows.push(['TOTAL', totals.current.toFixed(2), totals['31-60'].toFixed(2), totals['61-90'].toFixed(2), totals['91-120'].toFixed(2), totals['120+'].toFixed(2), totals.total.toFixed(2)]);

  const csv = generateCSV(headers, rows);
  downloadCSV(`DispatchDeck_AP_Aging_${now.toISOString().slice(0, 10)}.csv`, csv);
}

// ============================================
// RECONCILIATION TAB (lazy-loaded)
// ============================================
function renderReconciliationTab() {
  const el = document.getElementById('bi-tab-reconciliation');
  if (!el) return;

  // Lazy-load the reconciliation module
  if (window.ReconciliationModule) {
    window.ReconciliationModule.render(el, BiState);
  } else {
    el.innerHTML = '<div class="loading-state"><div class="loading-spinner"></div><p>Loading reconciliation...</p></div>';
    __ddChunkWrap(import('./reconciliation.js?v=10172101')).then(mod => { // TASK-10172 statement line 'Transfer, not an expense' · TASK-10169 S1 month close (integ/2129: one value above 10169's 10312128) · TASK-10162 S4 split one statement line + TASK-10172-A Equipment Purchase / Loan Payment categories (integ/2129) · TASK-10170 card-payment guard · TASK-10162 statement-line assign + mobile bulk cards · TASK-9947 refunds: pending card credits link, rejected never match · TASK-9917 personal charges stay reconcilable
      mod.initReconciliation(el, BiState);
    }).catch(err => {
      console.error('[BI] Failed to load reconciliation module:', err);
      el.innerHTML = `<div class="empty-state" style="padding:40px;text-align:center;">
        <span class="material-icons" style="font-size:48px;color:var(--text-secondary);">error_outline</span>
        <div style="margin-top:8px;color:var(--text-secondary);">Failed to load reconciliation module</div>
      </div>`;
    });
  }
}

// ============================================
// BOOKS CHECK TAB (lazy-loaded) — TASK-9210
// ============================================
// DD invoiced revenue vs QuickBooks, read-only. Same lazy-host shape as the
// reconciliation tab above: the module owns its own state + window.QboVarianceModule.
function renderBooksTab() {
  const tab = document.getElementById('bi-tab-books');
  if (!tab) return;
  // TASK-9480 — two panels share this tab: the TASK-9210 revenue check and the
  // QuickBooks expense tie-out (qboBooks.js). Each owns its own container.
  let el = document.getElementById('bi-books-revenue');
  if (!el) {
    tab.innerHTML = '<div id="bi-books-expenses" style="margin-bottom:24px;"></div><div id="bi-books-revenue"></div>';
    el = document.getElementById('bi-books-revenue');
  }
  renderBooksExpensesPanel();
  if (window.QboVarianceModule) {
    window.QboVarianceModule.render(el, BiState);
  } else {
    el.innerHTML = '<div class="loading-state"><div class="loading-spinner"></div><p>Loading books check...</p></div>';
    __ddChunkWrap(import('./qboVariance.js?v=1976461')).then(mod => {
      mod.initQboVariance(el, BiState);
    }).catch(err => {
      console.error('[BI] Failed to load books-check module:', err);
      el.innerHTML = `<div class="empty-state" style="padding:40px;text-align:center;">
        <span class="material-icons" style="font-size:48px;color:var(--text-secondary);">error_outline</span>
        <div style="margin-top:8px;color:var(--text-secondary);">Failed to load the books check</div>
      </div>`;
    });
  }
}

// TASK-9797 — Reports tab: Truck Breakout, Where Trucks Are, Rental Register,
// Contract Activity (biReports.js, lazy). The rules live in
// window.OpsReports (shared/ops-reports.js, generated from
// functions/src/shared/opsReports.js); biReports.js only loads + renders.
function renderReportsTab() {
  const host = document.getElementById('bi-tab-reports');
  if (!host) return;
  // TASK-10150 — Books decisions sits ABOVE the report picker in its own node;
  // biReports.js owns only #bi-reports-body (it replaces its container's HTML).
  let el = document.getElementById('bi-reports-body');
  if (!el) {
    host.innerHTML = `<div id="bi-books-decisions-entry" style="margin-bottom:16px;">
        <button type="button" class="btn btn-secondary" id="bi-books-decisions-open" style="display:inline-flex;align-items:center;gap:6px;">
          <span class="material-icons">fact_check</span> Books decisions
        </button>
        <span style="font-size:0.85rem;color:var(--text-secondary);margin-left:8px;">What was decided about the books, by whom, and what is still open.</span>
        <div id="bi-books-decisions" style="margin-top:12px;"></div>
      </div><div id="bi-reports-body"></div>`;
    el = document.getElementById('bi-reports-body');
    document.getElementById('bi-books-decisions-open').addEventListener('click', () => {
      const box = document.getElementById('bi-books-decisions');
      if (!box) return;
      if (box.childElementCount) { box.innerHTML = ''; return; } // second tap closes
      if (window.BooksDecisionsModule) { window.BooksDecisionsModule.render(box); return; }
      box.innerHTML = '<div class="loading-state"><div class="loading-spinner"></div><p>Loading books decisions...</p></div>';
      __ddChunkWrap(import('./booksDecisions.js?v=10150002')).then((mod) => mod.initBooksDecisions(box)).catch((err) => {
        console.error('[BI] Failed to load books decisions:', err);
        box.innerHTML = '<div class="empty-state" style="padding:24px;text-align:center;color:var(--text-secondary);">Failed to load Books decisions</div>';
      });
    });
  }
  if (window.BooksDecisionsModule && document.getElementById('bi-books-decisions')?.childElementCount) {
    window.BooksDecisionsModule.render(document.getElementById('bi-books-decisions'));
  }
  if (window.BiReportsModule) { window.BiReportsModule.render(el, BiState); return; }
  el.innerHTML = '<div class="loading-state"><div class="loading-spinner"></div><p>Loading reports...</p></div>';
  __ddChunkWrap(import('./biReports.js?v=9811302')).then(mod => {
    mod.initBiReports(el, BiState);
  }).catch(err => {
    console.error('[BI] Failed to load reports module:', err);
    el.innerHTML = `<div class="empty-state" style="padding:40px;text-align:center;">
      <span class="material-icons" style="font-size:48px;color:var(--text-secondary);">error_outline</span>
      <div style="margin-top:8px;color:var(--text-secondary);">Failed to load reports</div>
    </div>`;
  });
}

// TASK-9480 — QuickBooks expense tie-out (read-only), lazy like the panel above.
function renderBooksExpensesPanel() {
  const el = document.getElementById('bi-books-expenses');
  if (!el) return;
  if (window.QboBooksModule) {
    window.QboBooksModule.render(el, BiState);
    return;
  }
  el.innerHTML = '<div class="loading-state"><div class="loading-spinner"></div><p>Loading QuickBooks books...</p></div>';
  __ddChunkWrap(import('./qboBooks.js?v=10123001')).then(mod => { // TASK-10004 S1 owner draws label
    mod.initQboBooks(el, BiState);
  }).catch(err => {
    console.error('[BI] Failed to load QuickBooks books module:', err);
    el.innerHTML = `<div class="empty-state" style="padding:40px;text-align:center;">
      <span class="material-icons" style="font-size:48px;color:var(--text-secondary);">error_outline</span>
      <div style="margin-top:8px;color:var(--text-secondary);">Failed to load the QuickBooks books panel</div>
    </div>`;
  });
}

// ============================================
// FUEL CARD CSV IMPORT
// ============================================
let _fuelCardData = null;

function showFuelCardImport() {
  const existing = document.getElementById('modal-fuel-card-import');
  if (existing) existing.remove();

  const modal = document.createElement('div');
  modal.className = 'modal';
  modal.id = 'modal-fuel-card-import';
  modal.style.zIndex = '1100';
  modal.innerHTML = `
    <div class="modal-content" style="max-width:700px;display:flex;flex-direction:column;max-height:90dvh;">
      <div class="modal-header" style="flex-shrink:0;background:linear-gradient(135deg, #F59E0B, #EF4444);color:#fff;">
        <h3 style="display:flex;align-items:center;gap:8px;"><span class="material-icons">local_gas_station</span> Import Fuel Card Statement</h3>
        <button class="btn-icon" onclick="document.getElementById('modal-fuel-card-import').remove()" style="color:#fff;">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div style="padding:16px;overflow-y:auto;flex:1;-webkit-overflow-scrolling:touch;">
        <p style="font-size:0.85rem;color:var(--text-secondary);margin-bottom:16px;">
          Upload a CSV from your fuel card provider (WEX, EFS, Comdata, Fleet One, Fuelman). Smart Tools will parse transactions and match drivers/trucks automatically.
        </p>

        <div id="fuel-card-upload-area" style="text-align:center;padding:32px;background:var(--bg-secondary);border:2px dashed var(--border-light);border-radius:12px;cursor:pointer;" onclick="document.getElementById('fuel-card-file-input').click()">
          <span class="material-icons" style="font-size:48px;color:#F59E0B;">upload_file</span>
          <div style="font-size:15px;font-weight:600;margin-top:8px;">Drop fuel card CSV here</div>
          <div style="font-size:13px;color:var(--text-secondary);margin-top:4px;">CSV or PDF from your fuel card portal</div>
          <input type="file" id="fuel-card-file-input" accept=".csv,.pdf,.xlsx" style="display:none;" onchange="window.BIModule.handleFuelCardUpload(this)">
        </div>

        <div id="fuel-card-processing" class="hidden" style="text-align:center;padding:32px;">
          <div class="loading-spinner" style="margin:0 auto 12px;"></div>
          <div id="fuel-card-processing-text" style="font-size:14px;color:var(--text-secondary);">Reading your fuel card statement...</div>
        </div>

        <div id="fuel-card-preview" class="hidden"></div>
      </div>
      <div class="form-actions" style="flex-shrink:0;" id="fuel-card-actions">
        <button class="btn btn-secondary" onclick="document.getElementById('modal-fuel-card-import').remove()">Cancel</button>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
}

async function handleFuelCardUpload(input) {
  const file = input.files[0];
  if (!file) return;

  const uploadArea = document.getElementById('fuel-card-upload-area');
  const processing = document.getElementById('fuel-card-processing');
  if (uploadArea) uploadArea.classList.add('hidden');
  if (processing) processing.classList.remove('hidden');

  try {
    // Convert to base64 — TASK-1383 guarded shared reader (settles on every
    // path; existing catch below toasts the failure).
    const base64 = await window.FileReadUtil.readAsBase64(file);

    const mimeType = file.type || (file.name.endsWith('.csv') ? 'text/csv' : 'application/pdf');
    const tenantId = window.AppState?.tenantId;

    const fn = httpsCallable(functions, 'parseFuelCardStatement');
    const res = await fn({ tenantId, fileBase64: base64, mimeType, fileName: file.name });

    _fuelCardData = res.data;
    renderFuelCardPreview(res.data);
  } catch (err) {
    console.error('[BI] Fuel card parse error:', err);
    window.showToast?.('Failed to parse fuel card statement: ' + err.message, 'error');
    if (uploadArea) uploadArea.classList.remove('hidden');
    if (processing) processing.classList.add('hidden');
  }
}

function renderFuelCardPreview(data) {
  const processing = document.getElementById('fuel-card-processing');
  const preview = document.getElementById('fuel-card-preview');
  const actions = document.getElementById('fuel-card-actions');
  if (processing) processing.classList.add('hidden');
  if (!preview) return;
  preview.classList.remove('hidden');

  const txns = data.transactions || [];
  const total = data.totalAmount || txns.reduce((s, t) => s + (t.amount || 0), 0);
  const totalGal = data.totalGallons || txns.reduce((s, t) => s + (t.gallons || 0), 0);

  // Match drivers and trucks to existing roster
  const people = window.AppState?.people || [];
  const trucks = window.AppState?.trucks || [];

  preview.innerHTML = `
    <div style="margin-top:16px;">
      <div style="display:flex;gap:12px;margin-bottom:12px;">
        <div style="flex:1;padding:12px;background:#FEF3C7;border-radius:8px;text-align:center;">
          <div style="font-size:12px;color:#92400E;">Provider</div>
          <div style="font-weight:600;color:#78350F;">${escBI(data.provider || 'Unknown')}</div>
        </div>
        <div style="flex:1;padding:12px;background:#FEF3C7;border-radius:8px;text-align:center;">
          <div style="font-size:12px;color:#92400E;">Transactions</div>
          <div style="font-weight:600;color:#78350F;">${txns.length}</div>
        </div>
        <div style="flex:1;padding:12px;background:#FEF3C7;border-radius:8px;text-align:center;">
          <div style="font-size:12px;color:#92400E;">Total</div>
          <div style="font-weight:600;color:#78350F;">$${total.toFixed(2)}</div>
        </div>
        <div style="flex:1;padding:12px;background:#FEF3C7;border-radius:8px;text-align:center;">
          <div style="font-size:12px;color:#92400E;">Gallons</div>
          <div style="font-weight:600;color:#78350F;">${totalGal.toFixed(1)}</div>
        </div>
      </div>

      <div style="max-height:300px;overflow-y:auto;border:1px solid var(--border-light);border-radius:8px;">
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead>
            <tr style="background:var(--bg-secondary);position:sticky;top:0;">
              <th style="padding:8px;text-align:left;font-weight:600;">Date</th>
              <th style="padding:8px;text-align:left;font-weight:600;">Driver</th>
              <th style="padding:8px;text-align:left;font-weight:600;">Truck</th>
              <th style="padding:8px;text-align:left;font-weight:600;">Location</th>
              <th style="padding:8px;text-align:right;font-weight:600;">Gal</th>
              <th style="padding:8px;text-align:right;font-weight:600;">$/Gal</th>
              <th style="padding:8px;text-align:right;font-weight:600;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${txns.map((t, i) => {
              // Try to match driver
              const driverMatch = people.find(p => p.name?.toLowerCase() === t.driverName?.toLowerCase() || p.name?.toLowerCase().includes(t.driverName?.toLowerCase?.() || '__'));
              const truckMatch = trucks.find(tr => tr.unit === t.truckUnit || tr.nickname === t.truckUnit);
              return `<tr style="border-top:1px solid var(--border-light);${i % 2 ? 'background:var(--bg-secondary);' : ''}">
                <td style="padding:6px 8px;">${escBI(t.date || '')}</td>
                <td style="padding:6px 8px;${driverMatch ? 'color:#059669;' : 'color:#D97706;'}">${escBI(t.driverName || '—')}${driverMatch ? ' ✓' : ''}</td>
                <td style="padding:6px 8px;${truckMatch ? 'color:#059669;' : ''}">${escBI(t.truckUnit || '—')}</td>
                <td style="padding:6px 8px;font-size:11px;">${escBI(t.location || '—')}</td>
                <td style="padding:6px 8px;text-align:right;">${t.gallons?.toFixed(1) || '—'}</td>
                <td style="padding:6px 8px;text-align:right;">${t.pricePerGallon ? '$' + t.pricePerGallon.toFixed(3) : '—'}</td>
                <td style="padding:6px 8px;text-align:right;font-weight:600;">$${(t.amount || 0).toFixed(2)}</td>
              </tr>`;
            }).join('')}
          </tbody>
        </table>
      </div>

      <p style="font-size:0.8rem;color:var(--text-secondary);margin-top:8px;">
        <span style="color:#059669;">✓</span> = matched to roster. Unmatched drivers will be saved as-is.
      </p>
    </div>
  `;

  // Update actions
  if (actions) {
    actions.innerHTML = `
      <button class="btn btn-secondary" onclick="document.getElementById('modal-fuel-card-import').remove()">Cancel</button>
      <button class="btn btn-primary" id="btn-import-fuel-card" onclick="window.BIModule.confirmFuelCardImport()">
        <span class="material-icons">check</span> Import ${txns.length} Transactions
      </button>
    `;
  }
}

// TASK-10170 path 3 — the server writes the expenses (dedupe + card); the save
// step lives in the lazy biFuelCardImport.js.
async function confirmFuelCardImport() {
  if (!_fuelCardData?.transactions?.length) return;
  try {
    const m = await __ddChunkWrap(import('./biFuelCardImport.js?v=10170301'));
    await m.saveFuelCardImport(_fuelCardData, {
      httpsCallable, functions, esc: escBI, toast: (msg, kind) => window.showToast?.(msg, kind),
      onDone: () => { _fuelCardData = null; document.getElementById('modal-fuel-card-import')?.remove(); },
    });
  } catch (err) {
    console.error('[BI] Fuel card import error:', err);
    window.showToast?.('Failed to import: ' + err.message, 'error');
  }
}

// ============================================
// BANK BALANCE (Plaid)
// ============================================
async function refreshBankBalance() {
  const valEl = document.getElementById('bi-bank-balance-val');
  if (!valEl) return;
  valEl.textContent = 'Loading...';

  try {
    const fn = httpsCallable(functions, 'plaidGetBalance');
    const res = await fn({});
    const bal = res.data?.balance;
    if (bal !== undefined) {
      valEl.textContent = formatCurrency(bal);
      const inst = window.AppState?.tenantData?.plaidInstitution || '';
      const last4 = window.AppState?.tenantData?.plaidAccountLast4 || '';
      if (inst || last4) {
        valEl.innerHTML = `${formatCurrency(bal)}<div style="font-size:0.7rem;color:var(--text-secondary);margin-top:2px;">${escBI(inst)} ••${escBI(last4)}${res.data?.cached ? ' (cached)' : ''}</div>`;
      }
    }
  } catch (err) {
    console.error('[BI] Bank balance error:', err);
    valEl.textContent = '—';
  }
}

// Auto-fetch balance when financial tab renders (if Plaid connected)
if (window.AppState?.tenantData?.plaidConnected) {
  setTimeout(refreshBankBalance, 1000);
}

// ============================================
// BANK MODAL — Live Data (Plaid)
// ============================================
async function refreshBankModalBalance() {
  const balEl = document.getElementById('bi-bank-modal-balance');
  const updEl = document.getElementById('bi-bank-modal-updated');
  if (!balEl) return;
  balEl.textContent = 'Loading...';

  try {
    const fn = httpsCallable(functions, 'plaidGetBalance');
    const res = await fn({});
    const bal = res.data?.balance;
    if (bal !== undefined) {
      balEl.textContent = formatCurrency(bal);
      const now = new Date();
      updEl.textContent = res.data?.cached
        ? `Cached · Updated ${now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
        : `Live · ${now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    } else {
      balEl.textContent = '—';
      updEl.textContent = 'Unable to fetch';
    }
  } catch (err) {
    console.error('[BI] Bank modal balance error:', err);
    balEl.textContent = '—';
    const updEl2 = document.getElementById('bi-bank-modal-updated');
    // TASK-9380 (COD #1 UX) — plaidGetBalance now refuses a login without the
    // billing/bi module. That is a permission answer, not a broken bank link,
    // so say so and do NOT offer "reconnect" (which would fail the same way).
    // httpsCallable errors carry code "functions/permission-denied"; match the
    // suffix so a bare "permission-denied" reads the same.
    const denied = /permission-denied$/.test(String(err?.code || ''));
    if (updEl2) {
      updEl2.innerHTML = denied
        ? '<span style="color:#DC2626;">You don\'t have bank access in this company. Contact your fleet owner for access.</span>'
        : '<span style="color:#DC2626;">Connection error — <a href="#" onclick="window.BIModule.connectBankPlaid();return false;" style="color:#DC2626;text-decoration:underline;">reconnect</a></span>';
    }
  }
}

async function loadBankTransactions() {
  const listEl = document.getElementById('bi-bank-txn-list');
  if (!listEl) return;

  const days = parseInt(document.getElementById('bi-bank-txn-days')?.value || '30');
  listEl.innerHTML = '<div style="text-align:center;padding:24px;color:var(--text-secondary);"><div class="loading-spinner" style="margin:0 auto 8px;"></div>Loading transactions...</div>';

  try {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);

    const fn = httpsCallable(functions, 'plaidGetTransactions');
    const res = await fn({
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    });

    const txns = res.data?.transactions || [];
    if (!txns.length) {
      listEl.innerHTML = '<div style="text-align:center;padding:24px;color:var(--text-secondary);">No transactions in this period.</div>';
      return;
    }

    // Sort by date descending
    txns.sort((a, b) => (b.date || '').localeCompare(a.date || ''));

    listEl.innerHTML = txns.map(t => {
      const amt = t.amount || 0;
      // Plaid: positive = debit (money out), negative = credit (money in)
      const isCredit = amt < 0;
      const displayAmt = Math.abs(amt);
      const amtColor = isCredit ? '#10B981' : '#EF4444';
      const sign = isCredit ? '+' : '-';
      const dateStr = t.date ? new Date(t.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
      const cat = t.category || '';

      return `<div style="display:flex;justify-content:space-between;align-items:center;padding:8px 12px;border-bottom:1px solid var(--border-light);font-size:13px;">
        <div style="flex:1;min-width:0;">
          <div style="font-weight:500;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escBI(t.name || 'Transaction')}</div>
          <div style="font-size:11px;color:var(--text-secondary);">${dateStr}${cat ? ' · ' + escBI(cat) : ''}${t.pending ? ' · <span style="color:#F59E0B;">Pending</span>' : ''}</div>
        </div>
        <div style="font-weight:600;color:${amtColor};white-space:nowrap;margin-left:12px;">${sign}${formatCurrency(displayAmt)}</div>
      </div>`;
    }).join('');

    // Summary row
    const totalOut = txns.filter(t => (t.amount || 0) > 0).reduce((s, t) => s + t.amount, 0);
    const totalIn = txns.filter(t => (t.amount || 0) < 0).reduce((s, t) => s + Math.abs(t.amount), 0);
    listEl.innerHTML += `<div style="display:flex;justify-content:space-between;padding:10px 12px;background:#F8FAFC;font-size:12px;font-weight:600;">
      <span>${txns.length} transactions</span>
      <span><span style="color:#10B981;">+${formatCurrency(totalIn)}</span> / <span style="color:#EF4444;">-${formatCurrency(totalOut)}</span></span>
    </div>`;

  } catch (err) {
    console.error('[BI] Load bank transactions error:', err);
    listEl.innerHTML = `<div style="text-align:center;padding:24px;color:#DC2626;">
      <span class="material-icons" style="font-size:24px;margin-bottom:4px;">error</span>
      <div style="font-size:13px;">Failed to load transactions</div>
      <div style="font-size:12px;color:var(--text-secondary);margin-top:4px;">${escBI(err.message || 'Unknown error')}</div>
    </div>`;
  }
}

// ============================================
// PLAID LINK — Connect Bank Account
// ============================================
let _plaidScriptLoaded = false;

function _loadPlaidScript() {
  return new Promise((resolve, reject) => {
    if (_plaidScriptLoaded && window.Plaid) return resolve();
    const s = document.createElement('script');
    s.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
    s.onload = () => { _plaidScriptLoaded = true; resolve(); };
    s.onerror = () => reject(new Error('Failed to load Plaid Link SDK'));
    document.head.appendChild(s);
  });
}

async function connectBankPlaid() {
  const btn = document.querySelector('[onclick*="connectBankPlaid"]');
  if (btn) { btn.disabled = true; btn.innerHTML = '<span class="btn-spinner"></span> Connecting...'; }

  try {
    // 1. Load Plaid Link SDK
    await _loadPlaidScript();

    // 2. Get link token from backend
    const createToken = httpsCallable(functions, 'plaidCreateLinkToken');
    const tokenRes = await createToken({});
    const linkToken = tokenRes.data?.link_token;
    if (!linkToken) throw new Error('No link token returned');

    // 3. Open Plaid Link
    const handler = window.Plaid.create({
      token: linkToken,
      onSuccess: async (publicToken, metadata) => {
        try {
          window.showToast?.('Linking account...', 'info');
          const account = metadata.accounts?.[0] || {};
          const institution = metadata.institution || {};

          // 4. Exchange token on backend
          const exchangeToken = httpsCallable(functions, 'plaidExchangeToken');
          await exchangeToken({
            publicToken,
            accountId: account.id,
            institutionName: institution.name || '',
            accountName: account.name || '',
            accountMask: account.mask || '',
          });

          // 5. Update local state
          if (window.AppState?.tenantData) {
            window.AppState.tenantData.plaidConnected = true;
            window.AppState.tenantData.plaidInstitution = institution.name || '';
            window.AppState.tenantData.plaidAccountLast4 = account.mask || '';
          }

          window.showToast?.(`Connected to ${institution.name || 'bank'}!`, 'success');

          // Close bank modal and refresh
          const bankModal = document.getElementById('bi-bank-modal');
          if (bankModal) bankModal.remove();

          // Refresh balance
          refreshBankBalance();
        } catch (err) {
          console.error('[Plaid] Exchange error:', err);
          window.showToast?.('Failed to link account: ' + err.message, 'error');
        }
      },
      onExit: (err) => {
        if (err) console.warn('[Plaid] Link exit with error:', err);
        if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;">account_balance</span> Connect Bank (Plaid)'; }
      },
    });

    handler.open();
  } catch (err) {
    console.error('[Plaid] Connect error:', err);
    window.showToast?.('Failed to start bank connection: ' + err.message, 'error');
    if (btn) { btn.disabled = false; btn.innerHTML = '<span class="material-icons" style="font-size:16px;vertical-align:middle;">account_balance</span> Connect Bank (Plaid)'; }
  }
}

async function disconnectPlaid() {
  if (!confirm('Disconnect your linked bank account? You can reconnect anytime.')) return;
  try {
    const fn = httpsCallable(functions, 'plaidDisconnect');
    await fn({});
    if (window.AppState?.tenantData) {
      window.AppState.tenantData.plaidConnected = false;
      window.AppState.tenantData.plaidInstitution = '';
      window.AppState.tenantData.plaidAccountLast4 = '';
    }
    window.showToast?.('Bank disconnected', 'success');
    const bankModal = document.getElementById('bi-bank-modal');
    if (bankModal) bankModal.remove();
  } catch (err) {
    console.error('[Plaid] Disconnect error:', err);
    window.showToast?.('Failed to disconnect: ' + err.message, 'error');
  }
}

// ============================================
// WINDOW MODULE EXPORT
// ============================================
window.BIModule = {
  switchTab,
  openQbBooks, // TASK-9969 S2 — Financial Health QuickBooks row -> QuickBooks setup → Books
  refreshQbBooks, // TASK-9969 S2 — a QuickBooks import marks the transfer summary stale
  isRecognizedCost, // TASK-9726 — the Supplies report's expense nudge uses the P&L's own rule
  changePeriod,
  setPLChargesQuery, // TASK-9917 s4
  markCostPersonal, // TASK-9917 s4
  setRevenueBasis, // TASK-9242
  applyCustomRange,
  showReserveSettings,
  closeReserveSettings,
  saveReserveSettings,
  showLaborBurdenSettings,
  closeLaborBurdenSettings,
  saveLaborBurdenSettings,
  showAddCostModal,
  closeAddCostModal,
  saveCostEntry,
  deleteCostEntry,
  onCostCategoryChange,
  onCostJobChange,
  // Visual BI
  filterVisualCards,
  switchVisualCategory,
  drillDown,
  showPersonAnalytics,
  showVehicleAnalytics,
  showTrailerAnalytics,
  showAssetAnalytics,
  showCustomerAnalytics,
  showVendorAnalytics,
  closeAnalyticsModal,
  editLoanInfo,
  saveLoanInfo,
  closeLoanEdit,
  showLoanStatement,
  cycleCardPeriod,
  // Financing
  editLineOfCredit,
  saveLineOfCredit,
  deleteLineOfCredit,
  // TASK-4866 — read/write the card registry from hr.js WITHOUT a cross-import
  // (pitfall #1). Both views are statically imported by app.js, so
  // window.BIModule is defined before any handler runs.
  upsertCreditCardByName,
  listCreditCards,
  // TASK-4866 — the AP Cards manager's read/write API. Same rule as above: AP
  // reaches these through window.BIModule, never a cross-import (pitfall #1).
  saveCard,
  setCardArchived,
  CARD_TYPES,
  _onLocSubtypeChange,
  editInvestorNote,
  saveInvestorNote,
  deleteInvestorNote,
  // P&L views
  switchPLView,
  setPLDrillBy,
  sortPLTable,
  // CSV exports
  exportPLStatement,
  printPLStatement, // TASK-9701 S2
  exportIncomeStatement,
  exportExpenseReport,
  exportGeneralLedger,
  export1099Summary,
  exportBalanceSheet,
  exportARAgingReport,
  exportAPAgingReport,
  exportPayrollJournalEntries,
  exportBankFeedCSV,
  exportIIF_ChartOfAccounts,
  exportIIF_CostEntries,
  exportIIF_Revenue,
  // Balance sheet
  editBalanceSheetCash,
  manageBankAccounts,
  editBankAccount,
  saveBankAccount,
  deleteBankAccount,
  showBalanceSheetSetup,
  saveBalanceSheetConfig,
  // CPA exports
  exportContractorPaymentSummary,
  exportChartOfAccounts,
  exportDepreciationSchedule,
  // Bank balance (Plaid)
  refreshBankBalance,
  refreshBankModalBalance,
  loadBankTransactions,
  connectBankPlaid,
  disconnectPlaid,
  // Fuel card import
  showFuelCardImport,
  handleFuelCardUpload,
  confirmFuelCardImport,
};

export default { initBI };

function __ddChunkWrap(p){var h=globalThis.__ddChunk;return typeof h==="function"?h(p):p}
